import * as i0 from '@angular/core';
import { Pipe, Directive, HostListener, InjectionToken, NgModule, Injectable, inject, Component, Input, HostBinding, ViewChild, EventEmitter, Output, forwardRef, ElementRef, ViewChildren, ContentChildren, ContentChild, Optional, SecurityContext, Inject, ChangeDetectionStrategy } from '@angular/core';
import * as i2 from '@angular/router';
import { Router, RouterLinkActive, RouterModule, NavigationStart } from '@angular/router';
import * as i2$1 from '@skykick/platform-identity-auth-auth0-angular';
import { AbstractUserProvider, AuthModule } from '@skykick/platform-identity-auth-auth0-angular';
import * as i1 from '@angular/common';
import { DatePipe, CommonModule, DOCUMENT } from '@angular/common';
import { Subject, merge, of, BehaviorSubject, timer, combineLatest, throwError, forkJoin, ReplaySubject, from, Observable, Subscription } from 'rxjs';
import * as i1$1 from '@ng-bootstrap/ng-bootstrap';
import { NgbModule, NgbTooltipModule, NgbPopoverModule, NgbTooltipConfig, NgbDropdownModule, NgbAlertModule, NgbDatepickerModule, NgbModalModule, NgbPopoverConfig } from '@ng-bootstrap/ng-bootstrap';
import * as i2$3 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i2$2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule as FormsModule$1, UntypedFormGroup, UntypedFormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import * as i10 from 'ngx-infinite-scroll';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { debounceTime, distinctUntilChanged, filter, map, tap, share, debounce, switchMap, scan, shareReplay, catchError, takeUntil, take, first } from 'rxjs/operators';
import moment from 'moment';
import * as i5 from 'ngx-toastr';
import { ToastrModule } from 'ngx-toastr';
import * as i1$2 from '@angular/common/http';
import { HttpParams, HttpHeaders } from '@angular/common/http';
import * as i4 from '@skykick/caching';
import { CacheExpiry } from '@skykick/caching';
import { trigger, transition, style, animate, state, AUTO_STYLE } from '@angular/animations';
import * as i4$1 from '@angular/platform-browser';
import * as i9 from 'ngx-mask';
import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';
import cloneDeep from 'lodash-es/cloneDeep';
import { CookieService } from 'ngx-cookie-service';

var AppId;
(function (AppId) {
    AppId["Purchase"] = "purchase";
    AppId["PlatformApis"] = "platform-apis";
    AppId["Portal"] = "portal";
    AppId["Migrate"] = "migrate";
    AppId["Manager"] = "manager";
    AppId["Backup"] = "backup";
    AppId["PartnerCenter"] = "partner-center";
    AppId["Papi"] = "papi";
    AppId["PapiBackup"] = "papi-backup";
    AppId["Syndication"] = "syndication";
    AppId["CloudManager"] = "cloud-manager";
    AppId["CloudSecurity"] = "cloud-security";
    AppId["BillingPortal"] = "billing-portal";
    AppId["VNextMigrate"] = "vnext-migrate";
})(AppId || (AppId = {}));

const SKYKICK_DEFAULT_FORMAT = 'MMM d, yyyy h:mm aa';

var DifferenceRange;
(function (DifferenceRange) {
    DifferenceRange[DifferenceRange["Seconds"] = 0] = "Seconds";
    DifferenceRange[DifferenceRange["Minutes"] = 1] = "Minutes";
    DifferenceRange[DifferenceRange["Hours"] = 2] = "Hours";
    DifferenceRange[DifferenceRange["Days"] = 3] = "Days";
    DifferenceRange[DifferenceRange["Weeks"] = 4] = "Weeks";
    DifferenceRange[DifferenceRange["Months"] = 5] = "Months";
    DifferenceRange[DifferenceRange["Years"] = 6] = "Years";
})(DifferenceRange || (DifferenceRange = {}));
var DifferenceRange$1 = DifferenceRange;

class FutureRelativeDateRenderer {
    constructor(datePipe, date, difference, options) {
        this.datePipe = datePipe;
        this.date = date;
        this.difference = difference;
        this.options = options;
    }
    getSecond() {
        return this.getMinute();
    }
    getMinute() {
        return `in ${Math.ceil(this.difference / 1000 / 60)} min`;
    }
    getHour() {
        return `in ${Math.ceil(this.difference / 1000 / 60 / 60)} h`;
    }
    getDay() {
        return `in ${Math.ceil(this.difference / 1000 / 60 / 60 / 24)} d`;
    }
    getWeek() {
        return this.getMonth();
    }
    getMonth() {
        return this.getYear();
    }
    getYear() {
        return this.datePipe.transform(this.date, this.options.format, this.options.timezone, this.options.locale);
    }
}

class PastRelativeDateRenderer {
    constructor(datePipe, date, difference, options) {
        this.datePipe = datePipe;
        this.date = date;
        this.difference = difference;
        this.options = options;
    }
    getSecond() {
        return 'Just now';
    }
    getMinute() {
        return `${Math.floor(this.difference / 1000 / 60)} min ago`;
    }
    getHour() {
        return `${Math.floor(this.difference / 1000 / 60 / 60)} h ago`;
    }
    getDay() {
        return `${Math.floor(this.difference / 1000 / 60 / 60 / 24)} d ago`;
    }
    getWeek() {
        return `${Math.floor(this.difference / 1000 / 60 / 60 / 24 / 7)} wk ago`;
    }
    getMonth() {
        return this.getYear();
    }
    getYear() {
        return this.datePipe.transform(this.date, this.options.format, this.options.timezone, this.options.locale);
    }
}

class SkyKickDatePipe {
    constructor(datePipe) {
        this.datePipe = datePipe;
    }
    transform(value, format, timezone, locale, minTime) {
        if (value == null) {
            return null;
        }
        const valueDate = new Date(value);
        minTime = minTime || 0;
        this.origin = Date.now();
        const diffInTime = valueDate.getTime() - this.origin;
        const isInFuture = diffInTime > 0 ? true : false;
        const absoluteDiff = Math.abs(diffInTime);
        const range = this.calculateRange(absoluteDiff, minTime);
        const opts = {
            format: format || SKYKICK_DEFAULT_FORMAT,
            timezone,
            locale
        };
        let renderer;
        if (isInFuture) {
            renderer = new FutureRelativeDateRenderer(this.datePipe, valueDate, absoluteDiff, opts);
        }
        else {
            renderer = new PastRelativeDateRenderer(this.datePipe, valueDate, absoluteDiff, opts);
        }
        return this.generateDisplay(range, renderer);
    }
    calculateRange(absoluteDiff, minTime) {
        const MIN = 60 * 1000;
        const HOUR = 60 * MIN;
        const DAY = 24 * HOUR;
        const WEEK = 7 * DAY;
        const MONTH = 4 * WEEK;
        const YEAR = 12 * MONTH;
        if (absoluteDiff < MIN || absoluteDiff < minTime) {
            return DifferenceRange$1.Seconds;
        }
        else if (absoluteDiff < HOUR && absoluteDiff > minTime) {
            return DifferenceRange$1.Minutes;
        }
        else if (absoluteDiff < DAY && absoluteDiff > minTime) {
            return DifferenceRange$1.Hours;
        }
        else if (absoluteDiff < WEEK && absoluteDiff > minTime) {
            return DifferenceRange$1.Days;
        }
        else if (absoluteDiff < MONTH && absoluteDiff > minTime) {
            return DifferenceRange$1.Weeks;
        }
        else if (absoluteDiff < YEAR && absoluteDiff > minTime) {
            return DifferenceRange$1.Months;
        }
        else {
            return DifferenceRange$1.Years;
        }
    }
    generateDisplay(range, renderer) {
        switch (range) {
            case DifferenceRange$1.Seconds:
                return renderer.getSecond();
            case DifferenceRange$1.Minutes:
                return renderer.getMinute();
            case DifferenceRange$1.Hours:
                return renderer.getHour();
            case DifferenceRange$1.Days:
                return renderer.getDay();
            case DifferenceRange$1.Weeks:
                return renderer.getWeek();
            case DifferenceRange$1.Months:
                return renderer.getMonth();
            case DifferenceRange$1.Years:
                return renderer.getYear();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickDatePipe, deps: [{ token: i1.DatePipe }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: SkyKickDatePipe, name: "skdate" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickDatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'skdate'
                }]
        }], ctorParameters: () => [{ type: i1.DatePipe }] });

class DedupePipe {
    transform(items, other, deduper) {
        if (!items) {
            return [];
        }
        if (!other) {
            other = [];
        }
        if (!deduper) {
            return items.filter(i => !other.includes(i));
        }
        else if (typeof deduper === 'string') {
            // If deduper is a string, we can parse it assuming
            // its dot notation and find the nested properties on the objects
            const props = deduper.split('.');
            return items.filter(item => {
                let found = false;
                other.forEach(duped => {
                    if (found) {
                        return;
                    }
                    const a = this.findProp(item, props);
                    const b = this.findProp(duped, props);
                    found = (a === b);
                });
                return !found;
            });
        }
        else {
            // deduper is a lambda that can just be used directly to access the value on the model
            return items.filter(item => {
                let found = false;
                other.forEach(duped => {
                    if (found) {
                        return;
                    }
                    const a = deduper(item);
                    const b = deduper(duped);
                    found = (a === b);
                });
                return !found;
            });
        }
    }
    findProp(object, props, index) {
        if (index == null) {
            index = 0;
        }
        if (typeof object !== 'object') {
            throw new Error('Expecting an object, but got a primitive');
        }
        // If this is the final prop, return that value
        if (index === props.length - 1) {
            return object[props[index]];
        }
        else {
            return this.findProp(object[props[index]], props, index + 1);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DedupePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: DedupePipe, name: "dedupe", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DedupePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dedupe',
                    pure: false
                }]
        }] });

/**
 * This Pipe allows you to filter a list of items that containers objects, numbers, or strings.  If the objects
 * being filtered on have nested objects then those nexted objects are NOT searched. We will need additional logic
 * to recursively check for values in a nested object. This is generally a bad idea as you could run into
 *  self-referencing objects or just large objects that would reduce efficiency
 */
class FilterPipe {
    transform(items, searchText) {
        if (!items) {
            return [];
        }
        else if (!searchText) {
            return items;
        }
        switch (typeof items[0]) {
            case 'object':
                return items.filter(item => {
                    return Object.keys(item).some(k => {
                        if (!!item[k] && typeof item[k] === 'string') {
                            return item[k].toLowerCase().includes(searchText.toLowerCase());
                        }
                    });
                });
            case 'number':
                return items.filter(i => i === parseInt(searchText, 10));
            case 'string':
                return items.filter(i => i.includes(searchText));
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: FilterPipe, name: "filter" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'filter'
                }]
        }] });

class HumanizePipe {
    transform(value) {
        if ((typeof value) !== 'string') {
            return value;
        }
        value = value.split(/(?=[A-Z])/).join(' ');
        value = value[0].toUpperCase() + value.slice(1);
        return value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HumanizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: HumanizePipe, name: "humanize" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HumanizePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'humanize' }]
        }] });

class StopPropagationDirective {
    constructor() {
    }
    onClick(e) {
        e.stopPropagation();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: StopPropagationDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: StopPropagationDirective, selector: "[skStopPropagation]", host: { listeners: { "click": "onClick($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: StopPropagationDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[skStopPropagation]'
                }]
        }], ctorParameters: () => [], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

const SEWR_SYSTEM_DOMAIN = new InjectionToken('Base URL for SkyKick Sewr System.');
class UtilityModule {
    static forRoot(utilityConfig) {
        return {
            ngModule: UtilityModule,
            providers: [
                DatePipe,
                SkyKickDatePipe,
                { provide: SEWR_SYSTEM_DOMAIN, useValue: utilityConfig }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UtilityModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: UtilityModule, declarations: [DedupePipe,
            FilterPipe,
            HumanizePipe,
            SkyKickDatePipe,
            StopPropagationDirective], imports: [CommonModule], exports: [DedupePipe,
            FilterPipe,
            HumanizePipe,
            SkyKickDatePipe,
            StopPropagationDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UtilityModule, providers: [
            DatePipe,
            SkyKickDatePipe,
            { provide: SEWR_SYSTEM_DOMAIN, useValue: null }
        ], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UtilityModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DedupePipe,
                        FilterPipe,
                        HumanizePipe,
                        SkyKickDatePipe,
                        StopPropagationDirective
                    ],
                    imports: [
                        CommonModule,
                    ],
                    exports: [
                        DedupePipe,
                        FilterPipe,
                        HumanizePipe,
                        SkyKickDatePipe,
                        StopPropagationDirective
                    ],
                    providers: [
                        DatePipe,
                        SkyKickDatePipe,
                        { provide: SEWR_SYSTEM_DOMAIN, useValue: null }
                    ]
                }]
        }] });

class RouteResolverService {
    constructor(injector) {
        this.injector = injector;
        const sewrSystemConfig = injector.get(SEWR_SYSTEM_DOMAIN);
        if (sewrSystemConfig) {
            this.sewrSystemDomain = sewrSystemConfig.sewrSystemDomain;
        }
        else {
            this.sewrSystemDomain = `https://route.skykick${this.parseTopLevelDomain()}`;
        }
    }
    generatePipeRoute(appId, slug = '/') {
        return `${this.sewrSystemDomain}/pipe/${appId}/${this.normalizeSlug(slug)}`;
    }
    generateRatRoute(appId, slug = '/') {
        return `${this.sewrSystemDomain}/rat/${appId}/${this.normalizeSlug(slug)}`;
    }
    parseTopLevelDomain() {
        const matches = document.location.hostname.match(/\.(dev|com)/);
        return matches ? matches[0] : '.com';
    }
    normalizeSlug(slug) {
        return slug.startsWith('/') ? slug.substring(1) : slug;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: RouteResolverService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: RouteResolverService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: RouteResolverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i0.Injector }] });

/**
 * SkyKick Areas that are supported for left hand navigation
 */
var NavigationArea;
(function (NavigationArea) {
    NavigationArea["Home"] = "Home";
    NavigationArea["Migrations"] = "Cloud Migration";
    NavigationArea["CloudBackup"] = "Cloud Backup";
    NavigationArea["CloudManager"] = "Cloud Automation";
    NavigationArea["CloudSecurity"] = "SaaS Security";
    NavigationArea["BrandedServices"] = "Branded Services";
    NavigationArea["Developer"] = "Developer";
    NavigationArea["Communications"] = "Communications";
    NavigationArea["Billing"] = "Billing";
    NavigationArea["DNS"] = "DNS";
    NavigationArea["Admin"] = "Admin";
    NavigationArea["DeliveryAudit"] = "Delivery Audit";
    NavigationArea["SuppressionList"] = "Suppression List";
    NavigationArea["PlaceholderData"] = "Placeholder Data";
    NavigationArea["Categories"] = "Categories";
})(NavigationArea || (NavigationArea = {}));

/**
 * Indicates available access types for a navigation link
 */
var NavigationPermission;
(function (NavigationPermission) {
    /**
     * Navigation item is displayed if user has read access to a SkyKickArea
     */
    NavigationPermission[NavigationPermission["Read"] = 0] = "Read";
    /**
     * Navigation item is displayed only if user has create access to a SkyKickArea
     */
    NavigationPermission[NavigationPermission["ReadWrite"] = 1] = "ReadWrite";
    /**
     * Navigation item is displayed only if user is an Admin
     */
    NavigationPermission[NavigationPermission["Admin"] = 2] = "Admin";
    /**
     * Navigation item is displayed only if user has full access to a SkyKickArea
     */
    NavigationPermission[NavigationPermission["FullAccess"] = 3] = "FullAccess";
})(NavigationPermission || (NavigationPermission = {}));

const navAreaLegacyPermissionName = new Map([
    [NavigationArea.Home, "home"],
    [NavigationArea.Migrations, "Migrations"],
    [NavigationArea.CloudBackup, "Cloud_Backup"],
    [NavigationArea.CloudManager, "manager"],
    // Cloud Security does not have it's own privileges (yet), so Cloud Manager permission should be checked instead.
    [NavigationArea.CloudSecurity, "manager"],
    [NavigationArea.BrandedServices, "Cloud_Backup"],
    [NavigationArea.Developer, "developer"],
    [NavigationArea.Communications, "Migrations"],
    [NavigationArea.DNS, "Migrations"],
    [NavigationArea.Billing, "Billing"],
    [NavigationArea.Admin, "admin"]
]);
const navAreaLegacyPermissionType = new Map([
    [NavigationPermission.Read, "view"],
    [NavigationPermission.ReadWrite, "create"],
    [NavigationPermission.Admin, "editall"],
    [NavigationPermission.FullAccess, "Full_Access"]
]);
const getFlattenedPermissionName = (navPermissionsArea, navPermissionsType) => {
    const permissionName = navAreaLegacyPermissionName.get(navPermissionsArea);
    if (permissionName) {
        return getCustomFlattenedPermissionName(permissionName, navPermissionsType);
    }
    return '';
};
const getCustomFlattenedPermissionName = (navPermissionsArea, navPermissionsType) => {
    const permissionScope = navAreaLegacyPermissionType.get(navPermissionsType);
    if (permissionScope) {
        return `${navPermissionsArea}_${permissionScope}`;
    }
    return '';
};

/**
 * To make this guard work as expected navigationGuardData property of NavigationGuardData type should be specified for route.data
 */
const NavigationGuard = (route) => {
    const { navigationGuardData } = route.data;
    const { mandatoryPermissions, optionalPermissions, permissionAbsentRedirect, isPrimaryNavigation } = navigationGuardData;
    const userProvider = inject(AbstractUserProvider);
    const routeResolverService = inject(RouteResolverService);
    const router = inject(Router);
    const user = userProvider.getCurrentUser();
    let hasAccess = false;
    if (mandatoryPermissions?.length > 0) {
        hasAccess = mandatoryPermissions.every((p) => user.hasPermission(p));
    }
    if (optionalPermissions?.length > 0) {
        hasAccess = optionalPermissions.some((p) => user.hasPermission(p));
    }
    if (hasAccess) {
        return true;
    }
    if (isPrimaryNavigation) {
        if (user.hasPermission(getFlattenedPermissionName(NavigationArea.CloudBackup, NavigationPermission.FullAccess))) {
            window.location.href = routeResolverService.generateRatRoute(AppId.Backup);
        }
        else if (user.hasPermission(getFlattenedPermissionName(NavigationArea.Migrations, NavigationPermission.FullAccess))) {
            window.location.href = routeResolverService.generateRatRoute(AppId.Migrate);
        }
        else if (user.hasPermission(getFlattenedPermissionName(NavigationArea.CloudManager, NavigationPermission.Read))) {
            window.location.href = routeResolverService.generateRatRoute(AppId.Manager);
        }
        else if (user.hasPermission(getFlattenedPermissionName(NavigationArea.Admin, NavigationPermission.Read))) {
            router.navigate(['settings']);
        }
        else {
            router.navigate(['login']);
        }
    }
    else {
        router.navigate([permissionAbsentRedirect]);
    }
    return false;
};

/**
 * Data needed for navigation guard to work properly
 */
class NavigationGuardData {
}

/**
 * Common platform navigation menu item
 */
class NavigationItem {
    constructor() {
        /**
         * Indicates who can access the link
         *
         * All navigation links will default to "Read" permission
         *
         * @remarks
         * Multiple access types can be supported in future
         */
        this.permissions = NavigationPermission.Read;
    }
}
/**
 * Primary navigation menu item
 */
class PrimaryNavigationItem extends NavigationItem {
}
/**
 * Custom icon used in navigation menu
 */
class CustomIcon {
}

class PlatformNavigationService {
    constructor() {
        this.collapseNavSource = new Subject();
        this.collapseNav$ = this.collapseNavSource.asObservable();
    }
    /**
     * Collapse the left hand navigation
     */
    collapseNav() {
        this.collapseNavSource.next();
    }
    /**
     * This event handler will fire when a collapse event
     * is triggered from somewhere else in the application
     * @param handler - the callback function to run when collapse is emitted
     */
    onCollapse(handler) {
        return this.collapseNav$.subscribe(handler);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PlatformNavigationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PlatformNavigationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PlatformNavigationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class PlatformNavigationPrimaryComponent {
    get active() {
        return this.IsActive;
    }
    get currentCustomIcon() {
        if (!this.CustomIcon) {
            return null;
        }
        if (this.IsOpen) {
            return this.CustomIcon.activeUrl;
        }
        return this.isMouseOver ? this.CustomIcon.hoverUrl : this.CustomIcon.defaultUrl;
    }
    constructor() {
        this.IsOpen = false;
        this.IsActive = false;
        this.OpenLinkInNewTab = false;
        this.hasNewBadge = false;
        this.isMouseOver = false;
    }
    ngOnInit() {
    }
    toggle() {
        this.IsOpen = !this.IsOpen;
        this.IsActive = !this.OpenLinkInNewTab;
    }
    onMouseOver() {
        this.isMouseOver = true;
    }
    onMouseOut() {
        this.isMouseOver = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PlatformNavigationPrimaryComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PlatformNavigationPrimaryComponent, selector: "[sk-platform-navigation-primary]", inputs: { Icon: "Icon", CustomIcon: "CustomIcon", Label: "Label", Link: "Link", IsOpen: "IsOpen", IsActive: "IsActive", OpenLinkInNewTab: "OpenLinkInNewTab", hasNewBadge: "hasNewBadge" }, host: { properties: { "class.active": "this.active" }, classAttribute: "panel" }, ngImport: i0, template: "<li class=\"nav-item active-nav-parent\" (click)=\"toggle()\" (mouseover)=\"onMouseOver()\" (mouseout)=\"onMouseOut()\">\r\n    <a class=\"nav-link sk-primary-parent\" [href]=\"Link\" [ngClass]=\"{ 'collapsed' : !IsOpen, 'active' : IsActive }\"\r\n        [target]=\"OpenLinkInNewTab ? '_blank' : '_self'\">\r\n        <i *ngIf=\"Icon\" class=\"material-icons md-22\">{{ Icon }}</i>\r\n        <img *ngIf=\"CustomIcon\" [src]=\"currentCustomIcon\" class=\"custom-icon\" />\r\n        <span>\r\n            <div class=\"d-flex align-items-center\">\r\n                <div class=\"d-inline text-nowrap\" [innerHTML]=\"Label\"></div>\r\n                <span *ngIf=\"hasNewBadge\" class=\"badge badge-info ms-1 pl-50\" style=\"font-size: 11px;\">NEW</span>\r\n                <i *ngIf=\"OpenLinkInNewTab\" class=\"material-icons md-16 icon-muted pl-50\">open_in_new</i>\r\n            </div>\r\n        </span>\r\n    </a>\r\n</li>\r\n\r\n<div class=\"panel-collapse collapse\" [ngClass]=\"{ 'show' : IsOpen }\">\r\n    <ul class=\"nav sk-collapse-nav\">\r\n        <ng-content></ng-content>\r\n    </ul>\r\n</div>\r\n", styles: [".custom-icon{width:22px!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PlatformNavigationPrimaryComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-platform-navigation-primary]', host: {
                        class: 'panel'
                    }, template: "<li class=\"nav-item active-nav-parent\" (click)=\"toggle()\" (mouseover)=\"onMouseOver()\" (mouseout)=\"onMouseOut()\">\r\n    <a class=\"nav-link sk-primary-parent\" [href]=\"Link\" [ngClass]=\"{ 'collapsed' : !IsOpen, 'active' : IsActive }\"\r\n        [target]=\"OpenLinkInNewTab ? '_blank' : '_self'\">\r\n        <i *ngIf=\"Icon\" class=\"material-icons md-22\">{{ Icon }}</i>\r\n        <img *ngIf=\"CustomIcon\" [src]=\"currentCustomIcon\" class=\"custom-icon\" />\r\n        <span>\r\n            <div class=\"d-flex align-items-center\">\r\n                <div class=\"d-inline text-nowrap\" [innerHTML]=\"Label\"></div>\r\n                <span *ngIf=\"hasNewBadge\" class=\"badge badge-info ms-1 pl-50\" style=\"font-size: 11px;\">NEW</span>\r\n                <i *ngIf=\"OpenLinkInNewTab\" class=\"material-icons md-16 icon-muted pl-50\">open_in_new</i>\r\n            </div>\r\n        </span>\r\n    </a>\r\n</li>\r\n\r\n<div class=\"panel-collapse collapse\" [ngClass]=\"{ 'show' : IsOpen }\">\r\n    <ul class=\"nav sk-collapse-nav\">\r\n        <ng-content></ng-content>\r\n    </ul>\r\n</div>\r\n", styles: [".custom-icon{width:22px!important}\n"] }]
        }], ctorParameters: () => [], propDecorators: { Icon: [{
                type: Input
            }], CustomIcon: [{
                type: Input
            }], Label: [{
                type: Input
            }], Link: [{
                type: Input
            }], IsOpen: [{
                type: Input
            }], IsActive: [{
                type: Input
            }], OpenLinkInNewTab: [{
                type: Input
            }], hasNewBadge: [{
                type: Input
            }], active: [{
                type: HostBinding,
                args: ['class.active']
            }] } });

class PlatformNavigationSecondaryComponent {
    get currentCustomIcon() {
        if (!this.CustomIcon) {
            return null;
        }
        if (this.routerLinkActive?.isActive) {
            return this.CustomIcon.activeUrl;
        }
        return this.isMouseOver ? this.CustomIcon.hoverUrl : this.CustomIcon.defaultUrl;
    }
    constructor() {
        this.hasNewBadge = false;
        this.isUrlLink = false;
        this.isMouseOver = false;
    }
    ngOnInit() {
        if (this.Link.indexOf('http') !== -1) {
            this.isUrlLink = true;
        }
    }
    onMouseOver() {
        this.isMouseOver = true;
    }
    onMouseOut() {
        this.isMouseOver = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PlatformNavigationSecondaryComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PlatformNavigationSecondaryComponent, selector: "[sk-platform-navigation-secondary]", inputs: { Icon: "Icon", CustomIcon: "CustomIcon", Link: "Link", Label: "Label", OpenLinkInNewTab: "OpenLinkInNewTab", hasNewBadge: "hasNewBadge" }, host: { classAttribute: "nav-item active-nav-child" }, viewQueries: [{ propertyName: "routerLinkActive", first: true, predicate: RouterLinkActive, descendants: true }], ngImport: i0, template: "<div class=\"d-flex align-items-center\" (mouseover)=\"onMouseOver()\" (mouseout)=\"onMouseOut()\">\r\n        <a *ngIf=\"!isUrlLink\" class=\"nav-link p-0\" [routerLink]=\"Link\" routerLinkActive=\"active\" [target]=\"OpenLinkInNewTab ? '_blank' : '_self'\">\r\n            <div style=\"width: 2rem\">\r\n                <i *ngIf=\"Icon\" class=\"material-icons md-22 px-3 pt-1\">{{ Icon }}</i>\r\n                <img *ngIf=\"CustomIcon\" class=\"px-3\" [src]=\"currentCustomIcon\" />\r\n            </div>\r\n            <span class=\"d-inline text-nowrap ps-4\" [innerHTML]=\"Label\"></span>\r\n        </a>\r\n        <a *ngIf=\"isUrlLink\" class=\"nav-link\" [href]=\"Link\" [innerHTML]=\"Label\" [target]=\"OpenLinkInNewTab ? '_blank' : '_self'\">\r\n            <div style=\"width: 2rem\">\r\n                <i *ngIf=\"Icon\" class=\"material-icons md-22 px-3 pt-1\">{{ Icon }}</i>\r\n                <img *ngIf=\"CustomIcon\" class=\"px-3\" [src]=\"currentCustomIcon\" />\r\n            </div>\r\n            <span class=\"d-inline text-nowrap ps-4\" [innerHTML]=\"Label\"></span>\r\n        </a>\r\n    <span *ngIf=\"hasNewBadge\" class=\"badge badge-info ms-1 pl-50\" style=\"font-size: 11px;\">NEW</span>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i2.RouterLinkActive, selector: "[routerLinkActive]", inputs: ["routerLinkActiveOptions", "ariaCurrentWhenActive", "routerLinkActive"], outputs: ["isActiveChange"], exportAs: ["routerLinkActive"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PlatformNavigationSecondaryComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-platform-navigation-secondary]', host: {
                        class: 'nav-item active-nav-child'
                    }, template: "<div class=\"d-flex align-items-center\" (mouseover)=\"onMouseOver()\" (mouseout)=\"onMouseOut()\">\r\n        <a *ngIf=\"!isUrlLink\" class=\"nav-link p-0\" [routerLink]=\"Link\" routerLinkActive=\"active\" [target]=\"OpenLinkInNewTab ? '_blank' : '_self'\">\r\n            <div style=\"width: 2rem\">\r\n                <i *ngIf=\"Icon\" class=\"material-icons md-22 px-3 pt-1\">{{ Icon }}</i>\r\n                <img *ngIf=\"CustomIcon\" class=\"px-3\" [src]=\"currentCustomIcon\" />\r\n            </div>\r\n            <span class=\"d-inline text-nowrap ps-4\" [innerHTML]=\"Label\"></span>\r\n        </a>\r\n        <a *ngIf=\"isUrlLink\" class=\"nav-link\" [href]=\"Link\" [innerHTML]=\"Label\" [target]=\"OpenLinkInNewTab ? '_blank' : '_self'\">\r\n            <div style=\"width: 2rem\">\r\n                <i *ngIf=\"Icon\" class=\"material-icons md-22 px-3 pt-1\">{{ Icon }}</i>\r\n                <img *ngIf=\"CustomIcon\" class=\"px-3\" [src]=\"currentCustomIcon\" />\r\n            </div>\r\n            <span class=\"d-inline text-nowrap ps-4\" [innerHTML]=\"Label\"></span>\r\n        </a>\r\n    <span *ngIf=\"hasNewBadge\" class=\"badge badge-info ms-1 pl-50\" style=\"font-size: 11px;\">NEW</span>\r\n</div>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { Icon: [{
                type: Input
            }], CustomIcon: [{
                type: Input
            }], Link: [{
                type: Input
            }], Label: [{
                type: Input
            }], OpenLinkInNewTab: [{
                type: Input
            }], hasNewBadge: [{
                type: Input
            }], routerLinkActive: [{
                type: ViewChild,
                args: [RouterLinkActive]
            }] } });

/**
* Common left-hand navigation
* Every website has the option to override both primary and secondary navigation menu in their
* SkyKick area.
*
* Example usage:
*
* -In app.component.html
*     <nav sk-navigation>
      </nav>
* -In app.component.ts
  export class AppComponent implements OnInit {
        @ViewChild(NavigationComponent, { static: false })
        public skNavigation: NavigationComponent;

        ngAfterViewInit(): void {
            // Required to supply a navigation area
            this.skNavigation.SkNavigationArea = NavigationArea.CloudBackup;

            // Override primary navigation links
            this.skNavigation.SubNavigation = [
            {
                displayName: 'Foo',
                url: "/backup/foo"
            },
            {
                displayName: 'Bar',
                url: "/backup/bar"
            }];

            // Override secondary navigation links
            this.skNavigation.SubNavigation = [
            {
                displayName: 'Foo',
                url: "/backup/foo"
            },
            {
                displayName: 'Bar',
                url: "/backup/bar"
            }];
        }
  }
*/
class NavigationComponent {
    get SkNavigationArea() {
        return this.navigationArea;
    }
    set SkNavigationArea(value) {
        this.navigationArea = value;
        this.changeDetectorRef.detectChanges();
    }
    get PrimaryNavigation() {
        return this.primaryNavigation;
    }
    set PrimaryNavigation(value) {
        this.primaryNavigation = value;
        this.changeDetectorRef.detectChanges();
    }
    get SubNavigation() {
        return this.subNavigation;
    }
    set SubNavigation(value) {
        this.subNavigation = value;
        this.changeDetectorRef.detectChanges();
    }
    constructor(platformNavigationService, changeDetectorRef) {
        this.platformNavigationService = platformNavigationService;
        this.changeDetectorRef = changeDetectorRef;
        this.IsCollapsed = false;
        /**
         * Each microsite needs to supply a navigation area
         */
        this.navigationArea = NavigationArea.Home;
        /**
         * Microsites can supply primary navigation links
         */
        this.primaryNavigation = [];
        /**
         * Microsites can supply secondary navigation links
         */
        this.subNavigation = [];
    }
    ngOnInit() {
        this.subscription = this.platformNavigationService.onCollapse(() => this.collapse());
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    toggle() {
        this.IsCollapsed = !this.IsCollapsed;
    }
    collapse() {
        this.IsCollapsed = true;
    }
    isNavigationLinkActive(navItem) {
        return navItem.area === this.SkNavigationArea && !navItem.openInNewTab;
    }
    formatDisplayNameForId(navItemDisplayName) {
        return navItemDisplayName.replace(/ /g, "-").toLowerCase() + "-nav-menu-link";
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NavigationComponent, deps: [{ token: PlatformNavigationService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NavigationComponent, selector: "[sk-navigation]", inputs: { SkNavigationArea: "SkNavigationArea", PrimaryNavigation: "PrimaryNavigation", SubNavigation: "SubNavigation" }, host: { properties: { "class.collapsed": "this.IsCollapsed" }, classAttribute: "sk-left-pane" }, ngImport: i0, template: "<!-- Accordion -->\r\n<ul id=\"site-nav-bar\" class=\"nav sk-nav-platform\">\r\n    <div id=\"{{formatDisplayNameForId(item.displayName)}}\" *ngFor=\"let item of PrimaryNavigation\" sk-platform-navigation-primary [Label]=\"item.displayName\"\r\n        [Icon]=\"item.icon\" [IsActive]=\"isNavigationLinkActive(item)\" [IsOpen]=\"isNavigationLinkActive(item)\"\r\n        [Link]=\"item.url\" [OpenLinkInNewTab]=\"item.openInNewTab\" [hasNewBadge]=\"item.hasNewBadge\">\r\n        <ng-container *ngIf=\"isNavigationLinkActive(item)\">\r\n            <li\r\n                id=\"{{formatDisplayNameForId(subNavItem.displayName)}}\"\r\n                sk-platform-navigation-secondary\r\n                *ngFor=\"let subNavItem of SubNavigation\"\r\n                [Label]=\"subNavItem.displayName\"\r\n                [Link]=\"subNavItem.url\"\r\n                [OpenLinkInNewTab]=\"subNavItem.openInNewTab\"\r\n                [hasNewBadge]=\"subNavItem.hasNewBadge\"\r\n                [Icon]=\"subNavItem.icon\"\r\n                [CustomIcon]=\"subNavItem.customIcon\">\r\n            </li>\r\n        </ng-container>\r\n    </div>\r\n</ul>\r\n\r\n<div class=\"sk-nav-footer\">\r\n    <button id=\"navExpandCollapse\" type=\"button\" class=\"btn sk-btn-icon\" (click)=\"toggle()\">\r\n        <i class=\"material-icons md-24 expand-collapse-icon\"></i>\r\n    </button>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: PlatformNavigationPrimaryComponent, selector: "[sk-platform-navigation-primary]", inputs: ["Icon", "CustomIcon", "Label", "Link", "IsOpen", "IsActive", "OpenLinkInNewTab", "hasNewBadge"] }, { kind: "component", type: PlatformNavigationSecondaryComponent, selector: "[sk-platform-navigation-secondary]", inputs: ["Icon", "CustomIcon", "Link", "Label", "OpenLinkInNewTab", "hasNewBadge"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NavigationComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-navigation]', host: {
                        class: 'sk-left-pane'
                    }, template: "<!-- Accordion -->\r\n<ul id=\"site-nav-bar\" class=\"nav sk-nav-platform\">\r\n    <div id=\"{{formatDisplayNameForId(item.displayName)}}\" *ngFor=\"let item of PrimaryNavigation\" sk-platform-navigation-primary [Label]=\"item.displayName\"\r\n        [Icon]=\"item.icon\" [IsActive]=\"isNavigationLinkActive(item)\" [IsOpen]=\"isNavigationLinkActive(item)\"\r\n        [Link]=\"item.url\" [OpenLinkInNewTab]=\"item.openInNewTab\" [hasNewBadge]=\"item.hasNewBadge\">\r\n        <ng-container *ngIf=\"isNavigationLinkActive(item)\">\r\n            <li\r\n                id=\"{{formatDisplayNameForId(subNavItem.displayName)}}\"\r\n                sk-platform-navigation-secondary\r\n                *ngFor=\"let subNavItem of SubNavigation\"\r\n                [Label]=\"subNavItem.displayName\"\r\n                [Link]=\"subNavItem.url\"\r\n                [OpenLinkInNewTab]=\"subNavItem.openInNewTab\"\r\n                [hasNewBadge]=\"subNavItem.hasNewBadge\"\r\n                [Icon]=\"subNavItem.icon\"\r\n                [CustomIcon]=\"subNavItem.customIcon\">\r\n            </li>\r\n        </ng-container>\r\n    </div>\r\n</ul>\r\n\r\n<div class=\"sk-nav-footer\">\r\n    <button id=\"navExpandCollapse\" type=\"button\" class=\"btn sk-btn-icon\" (click)=\"toggle()\">\r\n        <i class=\"material-icons md-24 expand-collapse-icon\"></i>\r\n    </button>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: PlatformNavigationService }, { type: i0.ChangeDetectorRef }], propDecorators: { IsCollapsed: [{
                type: HostBinding,
                args: ['class.collapsed']
            }], SkNavigationArea: [{
                type: Input
            }], PrimaryNavigation: [{
                type: Input
            }], SubNavigation: [{
                type: Input
            }] } });

/**
 * Default left hand navigation
 */
class NavigationItemsProvider {
    constructor(resolverService) {
        this.resolverService = resolverService;
        this.navigationItems = [
            {
                displayName: NavigationArea.Home,
                url: this.resolverService.generateRatRoute(AppId.Portal),
                icon: 'home',
                permissions: NavigationPermission.Read,
                area: NavigationArea.Home
            },
            {
                displayName: NavigationArea.Migrations,
                url: this.resolverService.generateRatRoute(AppId.VNextMigrate),
                icon: 'cloud_upload',
                permissions: NavigationPermission.FullAccess,
                area: NavigationArea.Migrations
            },
            {
                displayName: NavigationArea.CloudBackup,
                url: this.resolverService.generateRatRoute(AppId.Backup),
                icon: 'restore',
                permissions: NavigationPermission.FullAccess,
                area: NavigationArea.CloudBackup
            },
            {
                displayName: NavigationArea.CloudSecurity,
                url: this.resolverService.generateRatRoute(AppId.CloudSecurity),
                customIcon: {
                    defaultUrl: 'https://skykick.azureedge.net/product/cloud-security/assets/icon-default.svg',
                    activeUrl: 'https://skykick.azureedge.net/product/cloud-security/assets/icon-active.svg',
                    hoverUrl: 'https://skykick.azureedge.net/product/cloud-security/assets/icon-hover.svg',
                },
                permissions: NavigationPermission.Read,
                area: NavigationArea.CloudSecurity
            },
            {
                displayName: NavigationArea.CloudManager,
                url: this.resolverService.generateRatRoute(AppId.CloudManager),
                icon: 'cloud_done',
                permissions: NavigationPermission.Read,
                area: NavigationArea.CloudManager
            },
            {
                displayName: NavigationArea.BrandedServices,
                url: this.resolverService.generateRatRoute(AppId.Syndication),
                icon: 'share',
                permissions: NavigationPermission.FullAccess,
                area: NavigationArea.BrandedServices
            },
            {
                displayName: NavigationArea.Developer,
                url: this.resolverService.generateRatRoute(AppId.Portal, 'partner/developer'),
                icon: 'code',
                permissions: NavigationPermission.Read,
                area: NavigationArea.Developer
            },
            {
                displayName: NavigationArea.DNS,
                url: this.resolverService.generateRatRoute(AppId.Portal, 'partner/dns-manager'),
                icon: 'dns',
                permissions: NavigationPermission.FullAccess,
                area: NavigationArea.DNS
            },
            {
                displayName: NavigationArea.Billing,
                url: this.resolverService.generateRatRoute(AppId.BillingPortal),
                icon: 'assignment',
                permissions: NavigationPermission.FullAccess,
                area: NavigationArea.Billing
            },
            {
                displayName: "Settings",
                url: this.resolverService.generateRatRoute(AppId.Portal, 'settings/users/members'),
                icon: 'settings',
                permissions: NavigationPermission.Read,
                area: NavigationArea.Admin
            }
        ];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NavigationItemsProvider, deps: [{ token: RouteResolverService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NavigationItemsProvider }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NavigationItemsProvider, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: RouteResolverService }] });

const RESTRICTED_AREAS_FOR_CUSTOMERS = [
    NavigationArea.BrandedServices
];
/**
 * Service to determine what navigation items can be displayed for a user
 */
class NavigationPermissionsService {
    constructor(userProvider) {
        this.userProvider = userProvider;
        this.user = this.userProvider.getCurrentUser();
    }
    canAccessNavigationItem(navArea, navPermission) {
        if (this.user.isCustomer && RESTRICTED_AREAS_FOR_CUSTOMERS.includes(navArea)) {
            return false;
        }
        if (!this.user.isDnsManagerEnabled && navArea === NavigationArea.DNS) {
            return false;
        }
        const flattenedPermission = getFlattenedPermissionName(navArea, navPermission);
        if (flattenedPermission !== '') {
            return this.user.hasPermission(flattenedPermission);
        }
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NavigationPermissionsService, deps: [{ token: i2$1.AbstractUserProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NavigationPermissionsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NavigationPermissionsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i2$1.AbstractUserProvider }] });

/**
 * Common left-hand navigation
 * By default all primary navigation links will be displayed by any SkyKick area website using
 * the component. Every website has the option to override the secondary navigation menu in their
 * SkyKick area. The primary navigation and secondary navigation menu items will be displayed
 * based on the permissions (Read, ReadWrite and Admin) defined on the menu item.
 *
 * Example usage:
 *
 * -In app.component.html
 *     <nav sk-platform-navigation>
        </nav>
 * -In app.component.ts
    export class AppComponent implements OnInit {
          @ViewChild(PlatformNavigationComponent, { static: false })
          public skPlatformNavigation: PlatformNavigationComponent;

          ngAfterViewInit(): void {
              // Required to supply a navigation area
              this.skPlatformNavigation.SkNavigationArea = NavigationArea.CloudBackup;

              // Override secondary navigation links
              this.skPlatformNavigation.SubNavigation = [
              {
                  displayName: 'Foo',
                  url: "/backup/foo",
                  permissions: NavigationPermission.Read
              },
              {
                  displayName: 'Bar',
                  url: "/backup/bar",
                  permissions: NavigationPermission.ReadWrite
              }];
          }
    }
 */
class PlatformNavigationComponent {
    get SkNavigationArea() {
        return this.navigationArea;
    }
    set SkNavigationArea(value) {
        this.navigationArea = value;
        this.changeDetectorRef.detectChanges();
    }
    get SubNavigation() {
        return this.subNavigation;
    }
    set SubNavigation(value) {
        this.subNavigation = value;
        this.changeDetectorRef.detectChanges();
    }
    constructor(platformNavigationService, changeDetectorRef, navigationPermissionService, navigationItemProvider) {
        this.platformNavigationService = platformNavigationService;
        this.changeDetectorRef = changeDetectorRef;
        this.navigationPermissionService = navigationPermissionService;
        this.navigationItemProvider = navigationItemProvider;
        this.IsCollapsed = false;
        /**
         * Each microsite needs to supply a navigation area
         */
        this.navigationArea = NavigationArea.Home;
        /**
         * Microsites can supply secondary navigation links
         */
        this.subNavigation = [];
    }
    ngOnInit() {
        this.subscription = this.platformNavigationService.onCollapse(() => this.collapse());
        this.navigationItems = this.navigationItemProvider.navigationItems;
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    toggle() {
        this.IsCollapsed = !this.IsCollapsed;
    }
    collapse() {
        this.IsCollapsed = true;
    }
    isNavigationLinkActive(navItem) {
        return navItem.area === this.SkNavigationArea && !navItem.openInNewTab;
    }
    hasAccess(navLinkArea, navLinkPermissions) {
        return this.navigationPermissionService.canAccessNavigationItem(navLinkArea, navLinkPermissions);
    }
    formatDisplayNameForId(navItemDisplayName) {
        return navItemDisplayName.replace(/ /g, "-").toLowerCase() + "-nav-menu-link";
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PlatformNavigationComponent, deps: [{ token: PlatformNavigationService }, { token: i0.ChangeDetectorRef }, { token: NavigationPermissionsService }, { token: NavigationItemsProvider }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PlatformNavigationComponent, selector: "[sk-platform-navigation]", inputs: { SkNavigationArea: "SkNavigationArea", SubNavigation: "SubNavigation" }, host: { properties: { "class.collapsed": "this.IsCollapsed" }, classAttribute: "sk-left-pane" }, providers: [NavigationItemsProvider], ngImport: i0, template: "<!-- Accordion -->\r\n<ul id=\"site-nav-bar\" class=\"nav sk-nav-platform\">\r\n    <div\r\n        id=\"{{formatDisplayNameForId(item.displayName)}}\"\r\n        *ngFor=\"let item of navigationItems\"\r\n        sk-platform-navigation-primary\r\n        [Label]=\"item.displayName\"\r\n        [Icon]=\"item.icon\"\r\n        [CustomIcon]=\"item.customIcon\"\r\n        [IsActive]=\"isNavigationLinkActive(item)\"\r\n        [IsOpen]=\"isNavigationLinkActive(item)\"\r\n        [Link]=\"item.url\"\r\n        [OpenLinkInNewTab]=\"item.openInNewTab\"\r\n        [hidden]=\"!hasAccess(item.area, item.permissions)\"\r\n        [hasNewBadge]=\"item.hasNewBadge\">\r\n        <ng-container *ngIf=\"isNavigationLinkActive(item)\">\r\n            <li\r\n                id=\"{{formatDisplayNameForId(subNavItem.displayName)}}\"\r\n                sk-platform-navigation-secondary\r\n                *ngFor=\"let subNavItem of SubNavigation\"\r\n                [hidden]=\"!hasAccess(item.area, subNavItem.permissions)\"\r\n                [Label]=\"subNavItem.displayName\"\r\n                [Link]=\"subNavItem.url\"\r\n                [OpenLinkInNewTab]=\"subNavItem.openInNewTab\"\r\n                [hasNewBadge]=\"subNavItem.hasNewBadge\"\r\n                [Icon]=\"subNavItem.icon\"\r\n                [CustomIcon]=\"subNavItem.customIcon\">\r\n            </li>\r\n        </ng-container>\r\n    </div>\r\n</ul>\r\n\r\n<div class=\"sk-nav-footer\">\r\n    <button id=\"navExpandCollapse\" type=\"button\" class=\"btn sk-btn-icon\" (click)=\"toggle()\">\r\n        <i class=\"material-icons md-24 expand-collapse-icon\"></i>\r\n    </button>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: PlatformNavigationPrimaryComponent, selector: "[sk-platform-navigation-primary]", inputs: ["Icon", "CustomIcon", "Label", "Link", "IsOpen", "IsActive", "OpenLinkInNewTab", "hasNewBadge"] }, { kind: "component", type: PlatformNavigationSecondaryComponent, selector: "[sk-platform-navigation-secondary]", inputs: ["Icon", "CustomIcon", "Link", "Label", "OpenLinkInNewTab", "hasNewBadge"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PlatformNavigationComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-platform-navigation]', host: {
                        class: 'sk-left-pane'
                    }, providers: [NavigationItemsProvider], template: "<!-- Accordion -->\r\n<ul id=\"site-nav-bar\" class=\"nav sk-nav-platform\">\r\n    <div\r\n        id=\"{{formatDisplayNameForId(item.displayName)}}\"\r\n        *ngFor=\"let item of navigationItems\"\r\n        sk-platform-navigation-primary\r\n        [Label]=\"item.displayName\"\r\n        [Icon]=\"item.icon\"\r\n        [CustomIcon]=\"item.customIcon\"\r\n        [IsActive]=\"isNavigationLinkActive(item)\"\r\n        [IsOpen]=\"isNavigationLinkActive(item)\"\r\n        [Link]=\"item.url\"\r\n        [OpenLinkInNewTab]=\"item.openInNewTab\"\r\n        [hidden]=\"!hasAccess(item.area, item.permissions)\"\r\n        [hasNewBadge]=\"item.hasNewBadge\">\r\n        <ng-container *ngIf=\"isNavigationLinkActive(item)\">\r\n            <li\r\n                id=\"{{formatDisplayNameForId(subNavItem.displayName)}}\"\r\n                sk-platform-navigation-secondary\r\n                *ngFor=\"let subNavItem of SubNavigation\"\r\n                [hidden]=\"!hasAccess(item.area, subNavItem.permissions)\"\r\n                [Label]=\"subNavItem.displayName\"\r\n                [Link]=\"subNavItem.url\"\r\n                [OpenLinkInNewTab]=\"subNavItem.openInNewTab\"\r\n                [hasNewBadge]=\"subNavItem.hasNewBadge\"\r\n                [Icon]=\"subNavItem.icon\"\r\n                [CustomIcon]=\"subNavItem.customIcon\">\r\n            </li>\r\n        </ng-container>\r\n    </div>\r\n</ul>\r\n\r\n<div class=\"sk-nav-footer\">\r\n    <button id=\"navExpandCollapse\" type=\"button\" class=\"btn sk-btn-icon\" (click)=\"toggle()\">\r\n        <i class=\"material-icons md-24 expand-collapse-icon\"></i>\r\n    </button>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: PlatformNavigationService }, { type: i0.ChangeDetectorRef }, { type: NavigationPermissionsService }, { type: NavigationItemsProvider }], propDecorators: { IsCollapsed: [{
                type: HostBinding,
                args: ['class.collapsed']
            }], SkNavigationArea: [{
                type: Input
            }], SubNavigation: [{
                type: Input
            }] } });

class NavigationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NavigationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: NavigationModule, declarations: [PlatformNavigationComponent,
            NavigationComponent,
            PlatformNavigationPrimaryComponent,
            PlatformNavigationSecondaryComponent], imports: [CommonModule,
            RouterModule], exports: [PlatformNavigationComponent,
            NavigationComponent,
            PlatformNavigationPrimaryComponent,
            PlatformNavigationSecondaryComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NavigationModule, imports: [CommonModule,
            RouterModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NavigationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PlatformNavigationComponent,
                        NavigationComponent,
                        PlatformNavigationPrimaryComponent,
                        PlatformNavigationSecondaryComponent
                    ],
                    imports: [
                        CommonModule,
                        RouterModule
                    ],
                    exports: [
                        PlatformNavigationComponent,
                        NavigationComponent,
                        PlatformNavigationPrimaryComponent,
                        PlatformNavigationSecondaryComponent
                    ]
                }]
        }] });

var ButtonType;
(function (ButtonType) {
    ButtonType["Primary"] = "primary";
    ButtonType["Secondary"] = "secondary";
    ButtonType["Tertiary"] = "tertiary";
    ButtonType["Danger"] = "danger";
    ButtonType["Link"] = "link";
    ButtonType["Basic"] = "basic";
})(ButtonType || (ButtonType = {}));

class BaseButton {
    constructor() {
        /**
         * The class provided in the HTML
         *  @ignore
         */
        this.hostClass = '';
    }
    /**
     *
     * @ignore
     */
    mapTypeToClass(type) {
        switch (type) {
            case ButtonType.Primary:
            case ButtonType.Danger:
            case ButtonType.Link:
            case ButtonType.Basic:
                return `btn-${type}`;
            case ButtonType.Tertiary:
                return 'btn-outline-primary';
            default:
                return `btn-outline-secondary`;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BaseButton, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: BaseButton, inputs: { hostClass: ["class", "hostClass"] }, host: { properties: { "class": "this.classes" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BaseButton, decorators: [{
            type: Directive
        }], propDecorators: { classes: [{
                type: HostBinding,
                args: ['class']
            }], hostClass: [{
                type: Input,
                args: ['class']
            }] } });

var IconButtonType;
(function (IconButtonType) {
    IconButtonType["Secondary"] = "secondary";
    IconButtonType["Toggle"] = "toggle";
})(IconButtonType || (IconButtonType = {}));
class IconButtonComponent extends BaseButton {
    mapTypeToClass(type) {
        switch (type) {
            case IconButtonType.Secondary:
                return 'btn-outline-secondary';
            case IconButtonType.Toggle:
                return 'btn-toggle';
            default:
                return '';
        }
    }
    /**
     * @ignore
     */
    ngOnInit() {
        const tempClasses = [
            'btn',
            'sk-btn-icon',
            this.mapTypeToClass(this.color),
            this.hostClass,
        ];
        // We do this only once, after that the variable
        // is driven by the @input
        if (this.active) {
            tempClasses.push('active');
        }
        this.classes = tempClasses.join(' ');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: IconButtonComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: IconButtonComponent, selector: "button[sk-icon-button]", inputs: { icon: "icon", active: "active", toggle: "toggle", color: "color" }, host: { properties: { "class.active": "this.active", "class.btn-toggle": "this.toggle" } }, usesInheritance: true, ngImport: i0, template: "<i class=\"material-icons md-22\">{{ icon }}</i>" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: IconButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'button[sk-icon-button]', template: "<i class=\"material-icons md-22\">{{ icon }}</i>" }]
        }], propDecorators: { icon: [{
                type: Input
            }], active: [{
                type: HostBinding,
                args: ['class.active']
            }, {
                type: Input
            }], toggle: [{
                type: HostBinding,
                args: ['class.btn-toggle']
            }, {
                type: Input
            }], color: [{
                type: Input
            }] } });

class ButtonDirective extends BaseButton {
    constructor() {
        super(...arguments);
        /**
         * The type of button to display.
         */
        this.color = ButtonType.Secondary;
        /**
         * 3 possible sizes available for a button.
         */
        this.size = 'reg';
        /**
         *
         * @ignore
         */
        this.sizeToClass = (value) => {
            const btnClasses = ['btn'];
            switch (value) {
                case 'wide':
                    btnClasses.push('btn-block');
                case 'lg':
                    btnClasses.push('btn-lg');
                    break;
            }
            return btnClasses;
        };
    }
    /**
     * @ignore
     */
    ngOnInit() {
        this.classes = [
            ...this.sizeToClass(this.size),
            this.mapTypeToClass(this.color),
            this.hostClass,
        ].join(' ');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ButtonDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: ButtonDirective, selector: "button[sk-button]", inputs: { color: "color", size: "size" }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[sk-button]'
                }]
        }], propDecorators: { color: [{
                type: Input
            }], size: [{
                type: Input
            }] } });

/**
 * The spinner component
 */
class SpinnerButtonComponent extends BaseButton {
    constructor() {
        super(...arguments);
        /**
         * Property to define the color of the button.
         */
        this.color = ButtonType.Secondary;
    }
    get showDisabled() {
        return this.isDisabled || this.isSpinning ? true : null;
    }
    /**
     * Handles the disabled
     */
    get disabled() { return this.isDisabled; }
    set disabled(value) {
        this.isDisabled = value || value === '' ? true : null;
    }
    /**
     * @deprecated Use `isSpinning` instead.
     */
    set active(value) {
        throw new Error('active is no longer supported Use `isSpinning` instead');
    }
    /**
     * Property to hide or show the spinner.
     */
    get isSpinning() { return this.spinning; }
    set isSpinning(value) {
        this.spinning = value;
    }
    /**
     * @ignore
     */
    ngOnInit() {
        const tempClasses = [
            'btn',
            'sk-has-spinner',
            'sk-abs-spinner',
            this.mapTypeToClass(this.color),
            this.hostClass,
        ];
        this.classes = tempClasses.join(' ');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SpinnerButtonComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: SpinnerButtonComponent, selector: "button[sk-spinner-button]", inputs: { disabled: "disabled", color: "color", active: "active", isSpinning: "isSpinning" }, host: { properties: { "attr.disabled": "this.showDisabled", "class.active": "this.isSpinning" } }, usesInheritance: true, ngImport: i0, template: "<div class=\"sk-btn-spinner\">\r\n    <div class=\"sk-spin-loader loader-dark\"></div>\r\n  </div>\r\n<span class=\"sk-loader-label\">\r\n    <ng-content></ng-content>\r\n</span>" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SpinnerButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'button[sk-spinner-button]', template: "<div class=\"sk-btn-spinner\">\r\n    <div class=\"sk-spin-loader loader-dark\"></div>\r\n  </div>\r\n<span class=\"sk-loader-label\">\r\n    <ng-content></ng-content>\r\n</span>" }]
        }], propDecorators: { showDisabled: [{
                type: HostBinding,
                args: ['attr.disabled']
            }], disabled: [{
                type: Input
            }], color: [{
                type: Input
            }], active: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.active']
            }], isSpinning: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.active']
            }] } });

/**
 * The combo component
 */
class ComboButtonComponent extends BaseButton {
    constructor() {
        super(...arguments);
        /**
         * Property to define the color of the button.
         */
        this.color = ButtonType.Secondary;
    }
    /**
     * @ignore
     */
    ngOnInit() {
        switch (this.color) {
            case ButtonType.Primary:
            case ButtonType.Danger:
                this.iconColor = 'md-white';
            case ButtonType.Secondary:
            case ButtonType.Basic:
                this.iconColor = null;
                break;
            default:
                this.iconColor = 'md-blue';
                break;
        }
        this.classes = [
            'btn',
            'sk-btn-combo',
            this.mapTypeToClass(this.color),
            this.hostClass,
        ].join(' ');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ComboButtonComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ComboButtonComponent, selector: "button[sk-combo-button]", inputs: { color: "color", icon: "icon" }, usesInheritance: true, ngImport: i0, template: "<i class=\"material-icons md-22\"  [ngClass]=\"iconColor\">{{ icon }}</i>\r\n<span>\r\n    <ng-content></ng-content>\r\n</span>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ComboButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'button[sk-combo-button]', template: "<i class=\"material-icons md-22\"  [ngClass]=\"iconColor\">{{ icon }}</i>\r\n<span>\r\n    <ng-content></ng-content>\r\n</span>\r\n" }]
        }], propDecorators: { color: [{
                type: Input
            }], icon: [{
                type: Input
            }] } });

class ButtonsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ButtonsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: ButtonsModule, declarations: [IconButtonComponent,
            SpinnerButtonComponent,
            ComboButtonComponent,
            ButtonDirective], imports: [CommonModule,
            UtilityModule], exports: [IconButtonComponent,
            SpinnerButtonComponent,
            ComboButtonComponent,
            ButtonDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ButtonsModule, imports: [CommonModule,
            UtilityModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ButtonsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        IconButtonComponent,
                        SpinnerButtonComponent,
                        ComboButtonComponent,
                        ButtonDirective
                    ],
                    imports: [
                        CommonModule,
                        UtilityModule
                    ],
                    exports: [
                        IconButtonComponent,
                        SpinnerButtonComponent,
                        ComboButtonComponent,
                        ButtonDirective
                    ]
                }]
        }] });

class BasicInputComponent {
    get value() { return this.inputValue; }
    set value(val) {
        this.inputValue = val;
        this.valueChange.emit(this.inputValue);
    }
    constructor() {
        this.disabled = false;
        this.valueChange = new EventEmitter();
    }
    reset() {
        this.value = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BasicInputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: BasicInputComponent, selector: "sk-basic-input", inputs: { placeholder: "placeholder", disabled: "disabled", value: "value" }, outputs: { valueChange: "valueChange" }, ngImport: i0, template: "<div class=\"sk-input-icon sk-input-btn\">\r\n    <input type=\"text\" class=\"form-control\" [placeholder]=\"placeholder\" [(ngModel)]=\"value\" [disabled]=\"disabled\" />\r\n    <i *ngIf=\"!value\" class=\"material-icons md-18\">search</i>\r\n    <i *ngIf=\"value\" class=\"material-icons md-18 sk-reset-search\" (click)=\"reset()\">clear</i>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BasicInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-basic-input', template: "<div class=\"sk-input-icon sk-input-btn\">\r\n    <input type=\"text\" class=\"form-control\" [placeholder]=\"placeholder\" [(ngModel)]=\"value\" [disabled]=\"disabled\" />\r\n    <i *ngIf=\"!value\" class=\"material-icons md-18\">search</i>\r\n    <i *ngIf=\"value\" class=\"material-icons md-18 sk-reset-search\" (click)=\"reset()\">clear</i>\r\n</div>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { placeholder: [{
                type: Input
            }], disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

/**
 * This switch component is a specialized control that acts as a replacement
 * for a traditional checkbox control. It represents a boolean value by toggling
 * between an ON and OFF state.  The implementor can choose to control the value
 * binding themselves or let the control manage the value directly.
 *
 * To manage the value yourself, use a (click) event handler in combination with the [checked]
 * attribute to provide 1-way binding into the control.
 *
 * To let the control manage the value, use the [(ngModel)] attribute which provides 2-way
 * binding of the value into the control and back out to the parent component
 *
 * Example:
 *
 * Basic hard-coded, checked (ON) switch
 * <sk-switch id="foo" checked></sk-switch>
 *
 * 1-way binding. You must control the checked value
 * <sk-switch id="foo" [checked]="isChecked"></sk-switch>
 *
 * 2-way binding. You let the control toggle the boolean value itself
 * <sk-switch id="foo" [(ngModel)]="isChecked"></sk-switch>
 *
 * Disabled switch
 * <sk-switch id="foo" [disabled]="isDisabled()" OR disabled></sk-switch>
 *
 * You can use the (change) handler to know when the value is changed.
 * Only works alongside ngModel usage
 * <sk-switch id="foo" (change)="onChange($event)"></sk-switch>
 *
 * The implementation of ControlValueAccessor has been taken from the Angular <mat-slide-toggle>
 * https://github.com/angular/components/blob/7c332765f4ae2851a4f03624267c4c1af2100237/src/material/slide-toggle/slide-toggle.ts
 */
class SwitchComponent {
    /**
     * Switch attribute to toggle the switch to be
     * the small version or not
     */
    set skSwitchSmall(value) {
        this.isSmall = typeof value !== undefined && value !== false;
    }
    get skSwitchSmall() {
        return this.isSmall;
    }
    /**
     * Switch attribute to disable the control.
     * Works just like native 'input'
     */
    set disabled(value) {
        this.isDisabled = typeof value !== undefined && value !== false;
        // Wait for the element to be initialzed before calling 'setDisabledState'.
        // This is done because 'inputElement' needs to be initialzed first.
        if (this.isInitialized) {
            this.setDisabledState(this.isDisabled);
        }
    }
    get disabled() {
        return this.isDisabled;
    }
    /** Whether the sw-switch element is checked or not. */
    get checked() { return this.isChecked; }
    set checked(value) {
        this.isChecked = this.coerceBooleanProperty(value);
    }
    keyEvent(event) {
        if (event.code === 'Space' ||
            (event.code === 'ArrowRight' && !this.isChecked) ||
            (event.code === 'ArrowLeft' && this.isChecked)) {
            this.toggle();
        }
    }
    constructor(renderer) {
        this.renderer = renderer;
        this.isInitialized = false;
        this.isSmall = false;
        this.isDisabled = false;
        this.isChecked = false;
        this.onChange = (_) => { };
        this.onTouched = () => { };
        /** An event will be dispatched each time the sw-switch changes its value. */
        this.change = new EventEmitter();
    }
    ngAfterViewInit() {
        // Calling 'setDisabledState' after 'inputElement' is initialized.
        this.setDisabledState(this.isDisabled);
        this.isInitialized = true;
    }
    /** Toggles the checked state of the sw-switch. */
    toggle() {
        this.isChecked = !this.isChecked;
        this.emitChangeEvent();
    }
    /** Method being called whenever the underlying input emits a change event. */
    onChangeEvent(event) {
        event.stopPropagation();
        this.isChecked = this.inputElement.nativeElement.checked;
        this.emitChangeEvent();
    }
    /** Method being called whenever the sk-switch has been clicked. */
    onInputClick(event) {
        event.stopPropagation();
    }
    /** Implemented as part of ControlValueAccessor. */
    writeValue(value) {
        this.isChecked = !!value;
    }
    /** Implemented as part of ControlValueAccessor. */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /** Implemented as part of ControlValueAccessor. */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    /** Implemented as a part of ControlValueAccessor. */
    setDisabledState(isDisabled) {
        this.renderer.setProperty(this.inputElement.nativeElement, 'disabled', isDisabled);
    }
    /**
     * Emits a change event on the `change` output. Also notifies the FormControl about the change.
     */
    emitChangeEvent() {
        this.onChange(this.isChecked);
        this.change.emit(this.isChecked);
    }
    coerceBooleanProperty(value) {
        return value != null && `${value}` !== 'false';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SwitchComponent, deps: [{ token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: SwitchComponent, selector: "sk-switch", inputs: { skSwitchSmall: "skSwitchSmall", id: "id", disabled: "disabled", checked: "checked" }, outputs: { change: "change" }, host: { listeners: { "keyup": "keyEvent($event)" } }, providers: [{
                provide: NG_VALUE_ACCESSOR,
                multi: true,
                useExisting: forwardRef(() => SwitchComponent),
            }], viewQueries: [{ propertyName: "inputElement", first: true, predicate: ["input"], descendants: true, read: ElementRef }], ngImport: i0, template: "<div class=\"sk-onoffswitch\" [class.sk-switch-sm]=\"skSwitchSmall\" [class.disabled]=\"disabled\">\r\n    <input \r\n        #input\r\n        tabindex=\"0\" \r\n        type=\"checkbox\" \r\n        name=\"sk-onoffswitch\" \r\n        class=\"sk-onoffswitch-checkbox\"\r\n        [id]=\"id\"\r\n        [checked]=\"checked\"\r\n        (change)=\"onChangeEvent($event)\"\r\n        (click)=\"onInputClick($event)\">\r\n\r\n    <label class=\"sk-onoffswitch-label\" [for]=\"id\">\r\n        <span class=\"sk-onoffswitch-icon-inner\"></span>\r\n        <span class=\"sk-onoffswitch-switch\"></span>\r\n    </label>\r\n</div>\r\n", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SwitchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-switch', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => SwitchComponent),
                        }], template: "<div class=\"sk-onoffswitch\" [class.sk-switch-sm]=\"skSwitchSmall\" [class.disabled]=\"disabled\">\r\n    <input \r\n        #input\r\n        tabindex=\"0\" \r\n        type=\"checkbox\" \r\n        name=\"sk-onoffswitch\" \r\n        class=\"sk-onoffswitch-checkbox\"\r\n        [id]=\"id\"\r\n        [checked]=\"checked\"\r\n        (change)=\"onChangeEvent($event)\"\r\n        (click)=\"onInputClick($event)\">\r\n\r\n    <label class=\"sk-onoffswitch-label\" [for]=\"id\">\r\n        <span class=\"sk-onoffswitch-icon-inner\"></span>\r\n        <span class=\"sk-onoffswitch-switch\"></span>\r\n    </label>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }], propDecorators: { inputElement: [{
                type: ViewChild,
                args: ['input', { read: ElementRef }]
            }], skSwitchSmall: [{
                type: Input
            }], id: [{
                type: Input
            }], disabled: [{
                type: Input
            }], checked: [{
                type: Input
            }], change: [{
                type: Output
            }], keyEvent: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }] } });

/**
 * This setting is used to configure what the initial state (which defines what futures states can be)
 * the TagsInputComponent is in upon rendering the control. The various states are as follows:
 *
 * Read - the component is read-only and the user cannot add/remove/update anything about the tags except they can click
 * Write - the component is in write-only mode and renders with the input available right away
 * ReadWrite - the component is in a hybrid mode which allows you to remove/add initially, but changes to Write mode once the
 *              "+ Add tag" button is clicked
 */
var TagsInputViewMode;
(function (TagsInputViewMode) {
    TagsInputViewMode["Read"] = "read";
    TagsInputViewMode["Write"] = "write";
    TagsInputViewMode["ReadWrite"] = "readwrite";
})(TagsInputViewMode || (TagsInputViewMode = {}));

class TagsInputComponent {
    clickOut(event) {
        if (!this.element.nativeElement.contains(event.target) && !event.target.closest('.dropdown-item')) {
            this.onBlur();
        }
    }
    constructor(element) {
        this.element = element;
        /**
         * (Optional)
         * Observable list of dropdown options available for autocomplete
         */
        this.autoCompleteOptions = [];
        /**
         * (Optional)
         * number of options to display in autocomplete dropdown
         */
        this.autoCompleteOptionsDisplayCount = 10;
        /**
         * List of tags
         */
        this.tags = [];
        /**
         * List of locked tags
         */
        this.lockedTags = [];
        /**
         * Emits the state of the tag list when it is modified
         */
        this.tagsChange = new EventEmitter();
        /**
         * Placeholder to display in the input box
         * Default: 'Add tag'
         */
        this.placeholder = 'Add tag';
        /**
         * Whether the control is disabled
         */
        this.disabled = false;
        /**
         * Whether to allow multiple tags
        */
        this.allowMultipleTags = true;
        /**
         * If false, user is only allowed to input values from the autocomplete options
         * Default: true
         */
        this.allowFreeform = true;
        /**
         * (Optional)
         * Tag string validation pattern.
         */
        this.validationPattern = '.*';
        /**
         * (Optional)
         * If 'validationPattern is supplied, the html spec says that a description of it should be provided
         * in the control title.
         */
        this.validationPatternDescription = 'Please provide a valid value.';
        /**
         * Controls what 'mode' the control should be rendered as
         *
         * 'read' - Read mode means all you can do is see the tags and click on them
         *
         * 'write' - Write mode means the control is rendered with the input available
         *          to make additions doesn't revert back to another mode upon blur
         *
         * 'readwrite' - ReadWrite mode means the control will be rendered as a hybrid where the input is closed with the option to
         *              click on a '+ Add tag' button to put it into 'write' mode
         */
        this.initialMode = TagsInputViewMode.Read;
        /*
         * The max number of tags to display in Read mode.
         * The remaining tags will be summarized in a final tag that displays the number
         * of additional tags not shown. The names of the tags will be put in the title
         * attribute of the summary tag so users can see them by hovering over the summary tag.
        */
        this.maxVisibleTags = Number.MAX_SAFE_INTEGER;
        /**
         * Event emitted when a tag is added
         */
        this.tagAdded = new EventEmitter();
        /**
         * Event emitted when a tag is removed
         */
        this.tagRemoved = new EventEmitter();
        /**
         *  Event emitted when a tag is clicked on
         */
        this.tagClicked = new EventEmitter();
        /**
         * Event emitted on keyup event returning the tagsInputModel validity status.
         * True when valid, false when invalid.
         */
        this.inputValidityStatusChanged = new EventEmitter();
        this.model = '';
        this.clickSubject = new Subject();
        this.focusSubject = new Subject();
        this.TagsInputViewMode = TagsInputViewMode;
        this.onChange = () => { };
        this.onTouched = () => { };
        this.search = (text) => {
            const debouncedText = text.pipe(debounceTime(200), distinctUntilChanged());
            const clickToOpenPopup = this.clickSubject.pipe(filter(() => !this.instance.isPopupOpen()));
            return merge(debouncedText, clickToOpenPopup, this.focusSubject).pipe(map(term => {
                const lcTerm = term.toLowerCase();
                let result = [];
                this.onTouched();
                if (term) {
                    for (const option of this.autoCompleteOptions) {
                        if (option.toLowerCase().includes(lcTerm)
                            && !this.tags.includes(option)) {
                            result.push(option);
                        }
                    }
                }
                else {
                    result = this.autoCompleteOptions;
                }
                return result
                    // Filter out tags that are already applied
                    .filter(res => !this.tags.includes(res))
                    // Only return the first X number of results
                    .slice(0, this.autoCompleteOptionsDisplayCount);
            }));
        };
    }
    writeValue(tags) {
        this.tags = tags;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    ngOnInit() {
        this.validationPattern = new RegExp(this.validationPattern);
        this.currentMode = this.initialMode;
    }
    goToEditMode(event) {
        event.stopPropagation();
        this.currentMode = TagsInputViewMode.Write;
        setTimeout(() => {
            this.tagsInputElem.nativeElement.focus();
        });
    }
    /**
     * Fired for every keydown event
     * @param $event keydown event
     */
    onKeyDown($event) {
        if ($event.key === 'Enter' && this.model && this.allowFreeform && this.tagsInputModel.valid) {
            this.addTag(this.model);
        }
        else if ($event.key === 'Backspace') {
            if (!this.model && this.tags.length > 0) {
                this.removeTag(this.tags.length - 1);
            }
        }
    }
    /**
     * Triggers 'onBlur' when the input tag inside the component loses focus.
     * It's required because 'clickOut' doesn't trigger if the input was disabled.
     */
    focusOut() {
        this.onBlur();
    }
    /**
     * When input loses focus, add tag to the list if entered
     */
    onBlur() {
        if (this.model && this.tagsInputModel.valid) {
            this.addTag(this.model);
        }
        if (this.initialMode === TagsInputViewMode.ReadWrite) {
            this.currentMode = TagsInputViewMode.ReadWrite;
        }
        this.tagsInputElem.nativeElement.blur();
    }
    /**
     * Fired when item in typeahead menu is selected
     * @param event typeahead event
     */
    onItemSelected(event) {
        event.preventDefault();
        this.addTag(event.item);
    }
    /**
     * Fired on the input's 'keyup' event
     */
    onInput() {
        this.inputValidityStatusChanged.emit(this.tagsInputModel.valid);
    }
    /**
     * Remove a tag from the control
     * @param index Index at which to remove the tag
     */
    onRemove(event, index) {
        event.stopPropagation();
        this.removeTag(index);
    }
    getValidationPattern() {
        if (typeof (this.validationPattern) === 'string') {
            return this.validationPattern;
        }
        else {
            return this.validationPattern.source;
        }
    }
    toggleWriteMode() {
        if (this.currentMode === TagsInputViewMode.ReadWrite) {
            this.currentMode = TagsInputViewMode.Write;
        }
    }
    addTag(tag) {
        // Check to make sure it is not a duplicate
        if (this.tags.includes(tag)) {
            this.model = '';
            return;
        }
        this.tags.push(tag);
        this.model = '';
        this.tagAdded.emit(tag);
        this.tagsChange.emit(this.tags);
        this.onChange(this.tags);
        this.onTouched();
    }
    removeTag(index) {
        const tag = this.tags[index];
        if (!this.lockedTags.includes(tag)) {
            const [removed] = this.tags.splice(index, 1);
            this.tagRemoved.emit(removed);
            this.tagsChange.emit(this.tags);
            this.onChange(this.tags);
            this.onTouched();
        }
    }
    inputHiddenInSingleTagMode() {
        return !this.allowMultipleTags && this.tags.length === 1;
    }
    onClick(event, tag) {
        event.stopPropagation();
        this.tagClicked.emit(tag);
    }
    isOverflow(element) {
        const parent = element?.parentNode;
        if (!element || !parent) {
            return false;
        }
        return element.offsetWidth > parent.clientWidth;
    }
    getTagTooltipText(element) {
        return this.isOverflow(element) ? element.innerText : '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TagsInputComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: TagsInputComponent, selector: "sk-tags-input", inputs: { autoCompleteOptions: "autoCompleteOptions", autoCompleteOptionsDisplayCount: "autoCompleteOptionsDisplayCount", tags: "tags", lockedTags: "lockedTags", placeholder: "placeholder", disabled: "disabled", allowMultipleTags: "allowMultipleTags", allowFreeform: "allowFreeform", validationPattern: "validationPattern", validationPatternDescription: "validationPatternDescription", initialMode: "initialMode", maxVisibleTags: "maxVisibleTags", externalError: "externalError" }, outputs: { tagsChange: "tagsChange", tagAdded: "tagAdded", tagRemoved: "tagRemoved", tagClicked: "tagClicked", inputValidityStatusChanged: "inputValidityStatusChanged" }, host: { listeners: { "document:click": "clickOut($event)" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TagsInputComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "instance", first: true, predicate: ["instance"], descendants: true, static: true }, { propertyName: "tagsInputModel", first: true, predicate: ["tagsInputModel"], descendants: true, static: true }, { propertyName: "tagsInputElem", first: true, predicate: ["tagsInputElem"], descendants: true, static: true }], ngImport: i0, template: "<div [hidden]=\"currentMode === TagsInputViewMode.Write\">\r\n    <div class=\"sk-tags d-flex flex-wrap\">\r\n        <span *ngIf=\"currentMode === TagsInputViewMode.ReadWrite\" (click)=\"goToEditMode($event)\"\r\n            class=\"badge badge-default pointer sk-tag-basic sk-tag\">+ Add tag</span>\r\n        <span *ngFor=\"let t of tags.slice(0, maxVisibleTags); let i = index\" class=\"badge badge-default sk-tag-basic sk-tag\"\r\n            (click)=\"onClick($event, t)\">{{t}}<a>\r\n                <i *ngIf=\"currentMode === TagsInputViewMode.ReadWrite && !lockedTags.includes(t)\" class=\"material-icons md-16 md-gray\" (click)=\"onRemove($event, i)\">clear</i></a>\r\n        </span>\r\n        <span\r\n            *ngIf=\"maxVisibleTags < tags.length\"\r\n            class=\"badge badge-default mb-1 sk-tag-basic sk-tag\"\r\n            [class.pointer]=\"currentMode === TagsInputViewMode.ReadWrite\"\r\n            [ngbTooltip]=\"tags.slice(maxVisibleTags).join(', ')\"\r\n            (click)=\"toggleWriteMode()\"\r\n        >\r\n            + {{tags.length - maxVisibleTags}}\r\n        </span>\r\n    </div>\r\n</div>\r\n<div [hidden]=\"currentMode !== TagsInputViewMode.Write\" class=\"sk-tags-input\"\r\n    [class.is-invalid]=\"tagsInputModel.invalid && (tagsInputModel.dirty || tagsInputModel.touched) || !!externalError\" (click)=\"tagsInputElem.focus()\">\r\n    <div class=\"sk-tag-container\">\r\n        <fieldset class=\"sk-tags\" [disabled]=\"disabled\">\r\n                <ul class=\"sk-tag-list\">\r\n                    <li *ngFor=\"let t of tags; let i = index\" class=\"sk-tag-item tag-inner-text mw-100\">\r\n                        <div class=\"badge badge-default sk-tag-basic sk-tag d-flex\">\r\n                            <div class=\"text-truncate\">\r\n                                <span #tagInnerText class=\"mw-100\" [innerText]=\"t\" [ngbTooltip]=\"getTagTooltipText(tagInnerText)\" [openDelay]=\"300\" placement=\"top-left\"></span>\r\n                            </div>\r\n                            <a><i *ngIf=\"!disabled && !lockedTags.includes(t)\" class=\"material-icons md-16 md-gray\"\r\n                                (click)=\"onRemove($event, i)\">clear</i></a>\r\n                        </div>\r\n                    </li>\r\n                </ul>\r\n\r\n                <input [hidden]=\"inputHiddenInSingleTagMode()\" type=\"text\" autocomplete=\"off\" class=\"sk-input w-100\" [(ngModel)]=\"model\" [ngbTypeahead]=\"search\"\r\n                    #instance=\"ngbTypeahead\" (focus)=\"focusSubject.next($event.target.value)\"\r\n                    (keyup)=\"onInput()\"\r\n                    (focusout)=\"focusOut()\"\r\n                    (click)=\"clickSubject.next($event.target.value)\" [focusFirst]=\"false\" (keydown)=\"onKeyDown($event)\"\r\n                    (selectItem)=\"onItemSelected($event)\" [placeholder]=\"placeholder\" [disabled]=\"disabled\"\r\n                    [pattern]=\"getValidationPattern()\" [title]=\"validationPatternDescription\" #tagsInputModel=\"ngModel\"\r\n                    #tagsInputElem>\r\n        </fieldset>\r\n        <small *ngIf=\"tagsInputModel.invalid && (tagsInputModel.dirty || tagsInputModel.touched)\"\r\n            class=\"invalid-feedback\">{{ validationPatternDescription }}</small>\r\n        <small id=\"tagsInputExternalError\" *ngIf=\"!!externalError\"\r\n            class=\"invalid-feedback\">{{ externalError }}</small>\r\n    </div>\r\n</div>\r\n", styles: [".is-invalid .sk-tags{border-color:#fa6173;box-shadow:inset 0 0 0 1px #fa6173}.pointer{cursor:pointer}.sk-tags-input .sk-tag-container .sk-tags{padding:.35rem .75rem;border:1px solid #c4cacf}input.sk-input.ng-invalid{border-color:transparent;box-shadow:0 0 0 1px transparent}.sk-tags[disabled],.sk-input[disabled]{border-color:#ebedef;background-color:#f4f6f8}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.PatternValidator, selector: "[pattern][formControlName],[pattern][formControl],[pattern][ngModel]", inputs: ["pattern"] }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i1$1.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "popperOptions", "triggers", "positionTarget", "container", "disableTooltip", "tooltipClass", "tooltipContext", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "directive", type: i1$1.NgbTypeahead, selector: "input[ngbTypeahead]", inputs: ["autocomplete", "container", "editable", "focusFirst", "inputFormatter", "ngbTypeahead", "resultFormatter", "resultTemplate", "selectOnExact", "showHint", "placement", "popperOptions", "popupClass"], outputs: ["selectItem"], exportAs: ["ngbTypeahead"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TagsInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-tags-input', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TagsInputComponent),
                            multi: true
                        }
                    ], template: "<div [hidden]=\"currentMode === TagsInputViewMode.Write\">\r\n    <div class=\"sk-tags d-flex flex-wrap\">\r\n        <span *ngIf=\"currentMode === TagsInputViewMode.ReadWrite\" (click)=\"goToEditMode($event)\"\r\n            class=\"badge badge-default pointer sk-tag-basic sk-tag\">+ Add tag</span>\r\n        <span *ngFor=\"let t of tags.slice(0, maxVisibleTags); let i = index\" class=\"badge badge-default sk-tag-basic sk-tag\"\r\n            (click)=\"onClick($event, t)\">{{t}}<a>\r\n                <i *ngIf=\"currentMode === TagsInputViewMode.ReadWrite && !lockedTags.includes(t)\" class=\"material-icons md-16 md-gray\" (click)=\"onRemove($event, i)\">clear</i></a>\r\n        </span>\r\n        <span\r\n            *ngIf=\"maxVisibleTags < tags.length\"\r\n            class=\"badge badge-default mb-1 sk-tag-basic sk-tag\"\r\n            [class.pointer]=\"currentMode === TagsInputViewMode.ReadWrite\"\r\n            [ngbTooltip]=\"tags.slice(maxVisibleTags).join(', ')\"\r\n            (click)=\"toggleWriteMode()\"\r\n        >\r\n            + {{tags.length - maxVisibleTags}}\r\n        </span>\r\n    </div>\r\n</div>\r\n<div [hidden]=\"currentMode !== TagsInputViewMode.Write\" class=\"sk-tags-input\"\r\n    [class.is-invalid]=\"tagsInputModel.invalid && (tagsInputModel.dirty || tagsInputModel.touched) || !!externalError\" (click)=\"tagsInputElem.focus()\">\r\n    <div class=\"sk-tag-container\">\r\n        <fieldset class=\"sk-tags\" [disabled]=\"disabled\">\r\n                <ul class=\"sk-tag-list\">\r\n                    <li *ngFor=\"let t of tags; let i = index\" class=\"sk-tag-item tag-inner-text mw-100\">\r\n                        <div class=\"badge badge-default sk-tag-basic sk-tag d-flex\">\r\n                            <div class=\"text-truncate\">\r\n                                <span #tagInnerText class=\"mw-100\" [innerText]=\"t\" [ngbTooltip]=\"getTagTooltipText(tagInnerText)\" [openDelay]=\"300\" placement=\"top-left\"></span>\r\n                            </div>\r\n                            <a><i *ngIf=\"!disabled && !lockedTags.includes(t)\" class=\"material-icons md-16 md-gray\"\r\n                                (click)=\"onRemove($event, i)\">clear</i></a>\r\n                        </div>\r\n                    </li>\r\n                </ul>\r\n\r\n                <input [hidden]=\"inputHiddenInSingleTagMode()\" type=\"text\" autocomplete=\"off\" class=\"sk-input w-100\" [(ngModel)]=\"model\" [ngbTypeahead]=\"search\"\r\n                    #instance=\"ngbTypeahead\" (focus)=\"focusSubject.next($event.target.value)\"\r\n                    (keyup)=\"onInput()\"\r\n                    (focusout)=\"focusOut()\"\r\n                    (click)=\"clickSubject.next($event.target.value)\" [focusFirst]=\"false\" (keydown)=\"onKeyDown($event)\"\r\n                    (selectItem)=\"onItemSelected($event)\" [placeholder]=\"placeholder\" [disabled]=\"disabled\"\r\n                    [pattern]=\"getValidationPattern()\" [title]=\"validationPatternDescription\" #tagsInputModel=\"ngModel\"\r\n                    #tagsInputElem>\r\n        </fieldset>\r\n        <small *ngIf=\"tagsInputModel.invalid && (tagsInputModel.dirty || tagsInputModel.touched)\"\r\n            class=\"invalid-feedback\">{{ validationPatternDescription }}</small>\r\n        <small id=\"tagsInputExternalError\" *ngIf=\"!!externalError\"\r\n            class=\"invalid-feedback\">{{ externalError }}</small>\r\n    </div>\r\n</div>\r\n", styles: [".is-invalid .sk-tags{border-color:#fa6173;box-shadow:inset 0 0 0 1px #fa6173}.pointer{cursor:pointer}.sk-tags-input .sk-tag-container .sk-tags{padding:.35rem .75rem;border:1px solid #c4cacf}input.sk-input.ng-invalid{border-color:transparent;box-shadow:0 0 0 1px transparent}.sk-tags[disabled],.sk-input[disabled]{border-color:#ebedef;background-color:#f4f6f8}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { autoCompleteOptions: [{
                type: Input
            }], autoCompleteOptionsDisplayCount: [{
                type: Input
            }], tags: [{
                type: Input
            }], lockedTags: [{
                type: Input
            }], tagsChange: [{
                type: Output
            }], placeholder: [{
                type: Input
            }], disabled: [{
                type: Input
            }], allowMultipleTags: [{
                type: Input
            }], allowFreeform: [{
                type: Input
            }], validationPattern: [{
                type: Input
            }], validationPatternDescription: [{
                type: Input
            }], initialMode: [{
                type: Input
            }], maxVisibleTags: [{
                type: Input
            }], externalError: [{
                type: Input
            }], tagAdded: [{
                type: Output
            }], tagRemoved: [{
                type: Output
            }], tagClicked: [{
                type: Output
            }], inputValidityStatusChanged: [{
                type: Output
            }], instance: [{
                type: ViewChild,
                args: ['instance', { static: true }]
            }], tagsInputModel: [{
                type: ViewChild,
                args: ['tagsInputModel', { static: true }]
            }], tagsInputElem: [{
                type: ViewChild,
                args: ['tagsInputElem', { static: true }]
            }], clickOut: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }] } });

class TransparentInputComponent {
    constructor() {
        this.valueChange = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TransparentInputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: TransparentInputComponent, selector: "[sk-transparent-input]", inputs: { value: "value" }, outputs: { valueChange: "valueChange" }, ngImport: i0, template: `<input type="text"
                [(ngModel)]="value"
                (ngModelChange)="valueChange.emit(value)"
                class="form-control sk-input-transparent sk-input-flush text-truncate">`, isInline: true, dependencies: [{ kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TransparentInputComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[sk-transparent-input]',
                    template: `<input type="text"
                [(ngModel)]="value"
                (ngModelChange)="valueChange.emit(value)"
                class="form-control sk-input-transparent sk-input-flush text-truncate">`
                }]
        }], propDecorators: { value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

class TagsInputEmailComponent extends TagsInputComponent {
    constructor(element) {
        super(element);
        /**
         * Whether the control accepts a list of email addresses in a single paste.
         */
        this.allowPastingMultipleEmails = false;
    }
    addTag(tag) {
        if (this.allowPastingMultipleEmails) {
            this.addMultipleEmailTags(tag);
        }
        else {
            super.addTag(tag);
        }
    }
    addMultipleEmailTags(multipleEmailTags) {
        var cleanTags = multipleEmailTags.replace(/[:;,&"()\<\>\\\[\]]/gi, "");
        var singleTags = cleanTags.split(" ");
        for (let i = 0; i < singleTags.length; i++) {
            if (singleTags[i].indexOf("@") > -1) {
                super.addTag(singleTags[i]);
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TagsInputEmailComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: TagsInputEmailComponent, selector: "sk-tags-input-email", inputs: { allowPastingMultipleEmails: "allowPastingMultipleEmails" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TagsInputEmailComponent),
                multi: true
            }
        ], usesInheritance: true, ngImport: i0, template: "<div [hidden]=\"currentMode === TagsInputViewMode.Write\">\r\n    <div class=\"sk-tags d-flex flex-wrap\">\r\n        <span *ngIf=\"currentMode === TagsInputViewMode.ReadWrite\" (click)=\"goToEditMode($event)\"\r\n            class=\"badge badge-default pointer sk-tag-basic sk-tag\">+ Add tag</span>\r\n        <span *ngFor=\"let t of tags.slice(0, maxVisibleTags); let i = index\" class=\"badge badge-default sk-tag-basic sk-tag\"\r\n            (click)=\"onClick($event, t)\">{{t}}<a>\r\n                <i *ngIf=\"currentMode === TagsInputViewMode.ReadWrite && !lockedTags.includes(t)\" class=\"material-icons md-16 md-gray\" (click)=\"onRemove($event, i)\">clear</i></a>\r\n        </span>\r\n        <span\r\n            *ngIf=\"maxVisibleTags < tags.length\"\r\n            class=\"badge badge-default mb-1 sk-tag-basic sk-tag\"\r\n            [class.pointer]=\"currentMode === TagsInputViewMode.ReadWrite\"\r\n            [ngbTooltip]=\"tags.slice(maxVisibleTags).join(', ')\"\r\n            (click)=\"toggleWriteMode()\"\r\n        >\r\n            + {{tags.length - maxVisibleTags}}\r\n        </span>\r\n    </div>\r\n</div>\r\n<div [hidden]=\"currentMode !== TagsInputViewMode.Write\" class=\"sk-tags-input\"\r\n    [class.is-invalid]=\"tagsInputModel.invalid && (tagsInputModel.dirty || tagsInputModel.touched) || !!externalError\" (click)=\"tagsInputElem.focus()\">\r\n    <div class=\"sk-tag-container\">\r\n        <fieldset class=\"sk-tags\" [disabled]=\"disabled\">\r\n                <ul class=\"sk-tag-list\">\r\n                    <li *ngFor=\"let t of tags; let i = index\" class=\"sk-tag-item tag-inner-text mw-100\">\r\n                        <div class=\"badge badge-default sk-tag-basic sk-tag d-flex\">\r\n                            <div class=\"text-truncate\">\r\n                                <span #tagInnerText class=\"mw-100\" [innerText]=\"t\" [ngbTooltip]=\"getTagTooltipText(tagInnerText)\" [openDelay]=\"300\" placement=\"top-left\"></span>\r\n                            </div>\r\n                            <a><i *ngIf=\"!disabled && !lockedTags.includes(t)\" class=\"material-icons md-16 md-gray\"\r\n                                (click)=\"onRemove($event, i)\">clear</i></a>\r\n                        </div>\r\n                    </li>\r\n                </ul>\r\n\r\n                <input [hidden]=\"inputHiddenInSingleTagMode()\" type=\"text\" autocomplete=\"off\" class=\"sk-input w-100\" [(ngModel)]=\"model\" [ngbTypeahead]=\"search\"\r\n                    #instance=\"ngbTypeahead\" (focus)=\"focusSubject.next($event.target.value)\"\r\n                    (keyup)=\"onInput()\"\r\n                    (focusout)=\"focusOut()\"\r\n                    (click)=\"clickSubject.next($event.target.value)\" [focusFirst]=\"false\" (keydown)=\"onKeyDown($event)\"\r\n                    (selectItem)=\"onItemSelected($event)\" [placeholder]=\"placeholder\" [disabled]=\"disabled\"\r\n                    [pattern]=\"getValidationPattern()\" [title]=\"validationPatternDescription\" #tagsInputModel=\"ngModel\"\r\n                    #tagsInputElem>\r\n        </fieldset>\r\n        <small *ngIf=\"tagsInputModel.invalid && (tagsInputModel.dirty || tagsInputModel.touched)\"\r\n            class=\"invalid-feedback\">{{ validationPatternDescription }}</small>\r\n        <small id=\"tagsInputExternalError\" *ngIf=\"!!externalError\"\r\n            class=\"invalid-feedback\">{{ externalError }}</small>\r\n    </div>\r\n</div>\r\n", styles: [".is-invalid .sk-tags{border-color:#fa6173;box-shadow:inset 0 0 0 1px #fa6173}.pointer{cursor:pointer}.sk-tags-input .sk-tag-container .sk-tags{padding:.35rem .75rem;border:1px solid #c4cacf}input.sk-input.ng-invalid{border-color:transparent;box-shadow:0 0 0 1px transparent}.sk-tags[disabled],.sk-input[disabled]{border-color:#ebedef;background-color:#f4f6f8}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.PatternValidator, selector: "[pattern][formControlName],[pattern][formControl],[pattern][ngModel]", inputs: ["pattern"] }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i1$1.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "popperOptions", "triggers", "positionTarget", "container", "disableTooltip", "tooltipClass", "tooltipContext", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "directive", type: i1$1.NgbTypeahead, selector: "input[ngbTypeahead]", inputs: ["autocomplete", "container", "editable", "focusFirst", "inputFormatter", "ngbTypeahead", "resultFormatter", "resultTemplate", "selectOnExact", "showHint", "placement", "popperOptions", "popupClass"], outputs: ["selectItem"], exportAs: ["ngbTypeahead"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TagsInputEmailComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-tags-input-email', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TagsInputEmailComponent),
                            multi: true
                        }
                    ], template: "<div [hidden]=\"currentMode === TagsInputViewMode.Write\">\r\n    <div class=\"sk-tags d-flex flex-wrap\">\r\n        <span *ngIf=\"currentMode === TagsInputViewMode.ReadWrite\" (click)=\"goToEditMode($event)\"\r\n            class=\"badge badge-default pointer sk-tag-basic sk-tag\">+ Add tag</span>\r\n        <span *ngFor=\"let t of tags.slice(0, maxVisibleTags); let i = index\" class=\"badge badge-default sk-tag-basic sk-tag\"\r\n            (click)=\"onClick($event, t)\">{{t}}<a>\r\n                <i *ngIf=\"currentMode === TagsInputViewMode.ReadWrite && !lockedTags.includes(t)\" class=\"material-icons md-16 md-gray\" (click)=\"onRemove($event, i)\">clear</i></a>\r\n        </span>\r\n        <span\r\n            *ngIf=\"maxVisibleTags < tags.length\"\r\n            class=\"badge badge-default mb-1 sk-tag-basic sk-tag\"\r\n            [class.pointer]=\"currentMode === TagsInputViewMode.ReadWrite\"\r\n            [ngbTooltip]=\"tags.slice(maxVisibleTags).join(', ')\"\r\n            (click)=\"toggleWriteMode()\"\r\n        >\r\n            + {{tags.length - maxVisibleTags}}\r\n        </span>\r\n    </div>\r\n</div>\r\n<div [hidden]=\"currentMode !== TagsInputViewMode.Write\" class=\"sk-tags-input\"\r\n    [class.is-invalid]=\"tagsInputModel.invalid && (tagsInputModel.dirty || tagsInputModel.touched) || !!externalError\" (click)=\"tagsInputElem.focus()\">\r\n    <div class=\"sk-tag-container\">\r\n        <fieldset class=\"sk-tags\" [disabled]=\"disabled\">\r\n                <ul class=\"sk-tag-list\">\r\n                    <li *ngFor=\"let t of tags; let i = index\" class=\"sk-tag-item tag-inner-text mw-100\">\r\n                        <div class=\"badge badge-default sk-tag-basic sk-tag d-flex\">\r\n                            <div class=\"text-truncate\">\r\n                                <span #tagInnerText class=\"mw-100\" [innerText]=\"t\" [ngbTooltip]=\"getTagTooltipText(tagInnerText)\" [openDelay]=\"300\" placement=\"top-left\"></span>\r\n                            </div>\r\n                            <a><i *ngIf=\"!disabled && !lockedTags.includes(t)\" class=\"material-icons md-16 md-gray\"\r\n                                (click)=\"onRemove($event, i)\">clear</i></a>\r\n                        </div>\r\n                    </li>\r\n                </ul>\r\n\r\n                <input [hidden]=\"inputHiddenInSingleTagMode()\" type=\"text\" autocomplete=\"off\" class=\"sk-input w-100\" [(ngModel)]=\"model\" [ngbTypeahead]=\"search\"\r\n                    #instance=\"ngbTypeahead\" (focus)=\"focusSubject.next($event.target.value)\"\r\n                    (keyup)=\"onInput()\"\r\n                    (focusout)=\"focusOut()\"\r\n                    (click)=\"clickSubject.next($event.target.value)\" [focusFirst]=\"false\" (keydown)=\"onKeyDown($event)\"\r\n                    (selectItem)=\"onItemSelected($event)\" [placeholder]=\"placeholder\" [disabled]=\"disabled\"\r\n                    [pattern]=\"getValidationPattern()\" [title]=\"validationPatternDescription\" #tagsInputModel=\"ngModel\"\r\n                    #tagsInputElem>\r\n        </fieldset>\r\n        <small *ngIf=\"tagsInputModel.invalid && (tagsInputModel.dirty || tagsInputModel.touched)\"\r\n            class=\"invalid-feedback\">{{ validationPatternDescription }}</small>\r\n        <small id=\"tagsInputExternalError\" *ngIf=\"!!externalError\"\r\n            class=\"invalid-feedback\">{{ externalError }}</small>\r\n    </div>\r\n</div>\r\n", styles: [".is-invalid .sk-tags{border-color:#fa6173;box-shadow:inset 0 0 0 1px #fa6173}.pointer{cursor:pointer}.sk-tags-input .sk-tag-container .sk-tags{padding:.35rem .75rem;border:1px solid #c4cacf}input.sk-input.ng-invalid{border-color:transparent;box-shadow:0 0 0 1px transparent}.sk-tags[disabled],.sk-input[disabled]{border-color:#ebedef;background-color:#f4f6f8}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { allowPastingMultipleEmails: [{
                type: Input
            }] } });

class InputModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: InputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: InputModule, declarations: [BasicInputComponent,
            TagsInputComponent,
            TransparentInputComponent,
            SwitchComponent,
            TagsInputEmailComponent], imports: [CommonModule,
            FormsModule$1,
            NgbModule,
            NgbTooltipModule], exports: [BasicInputComponent,
            TagsInputComponent,
            TagsInputEmailComponent,
            TransparentInputComponent,
            SwitchComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: InputModule, imports: [CommonModule,
            FormsModule$1,
            NgbModule,
            NgbTooltipModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: InputModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        BasicInputComponent,
                        TagsInputComponent,
                        TransparentInputComponent,
                        SwitchComponent,
                        TagsInputEmailComponent
                    ],
                    imports: [
                        CommonModule,
                        FormsModule$1,
                        NgbModule,
                        NgbTooltipModule
                    ],
                    exports: [
                        BasicInputComponent,
                        TagsInputComponent,
                        TagsInputEmailComponent,
                        TransparentInputComponent,
                        SwitchComponent
                    ]
                }]
        }] });

class HelpPopoverComponent {
    constructor(conf) {
        this.direction = "top";
        this.openDelay = 0;
        conf.triggers = 'hover';
        conf.container = 'body';
    }
    leave() {
        let mouseover = false;
        // let's close popover if mouse not hover it
        setTimeout(() => {
            if (!mouseover) {
                this.p.close();
            }
            // close popover in 300ms after mouse leave it
        }, 300);
        const popoverShims = document.querySelectorAll('.sk-popover-shim');
        if (!popoverShims.length) {
            return;
        }
        popoverShims.forEach((popoverShim) => {
            // let's close popover when mouse leaves it
            setTimeout(() => {
                popoverShim.addEventListener('mouseleave', () => {
                    this.p.close();
                });
                // let's add listener with delay after mouse leave icon, to escape false-positive leave event
                // when slowly moving cursor from top to bottom
            }, 80);
            popoverShim.addEventListener('mouseenter', () => {
                mouseover = true;
            });
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HelpPopoverComponent, deps: [{ token: i1$1.NgbPopoverConfig }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: HelpPopoverComponent, selector: "sk-help-popover", inputs: { message: "message", direction: "direction", openDelay: "openDelay" }, viewQueries: [{ propertyName: "p", first: true, predicate: ["p"], descendants: true }], ngImport: i0, template: "<ng-template [ngIf]=\"message\">\r\n    <ng-template #popContent><div [innerHtml]=\"message\"></div></ng-template>\r\n    <a class=\"ml-25 btn sk-btn-icon sk-hover-target px-0 height-inherit border-0 bd-popover\" (mouseleave)=\"leave()\"\r\n        [ngbPopover]=\"popContent\" [openDelay]=\"openDelay\" popoverClass=\"sk-popover-shim\"\r\n        #p=\"ngbPopover\" [autoClose]=\"'outside'\" triggers=\"mouseenter:click\">\r\n        <i class=\"material-icons md-20 icon-muted\">help_outline</i>\r\n    </a>\r\n</ng-template>\r\n<ng-template [ngIf]=\"!message\">\r\n    <div (mouseleave)=\"leave()\">\r\n        <ng-template #popContent><ng-content></ng-content></ng-template>\r\n        <a class=\"btn sk-btn-icon px-0 height-inherit border-0\"\r\n            [ngbPopover]=\"popContent\" [openDelay]=\"openDelay\" popoverClass=\"sk-popover-shim\" [placement]=\"direction\"\r\n            #p=\"ngbPopover\" [autoClose]=\"'outside'\" triggers=\"mouseenter:click\">\r\n            <i class=\"material-icons md-20 icon-muted\">help_outline</i>\r\n        </a>\r\n    </div>\r\n</ng-template>\r\n", styles: ["::ng-deep ngb-popover-window.popover.bs-popover-top>.popover-arrow{bottom:-7px}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgbPopover, selector: "[ngbPopover]", inputs: ["animation", "autoClose", "ngbPopover", "popoverTitle", "placement", "popperOptions", "triggers", "positionTarget", "container", "disablePopover", "popoverClass", "popoverContext", "openDelay", "closeDelay"], outputs: ["shown", "hidden"], exportAs: ["ngbPopover"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HelpPopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-help-popover', template: "<ng-template [ngIf]=\"message\">\r\n    <ng-template #popContent><div [innerHtml]=\"message\"></div></ng-template>\r\n    <a class=\"ml-25 btn sk-btn-icon sk-hover-target px-0 height-inherit border-0 bd-popover\" (mouseleave)=\"leave()\"\r\n        [ngbPopover]=\"popContent\" [openDelay]=\"openDelay\" popoverClass=\"sk-popover-shim\"\r\n        #p=\"ngbPopover\" [autoClose]=\"'outside'\" triggers=\"mouseenter:click\">\r\n        <i class=\"material-icons md-20 icon-muted\">help_outline</i>\r\n    </a>\r\n</ng-template>\r\n<ng-template [ngIf]=\"!message\">\r\n    <div (mouseleave)=\"leave()\">\r\n        <ng-template #popContent><ng-content></ng-content></ng-template>\r\n        <a class=\"btn sk-btn-icon px-0 height-inherit border-0\"\r\n            [ngbPopover]=\"popContent\" [openDelay]=\"openDelay\" popoverClass=\"sk-popover-shim\" [placement]=\"direction\"\r\n            #p=\"ngbPopover\" [autoClose]=\"'outside'\" triggers=\"mouseenter:click\">\r\n            <i class=\"material-icons md-20 icon-muted\">help_outline</i>\r\n        </a>\r\n    </div>\r\n</ng-template>\r\n", styles: ["::ng-deep ngb-popover-window.popover.bs-popover-top>.popover-arrow{bottom:-7px}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.NgbPopoverConfig }], propDecorators: { message: [{
                type: Input
            }], direction: [{
                type: Input
            }], openDelay: [{
                type: Input
            }], p: [{
                type: ViewChild,
                args: ['p']
            }] } });

class PopoverModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: PopoverModule, declarations: [HelpPopoverComponent], imports: [CommonModule,
            NgbPopoverModule], exports: [HelpPopoverComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PopoverModule, imports: [CommonModule,
            NgbPopoverModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        HelpPopoverComponent
                    ],
                    imports: [
                        CommonModule,
                        NgbPopoverModule
                    ],
                    exports: [
                        HelpPopoverComponent
                    ]
                }]
        }] });

class SortOperation {
    constructor(propName, reverse, emptyLast) {
        this.propName = propName;
        this.reverse = reverse;
        this.emptyLast = emptyLast;
    }
}

class SortingRack {
    get isReversed() {
        return this.current.reverse;
    }
    ;
    constructor() {
        this.properties = [];
    }
    bindSource($stream) {
        this.source = $stream;
    }
    getProperty(name) {
        return this.properties.find(p => p.name == name);
    }
    getDefault() {
        return this.defaultOp;
    }
    sort(propName, reverse) {
        let property = this.getProperty(propName);
        property.reverse = reverse;
        this.current = new SortOperation(property.name, property.reverse, property.emptyLast);
        this.$sort.next(this.current);
    }
}

class SortDirective {
    onClick() {
        this.handle.sort(this.name, !this.handle.isReversed);
    }
    get isSorting() {
        return this.handle.current.propName === this.name;
    }
    get isAscending() {
        return !this.handle.current.reverse;
    }
    get isDescending() {
        return this.handle.current.reverse;
    }
    constructor() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SortDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: SortDirective, selector: "[skSort]", inputs: { handle: ["skSort", "handle"], name: ["skSortName", "name"] }, host: { listeners: { "click": "onClick()" }, properties: { "class.sorted": "this.isSorting", "class.ascending": "this.isAscending", "class.descending": "this.isDescending" }, classAttribute: "sortable" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SortDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[skSort]',
                    host: {
                        class: 'sortable'
                    }
                }]
        }], ctorParameters: () => [], propDecorators: { handle: [{
                type: Input,
                args: ['skSort']
            }], name: [{
                type: Input,
                args: ['skSortName']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }], isSorting: [{
                type: HostBinding,
                args: ['class.sorted']
            }], isAscending: [{
                type: HostBinding,
                args: ['class.ascending']
            }], isDescending: [{
                type: HostBinding,
                args: ['class.descending']
            }] } });

class SortingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SortingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: SortingModule, declarations: [SortDirective], imports: [CommonModule], exports: [SortDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SortingModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SortingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SortDirective
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        SortDirective
                    ]
                }]
        }] });

class TextSpinnerComponent {
    constructor() {
        this.size = 'lg';
        this.spinnerLocation = 'center';
        this.loaderWrapperClasses = 'd-inline-flex';
    }
    ngOnInit() { }
    hasMessage() {
        return !!this.message;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TextSpinnerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: TextSpinnerComponent, selector: "sk-text-spinner", inputs: { message: "message", size: "size", spinnerLocation: "spinnerLocation", loaderWrapperClasses: "loaderWrapperClasses" }, ngImport: i0, template: "<div [ngClass]=\"spinnerLocation ? 'text-' + spinnerLocation : null\">\r\n    <div [ngClass]=\"loaderWrapperClasses\">\r\n        <div class=\"sk-spin-loader\" [ngClass]=\"'loader-' + size\"></div>\r\n    </div>\r\n    <div *ngIf=\"hasMessage()\" [innerHTML]=\"message\"></div>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TextSpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-text-spinner', template: "<div [ngClass]=\"spinnerLocation ? 'text-' + spinnerLocation : null\">\r\n    <div [ngClass]=\"loaderWrapperClasses\">\r\n        <div class=\"sk-spin-loader\" [ngClass]=\"'loader-' + size\"></div>\r\n    </div>\r\n    <div *ngIf=\"hasMessage()\" [innerHTML]=\"message\"></div>\r\n</div>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { message: [{
                type: Input
            }], size: [{
                type: Input
            }], spinnerLocation: [{
                type: Input
            }], loaderWrapperClasses: [{
                type: Input
            }] } });

class StatusSpinnerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: StatusSpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: StatusSpinnerModule, declarations: [TextSpinnerComponent], imports: [CommonModule], exports: [TextSpinnerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: StatusSpinnerModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: StatusSpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        TextSpinnerComponent
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        TextSpinnerComponent
                    ]
                }]
        }] });

class HighlightDirective {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        this.setTitle = false; //sets title attribute of HTML
    }
    ngOnChanges(changes) {
        if (!this.content) {
            return;
        }
        if (this.setTitle) {
            this.renderer.setProperty(this.el.nativeElement, 'title', this.content);
        }
        if (!this.searchedWord || !this.searchedWord.length || !this.classToApply) {
            this.renderer.setProperty(this.el.nativeElement, 'innerHTML', this.content);
            return;
        }
        this.renderer.setProperty(this.el.nativeElement, 'innerHTML', this.getFormattedText());
    }
    getFormattedText() {
        const re = new RegExp(`(${this.searchedWord})`, 'gi');
        return this.content.replace(re, `<span class="${this.classToApply}">$1</span>`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HighlightDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: HighlightDirective, selector: "[search-highlight]", inputs: { searchedWord: "searchedWord", content: "content", classToApply: "classToApply", setTitle: "setTitle" }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HighlightDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[search-highlight]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { searchedWord: [{
                type: Input
            }], content: [{
                type: Input
            }], classToApply: [{
                type: Input
            }], setTitle: [{
                type: Input
            }] } });

var AlertActionId;
(function (AlertActionId) {
    AlertActionId["BackupMailbox"] = "D2617A86-6B78-EB11-9889-000D3A32BDB8";
    AlertActionId["BackupSharePoint"] = "B4FFC1BB-A9FC-4E7C-BAF7-48DD2BE95BB5";
    AlertActionId["BackupOneDrive"] = "DA51A020-C0D5-4D77-A1E4-016CDE1AA81F";
})(AlertActionId || (AlertActionId = {}));

var LogicalOperator;
(function (LogicalOperator) {
    LogicalOperator[LogicalOperator["And"] = 0] = "And";
    LogicalOperator[LogicalOperator["Or"] = 1] = "Or";
})(LogicalOperator || (LogicalOperator = {}));
var ConditionOperator;
(function (ConditionOperator) {
    ConditionOperator[ConditionOperator["Equal"] = 0] = "Equal";
    ConditionOperator[ConditionOperator["NotEqual"] = 1] = "NotEqual";
    ConditionOperator[ConditionOperator["GreaterThan"] = 2] = "GreaterThan";
    ConditionOperator[ConditionOperator["LessThan"] = 3] = "LessThan";
    ConditionOperator[ConditionOperator["GreaterEqual"] = 4] = "GreaterEqual";
    ConditionOperator[ConditionOperator["LessEqual"] = 5] = "LessEqual";
    ConditionOperator[ConditionOperator["Null"] = 12] = "Null";
    ConditionOperator[ConditionOperator["NotNull"] = 13] = "NotNull";
    ConditionOperator[ConditionOperator["On"] = 25] = "On";
    ConditionOperator[ConditionOperator["OnOrBefore"] = 26] = "OnOrBefore";
    ConditionOperator[ConditionOperator["OnOrAfter"] = 27] = "OnOrAfter";
    ConditionOperator[ConditionOperator["Contains"] = 28] = "Contains";
})(ConditionOperator || (ConditionOperator = {}));

function formatNgbDateStruct(date) {
    function lessThan10Format(n) {
        return n < 10 ? `0${n.toString()}` : n.toString();
    }
    if (!date)
        return null;
    return `${date.year}-${lessThan10Format(date.month)}-${lessThan10Format(date.day)}T08:00:00.000Z`;
}

var AlertsFilterStatus;
(function (AlertsFilterStatus) {
    AlertsFilterStatus[AlertsFilterStatus["Active"] = 0] = "Active";
    AlertsFilterStatus[AlertsFilterStatus["Complete"] = 1] = "Complete";
    AlertsFilterStatus[AlertsFilterStatus["Archive"] = 2] = "Archive";
    AlertsFilterStatus[AlertsFilterStatus["Any"] = 100] = "Any";
})(AlertsFilterStatus || (AlertsFilterStatus = {}));

const ORDERSTATUSTYPES = [
    { name: 'Active', value: AlertsFilterStatus.Active },
    { name: 'Complete', value: AlertsFilterStatus.Complete },
    { name: 'Archived', value: AlertsFilterStatus.Archive },
    { name: 'Any Status', value: AlertsFilterStatus.Any }
];

// This represents the status of an alert
var PartnerStatus;
(function (PartnerStatus) {
    PartnerStatus[PartnerStatus["Active"] = 772370000] = "Active";
    PartnerStatus[PartnerStatus["Archive"] = 772370001] = "Archive";
    PartnerStatus[PartnerStatus["Complete"] = 772370002] = "Complete";
    PartnerStatus[PartnerStatus["AutoArchive"] = 772370099] = "AutoArchive";
})(PartnerStatus || (PartnerStatus = {}));

var ProductTypes;
(function (ProductTypes) {
    ProductTypes[ProductTypes["Migration"] = 0] = "Migration";
    ProductTypes[ProductTypes["Backup"] = 1] = "Backup";
    ProductTypes[ProductTypes["Any"] = 100] = "Any";
})(ProductTypes || (ProductTypes = {}));
const PRODUCTTYPES = [
    { name: 'Migration', value: ProductTypes.Migration },
    { name: 'Backup', value: ProductTypes.Backup },
    { name: 'All Products', value: ProductTypes.Any }
];

var SeverityTypes;
(function (SeverityTypes) {
    SeverityTypes[SeverityTypes["Informational"] = 0] = "Informational";
    SeverityTypes[SeverityTypes["Success"] = 1] = "Success";
    SeverityTypes[SeverityTypes["Warning"] = 2] = "Warning";
    SeverityTypes[SeverityTypes["Critical"] = 3] = "Critical";
    SeverityTypes[SeverityTypes["Any"] = 100] = "Any";
})(SeverityTypes || (SeverityTypes = {}));
const SEVERITYTYPES = [
    { name: 'Informational', value: SeverityTypes.Informational, alertFilterCriteriaName: 'IsInformational' },
    { name: 'Success', value: SeverityTypes.Success, alertFilterCriteriaName: 'IsSuccessful' },
    { name: 'Warning', value: SeverityTypes.Warning, alertFilterCriteriaName: 'IsWarning' },
    { name: 'Critical', value: SeverityTypes.Critical, alertFilterCriteriaName: 'IsError' },
    { name: 'Any Severity', value: SeverityTypes.Any, alertFilterCriteriaName: 'Any' }
];

class NotificationMethodsService {
    constructor(routeResolverService, translate) {
        this.routeResolverService = routeResolverService;
        this.translate = translate;
    }
    buildQueryForCustomerAlertSummary(orderFilter) {
        const take = 50;
        const skip = orderFilter.page * take;
        const query = {
            skipCount: skip,
            topCount: take,
            orders: [],
            criteria: this.buildQueryCriteria(orderFilter)
        };
        if (orderFilter.orderBy.orderByField === 'Severity') {
            query.orders = [
                { AttributeName: 'HasCriticalAlerts', OrderType: orderFilter.orderBy.orderByType },
                { AttributeName: 'HasWarningAlerts', OrderType: orderFilter.orderBy.orderByType },
                { AttributeName: 'HasInformationAlerts', OrderType: orderFilter.orderBy.orderByType },
                { AttributeName: 'HasSuccessAlerts', OrderType: orderFilter.orderBy.orderByType },
            ];
        }
        else {
            query.orders = [
                { AttributeName: orderFilter.orderBy.orderByField, OrderType: orderFilter.orderBy.orderByType }
            ];
        }
        // add a secondary default sort to sort by created on descending
        if (orderFilter.orderBy.orderByField !== 'CreatedOn') {
            query.orders.push({ AttributeName: 'CreatedOn', OrderType: 'Descending' });
        }
        return query;
    }
    buildQueryForCustomerLookup(customerId) {
        const criteria = {
            operator: LogicalOperator.And,
            conditions: [],
            filters: []
        };
        criteria.conditions.push({
            attributeName: 'OrderId',
            operator: ConditionOperator.Equal,
            values: [customerId]
        });
        const query = {
            skipCount: 0,
            topCount: 25,
            orders: [],
            criteria: criteria
        };
        return query;
    }
    buildQueryCriteria(orderFilter) {
        const criteria = {
            operator: LogicalOperator.And,
            conditions: [],
            filters: []
        };
        // process product
        if (orderFilter.product !== ProductTypes.Any) {
            criteria.conditions.push({
                attributeName: 'Product',
                operator: ConditionOperator.Equal,
                values: [orderFilter.product.toString()]
            });
        }
        // process severity
        let attributeName = null;
        switch (orderFilter.severity) {
            case SeverityTypes.Informational:
                attributeName = 'HasInformationAlerts';
                break;
            case SeverityTypes.Success:
                attributeName = 'HasSuccessAlerts';
                break;
            case SeverityTypes.Warning:
                attributeName = 'HasWarningAlerts';
                break;
            case SeverityTypes.Critical:
                attributeName = 'HasCriticalAlerts';
                break;
        }
        if (attributeName != null) {
            criteria.conditions.push({
                attributeName,
                operator: ConditionOperator.Equal,
                values: ['true']
            });
        }
        // Date range filter
        if (orderFilter.startDate && orderFilter.endDate) {
            criteria.conditions.push({
                attributeName: 'CreatedOn',
                operator: ConditionOperator.OnOrAfter,
                values: [formatNgbDateStruct(orderFilter.startDate)]
            }, {
                attributeName: 'CreatedOn',
                operator: ConditionOperator.OnOrBefore,
                values: [formatNgbDateStruct(orderFilter.endDate)]
            });
        }
        // Date till now filter
        else if (orderFilter.startDate) {
            criteria.conditions.push({
                attributeName: 'CreatedOn',
                operator: ConditionOperator.OnOrAfter,
                values: [formatNgbDateStruct(orderFilter.startDate)]
            });
        }
        if (orderFilter.searchCustomer) {
            criteria.conditions.push({
                attributeName: 'CustomerName',
                operator: ConditionOperator.Contains,
                values: [orderFilter.searchCustomer]
            });
        }
        return criteria;
    }
    buildQueryForOrderAlerts(orderId, alertsFilter, page, orderBy) {
        const take = 50;
        const query = {
            skipCount: page * take,
            topCount: take,
            orders: [],
            criteria: {
                filters: [],
                conditions: [],
                operator: LogicalOperator.And
            }
        };
        const conditions = [];
        let filters = [];
        const statusValues = [];
        if (orderBy.orderByField === 'Severity') {
            query.orders = [
                { AttributeName: 'IsError', OrderType: orderBy.orderByType },
                { AttributeName: 'IsWarning', OrderType: orderBy.orderByType },
                { AttributeName: 'IsInformational', OrderType: orderBy.orderByType },
                { AttributeName: 'IsSuccessful', OrderType: orderBy.orderByType },
            ];
        }
        else {
            query.orders = [
                { AttributeName: orderBy.orderByField, OrderType: orderBy.orderByType }
            ];
        }
        // add a secondary default sort to sort by created on descending
        if (orderBy.orderByField !== 'CreatedOn') {
            query.orders.push({ AttributeName: 'CreatedOn', OrderType: 'Descending' });
        }
        if (!alertsFilter) {
            // Setup the default filter.
            // This matches the default settings in notifications-customer-alerts-filter.component.ts
            alertsFilter = {
                status: AlertsFilterStatus.Active,
                severity: null,
                endDate: null,
                startDate: null
            };
        }
        switch (+alertsFilter.status) {
            case AlertsFilterStatus.Active:
                statusValues.push(PartnerStatus.Active);
                break;
            case AlertsFilterStatus.Complete:
                statusValues.push(PartnerStatus.Complete);
                break;
            case AlertsFilterStatus.Archive:
                statusValues.push(PartnerStatus.Archive);
                statusValues.push(PartnerStatus.AutoArchive);
                break;
            default:
                statusValues.push(PartnerStatus.Active);
                break;
        }
        if (statusValues) {
            filters = [
                {
                    operator: 1,
                    conditions: []
                }
            ];
            filters[0].conditions = statusValues.map(value => {
                return {
                    attributeName: 'PartnerStatus',
                    operator: 0,
                    values: [value]
                };
            });
        }
        if (alertsFilter) {
            // Severity filter
            if (alertsFilter.severity !== SeverityTypes.Any) {
                conditions.push({
                    attributeName: SEVERITYTYPES.find(x => x.value === alertsFilter.severity).alertFilterCriteriaName,
                    operator: 0,
                    values: ['true']
                });
            }
        }
        // Date range filter
        if (alertsFilter.startDate && alertsFilter.endDate) {
            conditions.push({
                attributeName: 'CreatedOn',
                operator: 27,
                values: [alertsFilter.startDate]
            }, {
                attributeName: 'CreatedOn',
                operator: 26,
                values: [alertsFilter.endDate]
            });
        }
        // Date till now filter
        else if (alertsFilter.startDate) {
            conditions.push({
                attributeName: 'CreatedOn',
                operator: 27,
                values: [alertsFilter.startDate]
            });
        }
        conditions.push({
            attributeName: 'OrderId',
            operator: 0,
            values: [
                orderId
            ]
        });
        query.criteria.filters = filters;
        query.criteria.conditions = conditions;
        return query;
    }
    getProductType(productType) {
        switch (productType) {
            case '0':
                return 'Migration';
            case '1':
                return 'Backup';
        }
    }
    getOrderAlertPageUrlByAlert(alert) {
        if (alert.product === 0) {
            return `${this.routeResolverService.generateRatRoute(AppId.Migrate)}partner/migrations/monitor#/order/${alert.orderId}/alerts`;
        }
        else if (alert.product === 1) {
            return `${this.routeResolverService.generateRatRoute(AppId.Backup)}partner/cloud-backup/manager#/order/${alert.backupServiceId}/alerts`;
        }
    }
    getOrderAlertPageUrlByCustomer(customer) {
        if (customer.productType === 'Migration') {
            return `${this.routeResolverService.generateRatRoute(AppId.Migrate)}partner/migrations/monitor#/order/${customer.orderId}/alerts`;
        }
        else if (customer.productType === 'Backup') {
            return `${this.routeResolverService.generateRatRoute(AppId.Backup)}partner/cloud-backup/manager#/order/${customer.backupServiceId}/alerts`;
        }
    }
    getInvokeAlertActionSuccessToastMessage(alertActionId) {
        switch (alertActionId.toUpperCase()) {
            case AlertActionId.BackupMailbox:
                return this.translate.instant('platform.alerts.messages.backup_has_been_enable_for_mailbox');
            case AlertActionId.BackupOneDrive:
                return this.translate.instant('platform.alerts.messages.backup_has_been_enabled_for_onedrive');
            case AlertActionId.BackupSharePoint:
                return this.translate.instant('platform.alerts.messages.backup_has_been_unabled_for_sharepoint');
        }
    }
    getOrderQueryForAlertCount() {
        const filter = {
            product: ProductTypes.Any,
            severity: SeverityTypes.Any,
            startDate: undefined,
            endDate: undefined,
            searchCustomer: '',
            page: 0,
            orderBy: {
                orderByType: 'Descending', orderByField: 'Severity'
            }
        };
        return filter;
    }
    getSeverityTypeDisplayName(severityType) {
        return SEVERITYTYPES.find(x => x.value === severityType).name;
    }
    getAlertFilterStatusDisplayName(alertFilterType) {
        return ORDERSTATUSTYPES.find(x => x.value === alertFilterType).name;
    }
    /* Checks that provided NotificationsInitializationConfig is valid */
    verifyModalConfig(config) {
        if (!config) {
            return false;
        }
        // CustomerId will navigate to order, order with specified alerts, or one specific alert
        if ((config.CustomerId === null ||
            config.CustomerId === undefined) &&
            config.BackupServiceId === null ||
            config.BackupServiceId === undefined) {
            // If no customerId, check if filter is set
            if (config.OrderFilter === null ||
                config.OrderFilter === undefined) {
                return false;
            }
            // If filter is set either prodcut or severtity must be set
            else if ((config.OrderFilter.product === null ||
                config.OrderFilter.product === undefined) &&
                (config.OrderFilter.severity === null ||
                    config.OrderFilter.severity === undefined)) {
                return false;
            }
        }
        // If alerts filter is provided it must have a severity or status
        if (config.AlertsFilter !== null &&
            config.AlertsFilter !== undefined) {
            if ((config.AlertsFilter.severity === null ||
                config.AlertsFilter.severity === undefined) &&
                (config.AlertsFilter.status === null ||
                    config.AlertsFilter.status === undefined)) {
                return false;
            }
        }
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationMethodsService, deps: [{ token: RouteResolverService }, { token: i2$3.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationMethodsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationMethodsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: RouteResolverService }, { type: i2$3.TranslateService }] });

class NotificationsFilterPipe {
    constructor(notificationMethodsService) {
        this.notificationMethodsService = notificationMethodsService;
    }
    transform(allCustomers, searchText, productType, severityType, startDate, endDate) {
        if (!allCustomers) {
            return [];
        }
        let filterList = [];
        if (searchText) {
            filterList.push('search');
        }
        if (productType) {
            filterList.push('productType');
        }
        if (severityType) {
            filterList.push('severityType');
        }
        if (startDate) {
            filterList.push('startDate');
        }
        if (endDate) {
            filterList.push('endDate');
        }
        if (!filterList.length) {
            return allCustomers;
        }
        if (filterList.length) {
            filterList.forEach(filter => {
                switch (filter) {
                    case 'search':
                        allCustomers = allCustomers.filter(item => item.customerName.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()));
                        break;
                    case 'productType':
                        allCustomers = allCustomers.filter(item => item.productType === this.notificationMethodsService.getProductType(productType));
                        break;
                    case 'severityType':
                        allCustomers = allCustomers.filter(item => {
                            switch (severityType) {
                                case '0': return item.hasInformationAlerts;
                                case '1': return item.hasSuccessAlerts;
                                case '2': return item.hasWarningAlerts;
                                case '3': return item.hasCriticalAlerts;
                            }
                        });
                        break;
                    case 'startDate':
                        allCustomers = allCustomers.filter(item => {
                            const date = moment({
                                d: startDate.day,
                                M: startDate.month - 1,
                                y: startDate.year
                            });
                            return moment.utc(item.createdOn).isAfter(date);
                        });
                        break;
                    case 'endDate':
                        allCustomers = allCustomers.filter(item => {
                            const date = moment({
                                d: endDate.day,
                                M: endDate.month - 1,
                                y: endDate.year
                            });
                            return moment.utc(item.createdOn).isBefore(date);
                        });
                        break;
                }
            });
        }
        return allCustomers;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsFilterPipe, deps: [{ token: NotificationMethodsService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: NotificationsFilterPipe, name: "notificationsFilter" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsFilterPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'notificationsFilter'
                }]
        }], ctorParameters: () => [{ type: NotificationMethodsService }] });

class DateBucket {
    constructor(init) {
        Object.assign(this, init);
    }
}

class DateBucketService {
    constructor(datePipe) {
        this.datePipe = datePipe;
    }
    bucketByDate(data, dateProperty) {
        if (!data) {
            return data;
        }
        const groupedCollection = data.reduce((prev, cur) => {
            // Minimum bucket size of 1 hour
            const curDate = this.datePipe.transform(this.getDateProp(cur, dateProperty), SKYKICK_DEFAULT_FORMAT, null, null, 1000 * 60 * 60);
            if (!prev[curDate]) {
                prev[curDate] = [cur];
            }
            else {
                prev[curDate].push(cur);
            }
            return prev;
        }, {});
        return Object.keys(groupedCollection).map(key => {
            return new DateBucket({
                dateKey: key,
                objects: groupedCollection[key]
            });
        });
    }
    getDateProp(obj, dateProperty) {
        const propArray = dateProperty.split('.');
        if (obj && propArray.length === 1) {
            return obj[dateProperty];
        }
        else {
            return this.getDateProp(obj[propArray[0]], propArray.slice(1).join('.'));
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DateBucketService, deps: [{ token: SkyKickDatePipe }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DateBucketService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DateBucketService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: SkyKickDatePipe }] });

/**
 * Adds a specified number of hours to a date object
 */
Date.prototype.addHours = function (hours) {
    const date = this;
    date.setTime(date.getTime() + (hours * 60 * 60 * 1000));
    return date;
};

class CacheService {
    constructor() {
        this.cacheMap = {};
        this.inFlightMap = {};
    }
    /**
     * Retrieves a value from the cache, or if not present, adds it to the cache.
     * @param key cache key value
     * @param source Source observable of the data to add to the cache
     * @param expireTime Date at which this item should be removed from the cache
     */
    getOrAdd(key, source, expireTime = new Date().addHours(1)) {
        if (this.cacheMap[key] && !this.hasExpired(this.cacheMap[key].expirationDate)) {
            return of(this.cacheMap[key].value);
        }
        else if (this.inFlightMap[key]) {
            return this.inFlightMap[key];
        }
        else {
            this.inFlightMap[key] = source
                .pipe(tap(value => {
                this.cacheMap[key] = new CacheItem(value, expireTime);
                this.inFlightMap[key] = null;
            }), share());
            return this.inFlightMap[key];
        }
    }
    /**
     * Retrieves a value from the cache, or null if not present
     * @param key cache key value
     */
    getValue(key) {
        return this.hasValue(key) ? this.cacheMap[key].value : null;
    }
    /**
     * Sets a value into the cache
     * @param key cache key
     * @param value value to insert
     * @param expireTime Date it which this item should be removed from the cache
     */
    setValue(key, value, expireTime = new Date().addHours(1)) {
        this.cacheMap[key] = new CacheItem(value, expireTime);
    }
    /**
     * Clears the value from the cache
     * @param key cache key
     */
    clear(key) {
        this.cacheMap[key] = null;
        this.inFlightMap[key] = null;
    }
    /**
     * Returns if this key is both in the cache (or currently 'in-flight') and not expired
     * @param key cache key
     */
    hasValue(key) {
        return (!!this.cacheMap[key] && !this.hasExpired(this.cacheMap[key].expirationDate)) ||
            !!this.inFlightMap[key];
    }
    hasExpired(date) {
        return date.getTime() < Date.now();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CacheService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CacheService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CacheService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });
class CacheItem {
    constructor(value, date) {
        this.value = value;
        this.expirationDate = date;
    }
}

class ResourceCollectionManager {
    constructor() {
        this.initializing = true;
    }
    get isInitializing() {
        return this.initializing;
    }
    set isInitializing(isInitializing) {
        this.initializing = isInitializing;
    }
    getObservable() {
        return this.$resources;
    }
}

class QueryOptions {
    constructor(Limit = 50, Skip = 0, SearchTerm) {
        this.Limit = Limit;
        this.Skip = Skip;
        this.SearchTerm = SearchTerm;
    }
    ToHttpParams() {
        let params = new HttpParams()
            .append('limit', this.Limit.toString())
            .append('skip', this.Skip.toString());
        if (this.SearchTerm) {
            params = params.append('searchTerm', this.SearchTerm);
        }
        return params;
    }
}

class PageableResourceCollectionManager extends ResourceCollectionManager {
    get isPaging() {
        return this._isPaging;
    }
    set isPaging(isPaging) {
        this._isPaging = isPaging;
    }
    get isSearching() {
        return this._isSearching;
    }
    set isSearching(isSearching) {
        this._isSearching = isSearching;
    }
    get hasMoreData() {
        return this._hasMoreData;
    }
    get hasData() {
        return this._hasData;
    }
    constructor(source, { pageSize = 10, debounceTime = 500 } = {}) {
        super();
        this._isPaging = false;
        this._isSearching = false;
        this._hasMoreData = false;
        this._hasData = false;
        this.searchTerm = undefined;
        this._pageIndex = 0;
        this._loadSubject = new BehaviorSubject(null);
        this._searchSubject = new BehaviorSubject(this.searchTerm);
        this._firstSearch = true;
        this._pageSize = pageSize;
        const search$ = this._searchSubject
            .pipe(debounce(() => {
            if (this._firstSearch) {
                this._firstSearch = false;
                return timer(0);
            }
            return timer(debounceTime);
        }), distinctUntilChanged(), tap(searchTerm => {
            this._pageIndex = 0;
            this.isSearching = true;
            this.searchTerm = searchTerm;
        }));
        const load$ = this._loadSubject
            .pipe(tap(() => this.isPaging = true));
        const options$ = combineLatest([
            search$,
            load$
        ]).pipe(
        // request one more than the page size.
        // if the number of items returned is the pageSize or less, then there are no more (below).
        map(([searchTerm, _]) => new QueryOptions(this._pageSize + 1, this._pageSize * this._pageIndex, searchTerm)));
        this.$resources =
            options$
                .pipe(switchMap(options => source(options)), 
            // request one more than the page size(above).
            // if the number of items returned is the pageSize or less, then there are no more.
            tap(items => this._hasMoreData = items.length > this._pageSize), map(items => items.length > this._pageSize ? items.slice(0, this._pageSize) : items), scan((acc, val) => this._pageIndex === 0 ? val : acc.concat(val), []), tap(items => {
                if (this.isInitializing) {
                    this._hasData = !!items.length;
                }
                this.isInitializing = false;
                this.isSearching = false;
                this.isPaging = false;
            }), shareReplay(1));
    }
    nextPage() {
        this._pageIndex = this._pageIndex + 1;
        this._loadSubject.next();
    }
    search(term) {
        this._searchSubject.next(term);
    }
    refresh() {
        this._pageIndex = 0;
        this._loadSubject.next();
        return this.getObservable();
    }
}

class PageableResourceCollectionManagerBuilder {
    setSource(source) {
        this._source = source;
        return this;
    }
    setPageSize(size) {
        this._pageSize = size;
        return this;
    }
    setSearchOptions(debounceTimeMilliseconds) {
        this._searchDebounceTime = debounceTimeMilliseconds;
        return this;
    }
    build() {
        return new PageableResourceCollectionManager(this._source, { pageSize: this._pageSize, debounceTime: this._searchDebounceTime });
    }
}

class SimpleResourceCollectionManager extends ResourceCollectionManager {
    constructor() {
        super();
    }
    setSource(source) {
        this.$source = source;
    }
    setup() {
        // A simple resource collection only cares about when data is done being loaded
        this.$resources = this.$source.pipe(tap(() => {
            this.isInitializing = false;
        }));
        return this;
    }
}

class SimpleResourceCollectionManagerBuilder {
    constructor() {
        this.manager = new SimpleResourceCollectionManager();
    }
    SetSource(source) {
        this.manager.setSource(source);
        return this;
    }
    Build() {
        return this.manager.setup();
    }
}

class ResourceCollectionManagerBuilder {
    UsePageable() {
        return new PageableResourceCollectionManagerBuilder();
    }
    UseSimple() {
        return new SimpleResourceCollectionManagerBuilder();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ResourceCollectionManagerBuilder, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ResourceCollectionManagerBuilder }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ResourceCollectionManagerBuilder, decorators: [{
            type: Injectable
        }] });

class EventHub {
    constructor() {
        this.eventListenerRegistry = {};
    }
    on(eventName, listener) {
        const eventListenerId = Date.now().toString();
        if (!this.eventListenerRegistry[eventName]) {
            this.eventListenerRegistry[eventName] = {};
        }
        this.eventListenerRegistry[eventName][eventListenerId] = listener;
        return () => delete this.eventListenerRegistry[eventName][eventListenerId];
    }
    emit(eventName, model) {
        const listeners = this.eventListenerRegistry[eventName];
        for (const eventListenerId of Object.keys(listeners)) {
            listeners[eventListenerId](model);
        }
    }
}

class SkyKickDateService {
    constructor(datePipe) {
        this.datePipe = datePipe;
    }
    getDefaultFormat(date) {
        return this.datePipe.transform(new Date(date), SKYKICK_DEFAULT_FORMAT);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickDateService, deps: [{ token: i1.DatePipe }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickDateService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickDateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.DatePipe }] });

class ApiConfigService {
    constructor() {
        this.endpointOverrides = new Map();
    }
    setEndpointOverride(identifier, url) {
        this.endpointOverrides.set(identifier, url);
    }
    getEndpointUrl(identifier, defaultUrl) {
        const overrideUrl = this.endpointOverrides.get(identifier);
        if (overrideUrl) {
            return of(overrideUrl);
        }
        else {
            return of(defaultUrl); // Fallback to default if no override is set
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ApiConfigService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ApiConfigService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ApiConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

var ApiEndpointIdentifier;
(function (ApiEndpointIdentifier) {
    ApiEndpointIdentifier["Banners"] = "banners";
    ApiEndpointIdentifier["PartnerWhiteLabelSettings"] = "partnerWhiteLabelSettings";
    ApiEndpointIdentifier["PartnerLogo"] = "partnerLogo";
    ApiEndpointIdentifier["NotificationsQuery"] = "notificationsQuery";
})(ApiEndpointIdentifier || (ApiEndpointIdentifier = {}));

const populateContentWithPlaceholders = (content, placeholders) => {
    const placeholderRegexp = new RegExp(`(${CrmPattern})|(${SkyKickPattern})`, 'g');
    // Prepare object with matches between placeholder name and its HTML view
    const placeholderTags = content.match(placeholderRegexp)?.reduce((acc, match) => {
        if (placeholders && !placeholders.includes(match)) {
            return acc;
        }
        return {
            ...acc,
            [match]: `<span class="sk-placeholder">${match}</span>`
        };
    }, {}) ?? {};
    let changedContent = content;
    // Ignore replacing placeholders view in links
    const excludeLinksPlaceholderRegexp = new RegExp(`<a[^>]*>.*?</a>|(\\b(?:${CrmPattern})|(${SkyKickPattern})\\b)`, 'gi');
    changedContent = changedContent.replace(excludeLinksPlaceholderRegexp, (match, capturedText) => {
        return capturedText ? placeholderTags[capturedText] || match : match;
    });
    return changedContent;
};
const formatPattern = (formatName) => {
    return `(\\:(?<${formatName}>[0-9a-zA-Z;:\\+#\\.\\-\\?]+))?`;
};
const SkyKickPattern = `SKYKICK\\.(?<ClassName>[\\w]+(\\.\\w+)*)\\.(?<Method>[\\w]+)${formatPattern('SkyKickFormat')}`;
const CrmPattern = `{{[\\s]*!(?<CrmClassName>[0-9a-zA-Z_ ]+):(?<CrmMethod>[0-9a-zA-Z_@\\/ ]+)${formatPattern('CrmFormat')};[\\s]*}}`;

class NotificationResourcesService {
    constructor(http, routeResolverService, userProvider, apiConfigService) {
        this.http = http;
        this.routeResolverService = routeResolverService;
        this.userProvider = userProvider;
        this.apiConfigService = apiConfigService;
    }
    handleError(errorResponse) {
        if (errorResponse.error instanceof ErrorEvent) {
            console.error('Client Side Error: ', errorResponse.error.message);
        }
        else {
            console.error('Server Side Error: ', errorResponse);
        }
        console.log('There is a problem with the service. We are notified & working on it. Please try again later.');
        return throwError(errorResponse);
    }
    getPAPIRoute() {
        return this.routeResolverService.generatePipeRoute(AppId.Papi);
    }
    getBackupRoute() {
        return this.routeResolverService.generatePipeRoute(AppId.Backup);
    }
    getNotificationsRoute() {
        return this.routeResolverService.generatePipeRoute(AppId.PlatformApis);
    }
    notificationsQuery(payload) {
        const defaultUrl = `${this.getPAPIRoute()}common/alerts/v2/customers/query`;
        return this.apiConfigService.getEndpointUrl(ApiEndpointIdentifier.NotificationsQuery, defaultUrl)
            .pipe(switchMap(url => {
            const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
            return this.http.post(url, JSON.stringify(payload), { headers });
        }), catchError(this.handleError) // Ensure you have a handleError method defined to deal with errors
        );
    }
    alertsQuery(payload) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.post(`${this.getPAPIRoute()}common/alerts/v2/query`, JSON.stringify(payload), { headers })
            .pipe(catchError(this.handleError));
    }
    getOrderLevelPreferences(orderId) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.get(`${this.getNotificationsRoute()}notifications/api/v1/Notifications/partners/${this.userProvider?.getCurrentUser().partnerId}/order/${orderId}/preferences`, { headers, withCredentials: true })
            .pipe(catchError(this.handleError));
    }
    syncOrderLevelPreferences(payload, orderId) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.post(`${this.getNotificationsRoute()}notifications/api/v1/Notifications/partners/${this.userProvider?.getCurrentUser().partnerId}/order/${orderId}/preferences`, JSON.stringify(payload), { headers })
            .pipe(catchError(this.handleError));
    }
    getAlertActions(alertId) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.get(`${this.getPAPIRoute()}common/alerts/v2/${alertId}/actions`, { headers })
            .pipe(catchError(this.handleError));
    }
    completeAlert(alertId) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const payload = { alertIds: [alertId] };
        return this.http.post(`${this.getPAPIRoute()}common/alerts/edit/Complete`, JSON.stringify(payload), { headers })
            .pipe(catchError(this.handleError));
    }
    invokeAlertAction(alertId, actionId) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.post(`${this.getPAPIRoute()}common/alerts/v2/${alertId}/actions/${actionId}`, {}, { headers })
            .pipe(catchError(this.handleError));
    }
    getCustomerById(selectedItem) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        if (selectedItem.backupOrder) {
            return this.http.get(`${this.getBackupRoute()}papi-backup/backup/${selectedItem.id}`, { headers }).pipe(catchError(this.handleError), map(x => x.result));
        }
        return this.http.get(`${this.getPAPIRoute()}migration/order/${selectedItem.id}`, { headers }).pipe(catchError(this.handleError));
    }
    getTypeAheadSearch(term) {
        return forkJoin([this.getBackupOrdersForTypeAhead(term), this.getMigrationOrdersForTypeAhead(term)]).pipe(map(result => [...result[0], ...result[1]]));
    }
    getMigrationOrdersForTypeAhead(term) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.post(`${this.getPAPIRoute()}migration/order/search`, { searchTerm: term, resultsPerPage: 12, pageNumber: 1, includeSavedOrders: false }, { headers })
            .pipe(map(response => {
            response.forEach(x => x.backupOrder = false);
            return response;
        }), catchError(this.handleError));
    }
    getBackupOrdersForTypeAhead(term) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.post(`${this.getBackupRoute()}papi-backup/backup/simplesearch`, { searchTerm: term, resultsPerPage: 12, pageNumber: 1, includeSavedOrders: false }, { headers })
            .pipe(map(response => {
            response.forEach(x => x.backupOrder = true);
            return response;
        }), catchError(this.handleError));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationResourcesService, deps: [{ token: i1$2.HttpClient }, { token: RouteResolverService }, { token: i2$1.AbstractUserProvider }, { token: ApiConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationResourcesService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationResourcesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.HttpClient }, { type: RouteResolverService }, { type: i2$1.AbstractUserProvider }, { type: ApiConfigService }] });

class NotificationsAlertDetailsComponent {
    constructor(notificationResourcesService, notificationsMethodsService, translate, toastrService) {
        this.notificationResourcesService = notificationResourcesService;
        this.notificationsMethodsService = notificationsMethodsService;
        this.translate = translate;
        this.toastrService = toastrService;
        this.alertCompleted = new EventEmitter();
        this.completingAlert = false;
        this.invokingAlertAction = false;
        this.invokedActionId = '';
    }
    ngOnInit() {
        this.notificationResourcesService.getAlertActions(this.alert.id).subscribe({
            next: x => {
                this.alertActions = x.items;
            },
            error: () => {
                this.toastrService.error(this.translate.instant('platform.alerts.messages.failed_to_get_actions'));
            }
        });
        this.setIconDetails();
    }
    alertRelativeTime() {
        if (this.alert) {
            return moment.utc(this.alert.createdOn).fromNow();
        }
    }
    onCompleteAlertClicked() {
        this.completingAlert = true;
        this.notificationResourcesService.completeAlert(this.alert.id).subscribe({
            complete: () => {
                this.toastrService.success(this.translate.instant('platform.alerts.alert_completed'));
                this.completingAlert = false;
                this.alertCompleted.emit(this.alert);
            },
            error: () => {
                this.toastrService.error(this.translate.instant('platform.alerts.messages.error'));
                this.completingAlert = false;
            }
        });
    }
    onAlertActionClicked(action) {
        this.invokingAlertAction = true;
        this.invokedActionId = action.id;
        this.notificationResourcesService.invokeAlertAction(this.alert.id, action.id).subscribe({
            complete: () => {
                this.toastrService.success(this.notificationsMethodsService.getInvokeAlertActionSuccessToastMessage(action.id));
                this.invokingAlertAction = false;
                this.alertCompleted.emit(this.alert);
            },
            error: error => {
                console.log('Alert Action invoke failure', error);
                if (error.data === 'All available licenses have been assigned. Additional Licenses Required.') {
                    this.toastrService.error(this.translate.instant('platform.alerts.messages.additional_licenses_required'));
                }
                else {
                    this.toastrService.error(this.translate.instant('platform.alerts.messages.failed_to_complete_action'));
                }
                this.invokingAlertAction = false;
            }
        });
    }
    setIconDetails() {
        const icon = {
            type: '',
            class: '',
            tooltip: ''
        };
        if (!this.alert) {
            return;
        }
        if (this.alert.isSuccessful) {
            icon.type = 'check_circle';
            icon.class = 'icon-success';
            icon.tooltip = this.translate.instant('platform.alerts.severity.success');
        }
        else if (this.alert.isInformational) {
            icon.type = 'info';
            icon.class = 'icon-info';
            icon.tooltip = this.translate.instant('platform.alerts.severity.informational');
        }
        else if (this.alert.isWarning) {
            icon.type = 'error';
            icon.class = 'icon-warning';
            icon.tooltip = this.translate.instant('platform.alerts.severity.warning');
        }
        else if (this.alert.isError) {
            icon.type = 'warning';
            icon.class = 'icon-danger';
            icon.tooltip = this.translate.instant('platform.alerts.severity.critical');
        }
        this.icon = icon;
    }
    getOrderAlertPageUrl() {
        return this.notificationsMethodsService.getOrderAlertPageUrlByAlert(this.alert);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsAlertDetailsComponent, deps: [{ token: NotificationResourcesService }, { token: NotificationMethodsService }, { token: i2$3.TranslateService }, { token: i5.ToastrService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsAlertDetailsComponent, selector: "sk-notifications-alert-details", inputs: { alert: "alert" }, outputs: { alertCompleted: "alertCompleted" }, ngImport: i0, template: "<p class=\"text-muted\">{{alertRelativeTime()}}</p>\r\n<h4 id=\"customer-alert-summary\">\r\n    <i class=\"material-icons md-22\" style=\"float:left;\" [class]=\"icon.class\" title=\"{{icon.tooltip}}\">{{icon.type}}</i>\r\n    {{alert?.subject}}\r\n</h4>\r\n<br>\r\n<h5 translate>platform.alerts.description_title</h5>\r\n<p [innerHtml]=\"alert?.description\" id=\"alert_detail_description\"></p>\r\n<br>\r\n<span *ngIf=\"alertActions\">\r\n    <button [id]=\"'activate-mailbox-button-'+i\" *ngFor=\"let action of alertActions; let i = index\" class=\"me-3\" sk-spinner-button color=\"outline-primary\"\r\n        [isSpinning]=\"invokingAlertAction && invokedActionId == action.id\"\r\n        [disabled]=\"completingAlert || invokingAlertAction\"\r\n        (click)=\"onAlertActionClicked(action)\">{{action.name}}</button>\r\n    <button *ngIf=\"alert.partnerStatus != partnerStatusComplete\" sk-spinner-button color=\"outline-primary\" [isSpinning]=\"completingAlert\" id=\"complete-alert-button\"\r\n        [disabled]=\"completingAlert || invokingAlertAction\" (click)=\"onCompleteAlertClicked()\">{{\r\n        'platform.alerts.complete' | translate}}</button>\r\n</span>\r\n<div class=\"d-flex justify-content-center\" *ngIf=\"!alertActions\">\r\n    <sk-text-spinner message=\"Fetching Alert Actions\"></sk-text-spinner>\r\n</div>\r\n<br>\r\n<br>\r\n<a id=\"alert_detail_helpcenter_link\" href=\"{{alert?.helpCenterUrl}}\" class=\"me-4 align-baseline\"\r\n    target=\"_blank\">{{'platform.alerts.view_documentation' | translate}} <i\r\n        class=\"material-icons md-16 link-icon align-middle\">launch</i></a>\r\n<a id=\"alert-detail-panel-go-to-order-link\" href=\"{{getOrderAlertPageUrl()}}\" target=\"_blank\"\r\n    class=\"align-baseline\">{{'platform.alerts.go_to_order' | translate}} <i\r\n        class=\"material-icons md-16 link-icon align-middle\">launch</i></a>\r\n", styles: [".link-icon{color:#056ed1!important}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TextSpinnerComponent, selector: "sk-text-spinner", inputs: ["message", "size", "spinnerLocation", "loaderWrapperClasses"] }, { kind: "component", type: SpinnerButtonComponent, selector: "button[sk-spinner-button]", inputs: ["disabled", "color", "active", "isSpinning"] }, { kind: "directive", type: i2$3.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }, { kind: "pipe", type: i2$3.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsAlertDetailsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-notifications-alert-details', template: "<p class=\"text-muted\">{{alertRelativeTime()}}</p>\r\n<h4 id=\"customer-alert-summary\">\r\n    <i class=\"material-icons md-22\" style=\"float:left;\" [class]=\"icon.class\" title=\"{{icon.tooltip}}\">{{icon.type}}</i>\r\n    {{alert?.subject}}\r\n</h4>\r\n<br>\r\n<h5 translate>platform.alerts.description_title</h5>\r\n<p [innerHtml]=\"alert?.description\" id=\"alert_detail_description\"></p>\r\n<br>\r\n<span *ngIf=\"alertActions\">\r\n    <button [id]=\"'activate-mailbox-button-'+i\" *ngFor=\"let action of alertActions; let i = index\" class=\"me-3\" sk-spinner-button color=\"outline-primary\"\r\n        [isSpinning]=\"invokingAlertAction && invokedActionId == action.id\"\r\n        [disabled]=\"completingAlert || invokingAlertAction\"\r\n        (click)=\"onAlertActionClicked(action)\">{{action.name}}</button>\r\n    <button *ngIf=\"alert.partnerStatus != partnerStatusComplete\" sk-spinner-button color=\"outline-primary\" [isSpinning]=\"completingAlert\" id=\"complete-alert-button\"\r\n        [disabled]=\"completingAlert || invokingAlertAction\" (click)=\"onCompleteAlertClicked()\">{{\r\n        'platform.alerts.complete' | translate}}</button>\r\n</span>\r\n<div class=\"d-flex justify-content-center\" *ngIf=\"!alertActions\">\r\n    <sk-text-spinner message=\"Fetching Alert Actions\"></sk-text-spinner>\r\n</div>\r\n<br>\r\n<br>\r\n<a id=\"alert_detail_helpcenter_link\" href=\"{{alert?.helpCenterUrl}}\" class=\"me-4 align-baseline\"\r\n    target=\"_blank\">{{'platform.alerts.view_documentation' | translate}} <i\r\n        class=\"material-icons md-16 link-icon align-middle\">launch</i></a>\r\n<a id=\"alert-detail-panel-go-to-order-link\" href=\"{{getOrderAlertPageUrl()}}\" target=\"_blank\"\r\n    class=\"align-baseline\">{{'platform.alerts.go_to_order' | translate}} <i\r\n        class=\"material-icons md-16 link-icon align-middle\">launch</i></a>\r\n", styles: [".link-icon{color:#056ed1!important}\n"] }]
        }], ctorParameters: () => [{ type: NotificationResourcesService }, { type: NotificationMethodsService }, { type: i2$3.TranslateService }, { type: i5.ToastrService }], propDecorators: { alert: [{
                type: Input
            }], alertCompleted: [{
                type: Output
            }] } });

class NotificationsAlertComponent {
    constructor() {
        this.alertCompleted = new EventEmitter();
    }
    onAlertCompleted(alert) {
        this.alertCompleted.emit(alert);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsAlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsAlertComponent, selector: "sk-notifications-alert", inputs: { alert: "alert" }, outputs: { alertCompleted: "alertCompleted" }, ngImport: i0, template: "<sk-notifications-alert-details (alertCompleted)=\"onAlertCompleted($event)\" [alert]=\"alert\"></sk-notifications-alert-details>\r\n", styles: [""], dependencies: [{ kind: "component", type: NotificationsAlertDetailsComponent, selector: "sk-notifications-alert-details", inputs: ["alert"], outputs: ["alertCompleted"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsAlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-notifications-alert', template: "<sk-notifications-alert-details (alertCompleted)=\"onAlertCompleted($event)\" [alert]=\"alert\"></sk-notifications-alert-details>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { alert: [{
                type: Input
            }], alertCompleted: [{
                type: Output
            }] } });

var NavigationLocation;
(function (NavigationLocation) {
    NavigationLocation["Customers"] = "customers";
    NavigationLocation["CustomerOrders"] = "customerOrders";
    NavigationLocation["AlertDetail"] = "alertDetail";
    NavigationLocation["OrderLevelPreferences"] = "OrderLevelPreferences";
    NavigationLocation["FetchingNavigationData"] = "FetchingNavigationData";
})(NavigationLocation || (NavigationLocation = {}));
class NotificationsNavigation {
    constructor(location) {
        this.location = location;
        this.customer = null;
        this.alertName = '';
    }
    returnToCustomersView() {
        this.alertName = '';
        this.customer = null;
        this.location = NavigationLocation.Customers;
    }
    returnToCustomerOrdersView() {
        this.alertName = '';
        this.location = NavigationLocation.CustomerOrders;
    }
    navigateToCustomerOrdersView(customer) {
        this.customer = customer;
        this.location = NavigationLocation.CustomerOrders;
    }
    navigateToAlertDetailView(alertName) {
        this.alertName = alertName;
        this.location = NavigationLocation.AlertDetail;
    }
    navigateToOrderLevelPreferences() {
        this.location = NavigationLocation.OrderLevelPreferences;
    }
}

const compare = (v1, v2) => (v1 < v2 ? -1 : v1 > v2 ? 1 : 0);
class NotificationsSortableHeaderDirective {
    constructor() {
        this.direction = "";
        this.default = false;
        this.sort = new EventEmitter();
    }
    ngOnInit() {
        if (this.default) {
            this.direction = 'Descending';
        }
    }
    rotate() {
        this.direction = this.direction == 'Descending' ? 'Ascending' : 'Descending';
        this.sort.emit({ column: this.sortable, direction: this.direction });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsSortableHeaderDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsSortableHeaderDirective, selector: "th[sortable]", inputs: { sortable: "sortable", direction: "direction", default: "default" }, outputs: { sort: "sort" }, host: { listeners: { "click": "rotate()" }, properties: { "class.ascending": "direction === \"Ascending\"", "class.descending": "direction === \"Descending\"", "class.sorted": "true" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsSortableHeaderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "th[sortable]",
                    host: {
                        "[class.ascending]": 'direction === "Ascending"',
                        "[class.descending]": 'direction === "Descending"',
                        "[class.sorted]": 'true',
                        "(click)": "rotate()"
                    }
                }]
        }], propDecorators: { sortable: [{
                type: Input
            }], direction: [{
                type: Input
            }], default: [{
                type: Input
            }], sort: [{
                type: Output
            }] } });

class UserProductPermissionsService {
    constructor(userProvider) {
        this.currentUser = userProvider.getCurrentUser();
    }
    hasMigrationsPermissions() {
        return this.currentUser.hasPermission(getFlattenedPermissionName(NavigationArea.Migrations, NavigationPermission.FullAccess));
    }
    hasBackupPermissions() {
        return this.currentUser.hasPermission(getFlattenedPermissionName(NavigationArea.CloudBackup, NavigationPermission.FullAccess));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UserProductPermissionsService, deps: [{ token: i2$1.AbstractUserProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UserProductPermissionsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UserProductPermissionsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i2$1.AbstractUserProvider }] });

class UserProductTypesService {
    constructor(userProductPermissionsService) {
        this.userProductPermissionsService = userProductPermissionsService;
    }
    getAvailableProductTypes() {
        let productTypes = [];
        if (this.userProductPermissionsService.hasMigrationsPermissions()) {
            productTypes.push(ProductTypes.Migration);
        }
        if (this.userProductPermissionsService.hasBackupPermissions()) {
            productTypes.push(ProductTypes.Backup);
        }
        return productTypes;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UserProductTypesService, deps: [{ token: UserProductPermissionsService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UserProductTypesService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UserProductTypesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UserProductPermissionsService }] });

class NotificationsSearchComponent {
    constructor(_service) {
        this._service = _service;
        this.searchTermChange = new EventEmitter();
        this.typeaheadSelect = new EventEmitter();
        this.searching = false;
        this.search = (text$) => {
            return text$.pipe(debounceTime(300), distinctUntilChanged(), tap(() => { this.searching = true; }), switchMap(term => {
                if (term.length <= 2) {
                    return of([]);
                }
                return this._service.getTypeAheadSearch(term).pipe(tap(() => { this.searchFailed = false; }), catchError(() => {
                    this.searchFailed = true;
                    this.searching = false;
                    return of([]);
                }));
            }), tap(() => { this.searching = false; }));
        };
    }
    ngOnInit() {
    }
    onSearch() {
        this.searchTermChange.emit(this.searchTerm);
    }
    onSearchClear() {
        this.searchTerm = '';
        this.searchTermChange.emit(this.searchTerm);
    }
    itemSelected($event) {
        this.typeaheadSelect.emit($event.item);
        this.searchTermChange.emit($event.item.name);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsSearchComponent, deps: [{ token: NotificationResourcesService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsSearchComponent, selector: "sk-notifications-search", inputs: { searchTerm: "searchTerm" }, outputs: { searchTermChange: "searchTermChange", typeaheadSelect: "typeaheadSelect" }, ngImport: i0, template: "<ng-template #rt let-r=\"result\" let-t=\"term\" >\r\n    <ngb-highlight [result]=\"r.name\" [term]=\"t\"></ngb-highlight>\r\n    <span class=\"badge badge-default my-auto ms-2\">{{(r.salesOrderId == null)? \"Migration\" : \"Backup\"}}</span>\r\n</ng-template>\r\n\r\n<div class=\"input-group sk-input-btn mb-3\">\r\n    <input id=\"customer-alerts-search-input\" type=\"text\" class=\"form-control\" placeholder=\"Search\" value=\"Results\" (selectItem)=\"itemSelected($event)\" [(ngModel)]=\"searchTerm\" [ngbTypeahead]=\"search\" [resultTemplate]=\"rt\"  (keyup.enter)=\"onSearch()\">\r\n    <i *ngIf=\"searching\" class=\"sk-spin-loader loader-sm material-icons spin-component-custom\"></i>\r\n    <i id=\"customer-alerts-search-input-clear-button\" class=\"material-icons md-18 sk-reset-search\" *ngIf=\"!searching\" (click)=\"onSearchClear()\" title=\"Reset and Exit Search\">\uE5CD</i>\r\n    <div class=\"input-group-append\">\r\n      <button id=\"sk-customer-search-btn\" class=\"btn btn-outline-secondary sk-btn-search-outline\" (click)=\"onSearch()\"type=\"button\"><i class=\"material-icons md-18\">\uE8B6</i></button>\r\n    </div>\r\n  </div>\r\n", styles: [".spin-component-custom{position:absolute;right:43px;top:4px;z-index:3}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: i1$1.NgbHighlight, selector: "ngb-highlight", inputs: ["highlightClass", "result", "term", "accentSensitive"] }, { kind: "directive", type: i1$1.NgbTypeahead, selector: "input[ngbTypeahead]", inputs: ["autocomplete", "container", "editable", "focusFirst", "inputFormatter", "ngbTypeahead", "resultFormatter", "resultTemplate", "selectOnExact", "showHint", "placement", "popperOptions", "popupClass"], outputs: ["selectItem"], exportAs: ["ngbTypeahead"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsSearchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-notifications-search', template: "<ng-template #rt let-r=\"result\" let-t=\"term\" >\r\n    <ngb-highlight [result]=\"r.name\" [term]=\"t\"></ngb-highlight>\r\n    <span class=\"badge badge-default my-auto ms-2\">{{(r.salesOrderId == null)? \"Migration\" : \"Backup\"}}</span>\r\n</ng-template>\r\n\r\n<div class=\"input-group sk-input-btn mb-3\">\r\n    <input id=\"customer-alerts-search-input\" type=\"text\" class=\"form-control\" placeholder=\"Search\" value=\"Results\" (selectItem)=\"itemSelected($event)\" [(ngModel)]=\"searchTerm\" [ngbTypeahead]=\"search\" [resultTemplate]=\"rt\"  (keyup.enter)=\"onSearch()\">\r\n    <i *ngIf=\"searching\" class=\"sk-spin-loader loader-sm material-icons spin-component-custom\"></i>\r\n    <i id=\"customer-alerts-search-input-clear-button\" class=\"material-icons md-18 sk-reset-search\" *ngIf=\"!searching\" (click)=\"onSearchClear()\" title=\"Reset and Exit Search\">\uE5CD</i>\r\n    <div class=\"input-group-append\">\r\n      <button id=\"sk-customer-search-btn\" class=\"btn btn-outline-secondary sk-btn-search-outline\" (click)=\"onSearch()\"type=\"button\"><i class=\"material-icons md-18\">\uE8B6</i></button>\r\n    </div>\r\n  </div>\r\n", styles: [".spin-component-custom{position:absolute;right:43px;top:4px;z-index:3}\n"] }]
        }], ctorParameters: () => [{ type: NotificationResourcesService }], propDecorators: { searchTerm: [{
                type: Input
            }], searchTermChange: [{
                type: Output
            }], typeaheadSelect: [{
                type: Output
            }] } });

const ORDER_FILTER_STORAGE_KEY = 'notificationsOrderFilter';
const NOTIFICATIONS_INIT_CONFIG$3 = 'notificationsInitConfig';
class NotificationsCustomersTableComponent {
    constructor(ngbInputDatepickerConfig, ngbDropdownConfig, ngbDatepickerConfig, notificationResourcesService, notificationMethodsService, translate, cacheService, userProductTypesService) {
        this.notificationResourcesService = notificationResourcesService;
        this.notificationMethodsService = notificationMethodsService;
        this.translate = translate;
        this.cacheService = cacheService;
        this.userProductTypesService = userProductTypesService;
        this.onDestroy$ = new Subject();
        this.moment = moment;
        this.toggleFilters = false;
        this.productTypes = [];
        this.severityTypes = SEVERITYTYPES;
        this.orderBy = { orderByType: 'Descending', orderByField: 'Severity' };
        this.currentPage$ = new Subject();
        this.page = 0;
        this.customers = [];
        this.initialLoad = true;
        this.loadingMore = true;
        ngbInputDatepickerConfig.container = 'body';
        ngbDropdownConfig.container = 'body';
        ngbDatepickerConfig.firstDayOfWeek = 0;
        ngbDatepickerConfig.displayMonths = 2;
        ngbDatepickerConfig.navigation = 'arrows';
    }
    ngOnInit() {
        this.initializeProductFilter();
        this.clearFilters();
        this.initializationConfig = this.cacheService.root().get(NOTIFICATIONS_INIT_CONFIG$3);
        const validModalConfig = this.notificationMethodsService.verifyModalConfig(this.initializationConfig);
        if (validModalConfig && (this.initializationConfig.CustomerId || this.initializationConfig.BackupServiceId)) {
            const customer = {
                backupOrder: (this.initializationConfig.BackupServiceId),
                id: this.initializationConfig.BackupServiceId ?? this.initializationConfig.CustomerId,
            };
            this.onOrderSelect(customer);
        }
        else {
            if (validModalConfig && this.initializationConfig.OrderFilter) {
                this.toggleFilters = true;
                this.addFilterToCache();
            }
            this.currentPage$.asObservable()
                .pipe(tap(_ => this.loadingMore = true), switchMap(_ => {
                const orderFilter = {
                    product: this.productType,
                    searchCustomer: this.searchTerm,
                    severity: this.severityType,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    page: this.page,
                    orderBy: this.orderBy
                };
                const query = this.notificationMethodsService.buildQueryForCustomerAlertSummary(orderFilter);
                return this.notificationResourcesService.notificationsQuery(query);
            }), takeUntil(this.onDestroy$))
                .subscribe({
                next: res => {
                    this.loadingMore = false;
                    this.initialLoad = false;
                    this.customers.push(...res.customers);
                    this.totalCustomerCount = res.totalCustomers;
                },
                error: () => {
                    this.loadingMore = false;
                    this.initialLoad = false;
                }
            });
            const orderFilter = this.cacheService.root().get(ORDER_FILTER_STORAGE_KEY);
            if (orderFilter !== null) {
                this.setFilters(orderFilter);
                this.setTally();
            }
            this.currentPage$.next(this.page);
        }
    }
    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
    onScroll() {
        if (this.customers.length >= this.totalCustomerCount) {
            return;
        }
        else if (this.loadingMore) {
            return;
        }
        this.page++;
        this.currentPage$.next(this.page);
    }
    onSort({ column, direction }) {
        // resetting other headers
        this.headers.forEach(header => {
            if (header.sortable !== column) {
                header.direction = '';
            }
        });
        this.orderBy.orderByField = column;
        this.orderBy.orderByType = direction;
        this.clearResultsAndApplyFilter();
    }
    clearResultsAndApplyFilter() {
        this.customers = [];
        this.page = 0;
        this.currentPage$.next(this.page);
    }
    onNavigate(customer) {
        // Save this so when we return we can navigate to the right spot.
        this.addFilterToCache();
        this.notificationsNavigation.navigateToCustomerOrdersView(customer);
    }
    applyFilters() {
        this.customers = [];
        this.page = 0;
        this.currentPage$.next(this.page);
    }
    setTally() {
        this.filterTally =
            (this.searchTerm ? 1 : 0) +
                (this.productType === this.getDefaultProductType() ? 0 : 1) +
                (this.severityType === SeverityTypes.Any ? 0 : 1) +
                (this.startDate ? 1 : 0) +
                (this.endDate ? 1 : 0);
    }
    setFilters(orderFilter) {
        this.productType = this.getProductTypeItem(orderFilter.product)?.value ?? this.getDefaultProductType();
        this.severityType = orderFilter.severity;
        this.searchTerm = orderFilter.searchCustomer;
        this.startDate = orderFilter.startDate;
        this.endDate = orderFilter.endDate;
    }
    clearFilters() {
        this.productType = this.getDefaultProductType();
        this.severityType = SeverityTypes.Any;
        this.startDate = null;
        this.endDate = null;
        this.filterTally = 0;
        this.searchTerm = '';
        this.customers = [];
        this.page = 0;
        this.currentPage$.next(this.page);
    }
    clearSearchTerm() {
        this.searchTerm = '';
        this.customers = [];
        this.page = 0;
        this.currentPage$.next(this.page);
    }
    getCustomerCountText() {
        if (this.totalCustomerCount === 0 || this.initialLoad) {
            return this.translate.instant('platform.alerts.no_notifications');
        }
        else if (this.totalCustomerCount === 1) {
            return this.translate.instant('platform.alerts.one_customer');
        }
        else {
            return `${this.totalCustomerCount} ${this.translate.instant('platform.alerts.customers')}`;
        }
    }
    onSearchTermUpdated(searchTerm) {
        this.searchTerm = searchTerm;
        this.setTally();
        this.applyFilters();
    }
    onOrderSelect(selectedItem) {
        this.notificationResourcesService.getCustomerById(selectedItem).subscribe(x => {
            const customer = {
                id: selectedItem.backupOrder ? x.salesOrderId : x.id,
                customerId: selectedItem.backupOrder ? x.salesOrderId : x.id,
                customerName: selectedItem.backupOrder ? x.customerInformation.companyName : x.orderName,
                orderId: selectedItem.backupOrder ? x.salesOrderId : x.id,
                backupServiceId: selectedItem.id,
                product: selectedItem.backupOrder ? ProductTypes.Backup : ProductTypes.Migration,
                productType: selectedItem.backupOrder ? 'Backup' : 'Migration',
                count: 0, // this data is not retrived
                mostRecentOrderAlertId: 'unknown', // this data is not retrived
                createdOn: 'unknown', // this data is not retrived
                hasSuccessAlerts: false, // this data is not retrived
                hasInformationAlerts: false, // this data is not retrived
                hasWarningAlerts: false, // this data is not retrived
                hasCriticalAlerts: false // this data is not retrived
            };
            this.onNavigate(customer);
        }, err => {
            this.loadingMore = false;
        });
    }
    getProductTypeDisplayName(productType) {
        return this.getProductTypeItem(productType)?.name || '';
    }
    getSeverityTypeDisplayName(severityType) {
        return this.notificationMethodsService.getSeverityTypeDisplayName(severityType);
    }
    addFilterToCache() {
        const configExists = (this.initializationConfig && this.initializationConfig.OrderFilter);
        this.cacheService.root().set(ORDER_FILTER_STORAGE_KEY, {
            product: configExists ? this.initializationConfig.OrderFilter.product : this.productType,
            searchCustomer: this.searchTerm,
            severity: configExists ? this.initializationConfig.OrderFilter.severity : this.severityType,
            startDate: this.startDate,
            endDate: this.endDate,
            page: 0,
            orderBy: this.orderBy
        });
    }
    initializeProductFilter() {
        const availableProductTypes = this.userProductTypesService.getAvailableProductTypes();
        const productTypes = PRODUCTTYPES.filter(type => type.value !== ProductTypes.Any);
        const anyProductType = PRODUCTTYPES.find(productType => productType.value === ProductTypes.Any);
        availableProductTypes.forEach(type => {
            const productType = productTypes.find(productType => productType.value === type);
            if (productType) {
                this.productTypes.push(productType);
            }
        });
        if (this.productTypes.length === productTypes.length) {
            this.productTypes.push(anyProductType);
        }
    }
    getProductTypeItem(productType) {
        return this.productTypes.find(x => x.value === productType);
    }
    getDefaultProductType() {
        return this.productTypes.find(x => x.value === ProductTypes.Any)
            ? ProductTypes.Any
            : this.productTypes[0]?.value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsCustomersTableComponent, deps: [{ token: i1$1.NgbInputDatepickerConfig }, { token: i1$1.NgbDropdownConfig }, { token: i1$1.NgbDatepickerConfig }, { token: NotificationResourcesService }, { token: NotificationMethodsService }, { token: i2$3.TranslateService }, { token: i4.CacheService }, { token: UserProductTypesService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsCustomersTableComponent, selector: "sk-notifications-customers-table", inputs: { notificationsNavigation: "notificationsNavigation", filter: "filter" }, viewQueries: [{ propertyName: "headers", predicate: NotificationsSortableHeaderDirective, descendants: true }], ngImport: i0, template: "<div class=\"sk-flex-table\">\r\n    <div class=\"sk-table-cap --d-none\">\r\n        <div class=\"d-inline-flex align-items-center\">\r\n            <h5 id=\"alerts-customer-view-header\">{{getCustomerCountText()}}</h5>\r\n            <a class=\"ml-50\"\r\n                href=\"https://support.skykick.com/hc/en-us/articles/1500002350602-SkyKick-platform-notifications-aka-alerts-\"\r\n                target=\"_blank\">Learn more</a>\r\n        </div>\r\n\r\n        <div class=\"d-inline-flex text-truncate flex-shrink-0\">\r\n            <button id=\"sk-button-filters\" (click)=\"toggleFilters = !toggleFilters\" type=\"button\"\r\n                class=\"btn btn-outline-secondary sk-btn-icon mr-100\">\r\n                <i class=\"material-icons md-22\">filter_list</i>\r\n                <span id=\"sk-applied-filters-count\" *ngIf=\"filterTally > 0\"\r\n                    class=\"badge rounded-pill badge-default-bold\">{{filterTally}}</span>\r\n            </button>\r\n            <sk-notifications-search (searchTermChange)=\"onSearchTermUpdated($event)\"\r\n                (typeaheadSelect)=\"onOrderSelect($event)\" [searchTerm]=\"searchTerm\">\r\n            </sk-notifications-search>\r\n        </div>\r\n    </div>\r\n\r\n    <div *ngIf=\"toggleFilters\" class=\"sk-table-cap\">\r\n        <div class=\"d-inline-flex align-items-center\">\r\n            <div ngbDropdown class=\"btn-group mr-100\">\r\n                <button\r\n                    id=\"sk-button-filter-product\"\r\n                    type=\"button\"\r\n                    class=\"btn btn-outline-secondary dropdown-toggle\"\r\n                    [disabled] = \"productTypes.length <= 1\"\r\n                    ngbDropdownToggle>\r\n                    {{getProductTypeDisplayName(productType)}}\r\n                </button>\r\n                <div class=\"dropdown-menu\" ngbDropdownMenu>\r\n                    <ng-container *ngFor=\"let item of productTypes; let index = index\">\r\n                        <a class=\"dropdown-item\" (click)=\"productType = item.value; setTally()\"\r\n                            id=\"productTypeDropdown{{index}}\">{{item.name}}</a>\r\n                    </ng-container>\r\n                </div>\r\n            </div>\r\n\r\n            <div ngbDropdown class=\"btn-group mr-100\">\r\n                <button id=\"sk-button-filter-severity\" type=\"button\" class=\"btn btn-outline-secondary dropdown-toggle\"\r\n                    ngbDropdownToggle>{{getSeverityTypeDisplayName(severityType)}}</button>\r\n                <div class=\"dropdown-menu\" ngbDropdownMenu>\r\n                    <ng-container *ngFor=\"let item of severityTypes; let index = index\">\r\n                        <a class=\"dropdown-item\" (click)=\"severityType = item.value; setTally()\"\r\n                            id=\"severityTypeDropdown{{index}}\">{{item.name}}</a>\r\n                    </ng-container>\r\n                </div>\r\n            </div>\r\n\r\n            <div class=\"input-group mr-100\">\r\n                <input id=\"sk-input-filter-start-date\" [(ngModel)]=\"startDate\" (ngModelChange)=\"setTally()\"\r\n                    class=\"form-control\" placeholder=\"Start Date\" name=\"d1\" ngbDatepicker #d1=\"ngbDatepicker\"\r\n                    (mousedown)=\"d1.toggle()\" readonly />\r\n                <div class=\"input-group-append\">\r\n                    <button class=\"btn btn-outline-secondary calendar\" (click)=\"d1.toggle()\" type=\"button\">\r\n                        <span class=\"md-datepicker-calendar-icon\"></span>\r\n                    </button>\r\n                </div>\r\n            </div>\r\n\r\n            <div class=\"input-group mr-100\">\r\n                <input id=\"sk-input-filters-end-date\" [(ngModel)]=\"endDate\" (ngModelChange)=\"setTally()\"\r\n                    class=\"form-control\" placeholder=\"End Date\" name=\"d2\" ngbDatepicker #d2=\"ngbDatepicker\"\r\n                    (mousedown)=\"d2.toggle()\" readonly />\r\n                <div class=\"input-group-append\">\r\n                    <button class=\"btn btn-outline-secondary calendar\" (click)=\"d2.toggle()\" type=\"button\">\r\n                        <span class=\"md-datepicker-calendar-icon\"></span>\r\n                    </button>\r\n                </div>\r\n            </div>\r\n\r\n            <button id=\"sk-button-apply-filters\" class=\"btn btn-primary ml-100 mr-50 text-nowrap sk-btn-combo\"\r\n                (click)=\"applyFilters()\">{{'platform.alerts.apply_filters' | translate}}</button>\r\n\r\n            <button id=\"sk-button-clear-filters\" class=\"btn btn-link text-nowrap\" (click)=\"clearFilters()\"\r\n                [disabled]=\"!filterTally\">{{'platform.alerts.clear_filters' | translate}}</button>\r\n        </div>\r\n    </div>\r\n\r\n    <div class=\"sk-scroll-container p-0 customer-table-scroll-container\">\r\n        <table class=\"table table-hover sk-standard-wrap-table sk-sortable sk-select-table sk-sticky-header sk-min-width-lg\">\r\n            <thead class=\"thead-white thead-sm thead-border-none\">\r\n                <tr>\r\n                    <th sortable=\"CustomerName\" (sort)=\"onSort($event)\" id=\"sk-customer-alerts-customername-header\"\r\n                        style=\"width:40%;\">\r\n                        {{'platform.alerts.customer' | translate}}</th>\r\n                    <th sortable=\"Count\" (sort)=\"onSort($event)\" id=\"sk-customer-alerts-notificationcount-header\"\r\n                        class=\"text-center\">{{'platform.notifications.title' | translate }}</th>\r\n                    <th sortable=\"Product\" (sort)=\"onSort($event)\" id=\"sk-customer-alerts-productname-header\">\r\n                        {{'platform.alerts.product' | translate}}</th>\r\n                    <th sortable=\"Severity\" (sort)=\"onSort($event)\" id=\"sk-customer-alerts-severity-header\"\r\n                        default=\"true\">{{'platform.alerts.type' | translate}}\r\n                    </th>\r\n                    <th sortable=\"CreatedOn\" (sort)=\"onSort($event)\" id=\"sk-customer-alerts-createdon-header\">\r\n                        {{'platform.alerts.date' | translate}}</th>\r\n                </tr>\r\n            </thead>\r\n            <tbody infinite-scroll (scrolled)=\"onScroll()\" [infiniteScrollDistance]=\"2\" [infiniteScrollThrottle]=\"50\" [infiniteScrollContainer]=\"'.modal-body'\" [fromRoot]=\"true\" [scrollWindow]=\"false\">\r\n                <tr *ngIf=\"customers.length === 0 && !loadingMore\">\r\n                    <td colspan=\"5\">\r\n                        <div id=\"sk-no-notifications-graphic\" class=\"text-center\">\r\n                            <i class=\"material-icons md-60 icon-primary\">check</i>\r\n                            <div class=\"text-muted\">{{'platform.alerts.no_notifications' | translate}}</div>\r\n                        </div>\r\n                    </td>\r\n                </tr>\r\n                <tr *ngFor=\"let customer of customers\" [id]=\"'alerts-customer-view-table-row-'+customer.orderId\"\r\n                    role=\"button\" (click)=\"onNavigate(customer)\">\r\n                    <td search-highlight [searchedWord]=\"searchTerm\" [content]=\"customer.customerName\"\r\n                        [classToApply]=\"'font-weight-bold'\" [setTitle]=\"'false'\">\r\n                        {{customer.customerName}}\r\n                    </td>\r\n                    <td class=\"text-center\">{{customer.count}}</td>\r\n                    <td>{{customer.productType}}</td>\r\n                    <td class=\"text-nowrap pr-200\">\r\n                        <i *ngIf=\"customer.hasCriticalAlerts\" class=\"material-icons md-22 icon-danger\"\r\n                           ngbTooltip=\"Critical\">warning</i>\r\n                        <i *ngIf=\"customer.hasWarningAlerts\" class=\"material-icons md-22 icon-warning\"\r\n                           ngbTooltip=\"Warning\">error</i>\r\n                        <i *ngIf=\"customer.hasInformationAlerts\" class=\"material-icons md-22 icon-info\"\r\n                           ngbTooltip=\"Informational\">info</i>\r\n                        <i *ngIf=\"customer.hasSuccessAlerts\" class=\"material-icons md-22 icon-success\"\r\n                           ngbTooltip=\"Success\">check_circle</i>\r\n                    </td>\r\n                    <td>{{moment.utc(customer.createdOn).fromNow()}}</td>\r\n                </tr>\r\n            </tbody>\r\n        </table>\r\n    </div>\r\n    <sk-text-spinner *ngIf=\"loadingMore\" message=\"Fetching Customer Notifications\"></sk-text-spinner>\r\n</div>\r\n", styles: [".customer-table-scroll-container{overflow-x:hidden}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "popperOptions", "triggers", "positionTarget", "container", "disableTooltip", "tooltipClass", "tooltipContext", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "directive", type: i1$1.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i1$1.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i1$1.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "directive", type: i1$1.NgbInputDatepicker, selector: "input[ngbDatepicker]", inputs: ["autoClose", "contentTemplate", "datepickerClass", "dayTemplate", "dayTemplateData", "displayMonths", "firstDayOfWeek", "footerTemplate", "markDisabled", "minDate", "maxDate", "navigation", "outsideDays", "placement", "popperOptions", "restoreFocus", "showWeekNumbers", "startDate", "container", "positionTarget", "weekdays", "disabled"], outputs: ["dateSelect", "navigate", "closed"], exportAs: ["ngbDatepicker"] }, { kind: "component", type: TextSpinnerComponent, selector: "sk-text-spinner", inputs: ["message", "size", "spinnerLocation", "loaderWrapperClasses"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i10.InfiniteScrollDirective, selector: "[infiniteScroll], [infinite-scroll], [data-infinite-scroll]", inputs: ["infiniteScrollDistance", "infiniteScrollUpDistance", "infiniteScrollThrottle", "infiniteScrollDisabled", "infiniteScrollContainer", "scrollWindow", "immediateCheck", "horizontal", "alwaysCallback", "fromRoot"], outputs: ["scrolled", "scrolledUp"] }, { kind: "directive", type: HighlightDirective, selector: "[search-highlight]", inputs: ["searchedWord", "content", "classToApply", "setTitle"] }, { kind: "directive", type: NotificationsSortableHeaderDirective, selector: "th[sortable]", inputs: ["sortable", "direction", "default"], outputs: ["sort"] }, { kind: "component", type: NotificationsSearchComponent, selector: "sk-notifications-search", inputs: ["searchTerm"], outputs: ["searchTermChange", "typeaheadSelect"] }, { kind: "pipe", type: i2$3.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsCustomersTableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-notifications-customers-table', template: "<div class=\"sk-flex-table\">\r\n    <div class=\"sk-table-cap --d-none\">\r\n        <div class=\"d-inline-flex align-items-center\">\r\n            <h5 id=\"alerts-customer-view-header\">{{getCustomerCountText()}}</h5>\r\n            <a class=\"ml-50\"\r\n                href=\"https://support.skykick.com/hc/en-us/articles/1500002350602-SkyKick-platform-notifications-aka-alerts-\"\r\n                target=\"_blank\">Learn more</a>\r\n        </div>\r\n\r\n        <div class=\"d-inline-flex text-truncate flex-shrink-0\">\r\n            <button id=\"sk-button-filters\" (click)=\"toggleFilters = !toggleFilters\" type=\"button\"\r\n                class=\"btn btn-outline-secondary sk-btn-icon mr-100\">\r\n                <i class=\"material-icons md-22\">filter_list</i>\r\n                <span id=\"sk-applied-filters-count\" *ngIf=\"filterTally > 0\"\r\n                    class=\"badge rounded-pill badge-default-bold\">{{filterTally}}</span>\r\n            </button>\r\n            <sk-notifications-search (searchTermChange)=\"onSearchTermUpdated($event)\"\r\n                (typeaheadSelect)=\"onOrderSelect($event)\" [searchTerm]=\"searchTerm\">\r\n            </sk-notifications-search>\r\n        </div>\r\n    </div>\r\n\r\n    <div *ngIf=\"toggleFilters\" class=\"sk-table-cap\">\r\n        <div class=\"d-inline-flex align-items-center\">\r\n            <div ngbDropdown class=\"btn-group mr-100\">\r\n                <button\r\n                    id=\"sk-button-filter-product\"\r\n                    type=\"button\"\r\n                    class=\"btn btn-outline-secondary dropdown-toggle\"\r\n                    [disabled] = \"productTypes.length <= 1\"\r\n                    ngbDropdownToggle>\r\n                    {{getProductTypeDisplayName(productType)}}\r\n                </button>\r\n                <div class=\"dropdown-menu\" ngbDropdownMenu>\r\n                    <ng-container *ngFor=\"let item of productTypes; let index = index\">\r\n                        <a class=\"dropdown-item\" (click)=\"productType = item.value; setTally()\"\r\n                            id=\"productTypeDropdown{{index}}\">{{item.name}}</a>\r\n                    </ng-container>\r\n                </div>\r\n            </div>\r\n\r\n            <div ngbDropdown class=\"btn-group mr-100\">\r\n                <button id=\"sk-button-filter-severity\" type=\"button\" class=\"btn btn-outline-secondary dropdown-toggle\"\r\n                    ngbDropdownToggle>{{getSeverityTypeDisplayName(severityType)}}</button>\r\n                <div class=\"dropdown-menu\" ngbDropdownMenu>\r\n                    <ng-container *ngFor=\"let item of severityTypes; let index = index\">\r\n                        <a class=\"dropdown-item\" (click)=\"severityType = item.value; setTally()\"\r\n                            id=\"severityTypeDropdown{{index}}\">{{item.name}}</a>\r\n                    </ng-container>\r\n                </div>\r\n            </div>\r\n\r\n            <div class=\"input-group mr-100\">\r\n                <input id=\"sk-input-filter-start-date\" [(ngModel)]=\"startDate\" (ngModelChange)=\"setTally()\"\r\n                    class=\"form-control\" placeholder=\"Start Date\" name=\"d1\" ngbDatepicker #d1=\"ngbDatepicker\"\r\n                    (mousedown)=\"d1.toggle()\" readonly />\r\n                <div class=\"input-group-append\">\r\n                    <button class=\"btn btn-outline-secondary calendar\" (click)=\"d1.toggle()\" type=\"button\">\r\n                        <span class=\"md-datepicker-calendar-icon\"></span>\r\n                    </button>\r\n                </div>\r\n            </div>\r\n\r\n            <div class=\"input-group mr-100\">\r\n                <input id=\"sk-input-filters-end-date\" [(ngModel)]=\"endDate\" (ngModelChange)=\"setTally()\"\r\n                    class=\"form-control\" placeholder=\"End Date\" name=\"d2\" ngbDatepicker #d2=\"ngbDatepicker\"\r\n                    (mousedown)=\"d2.toggle()\" readonly />\r\n                <div class=\"input-group-append\">\r\n                    <button class=\"btn btn-outline-secondary calendar\" (click)=\"d2.toggle()\" type=\"button\">\r\n                        <span class=\"md-datepicker-calendar-icon\"></span>\r\n                    </button>\r\n                </div>\r\n            </div>\r\n\r\n            <button id=\"sk-button-apply-filters\" class=\"btn btn-primary ml-100 mr-50 text-nowrap sk-btn-combo\"\r\n                (click)=\"applyFilters()\">{{'platform.alerts.apply_filters' | translate}}</button>\r\n\r\n            <button id=\"sk-button-clear-filters\" class=\"btn btn-link text-nowrap\" (click)=\"clearFilters()\"\r\n                [disabled]=\"!filterTally\">{{'platform.alerts.clear_filters' | translate}}</button>\r\n        </div>\r\n    </div>\r\n\r\n    <div class=\"sk-scroll-container p-0 customer-table-scroll-container\">\r\n        <table class=\"table table-hover sk-standard-wrap-table sk-sortable sk-select-table sk-sticky-header sk-min-width-lg\">\r\n            <thead class=\"thead-white thead-sm thead-border-none\">\r\n                <tr>\r\n                    <th sortable=\"CustomerName\" (sort)=\"onSort($event)\" id=\"sk-customer-alerts-customername-header\"\r\n                        style=\"width:40%;\">\r\n                        {{'platform.alerts.customer' | translate}}</th>\r\n                    <th sortable=\"Count\" (sort)=\"onSort($event)\" id=\"sk-customer-alerts-notificationcount-header\"\r\n                        class=\"text-center\">{{'platform.notifications.title' | translate }}</th>\r\n                    <th sortable=\"Product\" (sort)=\"onSort($event)\" id=\"sk-customer-alerts-productname-header\">\r\n                        {{'platform.alerts.product' | translate}}</th>\r\n                    <th sortable=\"Severity\" (sort)=\"onSort($event)\" id=\"sk-customer-alerts-severity-header\"\r\n                        default=\"true\">{{'platform.alerts.type' | translate}}\r\n                    </th>\r\n                    <th sortable=\"CreatedOn\" (sort)=\"onSort($event)\" id=\"sk-customer-alerts-createdon-header\">\r\n                        {{'platform.alerts.date' | translate}}</th>\r\n                </tr>\r\n            </thead>\r\n            <tbody infinite-scroll (scrolled)=\"onScroll()\" [infiniteScrollDistance]=\"2\" [infiniteScrollThrottle]=\"50\" [infiniteScrollContainer]=\"'.modal-body'\" [fromRoot]=\"true\" [scrollWindow]=\"false\">\r\n                <tr *ngIf=\"customers.length === 0 && !loadingMore\">\r\n                    <td colspan=\"5\">\r\n                        <div id=\"sk-no-notifications-graphic\" class=\"text-center\">\r\n                            <i class=\"material-icons md-60 icon-primary\">check</i>\r\n                            <div class=\"text-muted\">{{'platform.alerts.no_notifications' | translate}}</div>\r\n                        </div>\r\n                    </td>\r\n                </tr>\r\n                <tr *ngFor=\"let customer of customers\" [id]=\"'alerts-customer-view-table-row-'+customer.orderId\"\r\n                    role=\"button\" (click)=\"onNavigate(customer)\">\r\n                    <td search-highlight [searchedWord]=\"searchTerm\" [content]=\"customer.customerName\"\r\n                        [classToApply]=\"'font-weight-bold'\" [setTitle]=\"'false'\">\r\n                        {{customer.customerName}}\r\n                    </td>\r\n                    <td class=\"text-center\">{{customer.count}}</td>\r\n                    <td>{{customer.productType}}</td>\r\n                    <td class=\"text-nowrap pr-200\">\r\n                        <i *ngIf=\"customer.hasCriticalAlerts\" class=\"material-icons md-22 icon-danger\"\r\n                           ngbTooltip=\"Critical\">warning</i>\r\n                        <i *ngIf=\"customer.hasWarningAlerts\" class=\"material-icons md-22 icon-warning\"\r\n                           ngbTooltip=\"Warning\">error</i>\r\n                        <i *ngIf=\"customer.hasInformationAlerts\" class=\"material-icons md-22 icon-info\"\r\n                           ngbTooltip=\"Informational\">info</i>\r\n                        <i *ngIf=\"customer.hasSuccessAlerts\" class=\"material-icons md-22 icon-success\"\r\n                           ngbTooltip=\"Success\">check_circle</i>\r\n                    </td>\r\n                    <td>{{moment.utc(customer.createdOn).fromNow()}}</td>\r\n                </tr>\r\n            </tbody>\r\n        </table>\r\n    </div>\r\n    <sk-text-spinner *ngIf=\"loadingMore\" message=\"Fetching Customer Notifications\"></sk-text-spinner>\r\n</div>\r\n", styles: [".customer-table-scroll-container{overflow-x:hidden}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.NgbInputDatepickerConfig }, { type: i1$1.NgbDropdownConfig }, { type: i1$1.NgbDatepickerConfig }, { type: NotificationResourcesService }, { type: NotificationMethodsService }, { type: i2$3.TranslateService }, { type: i4.CacheService }, { type: UserProductTypesService }], propDecorators: { notificationsNavigation: [{
                type: Input
            }], filter: [{
                type: Input
            }], headers: [{
                type: ViewChildren,
                args: [NotificationsSortableHeaderDirective]
            }] } });

class NotificationsNavigationComponent {
    constructor() {
        this.NavigationLocation = NavigationLocation;
        this.dismiss = new EventEmitter();
    }
    ngOnInit() { }
    dismissModal() {
        this.dismiss.emit();
    }
    resetNavigation() {
        this.notificationsNavigation.returnToCustomersView();
    }
    onCustomerNameClicked() {
        this.notificationsNavigation.returnToCustomerOrdersView();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsNavigationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsNavigationComponent, selector: "sk-notifications-navigation", inputs: { notificationsNavigation: "notificationsNavigation" }, outputs: { dismiss: "dismiss" }, ngImport: i0, template: "<div class=\"sk-page-header header-light\">\r\n    <div class=\"sk-navbar-primary secondary-none\" *ngIf=\"notificationsNavigation\">\r\n        <div class=\"d-flex align-items-center min-w-0\">\r\n            <div class=\"d-flex min-w-0\">\r\n                <div class=\"d-flex flex-row align-items-center mw-100\">\r\n                    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.Customers\"\r\n                        class=\"sk-navbar-heading min-w-0\">\r\n                        <h3 class=\"modal-title font-weight-normal\">{{'platform.notifications.title' | translate}}</h3>\r\n                    </div>\r\n                    <div *ngIf=\"notificationsNavigation.location != NavigationLocation.Customers\">\r\n                        <button id=\"go-to-top-notifications\" type=\"button\" class=\"btn sk-btn-icon btn-toggle\"\r\n                            (click)=\"resetNavigation()\">\r\n                            <i class=\"material-icons md-22\">notifications</i>\r\n                        </button>\r\n                    </div>\r\n                    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.CustomerOrders\r\n                                || notificationsNavigation.location == NavigationLocation.AlertDetail\r\n                                || notificationsNavigation.location == NavigationLocation.OrderLevelPreferences\"\r\n                        class=\"sk-navbar-heading min-w-0\">\r\n                        <i class=\"material-icons md-22 ps-0\">keyboard_arrow_right</i>\r\n                    </div>\r\n                    <div id=\"go-back-to-customer\"\r\n                        *ngIf=\"notificationsNavigation.location == NavigationLocation.CustomerOrders\r\n                                                        || notificationsNavigation.location == NavigationLocation.OrderLevelPreferences\r\n                                                        || notificationsNavigation.location == NavigationLocation.AlertDetail\"\r\n                        class=\"text-truncate\" (click)=\"onCustomerNameClicked()\" role=\"button\">\r\n                        <h3 class=\"modal-title font-weight-normal text-truncate\">\r\n                            {{ notificationsNavigation?.customer.customerName }}\r\n                        </h3>\r\n                    </div>\r\n                    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.AlertDetail || notificationsNavigation.location == NavigationLocation.OrderLevelPreferences\"\r\n                        class=\"sk-navbar-heading min-w-0\">\r\n                        <i class=\"material-icons md-22 ps-0\">keyboard_arrow_right</i>\r\n                    </div>\r\n                    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.AlertDetail\"\r\n                        class=\"sk-navbar-heading min-w-0\">\r\n                        <h3 class=\"modal-title font-weight-normal text-truncate\">\r\n                            {{ notificationsNavigation?.alertName }}\r\n                        </h3>\r\n                    </div>\r\n                    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.OrderLevelPreferences\"\r\n                        class=\"sk-navbar-heading min-w-0\">\r\n                        <h3 class=\"modal-title font-weight-normal\">\r\n                            {{'platform.notifications.notification_preferences' | translate}}\r\n                        </h3>\r\n                    </div>\r\n                </div>\r\n            </div>\r\n        </div>\r\n        <div class=\"d-flex flex-shrink-0 min-w-0\">\r\n            <div class=\"d-flex\">\r\n                <button type=\"button\" class=\"btn sk-btn-icon sk-panel-btn\" (click)=\"dismissModal()\"\r\n                    id=\"sk-btn-alert-panel-close\">\r\n                    <i class=\"material-icons md-22\">close</i>\r\n                </button>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i2$3.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsNavigationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-notifications-navigation', template: "<div class=\"sk-page-header header-light\">\r\n    <div class=\"sk-navbar-primary secondary-none\" *ngIf=\"notificationsNavigation\">\r\n        <div class=\"d-flex align-items-center min-w-0\">\r\n            <div class=\"d-flex min-w-0\">\r\n                <div class=\"d-flex flex-row align-items-center mw-100\">\r\n                    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.Customers\"\r\n                        class=\"sk-navbar-heading min-w-0\">\r\n                        <h3 class=\"modal-title font-weight-normal\">{{'platform.notifications.title' | translate}}</h3>\r\n                    </div>\r\n                    <div *ngIf=\"notificationsNavigation.location != NavigationLocation.Customers\">\r\n                        <button id=\"go-to-top-notifications\" type=\"button\" class=\"btn sk-btn-icon btn-toggle\"\r\n                            (click)=\"resetNavigation()\">\r\n                            <i class=\"material-icons md-22\">notifications</i>\r\n                        </button>\r\n                    </div>\r\n                    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.CustomerOrders\r\n                                || notificationsNavigation.location == NavigationLocation.AlertDetail\r\n                                || notificationsNavigation.location == NavigationLocation.OrderLevelPreferences\"\r\n                        class=\"sk-navbar-heading min-w-0\">\r\n                        <i class=\"material-icons md-22 ps-0\">keyboard_arrow_right</i>\r\n                    </div>\r\n                    <div id=\"go-back-to-customer\"\r\n                        *ngIf=\"notificationsNavigation.location == NavigationLocation.CustomerOrders\r\n                                                        || notificationsNavigation.location == NavigationLocation.OrderLevelPreferences\r\n                                                        || notificationsNavigation.location == NavigationLocation.AlertDetail\"\r\n                        class=\"text-truncate\" (click)=\"onCustomerNameClicked()\" role=\"button\">\r\n                        <h3 class=\"modal-title font-weight-normal text-truncate\">\r\n                            {{ notificationsNavigation?.customer.customerName }}\r\n                        </h3>\r\n                    </div>\r\n                    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.AlertDetail || notificationsNavigation.location == NavigationLocation.OrderLevelPreferences\"\r\n                        class=\"sk-navbar-heading min-w-0\">\r\n                        <i class=\"material-icons md-22 ps-0\">keyboard_arrow_right</i>\r\n                    </div>\r\n                    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.AlertDetail\"\r\n                        class=\"sk-navbar-heading min-w-0\">\r\n                        <h3 class=\"modal-title font-weight-normal text-truncate\">\r\n                            {{ notificationsNavigation?.alertName }}\r\n                        </h3>\r\n                    </div>\r\n                    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.OrderLevelPreferences\"\r\n                        class=\"sk-navbar-heading min-w-0\">\r\n                        <h3 class=\"modal-title font-weight-normal\">\r\n                            {{'platform.notifications.notification_preferences' | translate}}\r\n                        </h3>\r\n                    </div>\r\n                </div>\r\n            </div>\r\n        </div>\r\n        <div class=\"d-flex flex-shrink-0 min-w-0\">\r\n            <div class=\"d-flex\">\r\n                <button type=\"button\" class=\"btn sk-btn-icon sk-panel-btn\" (click)=\"dismissModal()\"\r\n                    id=\"sk-btn-alert-panel-close\">\r\n                    <i class=\"material-icons md-22\">close</i>\r\n                </button>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</div>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { dismiss: [{
                type: Output
            }], notificationsNavigation: [{
                type: Input
            }] } });

const NOTIFICATIONS_INIT_CONFIG$2 = 'notificationsInitConfig';
class NotificationsCustomerAlertsTableComponent {
    constructor(cgbTooltipConfig, notificationResourcesService, notificationMethodsService, cacheService) {
        this.notificationResourcesService = notificationResourcesService;
        this.notificationMethodsService = notificationMethodsService;
        this.cacheService = cacheService;
        this.navigateToAlert = new EventEmitter();
        this.setAlertCount = new EventEmitter();
        this.onDestroy$ = new Subject();
        this.moment = moment;
        this.currentPage$ = new Subject();
        this.page = 0;
        this.alerts = [];
        this.loadingMore = true;
        this.orderBy = { orderByType: 'Descending', orderByField: 'Severity' };
        cgbTooltipConfig.openDelay = 500;
        cgbTooltipConfig.closeDelay = 100;
    }
    ngOnDestroy() {
        this.currentPage$.unsubscribe();
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
    ngOnInit() {
        // Create a subject which just returns a number.
        // When the .next() function is called the Observable is executed.
        // During execution, we use switchmap to map the subject observable(number) to the alerts query observable.
        // From there we add the result of the alerts query observable to an internal collection.
        this.currentPage$.asObservable()
            .pipe(tap(x => this.loadingMore = true), switchMap(_ => {
            return this.notificationResourcesService.alertsQuery(this.notificationMethodsService.buildQueryForOrderAlerts(this.notificationsNavigation.customer.orderId, this.alertsFilter, this.page, this.orderBy));
        }), takeUntil(this.onDestroy$))
            .subscribe({
            next: res => {
                this.loadingMore = false;
                this.totalAlertCount = res.total;
                this.setAlertCount.emit(this.totalAlertCount);
                if (res.total === 0) {
                    if (!this.filtersActive(this.alertsFilter)) {
                        this.notificationsNavigation.returnToCustomersView();
                    }
                }
                else {
                    this.initializationConfig = this.cacheService.root()
                        .get(NOTIFICATIONS_INIT_CONFIG$2);
                    if (this.notificationMethodsService.verifyModalConfig(this.initializationConfig) &&
                        this.initializationConfig.AlertIds) {
                        if (this.initializationConfig.AlertIds.length === 1) {
                            const alert = res.items.find(x => this.initializationConfig.AlertIds.includes(x.id));
                            this.onAlertClicked(alert);
                        }
                        else {
                            this.alerts = res.items.filter(x => this.initializationConfig.AlertIds.includes(x.id));
                        }
                    }
                    else {
                        this.alerts.push(...res.items);
                    }
                }
            },
            error: () => {
                this.loadingMore = false;
            }
        });
        // Trigger the first load
        this.currentPage$.next(this.page);
    }
    ngOnChanges(changesObject) {
        if (changesObject.alertsFilter) {
            this.clearResultsAndApplyFilter();
        }
    }
    filtersActive(filter) {
        return !!(filter.status || filter.severity || filter.startDate || filter.endDate);
    }
    onAlertClicked(alert) {
        this.navigateToAlert.emit(alert);
    }
    clearResultsAndApplyFilter() {
        this.alerts = [];
        this.page = 0;
        this.currentPage$.next(this.page);
    }
    onSort({ column, direction }) {
        // resetting other headers
        this.headers.forEach(header => {
            if (header.sortable !== column) {
                header.direction = '';
            }
        });
        this.orderBy.orderByField = column;
        this.orderBy.orderByType = direction;
        // Apply the sorting
        this.clearResultsAndApplyFilter();
    }
    onScroll() {
        if (this.alerts.length >= this.totalAlertCount) {
            return;
        }
        else if (this.loadingMore) {
            return;
        }
        this.page++;
        this.currentPage$.next(this.page);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsCustomerAlertsTableComponent, deps: [{ token: i1$1.NgbTooltipConfig }, { token: NotificationResourcesService }, { token: NotificationMethodsService }, { token: i4.CacheService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsCustomerAlertsTableComponent, selector: "sk-notifications-customer-alerts-table", inputs: { notificationsNavigation: "notificationsNavigation", alertsFilter: "alertsFilter" }, outputs: { navigateToAlert: "navigateToAlert", setAlertCount: "setAlertCount" }, providers: [NgbTooltipConfig], viewQueries: [{ propertyName: "headers", predicate: NotificationsSortableHeaderDirective, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"sk-scroll-container p-0 customer-table-scroll-container\">\r\n    <table\r\n        class=\"table table-hover sk-standard-wrap-table sk-sortable sk-select-table sk-sticky-header sk-min-width-lg\">\r\n        <thead class=\"thead-white thead-sm thead-border-none\">\r\n            <tr>\r\n                <th style=\"width:75%;\">{{'platform.alerts.subject' | translate}}</th>\r\n                <th style=\"width:6rem;\" sortable=\"Severity\" default=\"true\" (sort)=\"onSort($event)\">\r\n                    {{'platform.alerts.type' | translate}}</th>\r\n                <th sortable=\"CreatedOn\" (sort)=\"onSort($event)\">{{'platform.alerts.date' | translate}}</th>\r\n            </tr>\r\n        </thead>\r\n        <tbody infinite-scroll (scrolled)=\"onScroll()\" [infiniteScrollDistance]=\"2\" [infiniteScrollThrottle]=\"50\" [infiniteScrollContainer]=\"'.modal-body'\" [fromRoot]=\"true\" [scrollWindow]=\"false\">\r\n            <tr *ngIf=\"alerts.length === 0 && !loadingMore\">\r\n                <td colspan=\"5\">\r\n                    <div id=\"sk-no-alerts-graphic\" class=\"text-center\">\r\n                        <i class=\"material-icons md-60 icon-primary\">check</i>\r\n                        <div class=\"text-muted\">{{'platform.alerts.no_active_alerts' | translate}}</div>\r\n                    </div>\r\n                </td>\r\n            </tr>\r\n            <tr [id]=\"'customer-notifications-table-row-'+i\" *ngFor=\"let alert of alerts; let i = index\" data-index=\"i\"\r\n                role=\"button\" (click)=\"onAlertClicked(alert)\">\r\n                <td>{{alert.subject}}</td>\r\n                <td class=\"mr-50\" ngbTootip=\"Error\">\r\n                    <span *ngIf=\"alert.isError\" ngbTooltip=\"Error\" style=\"display:block;\">\r\n                        <i class=\"material-icons md-22 icon-danger\">warning</i>\r\n                    </span>\r\n                    <span *ngIf=\"alert.isWarning\" ngbTooltip=\"Warning\" style=\"display:block;\">\r\n                        <i class=\"material-icons md-22 icon-warning\">error</i>\r\n                    </span>\r\n                    <span *ngIf=\"alert.isInformational\" ngbTooltip=\"Information\" style=\"display:block;\">\r\n                        <i class=\"material-icons md-22 icon-info\">info</i>\r\n                    </span>\r\n                    <span *ngIf=\"alert.isSuccessful\" ngbTooltip=\"Success\" style=\"display:block;\">\r\n                        <i class=\"material-icons md-22 icon-success\">check_circle</i>\r\n                    </span>\r\n                </td>\r\n                <td>{{moment.utc(alert.createdOn).fromNow()}}</td>\r\n            </tr>\r\n        </tbody>\r\n    </table>\r\n    <sk-text-spinner *ngIf=\"loadingMore\" message=\"Fetching Order Alerts\"></sk-text-spinner>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "popperOptions", "triggers", "positionTarget", "container", "disableTooltip", "tooltipClass", "tooltipContext", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "component", type: TextSpinnerComponent, selector: "sk-text-spinner", inputs: ["message", "size", "spinnerLocation", "loaderWrapperClasses"] }, { kind: "directive", type: i10.InfiniteScrollDirective, selector: "[infiniteScroll], [infinite-scroll], [data-infinite-scroll]", inputs: ["infiniteScrollDistance", "infiniteScrollUpDistance", "infiniteScrollThrottle", "infiniteScrollDisabled", "infiniteScrollContainer", "scrollWindow", "immediateCheck", "horizontal", "alwaysCallback", "fromRoot"], outputs: ["scrolled", "scrolledUp"] }, { kind: "directive", type: NotificationsSortableHeaderDirective, selector: "th[sortable]", inputs: ["sortable", "direction", "default"], outputs: ["sort"] }, { kind: "pipe", type: i2$3.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsCustomerAlertsTableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-notifications-customer-alerts-table', providers: [NgbTooltipConfig], template: "<div class=\"sk-scroll-container p-0 customer-table-scroll-container\">\r\n    <table\r\n        class=\"table table-hover sk-standard-wrap-table sk-sortable sk-select-table sk-sticky-header sk-min-width-lg\">\r\n        <thead class=\"thead-white thead-sm thead-border-none\">\r\n            <tr>\r\n                <th style=\"width:75%;\">{{'platform.alerts.subject' | translate}}</th>\r\n                <th style=\"width:6rem;\" sortable=\"Severity\" default=\"true\" (sort)=\"onSort($event)\">\r\n                    {{'platform.alerts.type' | translate}}</th>\r\n                <th sortable=\"CreatedOn\" (sort)=\"onSort($event)\">{{'platform.alerts.date' | translate}}</th>\r\n            </tr>\r\n        </thead>\r\n        <tbody infinite-scroll (scrolled)=\"onScroll()\" [infiniteScrollDistance]=\"2\" [infiniteScrollThrottle]=\"50\" [infiniteScrollContainer]=\"'.modal-body'\" [fromRoot]=\"true\" [scrollWindow]=\"false\">\r\n            <tr *ngIf=\"alerts.length === 0 && !loadingMore\">\r\n                <td colspan=\"5\">\r\n                    <div id=\"sk-no-alerts-graphic\" class=\"text-center\">\r\n                        <i class=\"material-icons md-60 icon-primary\">check</i>\r\n                        <div class=\"text-muted\">{{'platform.alerts.no_active_alerts' | translate}}</div>\r\n                    </div>\r\n                </td>\r\n            </tr>\r\n            <tr [id]=\"'customer-notifications-table-row-'+i\" *ngFor=\"let alert of alerts; let i = index\" data-index=\"i\"\r\n                role=\"button\" (click)=\"onAlertClicked(alert)\">\r\n                <td>{{alert.subject}}</td>\r\n                <td class=\"mr-50\" ngbTootip=\"Error\">\r\n                    <span *ngIf=\"alert.isError\" ngbTooltip=\"Error\" style=\"display:block;\">\r\n                        <i class=\"material-icons md-22 icon-danger\">warning</i>\r\n                    </span>\r\n                    <span *ngIf=\"alert.isWarning\" ngbTooltip=\"Warning\" style=\"display:block;\">\r\n                        <i class=\"material-icons md-22 icon-warning\">error</i>\r\n                    </span>\r\n                    <span *ngIf=\"alert.isInformational\" ngbTooltip=\"Information\" style=\"display:block;\">\r\n                        <i class=\"material-icons md-22 icon-info\">info</i>\r\n                    </span>\r\n                    <span *ngIf=\"alert.isSuccessful\" ngbTooltip=\"Success\" style=\"display:block;\">\r\n                        <i class=\"material-icons md-22 icon-success\">check_circle</i>\r\n                    </span>\r\n                </td>\r\n                <td>{{moment.utc(alert.createdOn).fromNow()}}</td>\r\n            </tr>\r\n        </tbody>\r\n    </table>\r\n    <sk-text-spinner *ngIf=\"loadingMore\" message=\"Fetching Order Alerts\"></sk-text-spinner>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i1$1.NgbTooltipConfig }, { type: NotificationResourcesService }, { type: NotificationMethodsService }, { type: i4.CacheService }], propDecorators: { navigateToAlert: [{
                type: Output
            }], setAlertCount: [{
                type: Output
            }], notificationsNavigation: [{
                type: Input
            }], alertsFilter: [{
                type: Input
            }], headers: [{
                type: ViewChildren,
                args: [NotificationsSortableHeaderDirective]
            }] } });

const NOTIFICATIONS_INIT_CONFIG$1 = 'notificationsInitConfig';
class NotificationsCustomerAlertsFilterComponent {
    constructor(ngbInputDatepickerConfig, ngbDropdownConfig, ngbDatepickerConfig, translate, notificationMethodsService, cacheService) {
        this.translate = translate;
        this.notificationMethodsService = notificationMethodsService;
        this.cacheService = cacheService;
        this.alertsFilterOutput = new EventEmitter();
        this.alertsFilter = {
            status: AlertsFilterStatus.Active,
            severity: SeverityTypes.Any,
            startDate: '',
            endDate: '',
        };
        this.orderStatusTypes = ORDERSTATUSTYPES;
        this.severityTypes = SEVERITYTYPES;
        this.toggleFilters = false;
        ngbInputDatepickerConfig.container = 'body';
        ngbDropdownConfig.container = 'body';
        ngbDatepickerConfig.firstDayOfWeek = 0;
        ngbDatepickerConfig.displayMonths = 2;
        ngbDatepickerConfig.navigation = 'arrows';
    }
    ngOnInit() {
        this.clearFilters();
        this.initializationConfig = this.cacheService.root().get(NOTIFICATIONS_INIT_CONFIG$1);
        if (this.notificationMethodsService.verifyModalConfig(this.initializationConfig) &&
            this.initializationConfig.AlertsFilter) {
            this.alertsFilter = this.initializationConfig.AlertsFilter;
            this.toggleFilters = !this.toggleFilters;
            this.setSeverityFilter(this.alertsFilter.severity);
            this.setOrderStatusFilter(this.alertsFilter.status);
            this.setTally();
        }
        else {
            this.setOrderStatusFilter(AlertsFilterStatus.Active);
            this.setTally();
        }
        this.applyFilters();
    }
    setTally() {
        this.filterTally =
            (this.orderStatusType === AlertsFilterStatus.Any ? 0 : 1) +
                (this.severityType === SeverityTypes.Any ? 0 : 1) +
                (this.startDate ? 1 : 0) +
                (this.endDate ? 1 : 0);
    }
    clearFilters() {
        this.orderStatusType = AlertsFilterStatus.Any;
        this.alertsFilter.status = AlertsFilterStatus.Any;
        this.severityType = SeverityTypes.Any;
        this.alertsFilter.severity = SeverityTypes.Any;
        this.startDate = null;
        this.alertsFilter.startDate = null;
        this.endDate = null;
        this.alertsFilter.endDate = null;
        this.filterTally = 0;
    }
    onShowPreferencesClicked() {
        this.notificationsNavigation.navigateToOrderLevelPreferences();
    }
    applyFilters() {
        // Perform a deep copy so onChanges fires when properties are updated.
        const alertsFilterCopy = JSON.parse(JSON.stringify(this.alertsFilter || {}));
        this.alertsFilterOutput.emit(alertsFilterCopy);
    }
    setOrderStatusFilter(orderStatus) {
        this.alertsFilter.status = orderStatus;
        this.orderStatusType = orderStatus;
    }
    setSeverityFilter(severity) {
        this.alertsFilter.severity = severity;
        this.severityType = severity;
    }
    setStartDateFilter(startDate) {
        this.alertsFilter.startDate = formatNgbDateStruct(startDate);
    }
    setEndDateFilter(endDate) {
        this.alertsFilter.endDate = formatNgbDateStruct(endDate);
    }
    getOrderAlertPageUrl() {
        return this.notificationMethodsService.getOrderAlertPageUrlByCustomer(this.notificationsNavigation.customer);
    }
    getAlertCountText() {
        if (this.alertCount === 0 || this.alertCount === undefined) {
            return this.translate.instant('platform.alerts.no_notifications');
        }
        else if (this.alertCount === 1) {
            return this.translate.instant('platform.alerts.one_notification');
        }
        else {
            return `${this.alertCount} ${this.translate.instant('platform.alerts.notifications')}`;
        }
    }
    getSeverityTypeDisplayName(severityType) {
        return this.notificationMethodsService.getSeverityTypeDisplayName(severityType);
    }
    getAlertFilterDisplayName(alertFilteryType) {
        return this.notificationMethodsService.getAlertFilterStatusDisplayName(alertFilteryType);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsCustomerAlertsFilterComponent, deps: [{ token: i1$1.NgbInputDatepickerConfig }, { token: i1$1.NgbDropdownConfig }, { token: i1$1.NgbDatepickerConfig }, { token: i2$3.TranslateService }, { token: NotificationMethodsService }, { token: i4.CacheService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsCustomerAlertsFilterComponent, selector: "sk-notifications-customer-alerts-filter", inputs: { alertCount: "alertCount", notificationsNavigation: "notificationsNavigation" }, outputs: { alertsFilterOutput: "alertsFilterOutput" }, ngImport: i0, template: "<div class=\"sk-table-cap --d-none\">\r\n    <div class=\"d-inline-flex align-items-center\">\r\n        <h5 id=\"customer-notifications-panel-header\">{{ getAlertCountText() }}</h5>\r\n        <a id=\"alert-drilldown-panel-go-to-order-link\" href=\"{{getOrderAlertPageUrl()}}\" target=\"_blank\"\r\n            class=\"align-baseline ms-4\">{{ 'platform.notifications.go_to_order' | translate}} <i\r\n                class=\"material-icons md-16 link-icon align-middle\">launch</i></a>\r\n    </div>\r\n\r\n    <div class=\"d-inline-flex justify-content-end flex-row\">\r\n        <div class=\"sk-input-icon mr-100\">\r\n            <button class=\"btn btn-outline-secondary\" (click)=\"onShowPreferencesClicked()\" type=\"button\"\r\n                color=\"outline-secondary\">\r\n                {{ 'platform.order_level_preferences.preferences' | translate}}\r\n            </button>\r\n        </div>\r\n        <button id=\"sk-button-filters\" class=\"btn btn-outline-secondary sk-btn-icon mr-100\"\r\n            (click)=\"toggleFilters = !toggleFilters\" type=\"button\">\r\n            <i class=\"material-icons md-22\">filter_list</i>\r\n            <span id=\"sk-applied-filters-count\" *ngIf=\"filterTally > 0\" class=\"badge rounded-pill badge-default-bold\">\r\n                {{filterTally}}\r\n            </span>\r\n        </button>\r\n    </div>\r\n</div>\r\n\r\n<div class=\"sk-table-cap\">\r\n    <div *ngIf=\"toggleFilters\" class=\"d-inline-flex align-items-center\">\r\n        <div ngbDropdown class=\"mr-100 dropdown\">\r\n            <button type=\"button\" class=\"btn btn-outline-secondary dropdown-toggle\" ngbDropdownToggle\r\n                id=\"sk-button-filter-status\">{{getAlertFilterDisplayName(orderStatusType)}}</button>\r\n            <div class=\"dropdown-menu\" ngbDropdownMenu>\r\n                <ng-container *ngFor=\"let item of orderStatusTypes; let index = index\">\r\n                    <a class=\"dropdown-item\" (click)=\"setOrderStatusFilter(item.value); setTally()\"\r\n                        id=\"orderStatusTypeDropdown{{index}}\">{{item.name}}</a>\r\n                </ng-container>\r\n            </div>\r\n        </div>\r\n\r\n        <div ngbDropdown class=\"btn-group mr-100\">\r\n            <button type=\"button\" class=\"btn btn-outline-secondary dropdown-toggle\" ngbDropdownToggle\r\n                id=\"sk-button-filter-severity\">{{getSeverityTypeDisplayName(severityType)}}</button>\r\n            <div class=\"dropdown-menu\" ngbDropdownMenu>\r\n                <ng-container *ngFor=\"let item of severityTypes; let index = index\">\r\n                    <a class=\"dropdown-item\" (click)=\"setSeverityFilter(item.value); setTally()\"\r\n                        id=\"severityTypeDropdown{{index}}\">{{item.name}}</a>\r\n                </ng-container>\r\n            </div>\r\n        </div>\r\n\r\n        <div class=\"input-group mr-100\">\r\n            <input [(ngModel)]=\"startDate\" (ngModelChange)=\"setStartDateFilter(startDate); setTally()\" id=\"startDate\"\r\n                class=\"form-control\" placeholder=\"Start Date\" name=\"d1\" ngbDatepicker #d1=\"ngbDatepicker\"\r\n                (mousedown)=\"d1.toggle()\" readonly />\r\n            <div class=\"input-group-append\">\r\n                <button class=\"btn btn-outline-secondary calendar\" (click)=\"d1.toggle()\" type=\"button\">\r\n                    <span class=\"md-datepicker-calendar-icon\"></span>\r\n                </button>\r\n            </div>\r\n        </div>\r\n\r\n        <div class=\"input-group mr-100\">\r\n            <input [(ngModel)]=\"endDate\" (ngModelChange)=\"setEndDateFilter(endDate); setTally()\" id=\"endDate\"\r\n                class=\"form-control\" placeholder=\"End Date\" name=\"d2\" ngbDatepicker #d2=\"ngbDatepicker\"\r\n                (mousedown)=\"d2.toggle()\" readonly />\r\n            <div class=\"input-group-append\">\r\n                <button class=\"btn btn-outline-secondary calendar\" (click)=\"d2.toggle()\" type=\"button\">\r\n                    <span class=\"md-datepicker-calendar-icon\"></span>\r\n                </button>\r\n            </div>\r\n        </div>\r\n\r\n        <button id=\"sk-button-apply-filters\" class=\"btn btn-primary ml-100 mr-50 text-nowrap sk-btn-combo\"\r\n            (click)=\"applyFilters()\">{{'platform.alerts.apply_filters' | translate}}</button>\r\n\r\n        <button id=\"sk-button-clear-filters\" class=\"btn btn-link text-nowrap\" (click)=\"clearFilters(); applyFilters();\"\r\n            [disabled]=\"!filterTally\">{{'platform.alerts.clear_filters' | translate}}</button>\r\n    </div>\r\n</div>\r\n", styles: [".dropdown,.dropdown-toggle,.dropdown-menu{width:120px;max-width:100%;text-align:center}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i1$1.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i1$1.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "directive", type: i1$1.NgbInputDatepicker, selector: "input[ngbDatepicker]", inputs: ["autoClose", "contentTemplate", "datepickerClass", "dayTemplate", "dayTemplateData", "displayMonths", "firstDayOfWeek", "footerTemplate", "markDisabled", "minDate", "maxDate", "navigation", "outsideDays", "placement", "popperOptions", "restoreFocus", "showWeekNumbers", "startDate", "container", "positionTarget", "weekdays", "disabled"], outputs: ["dateSelect", "navigate", "closed"], exportAs: ["ngbDatepicker"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "pipe", type: i2$3.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsCustomerAlertsFilterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-notifications-customer-alerts-filter', template: "<div class=\"sk-table-cap --d-none\">\r\n    <div class=\"d-inline-flex align-items-center\">\r\n        <h5 id=\"customer-notifications-panel-header\">{{ getAlertCountText() }}</h5>\r\n        <a id=\"alert-drilldown-panel-go-to-order-link\" href=\"{{getOrderAlertPageUrl()}}\" target=\"_blank\"\r\n            class=\"align-baseline ms-4\">{{ 'platform.notifications.go_to_order' | translate}} <i\r\n                class=\"material-icons md-16 link-icon align-middle\">launch</i></a>\r\n    </div>\r\n\r\n    <div class=\"d-inline-flex justify-content-end flex-row\">\r\n        <div class=\"sk-input-icon mr-100\">\r\n            <button class=\"btn btn-outline-secondary\" (click)=\"onShowPreferencesClicked()\" type=\"button\"\r\n                color=\"outline-secondary\">\r\n                {{ 'platform.order_level_preferences.preferences' | translate}}\r\n            </button>\r\n        </div>\r\n        <button id=\"sk-button-filters\" class=\"btn btn-outline-secondary sk-btn-icon mr-100\"\r\n            (click)=\"toggleFilters = !toggleFilters\" type=\"button\">\r\n            <i class=\"material-icons md-22\">filter_list</i>\r\n            <span id=\"sk-applied-filters-count\" *ngIf=\"filterTally > 0\" class=\"badge rounded-pill badge-default-bold\">\r\n                {{filterTally}}\r\n            </span>\r\n        </button>\r\n    </div>\r\n</div>\r\n\r\n<div class=\"sk-table-cap\">\r\n    <div *ngIf=\"toggleFilters\" class=\"d-inline-flex align-items-center\">\r\n        <div ngbDropdown class=\"mr-100 dropdown\">\r\n            <button type=\"button\" class=\"btn btn-outline-secondary dropdown-toggle\" ngbDropdownToggle\r\n                id=\"sk-button-filter-status\">{{getAlertFilterDisplayName(orderStatusType)}}</button>\r\n            <div class=\"dropdown-menu\" ngbDropdownMenu>\r\n                <ng-container *ngFor=\"let item of orderStatusTypes; let index = index\">\r\n                    <a class=\"dropdown-item\" (click)=\"setOrderStatusFilter(item.value); setTally()\"\r\n                        id=\"orderStatusTypeDropdown{{index}}\">{{item.name}}</a>\r\n                </ng-container>\r\n            </div>\r\n        </div>\r\n\r\n        <div ngbDropdown class=\"btn-group mr-100\">\r\n            <button type=\"button\" class=\"btn btn-outline-secondary dropdown-toggle\" ngbDropdownToggle\r\n                id=\"sk-button-filter-severity\">{{getSeverityTypeDisplayName(severityType)}}</button>\r\n            <div class=\"dropdown-menu\" ngbDropdownMenu>\r\n                <ng-container *ngFor=\"let item of severityTypes; let index = index\">\r\n                    <a class=\"dropdown-item\" (click)=\"setSeverityFilter(item.value); setTally()\"\r\n                        id=\"severityTypeDropdown{{index}}\">{{item.name}}</a>\r\n                </ng-container>\r\n            </div>\r\n        </div>\r\n\r\n        <div class=\"input-group mr-100\">\r\n            <input [(ngModel)]=\"startDate\" (ngModelChange)=\"setStartDateFilter(startDate); setTally()\" id=\"startDate\"\r\n                class=\"form-control\" placeholder=\"Start Date\" name=\"d1\" ngbDatepicker #d1=\"ngbDatepicker\"\r\n                (mousedown)=\"d1.toggle()\" readonly />\r\n            <div class=\"input-group-append\">\r\n                <button class=\"btn btn-outline-secondary calendar\" (click)=\"d1.toggle()\" type=\"button\">\r\n                    <span class=\"md-datepicker-calendar-icon\"></span>\r\n                </button>\r\n            </div>\r\n        </div>\r\n\r\n        <div class=\"input-group mr-100\">\r\n            <input [(ngModel)]=\"endDate\" (ngModelChange)=\"setEndDateFilter(endDate); setTally()\" id=\"endDate\"\r\n                class=\"form-control\" placeholder=\"End Date\" name=\"d2\" ngbDatepicker #d2=\"ngbDatepicker\"\r\n                (mousedown)=\"d2.toggle()\" readonly />\r\n            <div class=\"input-group-append\">\r\n                <button class=\"btn btn-outline-secondary calendar\" (click)=\"d2.toggle()\" type=\"button\">\r\n                    <span class=\"md-datepicker-calendar-icon\"></span>\r\n                </button>\r\n            </div>\r\n        </div>\r\n\r\n        <button id=\"sk-button-apply-filters\" class=\"btn btn-primary ml-100 mr-50 text-nowrap sk-btn-combo\"\r\n            (click)=\"applyFilters()\">{{'platform.alerts.apply_filters' | translate}}</button>\r\n\r\n        <button id=\"sk-button-clear-filters\" class=\"btn btn-link text-nowrap\" (click)=\"clearFilters(); applyFilters();\"\r\n            [disabled]=\"!filterTally\">{{'platform.alerts.clear_filters' | translate}}</button>\r\n    </div>\r\n</div>\r\n", styles: [".dropdown,.dropdown-toggle,.dropdown-menu{width:120px;max-width:100%;text-align:center}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.NgbInputDatepickerConfig }, { type: i1$1.NgbDropdownConfig }, { type: i1$1.NgbDatepickerConfig }, { type: i2$3.TranslateService }, { type: NotificationMethodsService }, { type: i4.CacheService }], propDecorators: { alertsFilterOutput: [{
                type: Output
            }], alertCount: [{
                type: Input
            }], notificationsNavigation: [{
                type: Input
            }] } });

class Contact {
}

class OrderLevelPreferencesUtilities {
    constructor() {
    }
    getContactValues(contacts) {
        var contactValues = [];
        contacts.forEach(x => contactValues.push(x.value));
        return contactValues;
    }
    setContactValues(contactValues) {
        var contacts = [];
        for (let i = 0; i < contactValues.length; i++) {
            var contact = new Contact();
            contact.value = contactValues[i];
            contact.name = "unknown";
            contact.contactType = 1;
            contacts.push(contact);
        }
        return contacts;
    }
}

class NotificationsOrderLevelPreferencesComponent {
    constructor(notificationResourcesService, userProvider, toastrService) {
        this.notificationResourcesService = notificationResourcesService;
        this.userProvider = userProvider;
        this.toastrService = toastrService;
        this.emailValidationPattern = "^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$";
        this.form = null;
        this.savingPreferences = false;
        this.failedToLoadPreferences = false;
        this.isAdmin = false;
        this.onDestroy$ = new Subject();
        this.isAdmin = userProvider?.getCurrentUser().hasPermission('admin_editall') ?? false;
    }
    ngOnInit() {
        var observable = this.notificationResourcesService.getOrderLevelPreferences(this.orderId);
        observable
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
            next: (preferences) => this.initializeForm(preferences),
            error: () => {
                this.failedToLoadPreferences = true;
            }
        });
    }
    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
    initializeForm(preferences) {
        this.orderLevelPreferences = preferences;
        this.orderLevelPreferences.createdBy = "createdBy";
        this.preferenceUtility = new OrderLevelPreferencesUtilities();
        this.form = new UntypedFormGroup({
            criticalContacts: new UntypedFormControl({ value: this.preferenceUtility.getContactValues(preferences.criticalContacts), disabled: this.savingPreferences, }, Validators.pattern(this.emailValidationPattern)),
            warningContacts: new UntypedFormControl({ value: this.preferenceUtility.getContactValues(preferences.warningContacts), disabled: this.savingPreferences, }, Validators.pattern(this.emailValidationPattern)),
            informationContacts: new UntypedFormControl({ value: this.preferenceUtility.getContactValues(preferences.informationContacts), disabled: this.savingPreferences, }, Validators.pattern(this.emailValidationPattern)),
            successContacts: new UntypedFormControl({ value: this.preferenceUtility.getContactValues(preferences.successContacts), disabled: this.savingPreferences, }, Validators.pattern(this.emailValidationPattern)),
            billingContacts: new UntypedFormControl({ value: this.preferenceUtility.getContactValues(preferences.billingContacts), disabled: this.savingPreferences, }, Validators.pattern(this.emailValidationPattern)),
        });
    }
    onSubmit() {
        this.savingPreferences = true;
        this.orderLevelPreferences.billingContacts = this.preferenceUtility.setContactValues(this.form.get('billingContacts').value);
        this.orderLevelPreferences.criticalContacts = this.preferenceUtility.setContactValues(this.form.get('criticalContacts').value);
        this.orderLevelPreferences.informationContacts = this.preferenceUtility.setContactValues(this.form.get('informationContacts').value);
        this.orderLevelPreferences.successContacts = this.preferenceUtility.setContactValues(this.form.get('successContacts').value);
        this.orderLevelPreferences.warningContacts = this.preferenceUtility.setContactValues(this.form.get('warningContacts').value);
        this.notificationResourcesService.syncOrderLevelPreferences(this.orderLevelPreferences, this.orderId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
            next: (preferences) => {
                this.orderLevelPreferences = preferences;
                this.toastrService.success("Order preferences saved successfully.");
            },
            error: () => {
                this.toastrService.error("Failed to saved order preferences.");
            }
        })
            .add(() => {
            this.savingPreferences = false;
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsOrderLevelPreferencesComponent, deps: [{ token: NotificationResourcesService }, { token: i2$1.AbstractUserProvider }, { token: i5.ToastrService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsOrderLevelPreferencesComponent, selector: "sk-notifications-order-level-preferences", inputs: { orderId: "orderId" }, ngImport: i0, template: "<div>\r\n\r\n\t<div class=\"row align-items-center\">\r\n\t\t<div class=\"d-flex col-lg-12\">\r\n\r\n\t\t\t<h3 class=\"d-flex align-items-center mb-100 pr-50\" translate>platform.order_level_preferences.title</h3>\r\n\r\n\t\t\t<sk-help-popover direction=\"bottom\">\r\n\t\t\t\t{{'platform.order_level_preferences.help_popover' | translate}} <a\r\n\t\t\t\t\thref='https://support.skykick.com/hc/en-us/articles/1500002350602-SkyKick-platform-notifications-aka-alerts-'\r\n\t\t\t\t\ttranslate>platform.order_level_preferences.learn_more</a>\r\n\t\t\t</sk-help-popover>\r\n\r\n\r\n\t\t\t<div style=\"margin-left:auto;\" *ngIf=\"isAdmin\">\r\n\t\t\t\t<div id=\"go-to-all-notifications\" class=\"d-flex btn  m-r-1\">\r\n\t\t\t\t\t<a class=\"btn-link ng-binding\" href=\"/settings/notifications/account\"\r\n\t\t\t\t\t\ttranslate>platform.order_level_preferences.all_notification_settings</a>\r\n\t\t\t\t\t<i id=\"all-notifications-open-new-icon\" class=\"material-icons\" style=\"color:#056ED1\">open_in_new</i>\r\n\t\t\t\t</div>\r\n\t\t\t</div>\r\n\r\n\t\t</div>\r\n\t</div>\r\n\r\n\t<ng-container *ngIf=\"form && !failedToLoadPreferences; else loadingOrError\">\r\n\t\t<form class=\"form-group\" [formGroup]=\"form\" (ngSubmit)=\"onSubmit()\" (keydown.enter)=\"$event.preventDefault()\"\r\n\t\t\t[@fadeIn]>\r\n\r\n\t\t\t<label class=\"contacts-label\" for=\"criticalContacts\" translate>platform.alerts.severity.critical</label>\r\n\t\t\t<sk-tags-input-email class=\"sk-tags-input mb-3\" initialMode=\"write\" id=\"criticalContacts\"\r\n\t\t\t\tformControlName=\"criticalContacts\" placeholder=\"Add Recipients\"\r\n\t\t\t\tallowed-tags-pattern=\"^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$\"\r\n\t\t\t\tallowPastingMultipleEmails=\"true\"></sk-tags-input-email>\r\n\r\n\t\t\t<label class=\"contacts-label\" for=\"warningContacts\" translate>platform.alerts.severity.warning</label>\r\n\t\t\t<sk-tags-input-email class=\"sk-tags-input mb-3\" initialMode=\"write\" id=\"warningContacts\"\r\n\t\t\t\tformControlName=\"warningContacts\" placeholder=\"Add Recipients\"\r\n\t\t\t\tallowed-tags-pattern=\"^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$\"\r\n\t\t\t\tallowPastingMultipleEmails=\"true\"></sk-tags-input-email>\r\n\r\n\t\t\t<label class=\"contacts-label\" for=\"informationContacts\"\r\n\t\t\t\ttranslate>platform.alerts.severity.informational</label>\r\n\t\t\t<sk-tags-input-email class=\"sk-tags-input mb-3\" initialMode=\"write\" id=\"informationContacts\"\r\n\t\t\t\tformControlName=\"informationContacts\" placeholder=\"Add Recipients\"\r\n\t\t\t\tallowed-tags-pattern=\"^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$\"\r\n\t\t\t\tallowPastingMultipleEmails=\"true\"></sk-tags-input-email>\r\n\r\n\t\t\t<label class=\"contacts-label\" for=\"successContacts\" translate>platform.alerts.severity.success</label>\r\n\t\t\t<sk-tags-input-email class=\"sk-tags-input mb-3\" initialMode=\"write\" id=\"successContacts\"\r\n\t\t\t\tformControlName=\"successContacts\" placeholder=\"Add Recipients\"\r\n\t\t\t\tallowed-tags-pattern=\"^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$\"\r\n\t\t\t\tallowPastingMultipleEmails=\"true\"></sk-tags-input-email>\r\n\r\n\t\t\t<label class=\"contacts-label\" for=\"billingContacts\" translate>platform.alerts.severity.billing</label>\r\n\t\t\t<sk-tags-input-email class=\"sk-tags-input mb-3\" initialMode=\"write\" id=\"billingContacts\"\r\n\t\t\t\tformControlName=\"billingContacts\" placeholder=\"Add Recipients\"\r\n\t\t\t\tallowed-tags-pattern=\"^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$\"\r\n\t\t\t\tallowPastingMultipleEmails=\"true\"></sk-tags-input-email>\r\n\r\n\r\n\t\t\t<button type=\"submit\" sk-spinner-button [isSpinning]=\"savingPreferences\" [disabled]=\"savingPreferences\"\r\n\t\t\t\tcolor=\"primary\" class=\"btn-sm\">{{'platform.order_level_preferences.save_preferences' |\r\n\t\t\t\ttranslate}}</button>\r\n\t\t</form>\r\n\t</ng-container>\r\n\r\n\t<ng-template #loadingOrError>\r\n\t\t<sk-text-spinner *ngIf=\"!failedToLoadPreferences\" message=\"Fetching Order Preferences\"></sk-text-spinner>\r\n\t\t<ngb-alert *ngIf=\"failedToLoadPreferences\" [dismissible]=\"false\" [type]=\"'danger'\">\r\n\t\t\t<strong translate>platform.order_level_preferences.error_title</strong>\r\n\t\t\t{{'platform.order_level_preferences.error_message' | translate}}\r\n\t\t</ngb-alert>\r\n\t</ng-template>\r\n</div>", styles: [".contacts-label,.div1{font-weight:700;margin-bottom:0}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i1$1.NgbAlert, selector: "ngb-alert", inputs: ["animation", "dismissible", "type"], outputs: ["closed"], exportAs: ["ngbAlert"] }, { kind: "component", type: TextSpinnerComponent, selector: "sk-text-spinner", inputs: ["message", "size", "spinnerLocation", "loaderWrapperClasses"] }, { kind: "directive", type: i2$2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: TagsInputEmailComponent, selector: "sk-tags-input-email", inputs: ["allowPastingMultipleEmails"] }, { kind: "component", type: SpinnerButtonComponent, selector: "button[sk-spinner-button]", inputs: ["disabled", "color", "active", "isSpinning"] }, { kind: "component", type: HelpPopoverComponent, selector: "sk-help-popover", inputs: ["message", "direction", "openDelay"] }, { kind: "directive", type: i2$3.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }, { kind: "pipe", type: i2$3.TranslatePipe, name: "translate" }], animations: [
            trigger('fadeIn', [
                transition(':enter', [
                    style({ opacity: 0 }),
                    animate('1s ease-out', style({ opacity: 1 }))
                ]),
                transition(':leave', [
                    style({ opacity: 1 }),
                    animate('1s ease-in', style({ opacity: 0 }))
                ])
            ])
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsOrderLevelPreferencesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-notifications-order-level-preferences', animations: [
                        trigger('fadeIn', [
                            transition(':enter', [
                                style({ opacity: 0 }),
                                animate('1s ease-out', style({ opacity: 1 }))
                            ]),
                            transition(':leave', [
                                style({ opacity: 1 }),
                                animate('1s ease-in', style({ opacity: 0 }))
                            ])
                        ])
                    ], template: "<div>\r\n\r\n\t<div class=\"row align-items-center\">\r\n\t\t<div class=\"d-flex col-lg-12\">\r\n\r\n\t\t\t<h3 class=\"d-flex align-items-center mb-100 pr-50\" translate>platform.order_level_preferences.title</h3>\r\n\r\n\t\t\t<sk-help-popover direction=\"bottom\">\r\n\t\t\t\t{{'platform.order_level_preferences.help_popover' | translate}} <a\r\n\t\t\t\t\thref='https://support.skykick.com/hc/en-us/articles/1500002350602-SkyKick-platform-notifications-aka-alerts-'\r\n\t\t\t\t\ttranslate>platform.order_level_preferences.learn_more</a>\r\n\t\t\t</sk-help-popover>\r\n\r\n\r\n\t\t\t<div style=\"margin-left:auto;\" *ngIf=\"isAdmin\">\r\n\t\t\t\t<div id=\"go-to-all-notifications\" class=\"d-flex btn  m-r-1\">\r\n\t\t\t\t\t<a class=\"btn-link ng-binding\" href=\"/settings/notifications/account\"\r\n\t\t\t\t\t\ttranslate>platform.order_level_preferences.all_notification_settings</a>\r\n\t\t\t\t\t<i id=\"all-notifications-open-new-icon\" class=\"material-icons\" style=\"color:#056ED1\">open_in_new</i>\r\n\t\t\t\t</div>\r\n\t\t\t</div>\r\n\r\n\t\t</div>\r\n\t</div>\r\n\r\n\t<ng-container *ngIf=\"form && !failedToLoadPreferences; else loadingOrError\">\r\n\t\t<form class=\"form-group\" [formGroup]=\"form\" (ngSubmit)=\"onSubmit()\" (keydown.enter)=\"$event.preventDefault()\"\r\n\t\t\t[@fadeIn]>\r\n\r\n\t\t\t<label class=\"contacts-label\" for=\"criticalContacts\" translate>platform.alerts.severity.critical</label>\r\n\t\t\t<sk-tags-input-email class=\"sk-tags-input mb-3\" initialMode=\"write\" id=\"criticalContacts\"\r\n\t\t\t\tformControlName=\"criticalContacts\" placeholder=\"Add Recipients\"\r\n\t\t\t\tallowed-tags-pattern=\"^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$\"\r\n\t\t\t\tallowPastingMultipleEmails=\"true\"></sk-tags-input-email>\r\n\r\n\t\t\t<label class=\"contacts-label\" for=\"warningContacts\" translate>platform.alerts.severity.warning</label>\r\n\t\t\t<sk-tags-input-email class=\"sk-tags-input mb-3\" initialMode=\"write\" id=\"warningContacts\"\r\n\t\t\t\tformControlName=\"warningContacts\" placeholder=\"Add Recipients\"\r\n\t\t\t\tallowed-tags-pattern=\"^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$\"\r\n\t\t\t\tallowPastingMultipleEmails=\"true\"></sk-tags-input-email>\r\n\r\n\t\t\t<label class=\"contacts-label\" for=\"informationContacts\"\r\n\t\t\t\ttranslate>platform.alerts.severity.informational</label>\r\n\t\t\t<sk-tags-input-email class=\"sk-tags-input mb-3\" initialMode=\"write\" id=\"informationContacts\"\r\n\t\t\t\tformControlName=\"informationContacts\" placeholder=\"Add Recipients\"\r\n\t\t\t\tallowed-tags-pattern=\"^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$\"\r\n\t\t\t\tallowPastingMultipleEmails=\"true\"></sk-tags-input-email>\r\n\r\n\t\t\t<label class=\"contacts-label\" for=\"successContacts\" translate>platform.alerts.severity.success</label>\r\n\t\t\t<sk-tags-input-email class=\"sk-tags-input mb-3\" initialMode=\"write\" id=\"successContacts\"\r\n\t\t\t\tformControlName=\"successContacts\" placeholder=\"Add Recipients\"\r\n\t\t\t\tallowed-tags-pattern=\"^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$\"\r\n\t\t\t\tallowPastingMultipleEmails=\"true\"></sk-tags-input-email>\r\n\r\n\t\t\t<label class=\"contacts-label\" for=\"billingContacts\" translate>platform.alerts.severity.billing</label>\r\n\t\t\t<sk-tags-input-email class=\"sk-tags-input mb-3\" initialMode=\"write\" id=\"billingContacts\"\r\n\t\t\t\tformControlName=\"billingContacts\" placeholder=\"Add Recipients\"\r\n\t\t\t\tallowed-tags-pattern=\"^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$\"\r\n\t\t\t\tallowPastingMultipleEmails=\"true\"></sk-tags-input-email>\r\n\r\n\r\n\t\t\t<button type=\"submit\" sk-spinner-button [isSpinning]=\"savingPreferences\" [disabled]=\"savingPreferences\"\r\n\t\t\t\tcolor=\"primary\" class=\"btn-sm\">{{'platform.order_level_preferences.save_preferences' |\r\n\t\t\t\ttranslate}}</button>\r\n\t\t</form>\r\n\t</ng-container>\r\n\r\n\t<ng-template #loadingOrError>\r\n\t\t<sk-text-spinner *ngIf=\"!failedToLoadPreferences\" message=\"Fetching Order Preferences\"></sk-text-spinner>\r\n\t\t<ngb-alert *ngIf=\"failedToLoadPreferences\" [dismissible]=\"false\" [type]=\"'danger'\">\r\n\t\t\t<strong translate>platform.order_level_preferences.error_title</strong>\r\n\t\t\t{{'platform.order_level_preferences.error_message' | translate}}\r\n\t\t</ngb-alert>\r\n\t</ng-template>\r\n</div>", styles: [".contacts-label,.div1{font-weight:700;margin-bottom:0}\n"] }]
        }], ctorParameters: () => [{ type: NotificationResourcesService }, { type: i2$1.AbstractUserProvider }, { type: i5.ToastrService }], propDecorators: { orderId: [{
                type: Input
            }] } });

class NotificationsCustomerAlertsComponent {
    constructor() {
        this.NavigationLocation = NavigationLocation;
        this.returnToCustomersIfAlertCountZero = false;
    }
    onNavigateToAlert(alert) {
        this.selectedAlert = alert;
        this.notificationsNavigation.navigateToAlertDetailView(alert.subject);
    }
    applyAlertsFilter(alertsFilter) {
        this.alertsFilter = alertsFilter;
    }
    setAlertCount(count) {
        this.alertCount = count;
        if (count === 0 && this.returnToCustomersIfAlertCountZero) {
            this.notificationsNavigation.returnToCustomersView();
        }
        this.returnToCustomersIfAlertCountZero = false;
    }
    onAlertCompleted(alert) {
        this.notificationsNavigation.returnToCustomerOrdersView();
        // After completing an alert, we want return to the customers view if the current order
        // does not have anymore active alerts.
        this.returnToCustomersIfAlertCountZero = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsCustomerAlertsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsCustomerAlertsComponent, selector: "sk-notifications-customer-alerts", inputs: { notificationsNavigation: "notificationsNavigation" }, ngImport: i0, template: "<div class=\"sk-flex-table\">\r\n    <sk-notifications-customer-alerts-filter (alertsFilterOutput)=\"applyAlertsFilter($event)\" [alertCount]=\"alertCount\"\r\n        [notificationsNavigation]=\"notificationsNavigation\"\r\n        [hidden]=\"notificationsNavigation?.location != NavigationLocation.CustomerOrders\">\r\n    </sk-notifications-customer-alerts-filter>\r\n\r\n    <sk-notifications-customer-alerts-table\r\n        *ngIf=\"notificationsNavigation?.location == NavigationLocation.CustomerOrders\"\r\n        (navigateToAlert)=\"onNavigateToAlert($event)\" (setAlertCount)=\"setAlertCount($event)\"\r\n        [alertsFilter]=\"alertsFilter\" [notificationsNavigation]=\"notificationsNavigation\">\r\n    </sk-notifications-customer-alerts-table>\r\n\r\n    <sk-notifications-alert *ngIf=\"notificationsNavigation?.location == NavigationLocation.AlertDetail\"\r\n        (alertCompleted)=\"onAlertCompleted($event)\" [alert]=\"selectedAlert\">\r\n    </sk-notifications-alert>\r\n\r\n    <sk-notifications-order-level-preferences [orderId]=\"notificationsNavigation.customer.orderId\"\r\n        *ngIf=\"notificationsNavigation?.location == NavigationLocation.OrderLevelPreferences\">\r\n    </sk-notifications-order-level-preferences>\r\n</div>", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: NotificationsCustomerAlertsTableComponent, selector: "sk-notifications-customer-alerts-table", inputs: ["notificationsNavigation", "alertsFilter"], outputs: ["navigateToAlert", "setAlertCount"] }, { kind: "component", type: NotificationsAlertComponent, selector: "sk-notifications-alert", inputs: ["alert"], outputs: ["alertCompleted"] }, { kind: "component", type: NotificationsCustomerAlertsFilterComponent, selector: "sk-notifications-customer-alerts-filter", inputs: ["alertCount", "notificationsNavigation"], outputs: ["alertsFilterOutput"] }, { kind: "component", type: NotificationsOrderLevelPreferencesComponent, selector: "sk-notifications-order-level-preferences", inputs: ["orderId"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsCustomerAlertsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-notifications-customer-alerts', template: "<div class=\"sk-flex-table\">\r\n    <sk-notifications-customer-alerts-filter (alertsFilterOutput)=\"applyAlertsFilter($event)\" [alertCount]=\"alertCount\"\r\n        [notificationsNavigation]=\"notificationsNavigation\"\r\n        [hidden]=\"notificationsNavigation?.location != NavigationLocation.CustomerOrders\">\r\n    </sk-notifications-customer-alerts-filter>\r\n\r\n    <sk-notifications-customer-alerts-table\r\n        *ngIf=\"notificationsNavigation?.location == NavigationLocation.CustomerOrders\"\r\n        (navigateToAlert)=\"onNavigateToAlert($event)\" (setAlertCount)=\"setAlertCount($event)\"\r\n        [alertsFilter]=\"alertsFilter\" [notificationsNavigation]=\"notificationsNavigation\">\r\n    </sk-notifications-customer-alerts-table>\r\n\r\n    <sk-notifications-alert *ngIf=\"notificationsNavigation?.location == NavigationLocation.AlertDetail\"\r\n        (alertCompleted)=\"onAlertCompleted($event)\" [alert]=\"selectedAlert\">\r\n    </sk-notifications-alert>\r\n\r\n    <sk-notifications-order-level-preferences [orderId]=\"notificationsNavigation.customer.orderId\"\r\n        *ngIf=\"notificationsNavigation?.location == NavigationLocation.OrderLevelPreferences\">\r\n    </sk-notifications-order-level-preferences>\r\n</div>" }]
        }], ctorParameters: () => [], propDecorators: { notificationsNavigation: [{
                type: Input
            }] } });

class NotificationsComponent {
    constructor(activeModal, cacheService) {
        this.activeModal = activeModal;
        this.cacheService = cacheService;
        this.NavigationLocation = NavigationLocation;
    }
    static { this.overlayConfig = { windowClass: 'modal-panel modal-right', size: 'xl' }; }
    ngOnInit() {
        // Clear the filter out of storage when the notifications panel is opened/closed.
        this.cacheService.root().remove(ORDER_FILTER_STORAGE_KEY);
        if (typeof this.initializationConfig === 'undefined') {
            this.notificationsNavigation = new NotificationsNavigation(NavigationLocation.Customers);
        }
        else {
            this.handleInitializationWithConfig(this.initializationConfig);
        }
    }
    handleInitializationWithConfig(config) {
        if (typeof config.OrderFilter !== 'undefined') {
            this.notificationsNavigation = new NotificationsNavigation(NavigationLocation.Customers);
        }
        else if (config.CustomerId) {
            this.notificationsNavigation = new NotificationsNavigation(NavigationLocation.FetchingNavigationData);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsComponent, deps: [{ token: i1$1.NgbActiveModal }, { token: i4.CacheService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsComponent, selector: "[sk-notifications]", inputs: { filter: "filter", initializationConfig: "initializationConfig" }, host: { classAttribute: "sk-content-pane" }, ngImport: i0, template: "<sk-notifications-navigation (dismiss)=\"activeModal.dismiss($event)\"\r\n    [notificationsNavigation]=\"notificationsNavigation\">\r\n</sk-notifications-navigation>\r\n\r\n<div class=\"modal-body\">\r\n    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.FetchingNavigationData\">\r\n        <sk-text-spinner message=\"Fetching Order Alerts\"></sk-text-spinner>\r\n    </div>\r\n    <sk-notifications-customers-table\r\n        *ngIf=\"notificationsNavigation.location == NavigationLocation.Customers; else customerAlerts\"\r\n        [notificationsNavigation]=\"notificationsNavigation\" >\r\n    </sk-notifications-customers-table>\r\n    <ng-template #customerAlerts>\r\n        <sk-notifications-customer-alerts\r\n            *ngIf=\"notificationsNavigation.location != NavigationLocation.FetchingNavigationData\"\r\n            [notificationsNavigation]=\"notificationsNavigation\" >\r\n        </sk-notifications-customer-alerts>\r\n    </ng-template>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TextSpinnerComponent, selector: "sk-text-spinner", inputs: ["message", "size", "spinnerLocation", "loaderWrapperClasses"] }, { kind: "component", type: NotificationsNavigationComponent, selector: "sk-notifications-navigation", inputs: ["notificationsNavigation"], outputs: ["dismiss"] }, { kind: "component", type: NotificationsCustomersTableComponent, selector: "sk-notifications-customers-table", inputs: ["notificationsNavigation", "filter"] }, { kind: "component", type: NotificationsCustomerAlertsComponent, selector: "sk-notifications-customer-alerts", inputs: ["notificationsNavigation"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-notifications]', host: { class: 'sk-content-pane' }, template: "<sk-notifications-navigation (dismiss)=\"activeModal.dismiss($event)\"\r\n    [notificationsNavigation]=\"notificationsNavigation\">\r\n</sk-notifications-navigation>\r\n\r\n<div class=\"modal-body\">\r\n    <div *ngIf=\"notificationsNavigation.location == NavigationLocation.FetchingNavigationData\">\r\n        <sk-text-spinner message=\"Fetching Order Alerts\"></sk-text-spinner>\r\n    </div>\r\n    <sk-notifications-customers-table\r\n        *ngIf=\"notificationsNavigation.location == NavigationLocation.Customers; else customerAlerts\"\r\n        [notificationsNavigation]=\"notificationsNavigation\" >\r\n    </sk-notifications-customers-table>\r\n    <ng-template #customerAlerts>\r\n        <sk-notifications-customer-alerts\r\n            *ngIf=\"notificationsNavigation.location != NavigationLocation.FetchingNavigationData\"\r\n            [notificationsNavigation]=\"notificationsNavigation\" >\r\n        </sk-notifications-customer-alerts>\r\n    </ng-template>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i1$1.NgbActiveModal }, { type: i4.CacheService }], propDecorators: { filter: [{
                type: Input
            }], initializationConfig: [{
                type: Input
            }] } });

class NotificationsBellComponent {
    constructor(cgbTooltipConfig, notificationResourcesService, notificationMethodsService, modalService, userProductTypesService) {
        this.notificationResourcesService = notificationResourcesService;
        this.notificationMethodsService = notificationMethodsService;
        this.modalService = modalService;
        this.onDestroy$ = new Subject();
        cgbTooltipConfig.openDelay = 500;
        cgbTooltipConfig.closeDelay = 100;
        this.availableProductTypes = userProductTypesService.getAvailableProductTypes();
    }
    ngOnInit() {
        const filter = this.notificationMethodsService.getOrderQueryForAlertCount();
        this.notificationsQuery$ = this.notificationResourcesService.notificationsQuery(this.notificationMethodsService.buildQueryForCustomerAlertSummary(filter));
    }
    ngOnDestroy() {
        this.onDestroy$.next();
    }
    openNotifications() {
        this.modalService.open(NotificationsComponent, NotificationsComponent.overlayConfig);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsBellComponent, deps: [{ token: i1$1.NgbTooltipConfig }, { token: NotificationResourcesService }, { token: NotificationMethodsService }, { token: i1$1.NgbModal }, { token: UserProductTypesService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotificationsBellComponent, selector: "sk-notifications-bell", providers: [NgbTooltipConfig], ngImport: i0, template: "<ng-container *ngIf=\"(notificationsQuery$ | async) as notificationsQuery\">\r\n    <div *ngIf=\"notificationsQuery\" class=\"sk-notifications\"\r\n    [class.mr-100]=\"notificationsQuery.customers.length === 0 || notificationsQuery.totalCustomers < 10000\"\r\n    [class.mr-200]=\"notificationsQuery.customers.length > 0 && notificationsQuery.totalCustomers >= 10000\">\r\n        <div ngbTooltip=\"Notifications\">\r\n            <div class=\"notifications-bell\">\r\n                <button\r\n                    id=\"alertPanelButton\"\r\n                    type=\"button\"\r\n                    class=\"btn sk-btn-icon btn-toggle badge-overlap border-0\"\r\n                    (click)=\"openNotifications()\"\r\n                    [disabled]=\"availableProductTypes.length === 0\">\r\n                    <i class=\"material-icons\">notifications_none</i>\r\n                </button>\r\n                <span id=\"alertPanelCount\" *ngIf=\"notificationsQuery.customers.length > 0\" class=\"badge rounded-pill badge-danger-bold badge-sm\">{{notificationsQuery.totalCustomers}}</span>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</ng-container>\r\n", styles: ["#alertPanelCount{z-index:200}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "popperOptions", "triggers", "positionTarget", "container", "disableTooltip", "tooltipClass", "tooltipContext", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsBellComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-notifications-bell', providers: [NgbTooltipConfig], template: "<ng-container *ngIf=\"(notificationsQuery$ | async) as notificationsQuery\">\r\n    <div *ngIf=\"notificationsQuery\" class=\"sk-notifications\"\r\n    [class.mr-100]=\"notificationsQuery.customers.length === 0 || notificationsQuery.totalCustomers < 10000\"\r\n    [class.mr-200]=\"notificationsQuery.customers.length > 0 && notificationsQuery.totalCustomers >= 10000\">\r\n        <div ngbTooltip=\"Notifications\">\r\n            <div class=\"notifications-bell\">\r\n                <button\r\n                    id=\"alertPanelButton\"\r\n                    type=\"button\"\r\n                    class=\"btn sk-btn-icon btn-toggle badge-overlap border-0\"\r\n                    (click)=\"openNotifications()\"\r\n                    [disabled]=\"availableProductTypes.length === 0\">\r\n                    <i class=\"material-icons\">notifications_none</i>\r\n                </button>\r\n                <span id=\"alertPanelCount\" *ngIf=\"notificationsQuery.customers.length > 0\" class=\"badge rounded-pill badge-danger-bold badge-sm\">{{notificationsQuery.totalCustomers}}</span>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</ng-container>\r\n", styles: ["#alertPanelCount{z-index:200}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.NgbTooltipConfig }, { type: NotificationResourcesService }, { type: NotificationMethodsService }, { type: i1$1.NgbModal }, { type: UserProductTypesService }] });

class ToastsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ToastsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: ToastsModule, imports: [i5.ToastrModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ToastsModule, imports: [ToastrModule.forRoot({ timeOut: 7000, disableTimeOut: "extendedTimeOut", preventDuplicates: true })] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ToastsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        ToastrModule.forRoot({ timeOut: 7000, disableTimeOut: "extendedTimeOut", preventDuplicates: true }),
                    ],
                }]
        }] });

class NotificationsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: NotificationsModule, declarations: [NotificationsBellComponent,
            NotificationsComponent,
            NotificationsFilterPipe,
            HighlightDirective,
            NotificationsNavigationComponent,
            NotificationsCustomersTableComponent,
            NotificationsCustomerAlertsTableComponent,
            NotificationsCustomerAlertsComponent,
            NotificationsAlertComponent,
            NotificationsCustomerAlertsFilterComponent,
            NotificationsOrderLevelPreferencesComponent,
            NotificationsAlertDetailsComponent,
            NotificationsSortableHeaderDirective,
            NotificationsSearchComponent], imports: [CommonModule,
            NgbTooltipModule,
            NgbDropdownModule,
            NgbAlertModule,
            NgbDatepickerModule,
            NgbModalModule,
            SortingModule,
            StatusSpinnerModule,
            FormsModule$1,
            ReactiveFormsModule,
            InputModule,
            ButtonsModule,
            PopoverModule,
            NgbModule,
            ToastsModule, i2$3.TranslateModule, InfiniteScrollModule], exports: [NotificationsBellComponent,
            NotificationsComponent,
            NotificationsFilterPipe,
            NotificationsSortableHeaderDirective,
            NotificationsSearchComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsModule, imports: [CommonModule,
            NgbTooltipModule,
            NgbDropdownModule,
            NgbAlertModule,
            NgbDatepickerModule,
            NgbModalModule,
            SortingModule,
            StatusSpinnerModule,
            FormsModule$1,
            ReactiveFormsModule,
            InputModule,
            ButtonsModule,
            PopoverModule,
            NgbModule,
            ToastsModule,
            TranslateModule.forChild(),
            InfiniteScrollModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        NotificationsBellComponent,
                        NotificationsComponent,
                        NotificationsFilterPipe,
                        HighlightDirective,
                        NotificationsNavigationComponent,
                        NotificationsCustomersTableComponent,
                        NotificationsCustomerAlertsTableComponent,
                        NotificationsCustomerAlertsComponent,
                        NotificationsAlertComponent,
                        NotificationsCustomerAlertsFilterComponent,
                        NotificationsOrderLevelPreferencesComponent,
                        NotificationsAlertDetailsComponent,
                        NotificationsSortableHeaderDirective,
                        NotificationsSearchComponent
                    ],
                    imports: [
                        CommonModule,
                        NgbTooltipModule,
                        NgbDropdownModule,
                        NgbAlertModule,
                        NgbDatepickerModule,
                        NgbModalModule,
                        SortingModule,
                        StatusSpinnerModule,
                        FormsModule$1,
                        ReactiveFormsModule,
                        InputModule,
                        ButtonsModule,
                        PopoverModule,
                        NgbModule,
                        ToastsModule,
                        TranslateModule.forChild(),
                        InfiniteScrollModule
                    ],
                    exports: [
                        NotificationsBellComponent,
                        NotificationsComponent,
                        NotificationsFilterPipe,
                        NotificationsSortableHeaderDirective,
                        NotificationsSearchComponent
                    ]
                }]
        }] });

class BrandingLogoComponent {
    constructor(userProvider, routeResolverService) {
        this.routeResolverService = routeResolverService;
        this.logoUploadUrl = this.routeResolverService.generateRatRoute(AppId.Portal, 'settings/account#uploadImage');
        this.user = userProvider.getCurrentUser();
    }
    /**
     * Only portal administrators can upload a new logo
     */
    canUploadLogo() {
        return this.user.hasPermission('admin_editall');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BrandingLogoComponent, deps: [{ token: i2$1.AbstractUserProvider }, { token: RouteResolverService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: BrandingLogoComponent, selector: "sk-branding-logo", inputs: { brandLogo: ["url", "brandLogo"] }, ngImport: i0, template: "<img *ngIf=\"brandLogo\" class=\"partner-logo\" [src]=\"brandLogo\">\r\n<a *ngIf=\"!brandLogo\" [href]=\"logoUploadUrl\" class=\"btn btn-outline-secondary sk-btn-combo m-t-1\" [class.disabled]=\"!canUploadLogo()\">\r\n    <i class=\"material-icons md-22 icon-muted\">add_circle_outline</i>\r\n    <span class=\"text-muted align-top\">Upload Your Logo</span>\r\n</a>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BrandingLogoComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-branding-logo', template: "<img *ngIf=\"brandLogo\" class=\"partner-logo\" [src]=\"brandLogo\">\r\n<a *ngIf=\"!brandLogo\" [href]=\"logoUploadUrl\" class=\"btn btn-outline-secondary sk-btn-combo m-t-1\" [class.disabled]=\"!canUploadLogo()\">\r\n    <i class=\"material-icons md-22 icon-muted\">add_circle_outline</i>\r\n    <span class=\"text-muted align-top\">Upload Your Logo</span>\r\n</a>\r\n" }]
        }], ctorParameters: () => [{ type: i2$1.AbstractUserProvider }, { type: RouteResolverService }], propDecorators: { brandLogo: [{
                type: Input,
                args: ['url']
            }] } });

class ProfilePictureComponent {
    constructor() { }
    ngOnInit() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ProfilePictureComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ProfilePictureComponent, selector: "sk-profile-picture", inputs: { profilePictureUrl: ["url", "profilePictureUrl"] }, ngImport: i0, template: "<div class=\"sk-user-avatar\">\r\n  <img [src]=\"profilePictureUrl\">\r\n</div>", styles: [":host{cursor:pointer}:host:hover{opacity:85%}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ProfilePictureComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-profile-picture', template: "<div class=\"sk-user-avatar\">\r\n  <img [src]=\"profilePictureUrl\">\r\n</div>", styles: [":host{cursor:pointer}:host:hover{opacity:85%}\n"] }]
        }], ctorParameters: () => [], propDecorators: { profilePictureUrl: [{
                type: Input,
                args: ['url']
            }] } });

class PartnerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PartnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: PartnerModule, declarations: [BrandingLogoComponent,
            ProfilePictureComponent], imports: [CommonModule], exports: [BrandingLogoComponent,
            ProfilePictureComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PartnerModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PartnerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        BrandingLogoComponent,
                        ProfilePictureComponent
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        BrandingLogoComponent,
                        ProfilePictureComponent
                    ]
                }]
        }] });

class PartnerService {
    constructor(http, routeResolverService, apiConfigService) {
        this.http = http;
        this.routeResolverService = routeResolverService;
        this.apiConfigService = apiConfigService;
    }
    getPartnerLogo() {
        const defaultUrl = this.routeResolverService.generatePipeRoute(AppId.Papi, 'partner/logo');
        return this.apiConfigService.getEndpointUrl(ApiEndpointIdentifier.PartnerLogo, defaultUrl).pipe(switchMap(url => this.http.get(url)));
    }
    getPIN() {
        return this.http.get(this.routeResolverService.generatePipeRoute(AppId.Papi, 'common/pin'));
    }
    sendLicenseRequest(product, reason) {
        return this.http
            .post(this.routeResolverService.generatePipeRoute(AppId.Papi, 'usermanagement/request-license'), {
            product,
            reason
        }, {
            withCredentials: true
        });
    }
    sendMfaRequest(product) {
        return this.http
            .post(this.routeResolverService.generatePipeRoute(AppId.Papi, 'usermanagement/request-mfa-activation'), {
            product
        }, {
            withCredentials: true
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PartnerService, deps: [{ token: i1$2.HttpClient }, { token: RouteResolverService }, { token: ApiConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PartnerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PartnerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.HttpClient }, { type: RouteResolverService }, { type: ApiConfigService }] });

class BrandedServices {
    constructor(http, routeResolverService, userProvider, apiConfigService) {
        this.http = http;
        this.routeResolverService = routeResolverService;
        this.userProvider = userProvider;
        this.apiConfigService = apiConfigService;
    }
    getPartnerWhiteLabelSettings() {
        const defaultUrl = this.routeResolverService.generatePipeRoute(AppId.Papi, `whitelabel/${this.userProvider.getCurrentUser().partnerId}`);
        return this.apiConfigService.getEndpointUrl(ApiEndpointIdentifier.PartnerWhiteLabelSettings, defaultUrl).pipe(switchMap(url => this.http.get(url)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BrandedServices, deps: [{ token: i1$2.HttpClient }, { token: RouteResolverService }, { token: i2$1.AbstractUserProvider }, { token: ApiConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BrandedServices, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BrandedServices, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.HttpClient }, { type: RouteResolverService }, { type: i2$1.AbstractUserProvider }, { type: ApiConfigService }] });

class HeaderComponent {
    constructor(partnerService, userProvider, impersonationProvider, routeResolverService, brandedServices) {
        this.partnerService = partnerService;
        this.userProvider = userProvider;
        this.impersonationProvider = impersonationProvider;
        this.routeResolverService = routeResolverService;
        this.brandedServices = brandedServices;
        this.showCommunityLink = false;
        this.hostClass = true;
        this.profileUrl = this.routeResolverService.generateRatRoute(AppId.Portal, 'settings/profile');
        this.logoutUrl = this.routeResolverService.generateRatRoute(AppId.Portal, 'logout');
        this.retrievedPin = false;
        this.loadingLogo = true;
        this.user = this.userProvider.getCurrentUser();
        this.logoUrl = this.getPartnerLogoUrl();
        this.whiteLabelSettings$ = this.getWhiteLabelSettings();
    }
    getWhiteLabelSettings() {
        return this.brandedServices.getPartnerWhiteLabelSettings().pipe(tap(settings => {
            if (settings) {
                if (settings.faviconUrl) {
                    this.changeFavicon(settings.faviconUrl);
                }
                if (settings.pageTitle) {
                    this.changePageTitle(settings.pageTitle);
                }
            }
        }), catchError((error) => {
            return throwError(error);
        }));
    }
    getPartnerLogoUrl() {
        return this.partnerService.getPartnerLogo().pipe(map(res => res.url), tap(_ => this.loadingLogo = false), catchError((error) => {
            this.loadingLogo = false;
            return throwError(error);
        }));
    }
    getPin() {
        if (this.retrievedPin) {
            return;
        }
        this.retrievedPin = true;
        this.PIN$ = this.partnerService.getPIN().pipe(map(res => res.code));
    }
    getProfilePicture() {
        return this.user.picture;
    }
    getFullName() {
        return this.user.fullName;
    }
    getEmail() {
        return this.user.email;
    }
    isImpersonating() {
        return this.impersonationProvider.getImpersonationDetails(this.user).isImpersonation;
    }
    isCustomer() {
        return this.user.isCustomer;
    }
    changePageTitle(pageTitle) {
        if (pageTitle && pageTitle != '') {
            document.title = pageTitle;
        }
    }
    changeFavicon(faviconSrc) {
        var link = document.createElement('link'), oldLink = document.getElementById('dynamic-favicon');
        link.id = 'dynamic-favicon';
        link.rel = 'shortcut icon';
        link.href = faviconSrc;
        if (oldLink && document) {
            if (document.head != null) {
                document.head.removeChild(oldLink);
            }
        }
        if (document && document.head != null) {
            document.head.appendChild(link);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HeaderComponent, deps: [{ token: PartnerService }, { token: i2$1.AbstractUserProvider }, { token: i2$1.ISkyKickPartnerPortalUserImpersonationDetailsProvider }, { token: RouteResolverService }, { token: BrandedServices }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: HeaderComponent, selector: "header[sk-header]", inputs: { showCommunityLink: "showCommunityLink" }, host: { properties: { "class.sk-portal-header": "this.hostClass" } }, ngImport: i0, template: "<sk-branding-logo [class.hidden]=\"loadingLogo\" class=\"sk-partner-brand\" [url]=\"logoUrl | async\"></sk-branding-logo>\r\n<!-- Empty div that takes space when logo is loading to keep other elements on the right side-->\r\n<div [class.hidden]=\"!loadingLogo\" class=\"d-flex\"></div>\r\n\r\n<div class=\"sk-account-nav\">\r\n\r\n  <sk-notifications-bell *ngIf=\"!isCustomer()\"></sk-notifications-bell>\r\n\r\n  <div *ngIf=\"isCustomer()\" class=\"d-inline mr-100\">\r\n    <ng-container *ngIf=\"whiteLabelSettings$ | async as settings\">\r\n      <ng-container *ngIf=\"settings?.customerSupportDetails\">\r\n        <a *ngIf=\"settings.customerSupportDetails?.url\" tabindex=\"0\" target=\"_blank\" href=\"{{settings.customerSupportDetails?.url}}\" class=\"m-r-1 font-weight-bold\" translate>sk_header.customer_support</a>\r\n\r\n        <ng-template #popCustomerSupport>\r\n          <h6 *ngIf=\"settings.companyName\" class=\"m-b-1\">{{ settings.companyName }}</h6>\r\n          <div *ngIf=\"settings.customerSupportDetails?.hours\">{{ settings.customerSupportDetails.hours }}</div>\r\n          <a *ngIf=\"settings.customerSupportDetails?.email\" href=\"mailto:{{settings.customerSupportDetails.email}}\">\r\n            {{ settings.customerSupportDetails.email }}\r\n          </a>\r\n          <div *ngIf=\"settings.customerSupportDetails?.phone\">{{ settings.customerSupportDetails.phone }}</div>\r\n        </ng-template>\r\n        <a *ngIf=\"!settings.customerSupportDetails?.url\" class=\"m-r-1 font-weight-bold\" tabindex=\"0\" [ngbPopover]=\"popCustomerSupport\" popoverClass=\"sk-popover\" sk-popover-html sk-popover-trigger=\"manual\" sk-popover-md sk-popover-direction=\"bottom\" translate>sk_header.customer_support</a>\r\n      </ng-container>\r\n    </ng-container>\r\n  </div>\r\n\r\n  <div *ngIf=\"!isCustomer()\" class=\"sk-help-global mr-100\">\r\n    <div class=\"btn-group\" ngbDropdown>\r\n        <button type=\"button\" class=\"btn sk-btn-icon btn-toggle dropdown-toggle caret-hidden\" ngbDropdownToggle ngbTooltip=\"Help\">\r\n            <i class=\"material-icons md-22\">help</i>\r\n        </button>\r\n        <div class=\"dropdown-menu dropdown-menu-end\" ngbDropdownMenu>\r\n            <a class=\"dropdown-item\" href=\"https://support.skykick.com\" target=\"_blank\">Help Center <i\r\n                class=\"material-icons md-16 icon-muted pl-50\">open_in_new</i></a>\r\n            <a class=\"dropdown-item\" href=\"https://status.skykick.com\" target=\"_blank\">System Status <i\r\n                class=\"material-icons md-16 icon-muted pl-50\">open_in_new</i></a>\r\n            <a class=\"dropdown-item\" href=\"https://support.skykick.com/hc/en-us/articles/1500001761902\" target=\"_blank\">Contact Support <i\r\n                class=\"material-icons md-16 icon-muted pl-50\">open_in_new</i></a>\r\n            <a class=\"dropdown-item\" href=\"https://support.skykick.com/hc/en-us/community/topics\" target=\"_blank\"\r\n                *ngIf=\"showCommunityLink\">Community Forum <i\r\n                class=\"material-icons md-16 icon-muted pl-50\">open_in_new</i></a>\r\n        </div>\r\n    </div>\r\n  </div>\r\n\r\n  <div class=\"sk-user-account\">\r\n    <div ngbDropdown>\r\n      <sk-profile-picture class=\"sk-dropdown-custom d-flex\" (click)=\"getPin()\" ngbDropdownToggle [url]=\"getProfilePicture()\"></sk-profile-picture>\r\n\r\n      <div ngbDropdownMenu>\r\n        <span class=\"dropdown-header sk-header-flush\">\r\n          <legend>{{ getFullName() }}</legend>\r\n          <small class=\"d-block pb-3\">{{ getEmail() }}</small>\r\n          <small *ngIf=\"!isCustomer()\" class=\"d-block pb-3\">\r\n            <strong>PIN</strong>\r\n            <!-- Pin unavailable when impersonating -->\r\n            <span class=\"mx-1\" *ngIf=\"isImpersonating(); else notImpersonating\">N/A - Impersonating</span>\r\n\r\n            <!-- Retrieve and Display Pin -->\r\n            <ng-template #notImpersonating>\r\n              <span class=\"mx-1\" *ngIf=\"PIN$ | async as PIN; else pinloading\">{{ PIN }}</span>\r\n            </ng-template>\r\n\r\n            <!-- Loading spinner while retrieving Pin -->\r\n            <ng-template #pinloading>\r\n              <div class=\"mx-1 spinner-grow spinner-grow-sm bg-primary\" role=\"status\">\r\n                <span class=\"visually-hidden\">Loading...</span>\r\n              </div>\r\n            </ng-template>\r\n          </small>\r\n        </span>\r\n        <a ngbDropdownItem *ngIf=\"!isCustomer()\" [href]=\"profileUrl\" translate>sk_header.settings</a>\r\n        <a ngbDropdownItem *ngIf=\"!isCustomer()\" href=\"https://support.skykick.com\">Help</a>\r\n        <div class=\"dropdown-divider\"></div>\r\n        <a ngbDropdownItem [href]=\"logoutUrl\">Log Out</a></div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [".hidden{display:none!important}.sk-user-account{margin-left:7px}.sk-user-account .sk-dropdown-custom.dropdown-toggle:after{display:none}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i1$1.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i1$1.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "directive", type: i1$1.NgbDropdownItem, selector: "[ngbDropdownItem]", inputs: ["tabindex", "disabled"] }, { kind: "directive", type: i1$1.NgbPopover, selector: "[ngbPopover]", inputs: ["animation", "autoClose", "ngbPopover", "popoverTitle", "placement", "popperOptions", "triggers", "positionTarget", "container", "disablePopover", "popoverClass", "popoverContext", "openDelay", "closeDelay"], outputs: ["shown", "hidden"], exportAs: ["ngbPopover"] }, { kind: "directive", type: i1$1.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "popperOptions", "triggers", "positionTarget", "container", "disableTooltip", "tooltipClass", "tooltipContext", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "component", type: BrandingLogoComponent, selector: "sk-branding-logo", inputs: ["url"] }, { kind: "component", type: ProfilePictureComponent, selector: "sk-profile-picture", inputs: ["url"] }, { kind: "component", type: NotificationsBellComponent, selector: "sk-notifications-bell" }, { kind: "directive", type: i2$3.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'header[sk-header]', template: "<sk-branding-logo [class.hidden]=\"loadingLogo\" class=\"sk-partner-brand\" [url]=\"logoUrl | async\"></sk-branding-logo>\r\n<!-- Empty div that takes space when logo is loading to keep other elements on the right side-->\r\n<div [class.hidden]=\"!loadingLogo\" class=\"d-flex\"></div>\r\n\r\n<div class=\"sk-account-nav\">\r\n\r\n  <sk-notifications-bell *ngIf=\"!isCustomer()\"></sk-notifications-bell>\r\n\r\n  <div *ngIf=\"isCustomer()\" class=\"d-inline mr-100\">\r\n    <ng-container *ngIf=\"whiteLabelSettings$ | async as settings\">\r\n      <ng-container *ngIf=\"settings?.customerSupportDetails\">\r\n        <a *ngIf=\"settings.customerSupportDetails?.url\" tabindex=\"0\" target=\"_blank\" href=\"{{settings.customerSupportDetails?.url}}\" class=\"m-r-1 font-weight-bold\" translate>sk_header.customer_support</a>\r\n\r\n        <ng-template #popCustomerSupport>\r\n          <h6 *ngIf=\"settings.companyName\" class=\"m-b-1\">{{ settings.companyName }}</h6>\r\n          <div *ngIf=\"settings.customerSupportDetails?.hours\">{{ settings.customerSupportDetails.hours }}</div>\r\n          <a *ngIf=\"settings.customerSupportDetails?.email\" href=\"mailto:{{settings.customerSupportDetails.email}}\">\r\n            {{ settings.customerSupportDetails.email }}\r\n          </a>\r\n          <div *ngIf=\"settings.customerSupportDetails?.phone\">{{ settings.customerSupportDetails.phone }}</div>\r\n        </ng-template>\r\n        <a *ngIf=\"!settings.customerSupportDetails?.url\" class=\"m-r-1 font-weight-bold\" tabindex=\"0\" [ngbPopover]=\"popCustomerSupport\" popoverClass=\"sk-popover\" sk-popover-html sk-popover-trigger=\"manual\" sk-popover-md sk-popover-direction=\"bottom\" translate>sk_header.customer_support</a>\r\n      </ng-container>\r\n    </ng-container>\r\n  </div>\r\n\r\n  <div *ngIf=\"!isCustomer()\" class=\"sk-help-global mr-100\">\r\n    <div class=\"btn-group\" ngbDropdown>\r\n        <button type=\"button\" class=\"btn sk-btn-icon btn-toggle dropdown-toggle caret-hidden\" ngbDropdownToggle ngbTooltip=\"Help\">\r\n            <i class=\"material-icons md-22\">help</i>\r\n        </button>\r\n        <div class=\"dropdown-menu dropdown-menu-end\" ngbDropdownMenu>\r\n            <a class=\"dropdown-item\" href=\"https://support.skykick.com\" target=\"_blank\">Help Center <i\r\n                class=\"material-icons md-16 icon-muted pl-50\">open_in_new</i></a>\r\n            <a class=\"dropdown-item\" href=\"https://status.skykick.com\" target=\"_blank\">System Status <i\r\n                class=\"material-icons md-16 icon-muted pl-50\">open_in_new</i></a>\r\n            <a class=\"dropdown-item\" href=\"https://support.skykick.com/hc/en-us/articles/1500001761902\" target=\"_blank\">Contact Support <i\r\n                class=\"material-icons md-16 icon-muted pl-50\">open_in_new</i></a>\r\n            <a class=\"dropdown-item\" href=\"https://support.skykick.com/hc/en-us/community/topics\" target=\"_blank\"\r\n                *ngIf=\"showCommunityLink\">Community Forum <i\r\n                class=\"material-icons md-16 icon-muted pl-50\">open_in_new</i></a>\r\n        </div>\r\n    </div>\r\n  </div>\r\n\r\n  <div class=\"sk-user-account\">\r\n    <div ngbDropdown>\r\n      <sk-profile-picture class=\"sk-dropdown-custom d-flex\" (click)=\"getPin()\" ngbDropdownToggle [url]=\"getProfilePicture()\"></sk-profile-picture>\r\n\r\n      <div ngbDropdownMenu>\r\n        <span class=\"dropdown-header sk-header-flush\">\r\n          <legend>{{ getFullName() }}</legend>\r\n          <small class=\"d-block pb-3\">{{ getEmail() }}</small>\r\n          <small *ngIf=\"!isCustomer()\" class=\"d-block pb-3\">\r\n            <strong>PIN</strong>\r\n            <!-- Pin unavailable when impersonating -->\r\n            <span class=\"mx-1\" *ngIf=\"isImpersonating(); else notImpersonating\">N/A - Impersonating</span>\r\n\r\n            <!-- Retrieve and Display Pin -->\r\n            <ng-template #notImpersonating>\r\n              <span class=\"mx-1\" *ngIf=\"PIN$ | async as PIN; else pinloading\">{{ PIN }}</span>\r\n            </ng-template>\r\n\r\n            <!-- Loading spinner while retrieving Pin -->\r\n            <ng-template #pinloading>\r\n              <div class=\"mx-1 spinner-grow spinner-grow-sm bg-primary\" role=\"status\">\r\n                <span class=\"visually-hidden\">Loading...</span>\r\n              </div>\r\n            </ng-template>\r\n          </small>\r\n        </span>\r\n        <a ngbDropdownItem *ngIf=\"!isCustomer()\" [href]=\"profileUrl\" translate>sk_header.settings</a>\r\n        <a ngbDropdownItem *ngIf=\"!isCustomer()\" href=\"https://support.skykick.com\">Help</a>\r\n        <div class=\"dropdown-divider\"></div>\r\n        <a ngbDropdownItem [href]=\"logoutUrl\">Log Out</a></div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [".hidden{display:none!important}.sk-user-account{margin-left:7px}.sk-user-account .sk-dropdown-custom.dropdown-toggle:after{display:none}\n"] }]
        }], ctorParameters: () => [{ type: PartnerService }, { type: i2$1.AbstractUserProvider }, { type: i2$1.ISkyKickPartnerPortalUserImpersonationDetailsProvider }, { type: RouteResolverService }, { type: BrandedServices }], propDecorators: { showCommunityLink: [{
                type: Input
            }], hostClass: [{
                type: HostBinding,
                args: ['class.sk-portal-header']
            }] } });

class HeaderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: HeaderModule, declarations: [HeaderComponent], imports: [CommonModule,
            NgbModule,
            PartnerModule,
            NotificationsModule, i2$3.TranslateModule], exports: [HeaderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HeaderModule, imports: [CommonModule,
            NgbModule,
            PartnerModule,
            NotificationsModule,
            TranslateModule.forChild()] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        HeaderComponent
                    ],
                    imports: [
                        CommonModule,
                        NgbModule,
                        PartnerModule,
                        NotificationsModule,
                        TranslateModule.forChild()
                    ],
                    exports: [
                        HeaderComponent
                    ]
                }]
        }] });

class BannerService {
    constructor(http, routeResolverService, apiConfigService) {
        this.http = http;
        this.routeResolverService = routeResolverService;
        this.apiConfigService = apiConfigService;
        this.obs$ = null;
    }
    getBannerContent() {
        return of([]);
    }
    /**
     * @deprecated This method is deprecated because it's using PAPI API that pulls data from unsupported source (WordPress - skykick.wpengine.com)
     */
    getBannerContentFromWordPressViaPapi() {
        if (!this.obs$) {
            const defaultUrl = this.routeResolverService.generatePipeRoute(AppId.Papi, 'banners');
            this.obs$ = this.apiConfigService.getEndpointUrl(ApiEndpointIdentifier.Banners, defaultUrl)
                .pipe(switchMap(url => this.http.get(url)), map(banners => banners ? banners.map(banner => {
                if (!banner.uid) {
                    // Ensure each banner has a uid, creating one if necessary
                    banner.uid = btoa(unescape(encodeURIComponent(banner.message)));
                }
                return banner;
            }) : []), shareReplay(1) // Ensure that this observable is hot and shares the result among subscribers
            );
        }
        return this.obs$;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BannerService, deps: [{ token: i1$2.HttpClient }, { token: RouteResolverService }, { token: ApiConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BannerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BannerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.HttpClient }, { type: RouteResolverService }, { type: ApiConfigService }] });

class BannerContainerComponent {
    constructor(bannerService, userProvider, impersonationProvider, cacheService) {
        this.bannerService = bannerService;
        this.userProvider = userProvider;
        this.impersonationProvider = impersonationProvider;
        this.closedBanners = [];
        this.enabledBanners = new ReplaySubject();
        this.cache = cacheService.root().withNamespace('banners');
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    ngOnInit() {
        this.closedBanners = this.getClosedBanners();
        this.user = this.userProvider.getCurrentUser();
        this.subscription = this.bannerService
            .getBannerContent()
            .pipe(map(banners => banners.filter(ban => ban.enabled)))
            .subscribe(banners => {
            this.enabledBanners.next(banners);
        });
    }
    getBanners() {
        return this.enabledBanners.asObservable().pipe(map(banners => banners.filter(x => this.isNotClosed(x.uid))));
    }
    getClosedBanners() {
        let oldClosed = [];
        // For backwards compatibility we need to check the old spot as well.
        // We should be able to remove this after a couple weeks/months.
        const oldClosedRaw = localStorage.getItem('closedBanners');
        if (oldClosedRaw) {
            oldClosed = [...JSON.parse(oldClosedRaw)];
        }
        const newClosed = this.cache.get('closed') || [];
        for (const closed of oldClosed) {
            if (!newClosed.includes(closed)) {
                newClosed.push(closed);
            }
        }
        // Set the closed value here in case there are legacy banners that were not added.
        this.cache.set('closed', newClosed, CacheExpiry.Never);
        return newClosed;
    }
    isNotClosed(uid) {
        if (this.closedBanners && this.closedBanners.length > 0) {
            return !this.closedBanners.some(bannerUid => bannerUid === uid);
        }
        return true;
    }
    closeBanner($event, banner) {
        this.closedBanners.push(banner.uid);
        this.cache.set('closed', this.closedBanners, CacheExpiry.Never);
    }
    isImpersonating() {
        return this.impersonationProvider.getImpersonationDetails(this.user).isImpersonation;
    }
    getImpersonator() {
        return this.impersonationProvider.getImpersonationDetails(this.user).impersonator;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BannerContainerComponent, deps: [{ token: BannerService }, { token: i2$1.AbstractUserProvider }, { token: i2$1.ISkyKickPartnerPortalUserImpersonationDetailsProvider }, { token: i4.CacheService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: BannerContainerComponent, selector: "sk-banner-container", ngImport: i0, template: "<div class=\"alert alert-danger sk-banner-block\" *ngIf=\"isImpersonating()\">\r\n    <div class=\"position-relative\">\r\n        <div class=\"d-flex align-items-center\">\r\n            <i class=\"material-icons md-22 pull-xs-left\">error_outline</i>\r\n            <div class=\"mx-100\">\r\n                <span class=\"sk-alert-text\">You are currently impersonating {{ user.email }} as {{ getImpersonator() }}</span>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</div>\r\n<div *ngFor=\"let banner of getBanners() | async | slice:0:2;\">\r\n    <div class=\"alert alert-warning alert-dismissible sk-banner-block\">\r\n        <div class=\"position-relative\">\r\n            <div class=\"d-flex align-items-center\">\r\n                <i class=\"material-icons md-22 pull-xs-left\">error_outline</i>\r\n                <div class=\"mx-100\">\r\n                    <span class=\"sk-alert-text\">\r\n                        <div [innerHTML]=\"banner.message\"></div>\r\n                    </span>\r\n                </div>\r\n                <button (click)=\"closeBanner($event, banner)\" type=\"button\" class=\"btn-close\" data-bs-dismiss=\"alert\" aria-label=\"Close\">\r\n                </button>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.SlicePipe, name: "slice" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BannerContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-banner-container', template: "<div class=\"alert alert-danger sk-banner-block\" *ngIf=\"isImpersonating()\">\r\n    <div class=\"position-relative\">\r\n        <div class=\"d-flex align-items-center\">\r\n            <i class=\"material-icons md-22 pull-xs-left\">error_outline</i>\r\n            <div class=\"mx-100\">\r\n                <span class=\"sk-alert-text\">You are currently impersonating {{ user.email }} as {{ getImpersonator() }}</span>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</div>\r\n<div *ngFor=\"let banner of getBanners() | async | slice:0:2;\">\r\n    <div class=\"alert alert-warning alert-dismissible sk-banner-block\">\r\n        <div class=\"position-relative\">\r\n            <div class=\"d-flex align-items-center\">\r\n                <i class=\"material-icons md-22 pull-xs-left\">error_outline</i>\r\n                <div class=\"mx-100\">\r\n                    <span class=\"sk-alert-text\">\r\n                        <div [innerHTML]=\"banner.message\"></div>\r\n                    </span>\r\n                </div>\r\n                <button (click)=\"closeBanner($event, banner)\" type=\"button\" class=\"btn-close\" data-bs-dismiss=\"alert\" aria-label=\"Close\">\r\n                </button>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: BannerService }, { type: i2$1.AbstractUserProvider }, { type: i2$1.ISkyKickPartnerPortalUserImpersonationDetailsProvider }, { type: i4.CacheService }] });

class BannerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BannerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: BannerModule, declarations: [BannerContainerComponent], imports: [CommonModule,
            NgbModule,
            AuthModule], exports: [BannerContainerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BannerModule, imports: [CommonModule,
            NgbModule,
            AuthModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BannerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        BannerContainerComponent
                    ],
                    imports: [
                        CommonModule,
                        NgbModule,
                        AuthModule
                    ],
                    exports: [
                        BannerContainerComponent
                    ]
                }]
        }] });

var BadgeType;
(function (BadgeType) {
    BadgeType["Default"] = "default";
    BadgeType["Basic"] = "basic";
    BadgeType["Danger"] = "danger";
    BadgeType["Info"] = "info";
    BadgeType["Warning"] = "warning";
    BadgeType["Success"] = "success";
    BadgeType["DefaultBold"] = "default-bold";
    BadgeType["DangerBold"] = "danger-bold";
})(BadgeType || (BadgeType = {}));

class BadgeComponent {
    constructor() {
        this.isPill = false;
        /**
         * Defines the type/color of the badge
         */
        this.type = BadgeType.Default;
        /**
         * set `sm` for a smaller badge.
         */
        this.size = 'md';
        /**
         * @ignore
         * Reads parent class.
         */
        this.class = '';
    }
    /**
     * if `skBadgePill` is set then the badge becomes round.
     * use it for numbers only.
     */
    set skBadgePill(value) {
        this.isPill = typeof value !== undefined && value !== false;
    }
    get skBadgePill() {
        return this.isPill;
    }
    /**
     * @ignore
     */
    get getClass() {
        return [
            'badge',
            this.isPill ? 'rounded-pill' : '',
            this.size === 'sm' ? 'badge-sm' : '',
            this.mapTypeToClass(this.type),
            this.class
        ].join(' ');
    }
    mapTypeToClass(type) {
        switch (type) {
            case BadgeType.Basic:
            case BadgeType.Danger:
            case BadgeType.DangerBold:
            case BadgeType.Default:
            case BadgeType.DefaultBold:
            case BadgeType.Info:
            case BadgeType.Success:
            case BadgeType.Warning:
                return `badge-${type}`;
            default:
                return `badge-${BadgeType.Default}`;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BadgeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: BadgeComponent, selector: "sk-badge", inputs: { type: "type", skBadgePill: "skBadgePill", size: "size", class: "class" }, host: { properties: { "class": "this.getClass" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BadgeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-badge', template: '<ng-content></ng-content>' }]
        }], propDecorators: { type: [{
                type: Input
            }], skBadgePill: [{
                type: Input
            }], size: [{
                type: Input
            }], class: [{
                type: Input
            }], getClass: [{
                type: HostBinding,
                args: ['class']
            }] } });

class BadgesModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BadgesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: BadgesModule, declarations: [BadgeComponent], imports: [CommonModule], exports: [BadgeComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BadgesModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BadgesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        BadgeComponent
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        BadgeComponent
                    ]
                }]
        }] });

const DEFAULT_DURATION = 300;
class CardComponent {
    constructor() {
        /**
         * Highlights the card when the mouse hovers it when set to true.
         */
        this.selectable = false;
        /**
         * Highlights the card when set to true.
         */
        this.selected = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: CardComponent, selector: "sk-card", inputs: { selectable: "selectable", selected: "selected" }, ngImport: i0, template: "<div class=\"card\" [ngClass]=\"{'card-hover': selectable, 'active':selected }\">\r\n\r\n\r\n    <ng-content select=\"sk-card-header\"></ng-content>\r\n\r\n    <ng-content></ng-content>\r\n\r\n\r\n\r\n    <ng-content select=\"sk-card-footer\"></ng-content>\r\n\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-card', template: "<div class=\"card\" [ngClass]=\"{'card-hover': selectable, 'active':selected }\">\r\n\r\n\r\n    <ng-content select=\"sk-card-header\"></ng-content>\r\n\r\n    <ng-content></ng-content>\r\n\r\n\r\n\r\n    <ng-content select=\"sk-card-footer\"></ng-content>\r\n\r\n</div>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { selectable: [{
                type: Input
            }], selected: [{
                type: Input
            }] } });

class CardHeaderComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: CardHeaderComponent, selector: "sk-card-header", host: { classAttribute: "card-header sk-card-header " }, ngImport: i0, template: "\r\n\r\n\r\n<div class=\"d-flex justify-content-between\">\r\n    <div class=\"d-flex align-items-center\">\r\n        <ng-content select=\"sk-card-status\"></ng-content>\r\n        <ng-content ></ng-content>\r\n    </div>\r\n    <ng-content select=\"[slot=end]\"></ng-content>\r\n</div>\r\n\r\n", styles: [":host-context(.collapsable-card){border-bottom:0!important}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-card-header', host: { 'class': 'card-header sk-card-header ' }, template: "\r\n\r\n\r\n<div class=\"d-flex justify-content-between\">\r\n    <div class=\"d-flex align-items-center\">\r\n        <ng-content select=\"sk-card-status\"></ng-content>\r\n        <ng-content ></ng-content>\r\n    </div>\r\n    <ng-content select=\"[slot=end]\"></ng-content>\r\n</div>\r\n\r\n", styles: [":host-context(.collapsable-card){border-bottom:0!important}\n"] }]
        }] });

class CardBodyDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardBodyDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: CardBodyDirective, selector: "sk-card-body", host: { classAttribute: "card-block" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardBodyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'sk-card-body',
                    host: { 'class': 'card-block' }
                }]
        }] });

class CardFooterDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardFooterDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: CardFooterDirective, selector: "sk-card-footer", host: { classAttribute: "card-footer text-muted" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardFooterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'sk-card-footer',
                    host: { 'class': 'card-footer text-muted' },
                }]
        }] });

class CardTitleComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardTitleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: CardTitleComponent, selector: "sk-card-title", ngImport: i0, template: "<h4>\r\n    <ng-content></ng-content>\r\n</h4>\r\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardTitleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-card-title', template: "<h4>\r\n    <ng-content></ng-content>\r\n</h4>\r\n" }]
        }] });

class CardStatusComponent {
    constructor() {
        /**
         * displays an icon to the left of the `sk-card-header`
         */
        this.status = ' muted';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardStatusComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: CardStatusComponent, selector: "sk-card-status", inputs: { status: "status" }, host: { classAttribute: "sk-card-icon d-flex align-items-center pr-50" }, ngImport: i0, template: `<i  class="material-icons md-22 icon-{{status}}"><ng-content></ng-content></i>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardStatusComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sk-card-status',
                    host: { 'class': 'sk-card-icon d-flex align-items-center pr-50' },
                    template: `<i  class="material-icons md-22 icon-{{status}}"><ng-content></ng-content></i>`
                }]
        }], propDecorators: { status: [{
                type: Input
            }] } });

const references$1 = [
    CardComponent,
    CardHeaderComponent,
    CardBodyDirective,
    CardFooterDirective,
    CardTitleComponent,
    CardStatusComponent
];
class CardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: CardModule, declarations: [CardComponent,
            CardHeaderComponent,
            CardBodyDirective,
            CardFooterDirective,
            CardTitleComponent,
            CardStatusComponent], imports: [CommonModule,
            FormsModule$1], exports: [CardComponent,
            CardHeaderComponent,
            CardBodyDirective,
            CardFooterDirective,
            CardTitleComponent,
            CardStatusComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardModule, imports: [CommonModule,
            FormsModule$1] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: references$1,
                    imports: [
                        CommonModule,
                        FormsModule$1,
                    ],
                    exports: references$1
                }]
        }] });

const EMAIL_REGEX = "^['&A-Za-z0-9._%+-]+@[A-Za-z0-9-][A-Za-z0-9.-]*\\.[A-Za-z]{2,15}$";
const POSTAL_CODE_REGEX = "^[a-zA-Z0-9][a-zA-Z0-9\\- ]{0,10}[a-zA-Z0-9]$";

var DropdownType;
(function (DropdownType) {
    DropdownType["Primary"] = "primary";
    DropdownType["Secondary"] = "secondary";
    DropdownType["Tertiary"] = "tertiary";
    DropdownType["Link"] = "link";
    DropdownType["Basic"] = "basic";
})(DropdownType || (DropdownType = {}));

class BaseDropdown {
    /**
    *
    * @ignore
    */
    mapTypeToClass(type) {
        switch (type) {
            case DropdownType.Primary:
            case DropdownType.Link:
            case DropdownType.Basic:
                return `btn-${type}`;
            case DropdownType.Tertiary:
                return 'btn-outline-primary';
            default:
                return `btn-outline-secondary`;
        }
    }
}

class DropdownComponent extends BaseDropdown {
    constructor() {
        super();
        /**
         * Indicates whether the dropdown should be closed when clicking one of dropdown items or pressing ESC
         */
        this.autoClose = true;
        /**
         * Splits the dropdown and adds a small button with a chevron pointing down.
         */
        this.split = false;
        /**
         * Wider dropdown when set to true.
         */
        this.wide = false;
        /**
         * Size of the dropdown.
         */
        this.large = false;
        this.optionSelected = new EventEmitter();
        this.chosen = null;
    }
    /**
     * @ignore
     * @param {any} value
     */
    selectItem(value) {
        this.chosen = value;
        this.optionSelected.emit(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DropdownComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: DropdownComponent, selector: "sk-dropdown", inputs: { autoClose: "autoClose", label: "label", type: "type", disabled: "disabled", split: "split", wide: "wide", large: "large", options: "options" }, outputs: { optionSelected: "optionSelected" }, host: { properties: { "attr.disabled": "this.disabled" } }, usesInheritance: true, ngImport: i0, template: "<ng-container *ngIf=\"split\">\r\n    <div class=\"btn-group\" container=\"body\" [ngClass]=\"{'btn-block' : wide}\">\r\n        <button [disabled]=\"disabled\" type=\"button\" class=\"btn {{ mapTypeToClass(type) }}\"\r\n            [ngClass]=\"{'btn-block' : wide, 'btn-lg': large}\">\r\n            {{ label }}\r\n        </button>\r\n        <div class=\"btn-group\" ngbDropdown role=\"group\" [autoClose]=\"autoClose\">\r\n            <button class=\"btn {{ mapTypeToClass(type) }} dropdown-toggle-split dropdown-toggle\" ngbDropdownToggle\r\n                [class.btn-lg]=\"large\"></button>\r\n            <div *ngIf=\"options?.length > 0\" class=\"dropdown-menu\" ngbDropdownMenu>\r\n                <a ngbDropdownItem *ngFor=\"let kvp of options\" (click)=\"selectItem(kvp.value)\" [class.active]=\"(chosen === kvp.value)\" class=\"d-flex justify-content-between\"\r\n                    [disabled]=\"kvp.disabled\">\r\n                    <span>{{kvp.key}}</span>\r\n                    <i [hidden]=\"!(chosen === kvp.value)\" class=\"material-icons md-22 ml-100\">check</i>\r\n                </a>\r\n            </div>\r\n            <div *ngIf=\"options?.length === 0 || !options\" ngbDropdownMenu class=\"dropdown-menu\">\r\n                <ng-container *ngTemplateOutlet=\"contentTpl\"></ng-container>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</ng-container>\r\n<ng-container *ngIf=\"!split\">\r\n    <div ngbDropdown class=\"btn-group\" container=\"body\" [class.btn-block]=\"wide\" [autoClose]=\"autoClose\">\r\n        <button [disabled]=\"disabled\" ngbDropdownToggle\r\n            class=\"btn sk-btn-combo dropdown-toggle text-truncate {{ mapTypeToClass(type) }}\"\r\n            [ngClass]=\"{'btn-block' : wide, 'btn-lg': large}\">\r\n            {{ label }}\r\n        </button>\r\n        <div *ngIf=\"options?.length > 0\" ngbDropdownMenu class=\"dropdown-menu\">\r\n            <a ngbDropdownItem *ngFor=\"let kvp of options\" (click)=\"selectItem(kvp.value)\" [class.active]=\"(chosen === kvp.value)\" class=\"d-flex justify-content-between\"\r\n                [disabled]=\"kvp.disabled\">\r\n                <span>{{kvp.key}}</span>\r\n                <i [hidden]=\"!(chosen === kvp.value)\" class=\"material-icons md-22 ml-100\">check</i>\r\n            </a>\r\n        </div>\r\n        <div *ngIf=\"options?.length === 0 || !options\" ngbDropdownMenu class=\"dropdown-menu\">\r\n            <ng-container *ngTemplateOutlet=\"contentTpl\"></ng-container>\r\n        </div>\r\n    </div>\r\n</ng-container>\r\n<ng-template #contentTpl><ng-content></ng-content></ng-template>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$1.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i1$1.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i1$1.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "directive", type: i1$1.NgbDropdownItem, selector: "[ngbDropdownItem]", inputs: ["tabindex", "disabled"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DropdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-dropdown', template: "<ng-container *ngIf=\"split\">\r\n    <div class=\"btn-group\" container=\"body\" [ngClass]=\"{'btn-block' : wide}\">\r\n        <button [disabled]=\"disabled\" type=\"button\" class=\"btn {{ mapTypeToClass(type) }}\"\r\n            [ngClass]=\"{'btn-block' : wide, 'btn-lg': large}\">\r\n            {{ label }}\r\n        </button>\r\n        <div class=\"btn-group\" ngbDropdown role=\"group\" [autoClose]=\"autoClose\">\r\n            <button class=\"btn {{ mapTypeToClass(type) }} dropdown-toggle-split dropdown-toggle\" ngbDropdownToggle\r\n                [class.btn-lg]=\"large\"></button>\r\n            <div *ngIf=\"options?.length > 0\" class=\"dropdown-menu\" ngbDropdownMenu>\r\n                <a ngbDropdownItem *ngFor=\"let kvp of options\" (click)=\"selectItem(kvp.value)\" [class.active]=\"(chosen === kvp.value)\" class=\"d-flex justify-content-between\"\r\n                    [disabled]=\"kvp.disabled\">\r\n                    <span>{{kvp.key}}</span>\r\n                    <i [hidden]=\"!(chosen === kvp.value)\" class=\"material-icons md-22 ml-100\">check</i>\r\n                </a>\r\n            </div>\r\n            <div *ngIf=\"options?.length === 0 || !options\" ngbDropdownMenu class=\"dropdown-menu\">\r\n                <ng-container *ngTemplateOutlet=\"contentTpl\"></ng-container>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</ng-container>\r\n<ng-container *ngIf=\"!split\">\r\n    <div ngbDropdown class=\"btn-group\" container=\"body\" [class.btn-block]=\"wide\" [autoClose]=\"autoClose\">\r\n        <button [disabled]=\"disabled\" ngbDropdownToggle\r\n            class=\"btn sk-btn-combo dropdown-toggle text-truncate {{ mapTypeToClass(type) }}\"\r\n            [ngClass]=\"{'btn-block' : wide, 'btn-lg': large}\">\r\n            {{ label }}\r\n        </button>\r\n        <div *ngIf=\"options?.length > 0\" ngbDropdownMenu class=\"dropdown-menu\">\r\n            <a ngbDropdownItem *ngFor=\"let kvp of options\" (click)=\"selectItem(kvp.value)\" [class.active]=\"(chosen === kvp.value)\" class=\"d-flex justify-content-between\"\r\n                [disabled]=\"kvp.disabled\">\r\n                <span>{{kvp.key}}</span>\r\n                <i [hidden]=\"!(chosen === kvp.value)\" class=\"material-icons md-22 ml-100\">check</i>\r\n            </a>\r\n        </div>\r\n        <div *ngIf=\"options?.length === 0 || !options\" ngbDropdownMenu class=\"dropdown-menu\">\r\n            <ng-container *ngTemplateOutlet=\"contentTpl\"></ng-container>\r\n        </div>\r\n    </div>\r\n</ng-container>\r\n<ng-template #contentTpl><ng-content></ng-content></ng-template>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { autoClose: [{
                type: Input
            }], label: [{
                type: Input
            }], type: [{
                type: Input
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.disabled']
            }], split: [{
                type: Input
            }], wide: [{
                type: Input
            }], large: [{
                type: Input
            }], options: [{
                type: Input
            }], optionSelected: [{
                type: Output
            }] } });

class DropdownsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DropdownsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: DropdownsModule, declarations: [DropdownComponent], imports: [CommonModule,
            UtilityModule,
            NgbModule], exports: [DropdownComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DropdownsModule, imports: [CommonModule,
            UtilityModule,
            NgbModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DropdownsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DropdownComponent
                    ],
                    imports: [
                        CommonModule,
                        UtilityModule,
                        NgbModule
                    ],
                    exports: [
                        DropdownComponent
                    ]
                }]
        }] });

class ActionMenuComponent {
    constructor() {
        this.optionSelected = new EventEmitter();
    }
    /**
     * @ignore
     * @param {any} value
     */
    selectItem(value) {
        this.optionSelected.emit(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ActionMenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ActionMenuComponent, selector: "sk-action-menu", inputs: { options: "options" }, outputs: { optionSelected: "optionSelected" }, ngImport: i0, template: "<div ngbDropdown class=\"btn-group\" container=\"body\">\r\n    <i class=\"material-icons\" ngbDropdownToggle>more_horiz</i>\r\n    <div *ngIf=\"options?.length > 0\" ngbDropdownMenu class=\"dropdown-menu\">\r\n        <a ngbDropdownItem *ngFor=\"let kvp of options\" (click)=\"selectItem(kvp.value)\" class=\"d-flex\"\r\n            [disabled]=\"kvp.disabled\">\r\n            <i *ngIf=\"kvp.iconName\" class=\"material-icons\">{{kvp.iconName}}</i>\r\n            <span>{{kvp.key}}</span>\r\n        </a>\r\n    </div>\r\n</div>", styles: [".dropdown-toggle{cursor:pointer}.dropdown-toggle:after{display:none}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i1$1.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i1$1.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "directive", type: i1$1.NgbDropdownItem, selector: "[ngbDropdownItem]", inputs: ["tabindex", "disabled"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ActionMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-action-menu', template: "<div ngbDropdown class=\"btn-group\" container=\"body\">\r\n    <i class=\"material-icons\" ngbDropdownToggle>more_horiz</i>\r\n    <div *ngIf=\"options?.length > 0\" ngbDropdownMenu class=\"dropdown-menu\">\r\n        <a ngbDropdownItem *ngFor=\"let kvp of options\" (click)=\"selectItem(kvp.value)\" class=\"d-flex\"\r\n            [disabled]=\"kvp.disabled\">\r\n            <i *ngIf=\"kvp.iconName\" class=\"material-icons\">{{kvp.iconName}}</i>\r\n            <span>{{kvp.key}}</span>\r\n        </a>\r\n    </div>\r\n</div>", styles: [".dropdown-toggle{cursor:pointer}.dropdown-toggle:after{display:none}\n"] }]
        }], ctorParameters: () => [], propDecorators: { options: [{
                type: Input
            }], optionSelected: [{
                type: Output
            }] } });

class ActionMenuModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ActionMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: ActionMenuModule, declarations: [ActionMenuComponent], imports: [CommonModule,
            NgbModule], exports: [ActionMenuComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ActionMenuModule, imports: [CommonModule,
            NgbModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ActionMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ActionMenuComponent
                    ],
                    imports: [
                        CommonModule,
                        NgbModule,
                    ],
                    exports: [
                        ActionMenuComponent
                    ]
                }]
        }] });

class TreeNode {
    constructor(name, expandable, data, parent) {
        this.name = name;
        this.expandable = expandable;
        this.data = data;
        this.parent = parent;
        this.children = [];
        this.isExpanded = false;
        this.isDisabled = false;
    }
    get isExpandable() { return this.expandable; }
    hasChild(name) {
        return this.children.some(child => child.name === name);
    }
    getParent() {
        return this.parent;
    }
    getChild(name) {
        return this.children.find(child => child.name === name);
    }
    getPath() {
        let currentPath = this.name;
        let curNode = this.parent;
        while (curNode) {
            currentPath = `${curNode.name}/${currentPath}`;
            curNode = curNode.parent;
        }
        return currentPath;
    }
}

class TreeControl {
    constructor() {
        this.treeNodeSelected = new EventEmitter();
    }
}

class TreeViewComponent {
    set hideRoot(value) {
        this.hideRootNode = typeof value !== undefined && value !== false;
        if (this.hideRootNode) {
            this.isExpanded = true;
        }
    }
    get hideRoot() {
        return this.hideRootNode;
    }
    get isExpanded() {
        if (!this.root) {
            return false;
        }
        return this.root.isExpanded;
    }
    set isExpanded(value) {
        if (!!this.root) {
            this.root.isExpanded = value;
        }
    }
    get isDisabled() {
        if (!this.root) {
            return true;
        }
        return this.root.isDisabled;
    }
    set isDisabled(value) {
        if (!!this.root) {
            this.root.isDisabled = value;
        }
    }
    constructor() {
        this.branchSelectable = false;
    }
    selectNode(node) {
        if (!node.isDisabled) {
            this.treeControl.treeNodeSelected.emit(node);
        }
    }
    getChildren(node) {
        return node.children.sort(this.fileSort);
    }
    hasChildren() {
        if ((this.root) && (this.root.children)) {
            return this.root.children.length > 0;
        }
        return false;
    }
    hasParent() {
        return !!this.root.getParent();
    }
    nodeNameStyle() {
        const styles = [];
        if (this.root.isExpandable) {
            // branch node
            styles.push('branch-node');
            if (this.isDisabled) {
                styles.push('not-allowed');
            }
            else if (this.branchSelectable || this.hasChildren()) {
                styles.push('clickable');
            }
        }
        else {
            // leaf node
            styles.push('clickable');
        }
        return styles.join(' ');
    }
    expand() {
        if (this.root.isExpandable && this.hasChildren()) {
            this.isExpanded = !this.isExpanded;
        }
    }
    fileSort(a, b) {
        if (a.isExpandable && !b.isExpandable) {
            return -1;
        }
        else if (!a.isExpandable && b.isExpandable) {
            return 1;
        }
        else {
            return a.name.localeCompare(b.name);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TreeViewComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: TreeViewComponent, selector: "sk-tree-view", inputs: { root: "root", treeControl: "treeControl", branchIcon: "branchIcon", leafIcon: "leafIcon", branchSelectable: "branchSelectable", hideRoot: "hideRoot" }, ngImport: i0, template: "<div class=\"tree-item node\" [class.is-root]=\"!hasParent()\" [class.expandable]=\"root?.isExpandable\">\r\n    <div *ngIf=\"!hideRoot\" class=\"tree-branch py-2 d-flex align-items-center\">\r\n\r\n        <!-- Branch node -->\r\n        <div class=\"d-flex align-items-center\" *ngIf=\"root.isExpandable\" (click)=\"expand()\">\r\n            <i class=\"material-icons md-18 clickable\" [class.icon-size]=\"!hasChildren()\">\r\n                {{ hasChildren() ? (isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right') : '' }}</i>\r\n            <ng-container [ngTemplateOutlet]=\"branchIcon\" [ngTemplateOutletContext]=\"{ context: root}\"></ng-container>\r\n        </div>\r\n\r\n        <!-- Leaf node -->\r\n        <div *ngIf=\"!root.isExpandable && leafIcon\">\r\n            <ng-container [ngTemplateOutlet]=\"leafIcon\" [ngTemplateOutletContext]=\"{ context: root}\"></ng-container>\r\n        </div>\r\n\r\n        <!-- Node name -->\r\n        <div class=\"ms-1 node-name {{ nodeNameStyle() }}\" (click)=\"selectNode(root)\" (click)=\"expand()\">{{ root.name }}</div>\r\n    </div>\r\n\r\n    <!-- Sub views -->\r\n    <div *ngIf=\"isExpanded\">\r\n        <sk-tree-view class=\"d-block\" [class.icon-size]=\"hasParent()\" *ngFor=\"let child of getChildren(root)\"\r\n            [root]=\"child\"\r\n            [treeControl]=\"treeControl\"\r\n            [branchIcon]=\"branchIcon\"\r\n            [branchSelectable]=\"branchSelectable\"\r\n            [leafIcon]=\"leafIcon\">\r\n        </sk-tree-view>\r\n    </div>\r\n</div>\r\n", styles: [".branch-node{font-weight:500;cursor:default}.icon-size{padding-left:18px}.clickable:hover{cursor:pointer}.clickable:hover .node-name{text-decoration:underline}.not-allowed:hover{cursor:not-allowed}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: TreeViewComponent, selector: "sk-tree-view", inputs: ["root", "treeControl", "branchIcon", "leafIcon", "branchSelectable", "hideRoot"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TreeViewComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-tree-view', template: "<div class=\"tree-item node\" [class.is-root]=\"!hasParent()\" [class.expandable]=\"root?.isExpandable\">\r\n    <div *ngIf=\"!hideRoot\" class=\"tree-branch py-2 d-flex align-items-center\">\r\n\r\n        <!-- Branch node -->\r\n        <div class=\"d-flex align-items-center\" *ngIf=\"root.isExpandable\" (click)=\"expand()\">\r\n            <i class=\"material-icons md-18 clickable\" [class.icon-size]=\"!hasChildren()\">\r\n                {{ hasChildren() ? (isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right') : '' }}</i>\r\n            <ng-container [ngTemplateOutlet]=\"branchIcon\" [ngTemplateOutletContext]=\"{ context: root}\"></ng-container>\r\n        </div>\r\n\r\n        <!-- Leaf node -->\r\n        <div *ngIf=\"!root.isExpandable && leafIcon\">\r\n            <ng-container [ngTemplateOutlet]=\"leafIcon\" [ngTemplateOutletContext]=\"{ context: root}\"></ng-container>\r\n        </div>\r\n\r\n        <!-- Node name -->\r\n        <div class=\"ms-1 node-name {{ nodeNameStyle() }}\" (click)=\"selectNode(root)\" (click)=\"expand()\">{{ root.name }}</div>\r\n    </div>\r\n\r\n    <!-- Sub views -->\r\n    <div *ngIf=\"isExpanded\">\r\n        <sk-tree-view class=\"d-block\" [class.icon-size]=\"hasParent()\" *ngFor=\"let child of getChildren(root)\"\r\n            [root]=\"child\"\r\n            [treeControl]=\"treeControl\"\r\n            [branchIcon]=\"branchIcon\"\r\n            [branchSelectable]=\"branchSelectable\"\r\n            [leafIcon]=\"leafIcon\">\r\n        </sk-tree-view>\r\n    </div>\r\n</div>\r\n", styles: [".branch-node{font-weight:500;cursor:default}.icon-size{padding-left:18px}.clickable:hover{cursor:pointer}.clickable:hover .node-name{text-decoration:underline}.not-allowed:hover{cursor:not-allowed}\n"] }]
        }], ctorParameters: () => [], propDecorators: { root: [{
                type: Input
            }], treeControl: [{
                type: Input
            }], branchIcon: [{
                type: Input
            }], leafIcon: [{
                type: Input
            }], branchSelectable: [{
                type: Input
            }], hideRoot: [{
                type: Input
            }] } });

class TreeViewModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TreeViewModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: TreeViewModule, declarations: [TreeViewComponent], imports: [CommonModule], exports: [TreeViewComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TreeViewModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TreeViewModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        TreeViewComponent
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        TreeViewComponent
                    ]
                }]
        }] });

class BasicSearchControlComponent {
    set id(value) {
        this.internalId = value;
        this.externalId = null;
    }
    get id() {
        return this.internalId;
    }
    set value(val) {
        this.internalValue = val;
        this.onChange(val);
        this.onTouched();
    }
    get value() {
        return this.internalValue;
    }
    constructor() {
        this.externalId = '';
        this.onChange = () => { };
        this.onTouched = () => { };
    }
    writeValue(obj) {
        this.value = obj;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.inputComponent.disabled = isDisabled;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BasicSearchControlComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: BasicSearchControlComponent, selector: "sk-basic-search-control", inputs: { placeholder: "placeholder", id: "id", value: "value" }, host: { properties: { "attr.id": "this.externalId" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => BasicSearchControlComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "inputComponent", first: true, predicate: BasicInputComponent, descendants: true, static: true }], ngImport: i0, template: "<sk-basic-input [(value)]=\"value\" [placeholder]=\"placeholder\"></sk-basic-input>\r\n", styles: [""], dependencies: [{ kind: "component", type: BasicInputComponent, selector: "sk-basic-input", inputs: ["placeholder", "disabled", "value"], outputs: ["valueChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BasicSearchControlComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-basic-search-control', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => BasicSearchControlComponent),
                            multi: true
                        }
                    ], template: "<sk-basic-input [(value)]=\"value\" [placeholder]=\"placeholder\"></sk-basic-input>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { externalId: [{
                type: HostBinding,
                args: ['attr.id']
            }], inputComponent: [{
                type: ViewChild,
                args: [BasicInputComponent, { static: true }]
            }], placeholder: [{
                type: Input
            }], id: [{
                type: Input
            }], value: [{
                type: Input,
                args: ['value']
            }] } });

class TreeBuilder {
    constructor(name, data) {
        this.tree = new TreeNode(name, true, data);
        this.currentNode = this.tree;
    }
    /**
     * Adds a new branch node to the current node. Sets the active node to the newly added node.
     * @param name Name of the branch node
     * @param data (optional) - data to pass in to the branch node
     */
    WithBranch(name, data) {
        const newBranch = new TreeNode(name, true, data, this.currentNode);
        this.currentNode.children.push(newBranch);
        this.currentNode = newBranch;
        return this;
    }
    /**
     * Adds a new leaf node to the current node of the tree.
     * @param name Name of the leaf node
     * @param data (optional) - data to pass in to the leaf node
     */
    WithLeaf(name, data) {
        const newLeaf = new TreeNode(name, false, data, this.currentNode);
        this.currentNode.children.push(newLeaf);
        return this;
    }
    /**
     * Adds a new leaf node at the given path
     * @param name Name of the leaf node
     * @param path Path at which to insert the leaf node
     * @param data (optional) - data to pass in to the leaft node
     */
    WithLeafPath(name, path, data) {
        const pathParts = path.split('/').filter(p => !!p);
        // Remove last element in the path array. This is the file name
        pathParts.pop();
        while (pathParts.length > 0 && this.currentNode.hasChild(pathParts[0])) {
            this.currentNode = this.currentNode.getChild(pathParts[0]);
            pathParts.shift();
        }
        pathParts.forEach(branchName => {
            const newBranch = new TreeNode(branchName, true, null, this.currentNode);
            this.currentNode.children.push(newBranch);
            this.currentNode = newBranch;
        });
        return this.WithLeaf(name, data);
    }
    /**
     * Inserts a sub tree as a child of the current node
     * @param node Node to insert into the tree
     */
    WithSubTree(node) {
        this.currentNode.children.push(node);
        return this;
    }
    /**
     * Inserts an array of sub trees as children of the current node
     * @param nodes Sub trees to insert into the current ndoe
     */
    WithSubTrees(nodes) {
        this.currentNode.children.push(...nodes);
        return this;
    }
    /**
     * Moves the current node up one level in the tree. If already at the root, does nothing.
     */
    TraverseUp() {
        if (this.currentNode.getParent()) {
            this.currentNode = this.currentNode.getParent();
        }
        return this;
    }
    /**
     * Sets the current node to the root tree node
     */
    ToRoot() {
        this.currentNode = this.tree;
        return this;
    }
    /**
     * Finalizes and returns the root of the tree
     */
    Build() {
        return this.tree;
    }
}

class FolderPathControlComponent {
    set id(value) {
        this.internalId = value;
        this.externalId = null;
    }
    get id() {
        return this.internalId;
    }
    set value(val) {
        this.internalValue = val;
        this.onChange(val);
        this.onTouched();
    }
    get value() {
        return this.internalValue;
    }
    constructor() {
        this.height = '500px';
        this.externalId = '';
        this.treeControl = new TreeControl();
        this.freePathEnabled = true;
        this.isDisabled = false;
        this.onChange = () => { };
        this.onTouched = () => { };
    }
    set hideRoot(value) {
        this.hideRootNode = typeof value !== undefined && value !== false;
    }
    get hideRoot() {
        return this.hideRootNode;
    }
    ngOnInit() {
        this.treeSubscription = this.treeControl.treeNodeSelected
            .pipe(share())
            .subscribe(node => {
            this.value = node.getPath();
        });
    }
    writeValue(obj) {
        this.value = obj;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.isDisabled = isDisabled;
        if (isDisabled) {
            this.treeControl.treeNodeSelected.unsubscribe();
            this.treeSubscription = null;
        }
        else if (!this.treeSubscription) {
            this.treeSubscription = this.treeControl.treeNodeSelected
                .subscribe(node => {
                this.value = node.getPath();
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FolderPathControlComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: FolderPathControlComponent, selector: "sk-folder-path-control", inputs: { height: "height", branchIcon: "branchIcon", id: "id", value: "value", root: "root", treeControl: "treeControl", freePathEnabled: "freePathEnabled", hideRoot: "hideRoot" }, host: { properties: { "attr.id": "this.externalId" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FolderPathControlComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "treeViewComponent", first: true, predicate: TreeViewComponent, descendants: true, static: true }], ngImport: i0, template: "<div class=\"card sk-card overflow-auto\" [style.height]=\"height\">\r\n  <div class=\"card-block\">\r\n      <sk-tree-view [hideRoot]=\"hideRoot\" [root]=\"root\" [treeControl]=\"treeControl\" [branchIcon]=\"branchIcon\" [branchSelectable]=\"true\"></sk-tree-view>\r\n  </div>\r\n</div>\r\n<fieldset class=\"form-group pt-4\">\r\n  <label>Path</label>\r\n  <input type=\"text\" disabled=\"isDisabled || !freePathEnabled\" class=\"form-control\" [(ngModel)]=\"value\">\r\n</fieldset>\r\n\r\n", styles: [""], dependencies: [{ kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: TreeViewComponent, selector: "sk-tree-view", inputs: ["root", "treeControl", "branchIcon", "leafIcon", "branchSelectable", "hideRoot"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FolderPathControlComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-folder-path-control', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FolderPathControlComponent),
                            multi: true
                        }
                    ], template: "<div class=\"card sk-card overflow-auto\" [style.height]=\"height\">\r\n  <div class=\"card-block\">\r\n      <sk-tree-view [hideRoot]=\"hideRoot\" [root]=\"root\" [treeControl]=\"treeControl\" [branchIcon]=\"branchIcon\" [branchSelectable]=\"true\"></sk-tree-view>\r\n  </div>\r\n</div>\r\n<fieldset class=\"form-group pt-4\">\r\n  <label>Path</label>\r\n  <input type=\"text\" disabled=\"isDisabled || !freePathEnabled\" class=\"form-control\" [(ngModel)]=\"value\">\r\n</fieldset>\r\n\r\n" }]
        }], ctorParameters: () => [], propDecorators: { height: [{
                type: Input
            }], branchIcon: [{
                type: Input
            }], id: [{
                type: Input
            }], value: [{
                type: Input,
                args: ['value']
            }], externalId: [{
                type: HostBinding,
                args: ['attr.id']
            }], treeViewComponent: [{
                type: ViewChild,
                args: [TreeViewComponent, { static: true }]
            }], root: [{
                type: Input
            }], treeControl: [{
                type: Input
            }], freePathEnabled: [{
                type: Input
            }], hideRoot: [{
                type: Input
            }] } });

class FormsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FormsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: FormsModule, declarations: [BasicSearchControlComponent, FolderPathControlComponent], imports: [CommonModule,
            FormsModule$1,
            InputModule,
            TreeViewModule], exports: [BasicSearchControlComponent,
            FolderPathControlComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FormsModule, imports: [CommonModule,
            FormsModule$1,
            InputModule,
            TreeViewModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FormsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [BasicSearchControlComponent, FolderPathControlComponent],
                    imports: [
                        CommonModule,
                        FormsModule$1,
                        InputModule,
                        TreeViewModule
                    ],
                    exports: [
                        BasicSearchControlComponent,
                        FolderPathControlComponent
                    ]
                }]
        }] });

class NoDetailsComponent {
    constructor() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NoDetailsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NoDetailsComponent, selector: "sk-no-details", ngImport: i0, template: "<svg width=\"108px\" height=\"108px\" viewBox=\"0 0 108 108\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n    <title>art-details</title>\r\n    <defs>\r\n        <polygon id=\"path-1\" points=\"0 108 108 108 108 0 0 0\"></polygon>\r\n    </defs>\r\n    <g id=\"Onboard\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n        <g id=\"Connectors\" transform=\"translate(-1180.000000, -207.000000)\">\r\n            <g id=\"art-details\" transform=\"translate(1180.000000, 207.000000)\">\r\n                <polygon id=\"Fill-1\" fill=\"#FFFFFF\" points=\"0 108 108 108 108 0 0 0\"></polygon>\r\n                <g id=\"Group-32\">\r\n                    <path d=\"M53.4761,64.3154 C53.0871,59.3704 48.9031,55.4424 43.8781,55.4424 C42.3791,55.4424 40.9071,55.7904 39.5741,56.4574 C37.3081,53.8364 33.9721,52.2944 30.4901,52.2944 C23.8781,52.2944 18.5001,57.6714 18.5001,64.2844 L18.5001,64.3344 C15.6091,65.8554 13.7751,68.8584 13.7751,72.1594 C13.7751,77.0344 17.7411,81.0004 22.6161,81.0004 L49.3911,81.0004 C54.2661,81.0004 58.2311,77.0344 58.2311,72.1594 C58.2311,68.8434 56.3841,65.8284 53.4761,64.3154\" id=\"Fill-2\" fill=\"#E1F1FF\"></path>\r\n                    <path d=\"M86.5,66.5 C86.5,67.881 85.381,69 84,69 C82.619,69 81.5,67.881 81.5,66.5 C81.5,65.119 82.619,64 84,64 C85.381,64 86.5,65.119 86.5,66.5\" id=\"Fill-4\" fill=\"#DFE9EF\"></path>\r\n                    <path d=\"M92.5,52.5 C92.5,53.881 91.381,55 90,55 C88.619,55 87.5,53.881 87.5,52.5 C87.5,51.119 88.619,50 90,50 C91.381,50 92.5,51.119 92.5,52.5\" id=\"Fill-6\" fill=\"#00ADFF\"></path>\r\n                    <path d=\"M23.5,31.5 C23.5,32.881 22.381,34 21,34 C19.619,34 18.5,32.881 18.5,31.5 C18.5,30.119 19.619,29 21,29 C22.381,29 23.5,30.119 23.5,31.5\" id=\"Fill-8\" fill=\"#FFC007\"></path>\r\n                    <path d=\"M48.5,14.5 C48.5,16.985 46.485,19 44,19 C41.515,19 39.5,16.985 39.5,14.5 C39.5,12.015 41.515,10 44,10 C46.485,10 48.5,12.015 48.5,14.5\" id=\"Fill-10\" fill=\"#E1F1FF\"></path>\r\n                    <path d=\"M82.4072,39.8506 C82.1512,39.8506 81.8952,39.7526 81.7002,39.5576 C81.3092,39.1666 81.3092,38.5346 81.7002,38.1436 L83.8222,36.0226 C84.2122,35.6316 84.8452,35.6316 85.2362,36.0226 C85.6262,36.4126 85.6262,37.0456 85.2362,37.4366 L83.1142,39.5576 C82.9192,39.7526 82.6632,39.8506 82.4072,39.8506 M77.4572,44.7996 C77.2012,44.7996 76.9462,44.7026 76.7502,44.5066 C76.3602,44.1166 76.3602,43.4836 76.7502,43.0926 L78.8722,40.9716 C79.2622,40.5806 79.8952,40.5806 80.2862,40.9716 C80.6772,41.3626 80.6772,41.9956 80.2862,42.3856 L78.1642,44.5066 C77.9692,44.7026 77.7132,44.7996 77.4572,44.7996 M72.5082,49.7496 C72.2522,49.7496 71.9962,49.6526 71.8012,49.4566 C71.4102,49.0666 71.4102,48.4336 71.8012,48.0426 L73.9222,45.9216 C74.3122,45.5316 74.9452,45.5316 75.3362,45.9216 C75.7262,46.3126 75.7262,46.9456 75.3362,47.3356 L73.2152,49.4566 C73.0192,49.6526 72.7632,49.7496 72.5082,49.7496\" id=\"Fill-12\" fill=\"#DFE4EC\"></path>\r\n                    <path d=\"M88.5,28 C86.294,28 84.5,29.794 84.5,32 C84.5,34.206 86.294,36 88.5,36 C90.706,36 92.5,34.206 92.5,32 C92.5,29.794 90.706,28 88.5,28 M88.5,37 C85.743,37 83.5,34.757 83.5,32 C83.5,29.243 85.743,27 88.5,27 C91.257,27 93.5,29.243 93.5,32 C93.5,34.757 91.257,37 88.5,37\" id=\"Fill-14\" fill=\"#FFC007\"></path>\r\n                    <path d=\"M74.353,84.4365 L33.5,84.4365 C32.4,84.4365 31.5,83.5365 31.5,82.4365 L31.5,25.4365 C31.5,24.3355 32.4,23.4365 33.5,23.4365 L74.353,23.4365 C75.453,23.4365 76.353,24.3355 76.353,25.4365 L76.353,82.4365 C76.353,83.5365 75.453,84.4365 74.353,84.4365\" id=\"Fill-16\" fill=\"#FFFFFF\"></path>\r\n                    <path d=\"M33.5,24.4365 C32.949,24.4365 32.5,24.8845 32.5,25.4365 L32.5,82.4365 C32.5,82.9885 32.949,83.4365 33.5,83.4365 L74.353,83.4365 C74.904,83.4365 75.353,82.9885 75.353,82.4365 L75.353,25.4365 C75.353,24.8845 74.904,24.4365 74.353,24.4365 L33.5,24.4365 Z M74.353,85.4365 L33.5,85.4365 C31.846,85.4365 30.5,84.0905 30.5,82.4365 L30.5,25.4365 C30.5,23.7825 31.846,22.4365 33.5,22.4365 L74.353,22.4365 C76.007,22.4365 77.353,23.7825 77.353,25.4365 L77.353,82.4365 C77.353,84.0905 76.007,85.4365 74.353,85.4365 L74.353,85.4365 Z\" id=\"Fill-18\" fill=\"#0859A3\"></path>\r\n                    <polygon id=\"Fill-20\" fill=\"#EDF2F7\" points=\"36.4341 28 63.4931 28 36.4931 55\"></polygon>\r\n                    <mask id=\"mask-2\" fill=\"white\">\r\n                        <use xlink:href=\"#path-1\"></use>\r\n                    </mask>\r\n                    <g id=\"Clip-23\"></g>\r\n                    <polygon id=\"Fill-22\" fill=\"#0859A3\" mask=\"url(#mask-2)\" points=\"44.853 68.437 67.977 68.437 67.977 66.437 44.853 66.437\"></polygon>\r\n                    <polygon id=\"Fill-24\" fill=\"#00ADFF\" mask=\"url(#mask-2)\" points=\"39.183 68.79 41.891 68.79 41.891 66.082 39.183 66.082\"></polygon>\r\n                    <polygon id=\"Fill-25\" fill=\"#0859A3\" mask=\"url(#mask-2)\" points=\"44.853 59.437 67.977 59.437 67.977 57.437 44.853 57.437\"></polygon>\r\n                    <polygon id=\"Fill-26\" fill=\"#00ADFF\" mask=\"url(#mask-2)\" points=\"39.183 59.79 41.891 59.79 41.891 57.082 39.183 57.082\"></polygon>\r\n                    <polygon id=\"Fill-27\" fill=\"#0859A3\" mask=\"url(#mask-2)\" points=\"44.853 50.437 67.977 50.437 67.977 48.437 44.853 48.437\"></polygon>\r\n                    <polygon id=\"Fill-28\" fill=\"#00ADFF\" mask=\"url(#mask-2)\" points=\"39.183 50.79 41.891 50.79 41.891 48.082 39.183 48.082\"></polygon>\r\n                    <polygon id=\"Fill-29\" fill=\"#0859A3\" mask=\"url(#mask-2)\" points=\"44.853 41.437 67.977 41.437 67.977 39.437 44.853 39.437\"></polygon>\r\n                    <polygon id=\"Fill-30\" fill=\"#00ADFF\" mask=\"url(#mask-2)\" points=\"39.183 41.79 41.891 41.79 41.891 39.082 39.183 39.082\"></polygon>\r\n                    <path d=\"M20.5,53 C18.294,53 16.5,54.794 16.5,57 C16.5,59.206 18.294,61 20.5,61 C22.706,61 24.5,59.206 24.5,57 C24.5,54.794 22.706,53 20.5,53 M20.5,62 C17.743,62 15.5,59.757 15.5,57 C15.5,54.243 17.743,52 20.5,52 C23.257,52 25.5,54.243 25.5,57 C25.5,59.757 23.257,62 20.5,62\" id=\"Fill-31\" fill=\"#00ADFF\" mask=\"url(#mask-2)\"></path>\r\n                </g>\r\n            </g>\r\n        </g>\r\n    </g>\r\n</svg>\r\n", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NoDetailsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-no-details', template: "<svg width=\"108px\" height=\"108px\" viewBox=\"0 0 108 108\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n    <title>art-details</title>\r\n    <defs>\r\n        <polygon id=\"path-1\" points=\"0 108 108 108 108 0 0 0\"></polygon>\r\n    </defs>\r\n    <g id=\"Onboard\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n        <g id=\"Connectors\" transform=\"translate(-1180.000000, -207.000000)\">\r\n            <g id=\"art-details\" transform=\"translate(1180.000000, 207.000000)\">\r\n                <polygon id=\"Fill-1\" fill=\"#FFFFFF\" points=\"0 108 108 108 108 0 0 0\"></polygon>\r\n                <g id=\"Group-32\">\r\n                    <path d=\"M53.4761,64.3154 C53.0871,59.3704 48.9031,55.4424 43.8781,55.4424 C42.3791,55.4424 40.9071,55.7904 39.5741,56.4574 C37.3081,53.8364 33.9721,52.2944 30.4901,52.2944 C23.8781,52.2944 18.5001,57.6714 18.5001,64.2844 L18.5001,64.3344 C15.6091,65.8554 13.7751,68.8584 13.7751,72.1594 C13.7751,77.0344 17.7411,81.0004 22.6161,81.0004 L49.3911,81.0004 C54.2661,81.0004 58.2311,77.0344 58.2311,72.1594 C58.2311,68.8434 56.3841,65.8284 53.4761,64.3154\" id=\"Fill-2\" fill=\"#E1F1FF\"></path>\r\n                    <path d=\"M86.5,66.5 C86.5,67.881 85.381,69 84,69 C82.619,69 81.5,67.881 81.5,66.5 C81.5,65.119 82.619,64 84,64 C85.381,64 86.5,65.119 86.5,66.5\" id=\"Fill-4\" fill=\"#DFE9EF\"></path>\r\n                    <path d=\"M92.5,52.5 C92.5,53.881 91.381,55 90,55 C88.619,55 87.5,53.881 87.5,52.5 C87.5,51.119 88.619,50 90,50 C91.381,50 92.5,51.119 92.5,52.5\" id=\"Fill-6\" fill=\"#00ADFF\"></path>\r\n                    <path d=\"M23.5,31.5 C23.5,32.881 22.381,34 21,34 C19.619,34 18.5,32.881 18.5,31.5 C18.5,30.119 19.619,29 21,29 C22.381,29 23.5,30.119 23.5,31.5\" id=\"Fill-8\" fill=\"#FFC007\"></path>\r\n                    <path d=\"M48.5,14.5 C48.5,16.985 46.485,19 44,19 C41.515,19 39.5,16.985 39.5,14.5 C39.5,12.015 41.515,10 44,10 C46.485,10 48.5,12.015 48.5,14.5\" id=\"Fill-10\" fill=\"#E1F1FF\"></path>\r\n                    <path d=\"M82.4072,39.8506 C82.1512,39.8506 81.8952,39.7526 81.7002,39.5576 C81.3092,39.1666 81.3092,38.5346 81.7002,38.1436 L83.8222,36.0226 C84.2122,35.6316 84.8452,35.6316 85.2362,36.0226 C85.6262,36.4126 85.6262,37.0456 85.2362,37.4366 L83.1142,39.5576 C82.9192,39.7526 82.6632,39.8506 82.4072,39.8506 M77.4572,44.7996 C77.2012,44.7996 76.9462,44.7026 76.7502,44.5066 C76.3602,44.1166 76.3602,43.4836 76.7502,43.0926 L78.8722,40.9716 C79.2622,40.5806 79.8952,40.5806 80.2862,40.9716 C80.6772,41.3626 80.6772,41.9956 80.2862,42.3856 L78.1642,44.5066 C77.9692,44.7026 77.7132,44.7996 77.4572,44.7996 M72.5082,49.7496 C72.2522,49.7496 71.9962,49.6526 71.8012,49.4566 C71.4102,49.0666 71.4102,48.4336 71.8012,48.0426 L73.9222,45.9216 C74.3122,45.5316 74.9452,45.5316 75.3362,45.9216 C75.7262,46.3126 75.7262,46.9456 75.3362,47.3356 L73.2152,49.4566 C73.0192,49.6526 72.7632,49.7496 72.5082,49.7496\" id=\"Fill-12\" fill=\"#DFE4EC\"></path>\r\n                    <path d=\"M88.5,28 C86.294,28 84.5,29.794 84.5,32 C84.5,34.206 86.294,36 88.5,36 C90.706,36 92.5,34.206 92.5,32 C92.5,29.794 90.706,28 88.5,28 M88.5,37 C85.743,37 83.5,34.757 83.5,32 C83.5,29.243 85.743,27 88.5,27 C91.257,27 93.5,29.243 93.5,32 C93.5,34.757 91.257,37 88.5,37\" id=\"Fill-14\" fill=\"#FFC007\"></path>\r\n                    <path d=\"M74.353,84.4365 L33.5,84.4365 C32.4,84.4365 31.5,83.5365 31.5,82.4365 L31.5,25.4365 C31.5,24.3355 32.4,23.4365 33.5,23.4365 L74.353,23.4365 C75.453,23.4365 76.353,24.3355 76.353,25.4365 L76.353,82.4365 C76.353,83.5365 75.453,84.4365 74.353,84.4365\" id=\"Fill-16\" fill=\"#FFFFFF\"></path>\r\n                    <path d=\"M33.5,24.4365 C32.949,24.4365 32.5,24.8845 32.5,25.4365 L32.5,82.4365 C32.5,82.9885 32.949,83.4365 33.5,83.4365 L74.353,83.4365 C74.904,83.4365 75.353,82.9885 75.353,82.4365 L75.353,25.4365 C75.353,24.8845 74.904,24.4365 74.353,24.4365 L33.5,24.4365 Z M74.353,85.4365 L33.5,85.4365 C31.846,85.4365 30.5,84.0905 30.5,82.4365 L30.5,25.4365 C30.5,23.7825 31.846,22.4365 33.5,22.4365 L74.353,22.4365 C76.007,22.4365 77.353,23.7825 77.353,25.4365 L77.353,82.4365 C77.353,84.0905 76.007,85.4365 74.353,85.4365 L74.353,85.4365 Z\" id=\"Fill-18\" fill=\"#0859A3\"></path>\r\n                    <polygon id=\"Fill-20\" fill=\"#EDF2F7\" points=\"36.4341 28 63.4931 28 36.4931 55\"></polygon>\r\n                    <mask id=\"mask-2\" fill=\"white\">\r\n                        <use xlink:href=\"#path-1\"></use>\r\n                    </mask>\r\n                    <g id=\"Clip-23\"></g>\r\n                    <polygon id=\"Fill-22\" fill=\"#0859A3\" mask=\"url(#mask-2)\" points=\"44.853 68.437 67.977 68.437 67.977 66.437 44.853 66.437\"></polygon>\r\n                    <polygon id=\"Fill-24\" fill=\"#00ADFF\" mask=\"url(#mask-2)\" points=\"39.183 68.79 41.891 68.79 41.891 66.082 39.183 66.082\"></polygon>\r\n                    <polygon id=\"Fill-25\" fill=\"#0859A3\" mask=\"url(#mask-2)\" points=\"44.853 59.437 67.977 59.437 67.977 57.437 44.853 57.437\"></polygon>\r\n                    <polygon id=\"Fill-26\" fill=\"#00ADFF\" mask=\"url(#mask-2)\" points=\"39.183 59.79 41.891 59.79 41.891 57.082 39.183 57.082\"></polygon>\r\n                    <polygon id=\"Fill-27\" fill=\"#0859A3\" mask=\"url(#mask-2)\" points=\"44.853 50.437 67.977 50.437 67.977 48.437 44.853 48.437\"></polygon>\r\n                    <polygon id=\"Fill-28\" fill=\"#00ADFF\" mask=\"url(#mask-2)\" points=\"39.183 50.79 41.891 50.79 41.891 48.082 39.183 48.082\"></polygon>\r\n                    <polygon id=\"Fill-29\" fill=\"#0859A3\" mask=\"url(#mask-2)\" points=\"44.853 41.437 67.977 41.437 67.977 39.437 44.853 39.437\"></polygon>\r\n                    <polygon id=\"Fill-30\" fill=\"#00ADFF\" mask=\"url(#mask-2)\" points=\"39.183 41.79 41.891 41.79 41.891 39.082 39.183 39.082\"></polygon>\r\n                    <path d=\"M20.5,53 C18.294,53 16.5,54.794 16.5,57 C16.5,59.206 18.294,61 20.5,61 C22.706,61 24.5,59.206 24.5,57 C24.5,54.794 22.706,53 20.5,53 M20.5,62 C17.743,62 15.5,59.757 15.5,57 C15.5,54.243 17.743,52 20.5,52 C23.257,52 25.5,54.243 25.5,57 C25.5,59.757 23.257,62 20.5,62\" id=\"Fill-31\" fill=\"#00ADFF\" mask=\"url(#mask-2)\"></path>\r\n                </g>\r\n            </g>\r\n        </g>\r\n    </g>\r\n</svg>\r\n" }]
        }], ctorParameters: () => [] });

class AssetsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AssetsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: AssetsModule, declarations: [NoDetailsComponent], imports: [CommonModule], exports: [NoDetailsComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AssetsModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AssetsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        NoDetailsComponent
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        NoDetailsComponent
                    ]
                }]
        }] });

class OverlayService {
    constructor() {
        this.overlayWatcher = new BehaviorSubject(false);
    }
    open() {
        this.overlayWatcher.next(true);
    }
    close() {
        this.overlayWatcher.next(false);
    }
    toggle() {
        if (this.isOpen) {
            this.close();
        }
        else {
            this.open();
        }
    }
    onOpen(action) {
        this.overlayWatcher.pipe(filter(isOpen => isOpen === true)).subscribe(() => {
            this.isOpen = true;
            action();
        });
    }
    onClose(action) {
        this.overlayWatcher.pipe(filter(isOpen => isOpen === false)).subscribe(() => {
            this.isOpen = false;
            action();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: OverlayService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: OverlayService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: OverlayService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class OverlayComponent {
    constructor(_overlayService) {
        this._overlayService = _overlayService;
    }
    ngOnInit() {
        this._overlayService.onOpen(() => {
            this.isOpen = true;
        });
        this._overlayService.onClose(() => {
            this.isOpen = false;
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: OverlayComponent, deps: [{ token: OverlayService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: OverlayComponent, selector: "sk-overlay", ngImport: i0, template: "<div class=\"sk-overlay\" [hidden]=\"!isOpen\"></div>", styles: [":host .sk-overlay{position:absolute;inset:0;background:#00000080;z-index:1000}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: OverlayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-overlay', template: "<div class=\"sk-overlay\" [hidden]=\"!isOpen\"></div>", styles: [":host .sk-overlay{position:absolute;inset:0;background:#00000080;z-index:1000}\n"] }]
        }], ctorParameters: () => [{ type: OverlayService }] });

class PageHeaderNavItemComponent {
    constructor() {
        this.alerts = 0;
    }
    showAlerts() {
        return this.alerts > 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageHeaderNavItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PageHeaderNavItemComponent, selector: "[sk-page-header-nav-item]", inputs: { Label: ["label", "Label"], Link: ["link", "Link"], alerts: "alerts" }, ngImport: i0, template: "<li class=\"nav-item\">\r\n    <a [routerLink]=\"Link\" routerLinkActive=\"active\" class=\"align-items-center d-flex nav-link\">\r\n        {{ Label }}\r\n        <span *ngIf=\"showAlerts()\" class=\"badge rounded-pill badge-danger-bold  badge-sm ml-50\" [innerText]=\"alerts\"></span>\r\n    </a>\r\n\r\n</li>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i2.RouterLinkActive, selector: "[routerLinkActive]", inputs: ["routerLinkActiveOptions", "ariaCurrentWhenActive", "routerLinkActive"], outputs: ["isActiveChange"], exportAs: ["routerLinkActive"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageHeaderNavItemComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-page-header-nav-item]', template: "<li class=\"nav-item\">\r\n    <a [routerLink]=\"Link\" routerLinkActive=\"active\" class=\"align-items-center d-flex nav-link\">\r\n        {{ Label }}\r\n        <span *ngIf=\"showAlerts()\" class=\"badge rounded-pill badge-danger-bold  badge-sm ml-50\" [innerText]=\"alerts\"></span>\r\n    </a>\r\n\r\n</li>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { Label: [{
                type: Input,
                args: ['label']
            }], Link: [{
                type: Input,
                args: ['link']
            }], alerts: [{
                type: Input
            }] } });

class PageHeaderNavComponent {
    constructor() { }
    ngOnInit() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageHeaderNavComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PageHeaderNavComponent, selector: "[sk-page-header-nav]", host: { classAttribute: "nav" }, ngImport: i0, template: "<ng-content></ng-content>\r\n", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageHeaderNavComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-page-header-nav]', host: {
                        class: 'nav'
                    }, template: "<ng-content></ng-content>\r\n" }]
        }], ctorParameters: () => [] });

class PageHeaderToolbarComponent {
    constructor() { }
    ngOnInit() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageHeaderToolbarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PageHeaderToolbarComponent, selector: "[sk-page-header-toolbar]", host: { classAttribute: "d-flex" }, ngImport: i0, template: "<ng-content></ng-content>", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageHeaderToolbarComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-page-header-toolbar]', host: {
                        'class': 'd-flex'
                    }, template: "<ng-content></ng-content>" }]
        }], ctorParameters: () => [] });

class PageHeaderComponent {
    constructor() {
        this._skHidden = true;
        this.hideSearch = false;
        this.hideSettings = false;
    }
    set skHidden(value) {
        this._skHidden = typeof value !== 'undefined' && value === false;
    }
    get skHidden() {
        return this._skHidden;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PageHeaderComponent, selector: "[sk-page-header]", inputs: { skHidden: "skHidden", Search: ["search", "Search"], hideSearch: "hideSearch", hideSettings: "hideSettings" }, ngImport: i0, template: "<div class=\"sk-page-header header-light\">\r\n    <div class=\"sk-navbar-primary\" [class.secondary-none]=\"!skHidden\">\r\n        <ng-content select=\"h3\"></ng-content>\r\n\r\n        <div class=\"d-flex\">\r\n            <ng-content select=\"button\"></ng-content>\r\n            <button *ngIf=\"!hideSettings\" sk-icon-button icon=\"settings\"></button>\r\n            <sk-basic-input *ngIf=\"!hideSearch\" [(value)]=\"Search\" placeholder=\"Search\"></sk-basic-input>\r\n        </div>\r\n    </div>\r\n\r\n    <div *ngIf=\"skHidden\" class=\"sk-navbar-secondary\">\r\n        <ng-content select=\"ul\"></ng-content>\r\n        <ng-content select=\"div\"></ng-content>\r\n    </div>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconButtonComponent, selector: "button[sk-icon-button]", inputs: ["icon", "active", "toggle", "color"] }, { kind: "component", type: BasicInputComponent, selector: "sk-basic-input", inputs: ["placeholder", "disabled", "value"], outputs: ["valueChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-page-header]', template: "<div class=\"sk-page-header header-light\">\r\n    <div class=\"sk-navbar-primary\" [class.secondary-none]=\"!skHidden\">\r\n        <ng-content select=\"h3\"></ng-content>\r\n\r\n        <div class=\"d-flex\">\r\n            <ng-content select=\"button\"></ng-content>\r\n            <button *ngIf=\"!hideSettings\" sk-icon-button icon=\"settings\"></button>\r\n            <sk-basic-input *ngIf=\"!hideSearch\" [(value)]=\"Search\" placeholder=\"Search\"></sk-basic-input>\r\n        </div>\r\n    </div>\r\n\r\n    <div *ngIf=\"skHidden\" class=\"sk-navbar-secondary\">\r\n        <ng-content select=\"ul\"></ng-content>\r\n        <ng-content select=\"div\"></ng-content>\r\n    </div>\r\n</div>\r\n" }]
        }], propDecorators: { skHidden: [{
                type: Input
            }], Search: [{
                type: Input,
                args: ['search']
            }], hideSearch: [{
                type: Input
            }], hideSettings: [{
                type: Input
            }] } });

class PagePanelItemComponent {
    constructor() { }
    ngOnInit() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PagePanelItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PagePanelItemComponent, selector: "[sk-page-panel-item]", inputs: { skTitle: "skTitle" }, host: { classAttribute: "sk-panel-card" }, ngImport: i0, template: "<p>{{ skTitle }}</p>\r\n<ng-content></ng-content>\r\n", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PagePanelItemComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-page-panel-item]', host: {
                        class: 'sk-panel-card'
                    }, template: "<p>{{ skTitle }}</p>\r\n<ng-content></ng-content>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { skTitle: [{
                type: Input
            }] } });

class PagePanelService {
    constructor() {
        this._panelStream = new Subject();
        this._panelStreamObservable = this._panelStream.asObservable();
        this._manageStream = new Subject();
        this._manageStreamObservable = this._manageStream.asObservable();
    }
    onStreamEvent(next, error) {
        this._panelStreamObservable.subscribe({ next, error });
    }
    onManageEvent(next, error) {
        this._manageStreamObservable.subscribe({ next, error });
    }
    add(title, content, type) {
        this._panelStream.next({
            Panel: {
                Title: title,
                Content: content
            },
            Type: type
        });
    }
    open(type) {
        this._manageStream.next({
            IsOpen: true,
            Type: type
        });
    }
    close(type) {
        this._manageStream.next({
            IsOpen: false,
            Type: type
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PagePanelService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PagePanelService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PagePanelService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });
class PanelStreamEvent {
}
class PanelManageStreamEvent {
}

class PagePanelComponent {
    get isClosed() { return !this.open; }
    constructor(pagePanelService) {
        this.pagePanelService = pagePanelService;
        this.class = 'sk-panel-left sk-panel-sm';
        this.open = false;
        this.Panels = [];
    }
    ngOnInit() {
        this.pagePanelService.onManageEvent((event) => {
            if (this.name === event.Type) {
                this.open = event.IsOpen;
            }
            else {
                this.open = false;
            }
        }, (error) => {
            console.error(error);
        });
        this.pagePanelService.onStreamEvent((event) => {
            if (this.name === event.Type) {
                this.Panels.push(event.Panel);
            }
        }, (error) => {
            console.error(error);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PagePanelComponent, deps: [{ token: PagePanelService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PagePanelComponent, selector: "[sk-page-panel]", inputs: { name: "name", open: "open" }, host: { properties: { "class": "this.class", "class.d-none": "this.isClosed" } }, ngImport: i0, template: "<div class=\"sk-panel-body\">\r\n    <div *ngFor=\"let panel of Panels\" sk-page-panel-item [skTitle]=\"panel.Title\">\r\n        <p>{{ panel.Content }}</p>\r\n    </div>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: PagePanelItemComponent, selector: "[sk-page-panel-item]", inputs: ["skTitle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PagePanelComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-page-panel]', template: "<div class=\"sk-panel-body\">\r\n    <div *ngFor=\"let panel of Panels\" sk-page-panel-item [skTitle]=\"panel.Title\">\r\n        <p>{{ panel.Content }}</p>\r\n    </div>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: PagePanelService }], propDecorators: { name: [{
                type: Input
            }], class: [{
                type: HostBinding
            }], open: [{
                type: Input
            }], isClosed: [{
                type: HostBinding,
                args: ['class.d-none']
            }] } });

class PanelViewportOutlet {
    constructor(viewContainerRef) {
        this.viewContainerRef = viewContainerRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PanelViewportOutlet, deps: [{ token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: PanelViewportOutlet, selector: "[skPanelViewOutlet]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PanelViewportOutlet, decorators: [{
            type: Directive,
            args: [{
                    selector: '[skPanelViewOutlet]'
                }]
        }], ctorParameters: () => [{ type: i0.ViewContainerRef }] });

class OperationViewBase {
    get style() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: OperationViewBase, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: OperationViewBase, host: { properties: { "class.d-flex": "this.style" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: OperationViewBase, decorators: [{
            type: Directive
        }], propDecorators: { style: [{
                type: HostBinding,
                args: ['class.d-flex']
            }] } });

class ScopedTaskHandle {
    constructor(taskManagerService) {
        this.taskManagerService = taskManagerService;
    }
    async openTask(componentType, model) {
        const task = await this.taskManagerService.openTask(componentType, model);
        this.currentTask = task;
        return task.result;
    }
    destroy() {
        if (this.currentTask) {
            this.currentTask.abort();
        }
    }
}

var OperationCancelledType;
(function (OperationCancelledType) {
    OperationCancelledType[OperationCancelledType["Implicit"] = 0] = "Implicit";
    OperationCancelledType[OperationCancelledType["Explicit"] = 1] = "Explicit"; // Explicit cancellation of an operation (e.g. the User clicked on the 'X')
})(OperationCancelledType || (OperationCancelledType = {}));

/**
 * This factory service generates "ViewOperations" which are components that bind
 * together an Operation or some unit of work with a single Angular component.
 */
class TaskComponentFactoryService {
    constructor(componentFactory, injector) {
        this.componentFactory = componentFactory;
        this.injector = injector;
    }
    // tslint:disable-next-line: max-line-length
    create(componentType, operation, model, taskManagerService) {
        const factory = this.componentFactory.resolveComponentFactory(componentType);
        const ref = factory.create(this.injector);
        ref.instance.data = model;
        ref.instance.operation = operation;
        ref.instance.taskManagerService = taskManagerService;
        // If angular destroys the reference, cancel the operation
        ref.onDestroy(() => {
            operation.cancel(OperationCancelledType.Implicit);
        });
        return ref;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TaskComponentFactoryService, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TaskComponentFactoryService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TaskComponentFactoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i0.ComponentFactoryResolver }, { type: i0.Injector }] });

class OperationCancelledError extends Error {
    constructor(type) {
        super('The Operation was cancelled');
        this.type = type;
    }
    static suppressOnCancel(err) {
        if (!(err instanceof OperationCancelledError)) {
            throw err;
        }
    }
    static wasCancelledBySystem(err) {
        return (err instanceof OperationCancelledError) ? err.type === OperationCancelledType.Implicit : false;
    }
    static wasCancelledByUser(err) {
        return (err instanceof OperationCancelledError) ? err.type === OperationCancelledType.Explicit : false;
    }
}

/**
 * An operation is a slight abstraction around a promise with the
 * slight difference in that an Operation has the idea of not only
 * an output value, but also has a single input value.
 *
 * This concept allows you to string together Operations and make
 * the input to one Operation available to the component that consumes
 * the operation
 */
class Operation {
    constructor() {
        this.next = null;
        this.unitOfWork = new Promise((resolve, reject) => {
            this.resolver = resolve;
            this.rejector = reject;
        });
    }
    complete(result) {
        this.resolver(result);
    }
    async cancel(type = OperationCancelledType.Explicit) {
        if (this.next) {
            await this.next.cancel(type);
        }
        this.rejector(new OperationCancelledError(type));
    }
    fail(error) {
        this.rejector(error);
    }
    get result() {
        return this.unitOfWork;
    }
}

/**
 * The basic abstraction of a Task is defined as a collection of Operations
 * that are prescriptively iterated on in order to complete some arbitrary
 * unit of work.  Although a Task can be conceptualized as a tree of interdependent
 * Operations, the logical collection that is referenced in memory is only a single path
 * through said tree.
 *
 */
class Task {
    constructor() {
        this.root = null;
    }
    /**
     * Initialize the Task with a root operation
     * @param operation
     */
    init(operation) {
        this.root = operation;
        this.root.result.finally(() => {
            this.root = null;
        });
    }
    /**
     * Appends an operation to the Task which provides this task
     * a means of cancelling all of the operations if needed.
     * @template TIn - The 'data' type for the operation's input
     * @param operation - The operation to add to the Task
     */
    append(operation) {
        const currentOp = this.getCurrent();
        currentOp.next = operation;
        operation.result.finally(() => {
            currentOp.next = null;
        });
    }
    /**
     * Once the initial Operation completes, we know that the Task
     * itself is ready to complete as well
     */
    get result() {
        return this.root ? this.root.result : Promise.resolve(null);
    }
    /**
     * Cancel an entire Task which will cancel each
     * underlying operation starting with the active
     * operation and working its way back to the root
     * operation. By doing this, it allows dependent operations
     * to cancel before the dependers which means the "dependers"
     * can gracefully handle the cancel if needed
     */
    async cancel(reason) {
        if (this.root) {
            await this.root.cancel(reason);
        }
        return this.result;
    }
    /**
     * Get the current operation
     */
    getCurrent() {
        let cur = this.root;
        while (cur && cur.next) {
            cur = cur.next;
        }
        return cur;
    }
    get length() {
        if (this.root == null) {
            return 0;
        }
        let count = 1;
        let cur = this.root;
        while (cur.next) {
            count++;
            cur = cur.next;
        }
        return count;
    }
}

class ViewTask extends Task {
    get componentRef() {
        return this.componentStack[this.componentStack.length - 1];
    }
    constructor(taskComponentFactory, taskManagerService, componentType) {
        super();
        this.taskComponentFactory = taskComponentFactory;
        this.taskManagerService = taskManagerService;
        this.componentType = componentType;
        this.pState = 'closed';
        this.model = null;
        this.$finished = new Subject();
        this.$componentRef = new Subject();
        this.componentStack = [];
    }
    /**
     * Gets the state of the ViewTask which can be 'loaded', 'closed', or 'pending'
     *
     * closed - The task has no operations or component references associated with it.
     *          All tasks start out this way until you configure it with some values
     * pending - The task is staged and ready to be rendered. This means that a componentType and
     *           a model have been set (model is optional though)
     * loaded - The task has compiled components for the latest operation and is set as the current
     */
    get state() {
        return this.pState;
    }
    /**
     * Closes view task completely as an "abort" or implicit cancellation. Use this method
     * to trigger a cancellation that originated from the system
     */
    async abort() {
        // 1) Cancel all operations and then emit event to observers
        await this.cancel(OperationCancelledType.Implicit);
        // 2) Update task state to 'closed'
        this.pState = 'closed';
    }
    /**
     * Closes view task completely as a "close" or explicit cancellation.  Use this method
     * to trigger a cancellation that originated from a User action.
     */
    async close() {
        // 1) Cancel all operations and then emit event to observers
        await this.cancel(OperationCancelledType.Explicit);
        // 2) Update task state to 'closed'
        this.pState = 'closed';
    }
    /**
     * Cancels the current operation wich will essentially "go back" to the
     * previous operation if there is one.
     */
    async cancelCurrent() {
        await this.getCurrent().cancel();
    }
    configure(componentType, model) {
        // If the component type changes, update it
        if (componentType) {
            this.componentType = componentType;
        }
        // If the component type or model changes, update the model
        if (componentType || model) {
            this.model = model;
        }
        this.pState = 'pending';
        return this;
    }
    initialize(componentType, model) {
        const root = new Operation();
        this.init(root);
        const componentToRender = componentType || this.componentType;
        const modelToRender = model || this.model;
        const componentRef = this.taskComponentFactory.create(componentToRender, root, modelToRender, this.taskManagerService);
        root.result.finally(() => {
            this.$finished.next();
            this.pState = 'closed';
            this.componentStack
                .pop()
                .destroy();
        });
        this.componentStack.push(componentRef);
        this.pState = 'loaded';
        this.render();
    }
    render() {
        this.$componentRef.next(this.componentRef);
    }
    async add(componentType, model) {
        // Generate a new ViewOperation
        const operation = new Operation();
        const componentRef = this.taskComponentFactory.create(componentType, operation, model, this.taskManagerService);
        // Append the current tasks with the new operation
        this.append(operation);
        // Let any observers know that the current operation was updated
        this.componentStack.push(componentRef);
        this.render();
        // We have not added the newest operation to the stack and told any observers to render this operation's component.
        // We will now wait for the operation to complete and if it is either 1) Completed or 2) Cancelled we will dislay the next
        // operation's component. However, if a full ABORT signal is sent from a client, we don't want to render anything, so we
        // have to explicitly check if the Cancel signal being made in the catch block is a full on ABORT so we can keep from
        // trying to render further operations.
        try {
            const output = await operation.result;
            this.componentStack
                .pop()
                .destroy();
            this.render();
            return output;
        }
        catch (e) {
            // If we cancel the operation and it wasn't aborted, render the next operation
            if (!OperationCancelledError.wasCancelledBySystem(e)) {
                // Destroy the current component
                this.componentStack
                    .pop()
                    .destroy();
                this.render();
            }
            // rethrow so the caller can handle cancels
            throw e;
        }
    }
    hasParentOperation() {
        return this.length > 1;
    }
    /**
     * Subscribe to when the current operation has changed
     * @param observer The handler to the operation changing
     * @returns The Subscription to the observable
     */
    onComponentChanged(next) {
        return this.$componentRef.subscribe(next);
    }
    onFinish(next) {
        return this.$finished.subscribe(next);
    }
}

const defaultOptions = {
    hideCloseButton: false
};
class TaskManagerService {
    constructor(taskComponentFactoryService) {
        this.taskComponentFactoryService = taskComponentFactoryService;
        // This needs to be a ReplaySubject in case a task is started before any subscriptions occur. This
        // is likely to happen if a task is started in a constructor, as PanelComponent subscribes in ngOnInit.
        this.$components = new ReplaySubject(1);
        this.universalTasks = [];
        this.$current = new Subject();
        this.currentTask = null;
        this.taskOptions = defaultOptions;
    }
    /**
     * Sets the options
     * @param options The options you want to set
     */
    setOptions(options) {
        this.taskOptions = { ...this.taskOptions, ...options };
    }
    /**
     * Gets the options about how Tasks/Operations are configured
     * @returns The options
     */
    getOptions() {
        return this.taskOptions;
    }
    /**
     * Provide way to know when the state of the 'current' task
     * has changed. The two states are either 'open' or 'closed' and are
     * represented by a boolean: open is true and closed is false
     * @param observer the handler for when the current task is changed
     * @returns the Subscription
     */
    onCurrentTaskChanged(observer) {
        return this.$current.subscribe(observer);
    }
    /**
     * Registers a new UniversalTask with the current application which allows for a statically
     * lifecycled task to be available for the entirety of the application to manage
     *
     * @param taskName The name of the universal task being generated. Making these
     * task names constants in your consuming application is important to ensure you
     * are managing the same tasks
     * @param defaultComponentType The type of the component we want to use in the initial instantiation of the task's view
     * @returns The Universal Task being registered
     */
    registerUniversalTask(taskName, defaultComponentType) {
        const task = new ViewTask(this.taskComponentFactoryService, this, defaultComponentType);
        this.universalTasks.push({
            name: taskName,
            task
        });
        task.onComponentChanged(component => {
            this.$components.next(component);
        });
        task.onFinish(() => {
            if (this.currentTask === task) {
                this.clearActiveTask();
            }
        });
        return task;
    }
    /**
     * Closes the task by first cancelling all of the operations
     * and then clearing the active task if this task was the one active.
     * Use this method to trigger a cancellation from a User
     * @param taskName The name of the task to close
     */
    async closeTask(taskName) {
        const target = this.getUniversalTask(taskName);
        // Unload the task
        await target.close();
        // If it was the active one, clear out the active task
        if (this.isTaskActive(taskName)) {
            this.clearActiveTask();
        }
    }
    /**
     * Aborts the task by first cancelling all of the operations
     * and then clearing the active task if this task was the one active.
     * Use this method to trigger a cancellation needed by the system
     * @param taskName The name of the task to close
     */
    async abortTask(taskName) {
        const target = this.getUniversalTask(taskName);
        // Unload the task
        await target.abort();
        // If it was the active one, clear out the active task
        if (this.isTaskActive(taskName)) {
            this.clearActiveTask();
        }
    }
    configureTask(taskName, model, componentType) {
        // Get the target task
        const target = this.getUniversalTask(taskName);
        target.configure(componentType, model);
    }
    /**
     * Activates a universal task which will either render an existing task if there is one
     * already loaded, otherwise it will spin up a new Task using the component and model of
     * whatever was previously set
     * @param taskName
     * @param [model]
     */
    async activateTask(taskName, model, componentType) {
        // Get the target task
        const target = this.getUniversalTask(taskName);
        // If there is already a task rendered and its not a universal task, close it
        if (this.currentTask && !this.universalTasks.find(t => t.task === this.currentTask)) {
            await this.currentTask.abort();
        }
        // If the task we want to activate is already loaded,
        // leave it alone and simply set it as the current task
        if (target.state !== 'loaded') {
            // We can now safely initialize the task and render the desired state
            target.initialize(componentType, model);
        }
        else if (model || componentType) {
            // Force the existing task to abort
            await target.abort();
            // Initialize the new component and/or model
            target.initialize(componentType, model);
        }
        // Make this task the active one
        this.setCurrentTask(target);
        target.render();
        return target.result;
    }
    /**
     * Makes it easy to toggle a universal task into either the rendered state or pending state
     * @param taskName
     */
    async toggleUniversal(taskName) {
        if (this.isTaskActive(taskName)) {
            this.clearActiveTask();
        }
        else {
            return await this.activateTask(taskName);
        }
    }
    /**
     * Clears the active task without actually closing any tasks
     */
    clearActiveTask() {
        this.currentTask = null;
        this.$current.next(false);
    }
    /**
     * Determines whether the task of the name provided is currently in the active state
     * @param taskName The 'name' of the task we want to check
     * @returns The boolean of whether or not this task is active or not
     */
    isTaskActive(taskName) {
        if (!this.currentTask) {
            return false;
        }
        if (!taskName) {
            return this.currentTask != null;
        }
        const handle = this.getUniversalTask(taskName);
        return handle === this.currentTask;
    }
    /**
     * Determines whether task is loaded into memory or not
     * @param taskName The name of the task to check
     * @returns a boolean indicating if its loaded
     */
    isTaskLoaded(taskName) {
        return this.getUniversalTask(taskName).state === 'loaded';
    }
    /**
     * Appends a new operation to the current one
     * @template TModel The type of the data model being passed into the component
     * @template TResult The type fo the return result being yielded by the component
     * @param componentType The Type<any> of the component being generated
     * @param [model] The instance of the TModel
     * @returns The Promise of the operation being added
     */
    appendToActive(componentType, model) {
        const task = this.getActiveTask();
        return task.add(componentType, model);
    }
    /**
     * Subscribes to the latest ComponentRef that is targeted by all of the tasks
     * @param next the handler
     * @returns Subscription
     */
    subscribeToComponent(next) {
        return this.$components.subscribe(next);
    }
    /**
     * Returns the active task or 'null' if there is no active task
     */
    getActiveTask() {
        return this.currentTask;
    }
    /**
     * Gets UniversalTask by name
     * @param taskName The name of the task wanting to get a handle on
     * @returns The Universal Task
     */
    getUniversalTask(taskName) {
        const targetTask = this.universalTasks.find(t => t.name === taskName);
        if (!targetTask) {
            throw new Error(`Task named [${taskName}] was not found`);
        }
        return targetTask.task;
    }
    /**
     * Displays an isolated task that has no scope and needs to be managed
     * manually by the developer. Opening up multiple of these will overwrite
     * the previous task in favor of the new one
     * @template TInput The data type of the 'model'
     * @template TResult The data type of the return result
     * @param componentType The component to be rendered
     * @param [model] The 'model' we want to use as input to the component
     * @returns The task that you just opened
     */
    async openTask(componentType, model) {
        const task = new ViewTask(this.taskComponentFactoryService, this);
        // If there is already a task rendered and its not a universal task, close it
        if (this.currentTask && !this.universalTasks.find(t => t.task === this.currentTask)) {
            await this.currentTask.abort();
        }
        this.setCurrentTask(task);
        task.onComponentChanged(component => {
            this.$components.next(component);
        });
        task.onFinish(() => {
            this.clearActiveTask();
        });
        task.initialize(componentType, model);
        return task;
    }
    /**
     * This method allows you to create a "ScopedTaskHandle" which is just a
     * lightweight wrapper around a single Task handle. It is used by the "ScopedTaskBase"
     * in order to easily align the lifecycle of a Task with that of its governing Component
     *
     * @returns The ScopedTaskHandle
     */
    generateScope() {
        return new ScopedTaskHandle(this);
    }
    abortAllTasks() {
        this.clearActiveTask();
        this.universalTasks.forEach(task => {
            this.abortTask(task.name);
        });
    }
    setCurrentTask(task) {
        this.currentTask = task;
        this.$current.next(true);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TaskManagerService, deps: [{ token: TaskComponentFactoryService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TaskManagerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TaskManagerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: TaskComponentFactoryService }] });

var PanelSize;
(function (PanelSize) {
    PanelSize["lg"] = "lg";
    PanelSize["xl"] = "xl";
    PanelSize["xxl"] = "xxl";
})(PanelSize || (PanelSize = {}));

class SkyKickPanelViewBase {
    get class() {
        return this.classes;
    }
    constructor(globalTaskManagerService) {
        this.globalTaskManagerService = globalTaskManagerService;
        /**
         * Removes the class "sk-responsive"  when set to `false`.
         * null or true will add the class.
         *  @defaultValue `true`
         */
        this.responsive = true;
        /**
         * Adds the necessary class name depending on the values set
         * @see {@link PanelSize} for the possible values.
         */
        this.size = PanelSize.xl;
    }
    ngOnInit() {
        this.taskManagerService = this.taskManagerServiceOverride || this.globalTaskManagerService;
        if (!(this.size in PanelSize)) {
            this.size = PanelSize.xl;
            console.error('Not a valid size. Falling back to XL');
        }
        const tempClasses = [
            'sk-panel-right',
            `panel-${this.size}`
        ];
        if (this.responsive || this.responsive === null) {
            tempClasses.push('sk-responsive');
        }
        this.classes = tempClasses.join(' ');
    }
    isFooterUsed() {
        const children = this.footerRef.nativeElement.children;
        return (children.length ? children[0].children.length : false);
    }
    isTitleUsed() {
        return this.skTitle || !this.headerRef.nativeElement.children.length;
    }
    canGoBack() {
        if (this.taskManagerService.isTaskActive()) {
            return this.taskManagerService.getActiveTask().hasParentOperation();
        }
        else {
            return false;
        }
    }
    goBack() {
        this.taskManagerService.getActiveTask().cancelCurrent();
    }
    closeAll() {
        if (this.isClosable()) {
            this.taskManagerService.getActiveTask().close();
        }
    }
    isClosable() {
        const opts = this.taskManagerService.getOptions();
        return !opts.hideCloseButton;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickPanelViewBase, deps: [{ token: TaskManagerService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: SkyKickPanelViewBase, inputs: { skTitle: "skTitle", subtitle: "subtitle", responsive: "responsive", size: "size", taskManagerServiceOverride: "taskManagerServiceOverride" }, host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "headerRef", first: true, predicate: ["headerRef"], descendants: true, static: true }, { propertyName: "footerRef", first: true, predicate: ["footerRef"], descendants: true, static: true }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickPanelViewBase, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: TaskManagerService }], propDecorators: { skTitle: [{
                type: Input
            }], subtitle: [{
                type: Input
            }], responsive: [{
                type: Input
            }], size: [{
                type: Input
            }], taskManagerServiceOverride: [{
                type: Input
            }], class: [{
                type: HostBinding,
                args: ['class']
            }], headerRef: [{
                type: ViewChild,
                args: ['headerRef', { static: true }]
            }], footerRef: [{
                type: ViewChild,
                args: ['footerRef', { static: true }]
            }] } });

class ShowTooltipPipe {
    transform(textToShow, scrollWidth, clientWidth) {
        return scrollWidth > clientWidth
            ? textToShow
            : null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ShowTooltipPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: ShowTooltipPipe, name: "showTooltip" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ShowTooltipPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'showTooltip'
                }]
        }] });

class PanelViewComponent extends SkyKickPanelViewBase {
    constructor(taskManagerService, changeDetector) {
        super(taskManagerService);
        this.changeDetector = changeDetector;
    }
    ngAfterViewChecked() {
        this.changeDetector.detectChanges();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PanelViewComponent, deps: [{ token: TaskManagerService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PanelViewComponent, selector: "aside[sk-panel-view]", usesInheritance: true, ngImport: i0, template: "<div class=\"sk-panel-header\" [class.heading-only]=\"!subtitle\">\r\n  <div class=\"sk-header-row\">\r\n    <button type=\"button\" class=\"btn sk-btn-icon ps-0\" [hidden]=\"!canGoBack()\" (click)=\"goBack()\">\r\n        <i class=\"material-icons md-22\">arrow_back</i>\r\n    </button>\r\n\r\n    <h3 *ngIf=\"isTitleUsed()\" #header\r\n        class=\"text-truncate\"\r\n        [ngbTooltip]=\"skTitle | showTooltip: header.scrollWidth: header.clientWidth\"\r\n        [openDelay]=\"300\">{{ skTitle }}</h3>\r\n    <div #headerRef [hidden]=\"isTitleUsed()\">\r\n        <ng-content select=\"header\"></ng-content>\r\n    </div>\r\n\r\n    <button type=\"button\" class=\"btn sk-btn-icon btn-close\" [class.invisible]=\"!isClosable()\" (click)=\"closeAll()\">\r\n    </button>\r\n  </div>\r\n\r\n  <ng-content select=\".panel-search\"></ng-content>\r\n\r\n  <div *ngIf=\"subtitle\" class=\"sk-navbar-panel\">\r\n      <span class=\"font-weight-bold\">{{subtitle}}</span>\r\n  </div>\r\n</div>\r\n<ng-content select=\".sk-panel-content\"></ng-content>\r\n<div class=\"sk-panel-footer\" #footerRef [hidden]=\"!isFooterUsed()\">\r\n    <ng-content select=\"footer\"></ng-content>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "popperOptions", "triggers", "positionTarget", "container", "disableTooltip", "tooltipClass", "tooltipContext", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "pipe", type: ShowTooltipPipe, name: "showTooltip" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PanelViewComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aside[sk-panel-view]', template: "<div class=\"sk-panel-header\" [class.heading-only]=\"!subtitle\">\r\n  <div class=\"sk-header-row\">\r\n    <button type=\"button\" class=\"btn sk-btn-icon ps-0\" [hidden]=\"!canGoBack()\" (click)=\"goBack()\">\r\n        <i class=\"material-icons md-22\">arrow_back</i>\r\n    </button>\r\n\r\n    <h3 *ngIf=\"isTitleUsed()\" #header\r\n        class=\"text-truncate\"\r\n        [ngbTooltip]=\"skTitle | showTooltip: header.scrollWidth: header.clientWidth\"\r\n        [openDelay]=\"300\">{{ skTitle }}</h3>\r\n    <div #headerRef [hidden]=\"isTitleUsed()\">\r\n        <ng-content select=\"header\"></ng-content>\r\n    </div>\r\n\r\n    <button type=\"button\" class=\"btn sk-btn-icon btn-close\" [class.invisible]=\"!isClosable()\" (click)=\"closeAll()\">\r\n    </button>\r\n  </div>\r\n\r\n  <ng-content select=\".panel-search\"></ng-content>\r\n\r\n  <div *ngIf=\"subtitle\" class=\"sk-navbar-panel\">\r\n      <span class=\"font-weight-bold\">{{subtitle}}</span>\r\n  </div>\r\n</div>\r\n<ng-content select=\".sk-panel-content\"></ng-content>\r\n<div class=\"sk-panel-footer\" #footerRef [hidden]=\"!isFooterUsed()\">\r\n    <ng-content select=\"footer\"></ng-content>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: TaskManagerService }, { type: i0.ChangeDetectorRef }] });

class EmptyPanelComponent extends OperationViewBase {
    constructor() {
        super();
    }
    cancel() {
        this.operation.cancel();
    }
    complete() {
        this.operation.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: EmptyPanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: EmptyPanelComponent, selector: "sk-empty-panel", usesInheritance: true, ngImport: i0, template: "<aside sk-panel-view skTitle=\"Details\">\r\n  <div class=\"sk-panel-content\" style=\"overflow: initial\">\r\n    <div style=\"margin-bottom: 150px\" class=\"d-flex flex-column justify-content-center align-items-center h-100\">\r\n        <sk-no-details></sk-no-details>\r\n        <h4>No details available.</h4>\r\n    </div>\r\n  </div>\r\n  <footer>\r\n    <button class=\"btn btn-outline-secondary\" (click)=\"cancel()\">Cancel</button>\r\n  </footer>\r\n</aside>\r\n\r\n", styles: [""], dependencies: [{ kind: "component", type: NoDetailsComponent, selector: "sk-no-details" }, { kind: "component", type: PanelViewComponent, selector: "aside[sk-panel-view]" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: EmptyPanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-empty-panel', template: "<aside sk-panel-view skTitle=\"Details\">\r\n  <div class=\"sk-panel-content\" style=\"overflow: initial\">\r\n    <div style=\"margin-bottom: 150px\" class=\"d-flex flex-column justify-content-center align-items-center h-100\">\r\n        <sk-no-details></sk-no-details>\r\n        <h4>No details available.</h4>\r\n    </div>\r\n  </div>\r\n  <footer>\r\n    <button class=\"btn btn-outline-secondary\" (click)=\"cancel()\">Cancel</button>\r\n  </footer>\r\n</aside>\r\n\r\n" }]
        }], ctorParameters: () => [] });

class PanelSearchComponent {
    constructor() {
        this.withNav = false;
        this.searchChanged = new EventEmitter();
    }
    ngOnInit() {
    }
    ngOnChanges(changes) {
        this.searchChanged.emit(this.search);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PanelSearchComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PanelSearchComponent, selector: "sk-panel-search", inputs: { withNav: "withNav", search: "search", placeholder: "placeholder" }, outputs: { searchChanged: "searchChanged" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"sk-panel-search\" [ngClass]=\"{'with-nav': withNav}\">\r\n  <div class=\"sk-input-icon w-100\">\r\n    <input type=\"text\" class=\"form-control btn-block\" [(ngModel)]=\"search\" placeholder=\"{{ placeholder }}\"><i class=\"material-icons md-18\">search</i>\r\n  </div>\r\n</div>", styles: [""], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PanelSearchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-panel-search', template: "<div class=\"sk-panel-search\" [ngClass]=\"{'with-nav': withNav}\">\r\n  <div class=\"sk-input-icon w-100\">\r\n    <input type=\"text\" class=\"form-control btn-block\" [(ngModel)]=\"search\" placeholder=\"{{ placeholder }}\"><i class=\"material-icons md-18\">search</i>\r\n  </div>\r\n</div>" }]
        }], ctorParameters: () => [], propDecorators: { withNav: [{
                type: Input
            }], search: [{
                type: Input
            }], searchChanged: [{
                type: Output
            }], placeholder: [{
                type: Input
            }] } });

class SkyKickTabViewContainer {
    applyData(data) {
        this.Data = data;
    }
}

class PanelTabViewComponent extends SkyKickPanelViewBase {
    constructor(changeDetector, componentFactoryResolver, taskManagerService) {
        super(taskManagerService);
        this.changeDetector = changeDetector;
        this.componentFactoryResolver = componentFactoryResolver;
    }
    ngOnInit() {
        super.ngOnInit();
        let view = null;
        // If the default view is provided, try to use that
        if (this.default) {
            view = this.views.find(v => v.name === this.default);
        }
        // Otherwise default to the first view in the list
        if (!view) {
            view = this.views[0];
        }
        this.navigate(view);
    }
    ngAfterViewChecked() {
        this.changeDetector.detectChanges();
    }
    navigate(view) {
        this.current = view;
        const factory = this.componentFactoryResolver.resolveComponentFactory(this.current.component);
        const component = factory.create(this.outlet.viewContainerRef.parentInjector);
        try {
            component.instance.applyData(this.current.model);
        }
        catch (e) {
            console.error(e);
            throw new Error(`Make sure that the component you are trying to render inherits from ${SkyKickTabViewContainer.name}`);
        }
        this.outlet.viewContainerRef.clear();
        this.outlet.viewContainerRef.insert(component.hostView);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PanelTabViewComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ComponentFactoryResolver }, { token: TaskManagerService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PanelTabViewComponent, selector: "aside[sk-panel-tab-view]", inputs: { views: "views", default: "default" }, viewQueries: [{ propertyName: "outlet", first: true, predicate: PanelViewportOutlet, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"sk-panel-header\">\r\n  <div class=\"sk-header-row\">\r\n    <button type=\"button\" class=\"btn sk-btn-icon ps-0\" [hidden]=\"!canGoBack()\" (click)=\"goBack()\">\r\n      <i class=\"material-icons md-22\">arrow_back</i>\r\n    </button>\r\n\r\n    <h3 *ngIf=\"isTitleUsed()\" #header\r\n      class=\"text-truncate\"\r\n      [ngbTooltip]=\"skTitle | showTooltip: header.scrollWidth: header.clientWidth\"\r\n      [openDelay]=\"300\">{{ skTitle }}</h3>\r\n    <div #headerRef [hidden]=\"isTitleUsed()\">\r\n        <ng-content select=\"header\"></ng-content>\r\n    </div>\r\n\r\n    <button type=\"button\" class=\"btn sk-btn-icon btn-close\" [class.invisible]=\"!isClosable()\" (click)=\"closeAll()\">\r\n    </button>\r\n  </div>\r\n\r\n  <div class=\"sk-navbar-panel\">\r\n    <ul class=\"nav sk-tabs-panel\">\r\n      <li class=\"nav-item\" *ngFor=\"let view of views\" [id]=\"'sk-nav' + view.name\">\r\n        <a class=\"nav-link\" [class.active]=\"current.name == view.name\"\r\n                            [class.disabled]=\"view.isDisabled\"\r\n                            [class.d-none]=\"view.isHidden\" tabindex\r\n          (click)=\"navigate(view)\">{{ view.display$ | async }}</a>\r\n      </li>\r\n    </ul>\r\n  </div>\r\n\r\n  <ng-content select=\".panel-search\"></ng-content>\r\n</div>\r\n<div class=\"sk-panel-content\">\r\n  <ng-template skPanelViewOutlet></ng-template>\r\n</div>\r\n<div class=\"sk-panel-footer\" #footerRef [hidden]=\"!isFooterUsed()\">\r\n    <ng-content select=\"footer\"></ng-content>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "popperOptions", "triggers", "positionTarget", "container", "disableTooltip", "tooltipClass", "tooltipContext", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "directive", type: PanelViewportOutlet, selector: "[skPanelViewOutlet]" }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: ShowTooltipPipe, name: "showTooltip" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PanelTabViewComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aside[sk-panel-tab-view]', template: "<div class=\"sk-panel-header\">\r\n  <div class=\"sk-header-row\">\r\n    <button type=\"button\" class=\"btn sk-btn-icon ps-0\" [hidden]=\"!canGoBack()\" (click)=\"goBack()\">\r\n      <i class=\"material-icons md-22\">arrow_back</i>\r\n    </button>\r\n\r\n    <h3 *ngIf=\"isTitleUsed()\" #header\r\n      class=\"text-truncate\"\r\n      [ngbTooltip]=\"skTitle | showTooltip: header.scrollWidth: header.clientWidth\"\r\n      [openDelay]=\"300\">{{ skTitle }}</h3>\r\n    <div #headerRef [hidden]=\"isTitleUsed()\">\r\n        <ng-content select=\"header\"></ng-content>\r\n    </div>\r\n\r\n    <button type=\"button\" class=\"btn sk-btn-icon btn-close\" [class.invisible]=\"!isClosable()\" (click)=\"closeAll()\">\r\n    </button>\r\n  </div>\r\n\r\n  <div class=\"sk-navbar-panel\">\r\n    <ul class=\"nav sk-tabs-panel\">\r\n      <li class=\"nav-item\" *ngFor=\"let view of views\" [id]=\"'sk-nav' + view.name\">\r\n        <a class=\"nav-link\" [class.active]=\"current.name == view.name\"\r\n                            [class.disabled]=\"view.isDisabled\"\r\n                            [class.d-none]=\"view.isHidden\" tabindex\r\n          (click)=\"navigate(view)\">{{ view.display$ | async }}</a>\r\n      </li>\r\n    </ul>\r\n  </div>\r\n\r\n  <ng-content select=\".panel-search\"></ng-content>\r\n</div>\r\n<div class=\"sk-panel-content\">\r\n  <ng-template skPanelViewOutlet></ng-template>\r\n</div>\r\n<div class=\"sk-panel-footer\" #footerRef [hidden]=\"!isFooterUsed()\">\r\n    <ng-content select=\"footer\"></ng-content>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i0.ComponentFactoryResolver }, { type: TaskManagerService }], propDecorators: { outlet: [{
                type: ViewChild,
                args: [PanelViewportOutlet, { static: true }]
            }], views: [{
                type: Input
            }], default: [{
                type: Input
            }] } });

class PanelComponent {
    get isPanelOpen() {
        return !this.taskManagerService.isTaskActive();
    }
    constructor(globalTaskManagerService) {
        this.globalTaskManagerService = globalTaskManagerService;
    }
    ngOnInit() {
        this.taskManagerService = this.taskManagerServiceOverride || this.globalTaskManagerService;
        this.subscription = this.taskManagerService.subscribeToComponent((componentRef) => {
            this.loadView(componentRef.hostView);
        });
    }
    loadView(viewRef) {
        if (!viewRef.destroyed) {
            this.outlet.viewContainerRef.detach();
            this.outlet.viewContainerRef.insert(viewRef);
        }
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.taskManagerService.abortAllTasks();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PanelComponent, deps: [{ token: TaskManagerService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PanelComponent, selector: "[sk-panel]", inputs: { taskManagerServiceOverride: "taskManagerServiceOverride" }, host: { properties: { "hidden": "this.isPanelOpen" } }, viewQueries: [{ propertyName: "outlet", first: true, predicate: PanelViewportOutlet, descendants: true, static: true }], ngImport: i0, template: "<ng-template skPanelViewOutlet></ng-template>", styles: [":host{display:flex}:host .sk-panel-container{overflow:hidden;position:relative}:host .sk-panel{position:absolute;inset:0;width:100%}:host .sk-panel.sk-panel-staged{left:100%}:host .sk-panel.sk-panel-viewed{left:-100%}\n"], dependencies: [{ kind: "directive", type: PanelViewportOutlet, selector: "[skPanelViewOutlet]" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PanelComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-panel]', template: "<ng-template skPanelViewOutlet></ng-template>", styles: [":host{display:flex}:host .sk-panel-container{overflow:hidden;position:relative}:host .sk-panel{position:absolute;inset:0;width:100%}:host .sk-panel.sk-panel-staged{left:100%}:host .sk-panel.sk-panel-viewed{left:-100%}\n"] }]
        }], ctorParameters: () => [{ type: TaskManagerService }], propDecorators: { outlet: [{
                type: ViewChild,
                args: [PanelViewportOutlet, { static: true }]
            }], isPanelOpen: [{
                type: HostBinding,
                args: ['hidden']
            }], taskManagerServiceOverride: [{
                type: Input
            }] } });

class LayoutModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: LayoutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: LayoutModule, declarations: [PageHeaderComponent,
            PageHeaderNavComponent,
            PageHeaderNavItemComponent,
            PageHeaderToolbarComponent,
            PagePanelComponent,
            PagePanelItemComponent,
            PanelComponent,
            PanelViewportOutlet,
            OverlayComponent,
            PanelViewComponent,
            PanelTabViewComponent,
            PanelSearchComponent,
            EmptyPanelComponent,
            ShowTooltipPipe], imports: [CommonModule,
            ButtonsModule,
            InputModule,
            FormsModule$1,
            RouterModule,
            AssetsModule,
            NgbModule], exports: [PageHeaderComponent,
            PageHeaderNavComponent,
            PageHeaderNavItemComponent,
            PageHeaderToolbarComponent,
            PagePanelComponent,
            PagePanelItemComponent,
            PanelComponent,
            PanelViewportOutlet,
            OverlayComponent,
            PanelViewComponent,
            PanelTabViewComponent,
            PanelSearchComponent,
            EmptyPanelComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: LayoutModule, imports: [CommonModule,
            ButtonsModule,
            InputModule,
            FormsModule$1,
            RouterModule,
            AssetsModule,
            NgbModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: LayoutModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PageHeaderComponent,
                        PageHeaderNavComponent,
                        PageHeaderNavItemComponent,
                        PageHeaderToolbarComponent,
                        PagePanelComponent,
                        PagePanelItemComponent,
                        PanelComponent,
                        PanelViewportOutlet,
                        OverlayComponent,
                        PanelViewComponent,
                        PanelTabViewComponent,
                        PanelSearchComponent,
                        EmptyPanelComponent,
                        ShowTooltipPipe,
                    ],
                    imports: [
                        CommonModule,
                        ButtonsModule,
                        InputModule,
                        FormsModule$1,
                        RouterModule,
                        AssetsModule,
                        NgbModule,
                    ],
                    exports: [
                        PageHeaderComponent,
                        PageHeaderNavComponent,
                        PageHeaderNavItemComponent,
                        PageHeaderToolbarComponent,
                        PagePanelComponent,
                        PagePanelItemComponent,
                        PanelComponent,
                        PanelViewportOutlet,
                        OverlayComponent,
                        PanelViewComponent,
                        PanelTabViewComponent,
                        PanelSearchComponent,
                        EmptyPanelComponent
                    ]
                }]
        }] });

class PagePanel {
}

class SkyKickModalResult {
    /**
     *
     * @param data Optional data passed in on a 'dismiss' or 'close' event of the modal
     * @param wasClosed Set to true on a close event, false on a dismiss event
     */
    constructor(data, wasClosed) {
        this.data = data;
        this.wasClosed = wasClosed;
    }
}

class SkyKickModal {
    get componentInstance() { return this.handle.componentInstance; }
    get result$() {
        return from(this.result);
    }
    get result() {
        return new Promise((resolve, reject) => {
            this.handle.result
                .then((data) => {
                resolve(new SkyKickModalResult(data, true));
            })
                .catch((err) => {
                if (err instanceof Error) {
                    reject(err);
                }
                else {
                    resolve(new SkyKickModalResult(err, false));
                }
            });
        });
    }
    constructor(handle) {
        this.handle = handle;
        this.closedSubject = new Subject();
        this.onClose$ = this.closedSubject.asObservable();
        this.setupCloseTracking();
    }
    close(result) {
        this.handle.close(result);
    }
    dismiss(reason) {
        this.handle.dismiss(reason);
    }
    /**
     * Setup listeners for when the modal is closed or dismissed.
     */
    setupCloseTracking() {
        this.handle.result
            .then(result => {
            this.closedSubject.next(result || null);
            this.closedSubject.complete();
        })
            .catch(() => {
            this.closedSubject.next(null);
            this.closedSubject.complete();
        });
    }
}

var ModalType;
(function (ModalType) {
    ModalType["Default"] = "default";
    ModalType["Danger"] = "danger";
    ModalType["Warning"] = "warning";
    ModalType["Success"] = "success";
})(ModalType || (ModalType = {}));

class ModalFooterComponent {
    constructor() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ModalFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ModalFooterComponent, selector: "sk-modal-footer", host: { classAttribute: "modal-footer" }, queries: [{ propertyName: "btns", predicate: ButtonDirective }], ngImport: i0, template: "<ng-content></ng-content>\r\n", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ModalFooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-modal-footer', host: {
                        class: 'modal-footer'
                    }, template: "<ng-content></ng-content>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { btns: [{
                type: ContentChildren,
                args: [ButtonDirective]
            }] } });

class ModalHeaderComponent {
    get getClasses() {
        const classes = ['modal-header'];
        switch (this.type) {
            case ModalType.Warning:
            case ModalType.Danger:
            case ModalType.Success:
                classes.push(`sk-${this.type}`);
                break;
        }
        return classes.join(' ');
    }
    constructor() {
        this.dismiss = new EventEmitter();
        this.type = ModalType.Default;
    }
    doDismiss() {
        this.dismiss.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ModalHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ModalHeaderComponent, selector: "sk-modal-header", inputs: { type: "type" }, outputs: { dismiss: "dismiss" }, host: { properties: { "class": "this.getClasses" }, classAttribute: "modal-header" }, ngImport: i0, template: "<h4 class=\"modal-title\">\r\n    <ng-content></ng-content>\r\n</h4>\r\n<button type=\"button\" class=\"btn-close\" (click)=\"doDismiss()\" data-bs-dismiss=\"modal\" aria-label=\"Close\">\r\n</button>\r\n", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ModalHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-modal-header', host: {
                        class: 'modal-header'
                    }, template: "<h4 class=\"modal-title\">\r\n    <ng-content></ng-content>\r\n</h4>\r\n<button type=\"button\" class=\"btn-close\" (click)=\"doDismiss()\" data-bs-dismiss=\"modal\" aria-label=\"Close\">\r\n</button>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { dismiss: [{
                type: Output
            }], type: [{
                type: Input
            }], getClasses: [{
                type: HostBinding,
                args: ['class']
            }] } });

class ModalComponent {
    constructor() {
        /**
         * Type of the modal wanting to be displayed. Can be 'warning' or 'error/danger'
         */
        this.type = ModalType.Default;
        this.dismiss = new EventEmitter();
    }
    doDismiss() {
        this.dismiss.emit();
    }
    /**
     * Sets the composite components theme so that
     * these elements inherit the color scheme of the modal
     */
    setClasses() {
        // We want all non-Primary action buttons to be 'secondary'
        this.footer.btns.forEach(btn => btn.color = ButtonType.Secondary);
        // If the modal has a type, let child components inherit the values
        this.header.type = this.type;
        if (this.footer.btns.length) {
            this.footer.btns.last.color = this.mapModalTypeToPrimaryButtonType(this.type);
        }
    }
    /**
     * Maps modal type to primary button type
     * @param type The modal type
     * @returns The button type to use
     */
    mapModalTypeToPrimaryButtonType(type) {
        switch (type) {
            case ModalType.Danger:
                return ButtonType.Danger;
            case ModalType.Warning:
            case ModalType.Default:
            default:
                return ButtonType.Primary;
        }
    }
    /**
     * Lifecycle Hooks
     */
    ngAfterContentChecked() {
        this.setClasses();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ModalComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ModalComponent, selector: "sk-modal", inputs: { type: "type" }, outputs: { dismiss: "dismiss" }, queries: [{ propertyName: "footer", first: true, predicate: ModalFooterComponent, descendants: true, static: true }, { propertyName: "header", first: true, predicate: ModalHeaderComponent, descendants: true, static: true }], ngImport: i0, template: "<ng-content select=\"sk-modal-header\"></ng-content>\r\n\r\n<ng-content select=\"sk-modal-body\"></ng-content>\r\n\r\n<ng-content select=\"sk-modal-footer\"></ng-content>\r\n", styles: [":host{display:block}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-modal', template: "<ng-content select=\"sk-modal-header\"></ng-content>\r\n\r\n<ng-content select=\"sk-modal-body\"></ng-content>\r\n\r\n<ng-content select=\"sk-modal-footer\"></ng-content>\r\n", styles: [":host{display:block}\n"] }]
        }], ctorParameters: () => [], propDecorators: { footer: [{
                type: ContentChild,
                args: [ModalFooterComponent, { static: true }]
            }], header: [{
                type: ContentChild,
                args: [ModalHeaderComponent, { static: true }]
            }], type: [{
                type: Input
            }], dismiss: [{
                type: Output
            }] } });

class ModalBodyComponent {
    constructor() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ModalBodyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ModalBodyComponent, selector: "sk-modal-body", host: { classAttribute: "modal-body" }, ngImport: i0, template: "<ng-content></ng-content>\r\n", styles: [":host{display:block}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ModalBodyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-modal-body', host: {
                        class: 'modal-body'
                    }, template: "<ng-content></ng-content>\r\n", styles: [":host{display:block}\n"] }]
        }], ctorParameters: () => [] });

class ErrorModalComponent {
    constructor(modal) {
        this.modal = modal;
    }
    ngOnInit() {
    }
    dismiss() {
        this.modal.dismiss();
    }
    close() {
        this.modal.close();
    }
    alternativeAction() {
        this.options.alternative.btnCallback(this.modal);
    }
    hasAlternativeAction() {
        return !!this.options.alternative;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ErrorModalComponent, deps: [{ token: i1$1.NgbActiveModal }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ErrorModalComponent, selector: "sk-error-modal", ngImport: i0, template: "<sk-modal type=\"danger\">\r\n    <sk-modal-header (dismiss)=\"dismiss()\">{{ options.title }}</sk-modal-header>\r\n\r\n    <sk-modal-body>\r\n        <p [innerHtml]=\"options.body\" class=\"display-as-is\"></p>\r\n    </sk-modal-body>\r\n\r\n    <sk-modal-footer>\r\n        <button sk-button *ngIf=\"hasAlternativeAction()\"\r\n        (click)=\"alternativeAction()\">{{ options.alternative.btnLabel }}</button>\r\n        <button sk-button color=\"primary\" (click)=\"close()\">{{ options.btnLabel }}</button>\r\n    </sk-modal-footer>\r\n</sk-modal>\r\n\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: ButtonDirective, selector: "button[sk-button]", inputs: ["color", "size"] }, { kind: "component", type: ModalComponent, selector: "sk-modal", inputs: ["type"], outputs: ["dismiss"] }, { kind: "component", type: ModalFooterComponent, selector: "sk-modal-footer" }, { kind: "component", type: ModalBodyComponent, selector: "sk-modal-body" }, { kind: "component", type: ModalHeaderComponent, selector: "sk-modal-header", inputs: ["type"], outputs: ["dismiss"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ErrorModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-error-modal', template: "<sk-modal type=\"danger\">\r\n    <sk-modal-header (dismiss)=\"dismiss()\">{{ options.title }}</sk-modal-header>\r\n\r\n    <sk-modal-body>\r\n        <p [innerHtml]=\"options.body\" class=\"display-as-is\"></p>\r\n    </sk-modal-body>\r\n\r\n    <sk-modal-footer>\r\n        <button sk-button *ngIf=\"hasAlternativeAction()\"\r\n        (click)=\"alternativeAction()\">{{ options.alternative.btnLabel }}</button>\r\n        <button sk-button color=\"primary\" (click)=\"close()\">{{ options.btnLabel }}</button>\r\n    </sk-modal-footer>\r\n</sk-modal>\r\n\r\n" }]
        }], ctorParameters: () => [{ type: i1$1.NgbActiveModal }] });

class SimpleModalComponent {
    constructor(modal) {
        this.modal = modal;
    }
    ngOnInit() {
    }
    dismiss() {
        this.modal.dismiss();
    }
    close() {
        this.modal.close();
    }
    alternativeAction() {
        this.options.alternative.btnCallback(this.modal);
    }
    hasAlternativeAction() {
        return !!this.options.alternative;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SimpleModalComponent, deps: [{ token: i1$1.NgbActiveModal }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: SimpleModalComponent, selector: "sk-simple-modal", ngImport: i0, template: "<sk-modal>\r\n    <sk-modal-header (dismiss)=\"dismiss()\"> {{ options.title }}</sk-modal-header>\r\n\r\n    <sk-modal-body>\r\n        <p [innerHtml]=\"options.body\" class=\"display-as-is\"></p>\r\n    </sk-modal-body>\r\n\r\n    <sk-modal-footer>\r\n        <button sk-button *ngIf=\"hasAlternativeAction()\"\r\n            (click)=\"alternativeAction()\">{{ options.alternative.btnLabel }}</button>\r\n        <button sk-button color=\"primary\" (click)=\"close()\">{{ options.btnLabel }}</button>\r\n    </sk-modal-footer>\r\n</sk-modal>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: ButtonDirective, selector: "button[sk-button]", inputs: ["color", "size"] }, { kind: "component", type: ModalComponent, selector: "sk-modal", inputs: ["type"], outputs: ["dismiss"] }, { kind: "component", type: ModalFooterComponent, selector: "sk-modal-footer" }, { kind: "component", type: ModalBodyComponent, selector: "sk-modal-body" }, { kind: "component", type: ModalHeaderComponent, selector: "sk-modal-header", inputs: ["type"], outputs: ["dismiss"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SimpleModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-simple-modal', template: "<sk-modal>\r\n    <sk-modal-header (dismiss)=\"dismiss()\"> {{ options.title }}</sk-modal-header>\r\n\r\n    <sk-modal-body>\r\n        <p [innerHtml]=\"options.body\" class=\"display-as-is\"></p>\r\n    </sk-modal-body>\r\n\r\n    <sk-modal-footer>\r\n        <button sk-button *ngIf=\"hasAlternativeAction()\"\r\n            (click)=\"alternativeAction()\">{{ options.alternative.btnLabel }}</button>\r\n        <button sk-button color=\"primary\" (click)=\"close()\">{{ options.btnLabel }}</button>\r\n    </sk-modal-footer>\r\n</sk-modal>\r\n" }]
        }], ctorParameters: () => [{ type: i1$1.NgbActiveModal }] });

class WarningModalComponent {
    constructor(modal) {
        this.modal = modal;
    }
    ngOnInit() {
    }
    dismiss() {
        this.modal.dismiss();
    }
    close() {
        this.modal.close();
    }
    color() {
        if (this.options.subType) {
            return this.options.subType === 'standard' ? 'primary' : 'danger';
        }
        return 'primary';
    }
    alternativeAction() {
        this.options.alternative.btnCallback(this.modal);
    }
    hasAlternativeAction() {
        return !!this.options.alternative;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: WarningModalComponent, deps: [{ token: i1$1.NgbActiveModal }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: WarningModalComponent, selector: "sk-warning-modal", ngImport: i0, template: "<sk-modal type=\"warning\" id=\"sk-WarningModal\">\r\n    <sk-modal-header (dismiss)=\"dismiss()\"> {{ options.title }}</sk-modal-header>\r\n\r\n    <sk-modal-body>\r\n        <p [innerHtml]=\"options.body\" class=\"display-as-is\"></p>\r\n        <div *ngIf=\"options.verifyLabel\" class=\"form-check\">\r\n            <input type=\"checkbox\" name=\"verify\" [(ngModel)]=\"verify\" class=\"form-check-input\" id=\"sk-WarningVerifyCbx\">\r\n            <label class=\"form-check-label\" for=\"sk-WarningVerifyCbx\" [innerHtml]=\"options.verifyLabel\"></label>\r\n          </div>\r\n    </sk-modal-body>\r\n\r\n    <sk-modal-footer>\r\n        <button sk-button id=\"sk-WarningCancelBtn\"  (click)=\"alternativeAction()\">{{ options.alternative.btnLabel }}</button>\r\n        <button sk-button id=\"sk-WarningConfirmBtn\" [disabled]=\"options.verifyLabel && !verify\" [color]=\"color()\" (click)=\"close()\">{{ options.btnLabel }}</button>\r\n    </sk-modal-footer>\r\n</sk-modal>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: ButtonDirective, selector: "button[sk-button]", inputs: ["color", "size"] }, { kind: "directive", type: i2$2.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: ModalComponent, selector: "sk-modal", inputs: ["type"], outputs: ["dismiss"] }, { kind: "component", type: ModalFooterComponent, selector: "sk-modal-footer" }, { kind: "component", type: ModalBodyComponent, selector: "sk-modal-body" }, { kind: "component", type: ModalHeaderComponent, selector: "sk-modal-header", inputs: ["type"], outputs: ["dismiss"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: WarningModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-warning-modal', template: "<sk-modal type=\"warning\" id=\"sk-WarningModal\">\r\n    <sk-modal-header (dismiss)=\"dismiss()\"> {{ options.title }}</sk-modal-header>\r\n\r\n    <sk-modal-body>\r\n        <p [innerHtml]=\"options.body\" class=\"display-as-is\"></p>\r\n        <div *ngIf=\"options.verifyLabel\" class=\"form-check\">\r\n            <input type=\"checkbox\" name=\"verify\" [(ngModel)]=\"verify\" class=\"form-check-input\" id=\"sk-WarningVerifyCbx\">\r\n            <label class=\"form-check-label\" for=\"sk-WarningVerifyCbx\" [innerHtml]=\"options.verifyLabel\"></label>\r\n          </div>\r\n    </sk-modal-body>\r\n\r\n    <sk-modal-footer>\r\n        <button sk-button id=\"sk-WarningCancelBtn\"  (click)=\"alternativeAction()\">{{ options.alternative.btnLabel }}</button>\r\n        <button sk-button id=\"sk-WarningConfirmBtn\" [disabled]=\"options.verifyLabel && !verify\" [color]=\"color()\" (click)=\"close()\">{{ options.btnLabel }}</button>\r\n    </sk-modal-footer>\r\n</sk-modal>\r\n" }]
        }], ctorParameters: () => [{ type: i1$1.NgbActiveModal }] });

class DestructiveModalComponent {
    constructor(modal) {
        this.modal = modal;
    }
    ngOnInit() {
    }
    dismiss() {
        this.modal.dismiss();
    }
    close() {
        this.modal.close();
    }
    alternativeAction() {
        this.options.alternative.btnCallback(this.modal);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DestructiveModalComponent, deps: [{ token: i1$1.NgbActiveModal }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: DestructiveModalComponent, selector: "sk-destructive-modal", ngImport: i0, template: "<sk-modal type=\"danger\">\r\n    <sk-modal-header (dismiss)=\"dismiss()\">{{ options.title }}</sk-modal-header>\r\n\r\n    <sk-modal-body>\r\n        <p [innerHtml]=\"options.body\" class=\"display-as-is\"></p>\r\n    </sk-modal-body>\r\n\r\n    <sk-modal-footer>\r\n        <button sk-button (click)=\"alternativeAction()\">{{ options.alternative.btnLabel }}</button>\r\n        <button sk-button color=\"danger\" (click)=\"close()\">{{ options.btnLabel }}</button>\r\n    </sk-modal-footer>\r\n</sk-modal>\r\n", styles: [""], dependencies: [{ kind: "directive", type: ButtonDirective, selector: "button[sk-button]", inputs: ["color", "size"] }, { kind: "component", type: ModalComponent, selector: "sk-modal", inputs: ["type"], outputs: ["dismiss"] }, { kind: "component", type: ModalFooterComponent, selector: "sk-modal-footer" }, { kind: "component", type: ModalBodyComponent, selector: "sk-modal-body" }, { kind: "component", type: ModalHeaderComponent, selector: "sk-modal-header", inputs: ["type"], outputs: ["dismiss"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DestructiveModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-destructive-modal', template: "<sk-modal type=\"danger\">\r\n    <sk-modal-header (dismiss)=\"dismiss()\">{{ options.title }}</sk-modal-header>\r\n\r\n    <sk-modal-body>\r\n        <p [innerHtml]=\"options.body\" class=\"display-as-is\"></p>\r\n    </sk-modal-body>\r\n\r\n    <sk-modal-footer>\r\n        <button sk-button (click)=\"alternativeAction()\">{{ options.alternative.btnLabel }}</button>\r\n        <button sk-button color=\"danger\" (click)=\"close()\">{{ options.btnLabel }}</button>\r\n    </sk-modal-footer>\r\n</sk-modal>\r\n" }]
        }], ctorParameters: () => [{ type: i1$1.NgbActiveModal }] });

class SuccessModalComponent {
    constructor(modal) {
        this.modal = modal;
    }
    ngOnInit() {
    }
    dismiss() {
        this.modal.dismiss();
    }
    close() {
        this.modal.close();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SuccessModalComponent, deps: [{ token: i1$1.NgbActiveModal }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: SuccessModalComponent, selector: "sk-success-modal", ngImport: i0, template: "<sk-modal type=\"success\">\r\n    <sk-modal-header (dismiss)=\"dismiss()\">{{ options.title }}</sk-modal-header>\r\n\r\n    <sk-modal-body>\r\n        <p [innerHtml]=\"options.body\" class=\"display-as-is\"></p>\r\n    </sk-modal-body>\r\n\r\n    <sk-modal-footer>\r\n        <button sk-button color=\"primary\" (click)=\"close()\">{{ options.btnLabel }}</button>\r\n    </sk-modal-footer>\r\n</sk-modal>\r\n", styles: [""], dependencies: [{ kind: "directive", type: ButtonDirective, selector: "button[sk-button]", inputs: ["color", "size"] }, { kind: "component", type: ModalComponent, selector: "sk-modal", inputs: ["type"], outputs: ["dismiss"] }, { kind: "component", type: ModalFooterComponent, selector: "sk-modal-footer" }, { kind: "component", type: ModalBodyComponent, selector: "sk-modal-body" }, { kind: "component", type: ModalHeaderComponent, selector: "sk-modal-header", inputs: ["type"], outputs: ["dismiss"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SuccessModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-success-modal', template: "<sk-modal type=\"success\">\r\n    <sk-modal-header (dismiss)=\"dismiss()\">{{ options.title }}</sk-modal-header>\r\n\r\n    <sk-modal-body>\r\n        <p [innerHtml]=\"options.body\" class=\"display-as-is\"></p>\r\n    </sk-modal-body>\r\n\r\n    <sk-modal-footer>\r\n        <button sk-button color=\"primary\" (click)=\"close()\">{{ options.btnLabel }}</button>\r\n    </sk-modal-footer>\r\n</sk-modal>\r\n" }]
        }], ctorParameters: () => [{ type: i1$1.NgbActiveModal }] });

/**
 * A wrapper around bootstrap's NgbModal service. This differs from bootstrap's modal service in how modal promises are resolved.
 * In a bootstrap modal, the modal can either be closed (which resolves the promise), or dismissed (which rejects the promise).
 *
 * In this wrapper, however, the promise is always resolved. A SkyKickModalResult object is returned, which has a field
 * 'wasClosed'. This is set to true for a 'modal.close()' event and false for a 'modal.dismiss()' event. This eliminates the need
 * for the consumer to differentiate between promise rejections for business reasons and promise rejections for actual errors.
 */
class SkyKickModalService {
    constructor(bootstrapModalService, router) {
        this.bootstrapModalService = bootstrapModalService;
        this.router = router;
        this.activeModals = [];
        // Listen for navigation events to close modals
        this.router.events
            .subscribe(event => {
            if (event instanceof NavigationStart) {
                this.closeAllActiveModals();
            }
        });
    }
    /**
     * Display a basic SkyKick Error Modal
     * @param options Customize your modal using these options
     * @returns A SkyKick Modal handle
     */
    error(options) {
        return this.setupBasic(ErrorModalComponent, options);
    }
    /**
     * Display a basic SkyKick Warning Modal
     * @param options Customize your modal using these options
     * @returns A SkyKick Modal handle
     */
    warning(options) {
        return this.setupBasic(WarningModalComponent, options);
    }
    /**
     * Display a basic SkyKick Simple Modal
     * @param options Customize your modal using these options
     * @returns A SkyKick Modal handle
     */
    simple(options) {
        return this.setupBasic(SimpleModalComponent, options);
    }
    success(options) {
        return this.setupBasic(SuccessModalComponent, options);
    }
    destructive(options) {
        return this.setupBasic(DestructiveModalComponent, options);
    }
    /**
     * Opens a new modal window and returns a reference to the newly opened window
     * @param content Content can be provided as a TemplateRef or a component type
     * @param options NgbModal options
     */
    open(content, options) {
        const modalRef = this.bootstrapModalService.open(content, options);
        const handle = new SkyKickModal(modalRef);
        this.trackModal(handle);
        return handle;
    }
    /**
     * Dismiss all currently displayed modal windows with the supplied reason.
     * @param reason Reason for dismissing
     */
    dismissAll(reason) {
        this.bootstrapModalService.dismissAll(reason);
    }
    /**
     * Indicates if there are currently any open modal windows in the application.
     */
    hasOpenModals() {
        return this.bootstrapModalService.hasOpenModals();
    }
    /**
     * Helper method for settings up basic modals like Error, Warning, and Simple
     * @template TComponent The component Type to display
     * @param component The component ctor
     * @param options SkyKick Modal Options
     * @returns  A SkyKickModal to handle the modal
     */
    setupBasic(component, options) {
        const modalRef = this.bootstrapModalService.open(component);
        modalRef.componentInstance.options = options;
        // Wrap the modal in SkyKickModal and track it
        const handle = new SkyKickModal(modalRef);
        this.trackModal(handle);
        return handle;
    }
    /**
     * Add a modal to the active modals list and set up cleanup.
     * @param handle The modal handle to track
     */
    trackModal(handle) {
        this.activeModals.push(handle);
        // Remove the modal from the list when it's closed
        handle.onClose$
            .subscribe(() => {
            this.activeModals = this.activeModals.filter(modal => modal !== handle);
        });
    }
    /**
     * Close all active modals.
     */
    closeAllActiveModals() {
        for (const modal of this.activeModals) {
            modal.close();
        }
        this.activeModals = [];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickModalService, deps: [{ token: i1$1.NgbModal }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickModalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickModalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.NgbModal }, { type: i2.Router }] });

class CanDeactivatePage {
    constructor(modalService, translateService) {
        this.modalService = modalService;
        this.translateService = translateService;
        this.canDeactivateModalSubject = new Subject();
    }
    openCanDeactivateModal() {
        if (this.showCanDeactivateModal) {
            const modal = this.modalService.warning({
                title: this.translateService.instant('can_deactivate.modal_title'),
                body: this.translateService.instant('can_deactivate.modal_text'),
                btnLabel: this.translateService.instant('can_deactivate.modal_ok_button_text'),
                alternative: {
                    btnLabel: this.translateService.instant('can_deactivate.modal_cancel_button_text'),
                    btnCallback: () => {
                        modal.dismiss();
                    },
                },
            });
            modal.result$.subscribe((res) => {
                this.canDeactivateModalSubject.next(res.wasClosed);
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CanDeactivatePage, deps: [{ token: SkyKickModalService }, { token: i2$3.TranslateService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: CanDeactivatePage, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CanDeactivatePage, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: SkyKickModalService }, { type: i2$3.TranslateService }] });

const CanDeactivateGuard = (component) => {
    component.openCanDeactivateModal();
    return component.showCanDeactivateModal
        ? component.canDeactivateModalSubject.asObservable()
        : true;
};

class Page {
    constructor(Name, Route, Alerts = null) {
        this.Name = Name;
        this.Route = Route;
        this.Alerts = Alerts;
    }
}

/**
 * This base class allows you to easily setup a component that inherits from it
 * as a single "scope" which allows the lifecycle of the Tasks opened from  it to be
 * easily aligned with that of the component. By using the 'openTask()' method below
 * you can assure that the Tasks opened during the existence of the inheriting component
 * will be closed and destroyed properly upon destroying the component itself
 *
 * You can open as many tasks as you want under a scope, but they will overwrite each
 * other so there is no "going back" once you open a subsequent Task
 */
class ScopedTaskBase {
    constructor(taskManagerService) {
        this.taskManagerService = taskManagerService;
        this.scope = null;
    }
    /**
     * Opens a new Task within the scope of a given Component and will automatically cancel and
     * destroy the Task once the Component is also being destroyed
     * @template TInput The type of input to the task
     * @template TResult The type of return result you expect
     * @param componentType The component 'Type' you want to open
     * @param [model] The 'model' you want to pass to the component of type 'TInput'
     * @returns A promise that will complete when the Task is completed or cancelled
     */
    async openTask(componentType, model) {
        if (!this.scope) {
            this.scope = this.taskManagerService.generateScope();
        }
        return this.scope.openTask(componentType, model);
    }
    ngOnDestroy() {
        if (this.scope) {
            return this.scope.destroy();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ScopedTaskBase, deps: [{ token: TaskManagerService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ScopedTaskBase }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ScopedTaskBase, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: TaskManagerService }] });

class SkykickPlatformPage extends ScopedTaskBase {
    /**
     *
     * @param activatedRoute route information about the current route associated with the component
     * @param panelManager see PanelManager service
     */
    constructor(activatedRoute, taskManagerService, injector = null) {
        super(taskManagerService);
        this.activatedRoute = activatedRoute;
        this.class = 'sk-content-pane';
        this.Pages = [];
        if (!this.activatedRoute.routeConfig)
            return;
        this.activatedRoute
            .routeConfig
            .children
            .filter(r => r.data && (r.data.type === 'secondary' || r.data.type === 'secondary-dynamic'))
            .forEach((config) => {
            let renderLink = true;
            // Don't render a link to the page if the component has a `CanActivate` guard
            if (injector && config.canActivate) {
                const guards = config.canActivate.map(guard => injector.get(guard));
                guards.forEach(g => {
                    if (!g.canActivate(null, null)) {
                        renderLink = false;
                    }
                });
            }
            if (renderLink) {
                if (config.data.type === 'secondary') {
                    this.Pages.push(new Page(config.data.name, config.path));
                }
                else {
                    config.data.nav.forEach((navItem) => {
                        this.Pages.push(new Page(navItem.name, navItem.path));
                    });
                }
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkykickPlatformPage, deps: [{ token: i2.ActivatedRoute }, { token: TaskManagerService }, { token: i0.Injector, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: SkykickPlatformPage, host: { properties: { "class": "this.class" } }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkykickPlatformPage, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: i2.ActivatedRoute }, { type: TaskManagerService }, { type: i0.Injector, decorators: [{
                    type: Optional
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });

class SkyKickProductPage extends ScopedTaskBase {
    constructor(taskManagerService) {
        super(taskManagerService);
        this.class = 'sk-content-column';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickProductPage, deps: [{ token: TaskManagerService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: SkyKickProductPage, host: { properties: { "class": "this.class" } }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickProductPage, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: TaskManagerService }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });

class PanelEvent {
    constructor(Type) {
        this.Type = Type;
    }
}

class PanelCloseEvent extends PanelEvent {
    constructor() {
        super('close');
    }
}

class PanelDismissEvent extends PanelEvent {
    constructor() {
        super('dismiss');
    }
}

class PanelOpenEvent extends PanelEvent {
    constructor() {
        super('open');
    }
}

class SkyKickPanelTabView {
    constructor(component, display, name, model, isHidden, isDisabled) {
        this.component = component;
        this.name = name;
        this.model = model;
        this.isHidden = isHidden;
        this.isDisabled = isDisabled;
        if (typeof (display) === 'string') {
            this.display$ = of(display);
        }
        else {
            this.display$ = display;
        }
    }
}

class SkyKickPanelTabViewBase extends OperationViewBase {
    constructor() {
        super(...arguments);
        this.Views = [];
    }
    bootstrap(...views) {
        this.reset();
        views.forEach(v => this.Views.push(v));
    }
    reset() {
        this.Views.length = 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickPanelTabViewBase, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: SkyKickPanelTabViewBase, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SkyKickPanelTabViewBase, decorators: [{
            type: Directive
        }] });

class SortableProperty {
    constructor(name, resolve, emptyLast) {
        this.name = name;
        this.resolve = resolve;
        this.emptyLast = emptyLast;
        this.reverse = false;
    }
}

class SortingRackBuilder {
    constructor() {
        this.rack = new SortingRack();
    }
    AddProperty(name, selector, emptyLast) {
        const prop = new SortableProperty(name, selector, emptyLast);
        this.rack.properties.push(prop);
        return this;
    }
    AddSource(source) {
        this.rack.source = source;
        return this;
    }
    AddDefault(name, reverse) {
        this.rack.defaultOp = new SortOperation(name, reverse);
        return this;
    }
    Build() {
        const defaultSortOp = new SortOperation(this.rack.properties[0].name, this.rack.properties[0].reverse, this.rack.properties[0].emptyLast);
        this.rack.$sort = new BehaviorSubject(this.rack.defaultOp || defaultSortOp);
        const stream = combineLatest([
            this.rack.source,
            this.rack.$sort.asObservable()
        ])
            .pipe(map(([models, sortOp]) => {
            // If the list is empty, just return it
            if (!models.length) {
                return models;
            }
            // Get the property from the rack for the given sort operation
            const property = this.rack.getProperty(sortOp.propName);
            // Find a model that has a value for the given property
            const modelWithValue = models.find(m => property.resolve(m) != null);
            // If there are no models with a value, sorting doesn't really matter
            if (!modelWithValue) {
                return models;
            }
            const type = property.resolve(modelWithValue).constructor.name.toLowerCase();
            switch (type) {
                case 'date':
                    models.sort((a, b) => {
                        const aDate = property.resolve(a);
                        const bDate = property.resolve(b);
                        let compValue;
                        if (!aDate) {
                            compValue = sortOp.emptyLast ? 1 : -1;
                        }
                        else if (!bDate) {
                            compValue = sortOp.emptyLast ? -1 : 1;
                        }
                        else {
                            compValue = aDate.getTime() - bDate.getTime();
                        }
                        if (sortOp.reverse) {
                            compValue *= -1;
                        }
                        return compValue;
                    });
                    break;
                case 'number':
                    models.sort((a, b) => {
                        const aNumber = property.resolve(a);
                        const bNumber = property.resolve(b);
                        let compValue;
                        if (aNumber == null) {
                            compValue = sortOp.emptyLast ? 1 : -1;
                        }
                        else if (bNumber == null) {
                            compValue = sortOp.emptyLast ? -1 : 1;
                        }
                        else {
                            compValue = aNumber - bNumber;
                        }
                        if (sortOp.reverse) {
                            compValue *= -1;
                        }
                        return compValue;
                    });
                    break;
                case 'string':
                default:
                    models.sort((a, b) => {
                        const aString = this.rack.getProperty(sortOp.propName).resolve(a);
                        const bString = this.rack.getProperty(sortOp.propName).resolve(b);
                        let compValue;
                        if (aString == null) {
                            compValue = sortOp.emptyLast ? 1 : -1;
                        }
                        else if (bString == null) {
                            compValue = sortOp.emptyLast ? -1 : 1;
                        }
                        else {
                            compValue = aString.localeCompare(bString);
                        }
                        if (sortOp.reverse) {
                            compValue *= -1;
                        }
                        return compValue;
                    });
            }
            return models;
        }));
        this.rack.bindSource(stream);
        const defaultOperation = this.rack.getDefault();
        this.rack.sort(defaultOperation.propName, defaultOperation.reverse);
        return this.rack;
    }
}

class LoggerService {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: LoggerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: LoggerService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: LoggerService, decorators: [{
            type: Injectable
        }] });

class TableCapDirective {
    constructor(el) {
        this.el = el;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableCapDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: TableCapDirective, selector: "[skTableCap]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableCapDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[skTableCap]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class TableWrapDirective {
    constructor(el) {
        this.el = el;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableWrapDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: TableWrapDirective, selector: "[skTableWrap]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableWrapDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[skTableWrap]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

class TableHandleComponent {
    constructor() {
        this.isBorderVisible = false;
    }
    ngAfterContentInit() {
        const capEl = this.cap.el.nativeElement;
        const wrapEl = this.wrap.el.nativeElement;
        wrapEl.onscroll = (event) => {
            if (!this.isBorderVisible && event.target.scrollTop > 34) {
                capEl.classList.add('border-bottom');
                this.isBorderVisible = true;
            }
            else if (this.isBorderVisible && event.target.scrollTop <= 34) {
                capEl.classList.remove('border-bottom');
                this.isBorderVisible = false;
            }
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableHandleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: TableHandleComponent, selector: "[skTableHandle]", queries: [{ propertyName: "cap", first: true, predicate: TableCapDirective, descendants: true, static: true }, { propertyName: "wrap", first: true, predicate: TableWrapDirective, descendants: true, static: true }], ngImport: i0, template: "<ng-content></ng-content>", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableHandleComponent, decorators: [{
            type: Component,
            args: [{ selector: '[skTableHandle]', template: "<ng-content></ng-content>" }]
        }], ctorParameters: () => [], propDecorators: { cap: [{
                type: ContentChild,
                args: [TableCapDirective, { static: true }]
            }], wrap: [{
                type: ContentChild,
                args: [TableWrapDirective, { static: true }]
            }] } });

class TableLoaderComponent {
    constructor() { }
    ngOnInit() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableLoaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: TableLoaderComponent, selector: "tr[sk-table-loader]", inputs: { colSpan: "colSpan" }, ngImport: i0, template: "<td [colSpan]=\"colSpan\" class=\"text-center py-4\">\r\n    <div class=\"sk-spin-loader d-inline-block\">Loading\u2026</div>\r\n</td>", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableLoaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tr[sk-table-loader]', template: "<td [colSpan]=\"colSpan\" class=\"text-center py-4\">\r\n    <div class=\"sk-spin-loader d-inline-block\">Loading\u2026</div>\r\n</td>" }]
        }], ctorParameters: () => [], propDecorators: { colSpan: [{
                type: Input
            }] } });

/**
 * Empty data component for a table
 */
class TableEmptyComponent {
    constructor() {
        /**
         * Defines the message to display when no data available.
         */
        this.message = "No results";
    }
    ngOnInit() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableEmptyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: TableEmptyComponent, selector: "tr[sk-table-empty]", inputs: { colSpan: "colSpan", message: "message" }, ngImport: i0, template: "<td [colSpan]=\"colSpan\" class=\"text-center\" [innerHtml]=\"message\"></td>", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableEmptyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tr[sk-table-empty]', template: "<td [colSpan]=\"colSpan\" class=\"text-center\" [innerHtml]=\"message\"></td>" }]
        }], ctorParameters: () => [], propDecorators: { colSpan: [{
                type: Input,
                args: ['colSpan']
            }], message: [{
                type: Input,
                args: ['message']
            }] } });

class ResourceTableComponent {
    get isSearching() {
        return this.manager.isSearching;
    }
    constructor(el) {
        this.el = el;
        this.onNextPage = new EventEmitter();
        this.colSpan = this.el.nativeElement.parentElement.querySelector('thead').querySelector('tr').children.length;
    }
    goToNextPage() {
        this.onNextPage.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ResourceTableComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ResourceTableComponent, selector: "tbody[sk-resource-table]", inputs: { manager: "manager" }, outputs: { onNextPage: "on-next-page" }, host: { properties: { "class.sk-resources-updating": "this.isSearching" } }, ngImport: i0, template: "<ng-content></ng-content>\r\n\r\n<tr *ngIf=\"!(manager.getObservable() | async)?.length && !manager.isInitializing\">\r\n    <td [colSpan]=\"colSpan\" class=\"text-center\">No results</td>\r\n</tr>\r\n<tr *ngIf=\"!manager.isInitializing && !manager.isPaging && manager.hasMoreData\" (click)=\"goToNextPage()\" style=\"cursor:pointer\">\r\n    <td [colSpan]=\"colSpan\" class=\"text-center py-2\">\r\n        <button class=\"btn btn-outline\">Load more</button>\r\n    </td>\r\n</tr>\r\n<tr sk-table-loader *ngIf=\"manager.isInitializing || manager.isPaging\" [colSpan]=\"colSpan\"></tr>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TableLoaderComponent, selector: "tr[sk-table-loader]", inputs: ["colSpan"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ResourceTableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tbody[sk-resource-table]', template: "<ng-content></ng-content>\r\n\r\n<tr *ngIf=\"!(manager.getObservable() | async)?.length && !manager.isInitializing\">\r\n    <td [colSpan]=\"colSpan\" class=\"text-center\">No results</td>\r\n</tr>\r\n<tr *ngIf=\"!manager.isInitializing && !manager.isPaging && manager.hasMoreData\" (click)=\"goToNextPage()\" style=\"cursor:pointer\">\r\n    <td [colSpan]=\"colSpan\" class=\"text-center py-2\">\r\n        <button class=\"btn btn-outline\">Load more</button>\r\n    </td>\r\n</tr>\r\n<tr sk-table-loader *ngIf=\"manager.isInitializing || manager.isPaging\" [colSpan]=\"colSpan\"></tr>\r\n" }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { isSearching: [{
                type: HostBinding,
                args: ['class.sk-resources-updating']
            }], manager: [{
                type: Input
            }], onNextPage: [{
                type: Output,
                args: ['on-next-page']
            }] } });

class MultiSelectPanelComponent extends OperationViewBase {
    constructor() {
        super();
    }
    cancel() {
        this.operation.cancel();
    }
    complete() {
        this.operation.complete();
    }
    ngOnInit() {
        this.info = this.data;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: MultiSelectPanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: MultiSelectPanelComponent, selector: "sk-multi-select-panel", usesInheritance: true, ngImport: i0, template: "<aside sk-panel-view [skTitle]=\"info.title\">\r\n    <div class=\"sk-panel-content\">\r\n        <div class=\"d-flex flex-column justify-content-center align-items-center flex-fill\">\r\n            <div class=\"text-lg-center\">\r\n                <i class=\"material-icons\" style=\"font-size:92px;color:#bbb\">people</i>\r\n                <h4>{{ info.items.length }} {{ info.itemType }} selected</h4>\r\n            </div>\r\n        </div>\r\n    </div>\r\n\r\n    <footer>\r\n        <button class=\"btn btn-primary me-2\" (click)=\"complete()\">{{ info.actionBtn }}</button>\r\n        <button class=\"btn btn-secondary\" (click)=\"cancel()\">Cancel</button>\r\n    </footer>\r\n</aside>\r\n", styles: [""], dependencies: [{ kind: "component", type: PanelViewComponent, selector: "aside[sk-panel-view]" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: MultiSelectPanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-multi-select-panel', template: "<aside sk-panel-view [skTitle]=\"info.title\">\r\n    <div class=\"sk-panel-content\">\r\n        <div class=\"d-flex flex-column justify-content-center align-items-center flex-fill\">\r\n            <div class=\"text-lg-center\">\r\n                <i class=\"material-icons\" style=\"font-size:92px;color:#bbb\">people</i>\r\n                <h4>{{ info.items.length }} {{ info.itemType }} selected</h4>\r\n            </div>\r\n        </div>\r\n    </div>\r\n\r\n    <footer>\r\n        <button class=\"btn btn-primary me-2\" (click)=\"complete()\">{{ info.actionBtn }}</button>\r\n        <button class=\"btn btn-secondary\" (click)=\"cancel()\">Cancel</button>\r\n    </footer>\r\n</aside>\r\n" }]
        }], ctorParameters: () => [] });

class MultiSelectInfo {
    constructor(title, items, itemType, actionBtn) {
        this.title = title;
        this.items = items;
        this.itemType = itemType;
        this.actionBtn = actionBtn;
    }
}

class TablesModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TablesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: TablesModule, declarations: [TableCapDirective,
            TableWrapDirective,
            TableHandleComponent,
            MultiSelectPanelComponent,
            ResourceTableComponent,
            TableEmptyComponent,
            TableLoaderComponent], imports: [CommonModule,
            LayoutModule], exports: [TableCapDirective,
            TableWrapDirective,
            TableHandleComponent,
            MultiSelectPanelComponent,
            ResourceTableComponent,
            TableEmptyComponent,
            TableLoaderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TablesModule, imports: [CommonModule,
            LayoutModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TablesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        TableCapDirective,
                        TableWrapDirective,
                        TableHandleComponent,
                        MultiSelectPanelComponent,
                        ResourceTableComponent,
                        TableEmptyComponent,
                        TableLoaderComponent
                    ],
                    imports: [
                        CommonModule,
                        LayoutModule
                    ],
                    exports: [
                        TableCapDirective,
                        TableWrapDirective,
                        TableHandleComponent,
                        MultiSelectPanelComponent,
                        ResourceTableComponent,
                        TableEmptyComponent,
                        TableLoaderComponent
                    ]
                }]
        }] });

class ToolbarComponent {
    constructor() { }
    ngOnInit() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ToolbarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ToolbarComponent, selector: "sk-toolbar", ngImport: i0, template: "<div class=\"flex\">\r\n  <ng-content select=\"[sk-toolbar-left]\"></ng-content>\r\n</div>\r\n<div class=\"flex\">\r\n  <ng-content select=\"[sk-toolbar-right]\"></ng-content>\r\n</div>", styles: [":host{display:flex;justify-content:space-between;background:#f4f7fa;padding:4px 2rem;border-bottom:1px solid #e2e6e9}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ToolbarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-toolbar', template: "<div class=\"flex\">\r\n  <ng-content select=\"[sk-toolbar-left]\"></ng-content>\r\n</div>\r\n<div class=\"flex\">\r\n  <ng-content select=\"[sk-toolbar-right]\"></ng-content>\r\n</div>", styles: [":host{display:flex;justify-content:space-between;background:#f4f7fa;padding:4px 2rem;border-bottom:1px solid #e2e6e9}\n"] }]
        }], ctorParameters: () => [] });

class ToolbarBtnComponent {
    constructor() {
        this.class = 'btn sk-btn-combo sk-toolbar-btn';
    }
    ngOnInit() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ToolbarBtnComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ToolbarBtnComponent, selector: "button[sk-toolbar-btn]", inputs: { label: "label", iconClass: ["icon-class", "iconClass"], icon: "icon" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: "<i class=\"material-icons md-18 {{ iconClass }}\">{{ icon }}</i>\r\n<span>{{ label }}</span>\r\n", styles: [":host{color:#4a525dcc}:host>*{color:#4a525dcc}:host:hover *{color:#4a525d}:host.dropdown-toggle:after{vertical-align:.45rem}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ToolbarBtnComponent, decorators: [{
            type: Component,
            args: [{ selector: 'button[sk-toolbar-btn]', template: "<i class=\"material-icons md-18 {{ iconClass }}\">{{ icon }}</i>\r\n<span>{{ label }}</span>\r\n", styles: [":host{color:#4a525dcc}:host>*{color:#4a525dcc}:host:hover *{color:#4a525d}:host.dropdown-toggle:after{vertical-align:.45rem}\n"] }]
        }], ctorParameters: () => [], propDecorators: { label: [{
                type: Input
            }], iconClass: [{
                type: Input,
                args: ['icon-class']
            }], icon: [{
                type: Input
            }], class: [{
                type: HostBinding
            }] } });

class ToolbarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ToolbarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: ToolbarModule, declarations: [ToolbarComponent,
            ToolbarBtnComponent], imports: [CommonModule], exports: [ToolbarComponent,
            ToolbarBtnComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ToolbarModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ToolbarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ToolbarComponent,
                        ToolbarBtnComponent
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        ToolbarComponent,
                        ToolbarBtnComponent
                    ]
                }]
        }] });

class TaskManagerFactoryService {
    constructor(globalTaskManager, taskComponentFactoryService) {
        this.globalTaskManager = globalTaskManager;
        this.taskComponentFactoryService = taskComponentFactoryService;
        this.taskManagers = {};
    }
    getTaskManager(name = null) {
        if (!name) {
            return this.globalTaskManager;
        }
        if (!this.taskManagers[name]) {
            this.taskManagers[name] = new TaskManagerService(this.taskComponentFactoryService);
        }
        return this.taskManagers[name];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TaskManagerFactoryService, deps: [{ token: TaskManagerService }, { token: TaskComponentFactoryService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TaskManagerFactoryService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TaskManagerFactoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: TaskManagerService }, { type: TaskComponentFactoryService }] });

class UniversalTaskButtonDirective {
    constructor(taskManagerService) {
        this.taskManagerService = taskManagerService;
    }
    get isActive() {
        return this.taskManagerService.isTaskActive(this.taskName);
    }
    get isStashed() {
        const task = this.taskManagerService.getUniversalTask(this.taskName);
        return !this.isActive && task.state === 'loaded';
    }
    async onClick() {
        try {
            await this.taskManagerService.toggleUniversal(this.taskName);
        }
        catch (e) {
            OperationCancelledError.suppressOnCancel(e);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UniversalTaskButtonDirective, deps: [{ token: TaskManagerService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: UniversalTaskButtonDirective, selector: "button[skUniversalTaskButton]", inputs: { taskName: "taskName" }, host: { listeners: { "click": "onClick()" }, properties: { "class.active": "this.isActive", "class.stashed": "this.isStashed" }, classAttribute: "sk-panel-btn" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UniversalTaskButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[skUniversalTaskButton]',
                    host: {
                        class: 'sk-panel-btn'
                    }
                }]
        }], ctorParameters: () => [{ type: TaskManagerService }], propDecorators: { taskName: [{
                type: Input
            }], isActive: [{
                type: HostBinding,
                args: ['class.active']
            }], isStashed: [{
                type: HostBinding,
                args: ['class.stashed']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class TasksModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TasksModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: TasksModule, declarations: [UniversalTaskButtonDirective], imports: [ButtonsModule], exports: [UniversalTaskButtonDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TasksModule, imports: [ButtonsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TasksModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        UniversalTaskButtonDirective
                    ],
                    imports: [
                        ButtonsModule
                    ],
                    exports: [
                        UniversalTaskButtonDirective
                    ]
                }]
        }] });

class ModalsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ModalsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: ModalsModule, declarations: [ErrorModalComponent,
            ModalComponent,
            SimpleModalComponent,
            ModalFooterComponent,
            ModalBodyComponent,
            ModalHeaderComponent,
            WarningModalComponent,
            SuccessModalComponent,
            DestructiveModalComponent], imports: [CommonModule,
            ButtonsModule,
            FormsModule$1], exports: [ErrorModalComponent,
            SimpleModalComponent,
            WarningModalComponent,
            SuccessModalComponent,
            DestructiveModalComponent,
            ModalComponent,
            ModalFooterComponent,
            ModalBodyComponent,
            ModalHeaderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ModalsModule, imports: [CommonModule,
            ButtonsModule,
            FormsModule$1] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ModalsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ErrorModalComponent,
                        ModalComponent,
                        SimpleModalComponent,
                        ModalFooterComponent,
                        ModalBodyComponent,
                        ModalHeaderComponent,
                        WarningModalComponent,
                        SuccessModalComponent,
                        DestructiveModalComponent,
                    ],
                    imports: [
                        CommonModule,
                        ButtonsModule,
                        FormsModule$1
                    ],
                    exports: [
                        ErrorModalComponent,
                        SimpleModalComponent,
                        WarningModalComponent,
                        SuccessModalComponent,
                        DestructiveModalComponent,
                        ModalComponent,
                        ModalFooterComponent,
                        ModalBodyComponent,
                        ModalHeaderComponent
                    ]
                }]
        }] });

/*
 *  The facade
 */

class DownloadService {
    constructor() {
        this.downloadLink = document.createElement('a');
        this.defaultRegex = /(?<=filename=)(.*)(?=; filename)/; // find everything after 'filename=' and before '; filename'
        this.utf8regex = /(?<=filename\*=UTF-8'')(.*)/; // find everything after 'filename\*=UTF-8'''
    }
    getDownload(body, headers) {
        let fileName = this.getContentDispositionFromResponse(headers, this.utf8regex);
        // Some old browsers may not allow utf-8 encoding, so just in case, we'll try one more time without it
        if (!fileName) {
            fileName = this.getContentDispositionFromResponse(headers, this.defaultRegex)?.replace(/['"]/g, '');
        }
        if (fileName) {
            const binaryData = [];
            binaryData.push(body);
            this.downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'blob' }));
            this.downloadLink.setAttribute('download', decodeURIComponent(fileName));
            this.downloadLink.click();
            window.URL.revokeObjectURL(this.downloadLink.href);
        }
        else {
            throw new Error('file name is missing');
        }
    }
    getDownloadFromPath(filePath) {
        if (filePath) {
            this.downloadLink.href = filePath;
            const fileName = filePath.replace(/^.*[\\\/]/, '');
            this.downloadLink.setAttribute('download', fileName);
            this.downloadLink.click();
        }
    }
    getContentDispositionFromResponse(headers, regex) {
        return headers.get('Content-Disposition')?.match(regex)?.[0];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DownloadService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DownloadService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: DownloadService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

var PartnerAuthenticationType;
(function (PartnerAuthenticationType) {
    PartnerAuthenticationType[PartnerAuthenticationType["SkykickAuth"] = 0] = "SkykickAuth";
    PartnerAuthenticationType[PartnerAuthenticationType["O365Auth"] = 1] = "O365Auth";
})(PartnerAuthenticationType || (PartnerAuthenticationType = {}));

/*
    https://angular.io/guide/dependency-injection-providers#using-an-injectiontoken-object
    InjectionToken's are a way to inject some value into a component/service via Angular's DI container.

    The component/service expecting the value provided by this token needs to have the following code in it's constructor:
        constructor(
            @Inject(PRODUCT_NAME_TOKEN) readonly productName: string
        ) {}

    The value can be provided to the component/service through specifing it in the module's 'providers' metadata property as follows:
        @NgModule({
        providers: [
            { provide: PRODUCT_NAME_TOKEN, useValue: 'Cloud Manager' }
        ]
})
 */
const PRODUCT_NAME_TOKEN = new InjectionToken('The name of the product to be injected into the component by the consuming app.');

class MfaRequiredComponent {
    get partnerSettingsRoute() {
        return this.routeResolverService
            .generatePipeRoute(AppId.Papi, `partner/admin/${this.partnerId}/authenticationsettings`);
    }
    get partnerId() {
        return this.user
            .partnerId;
    }
    get user() {
        return this.userProvider
            .getCurrentUser();
    }
    get isAdmin() {
        return this.user
            .hasPermission('admin_editall');
    }
    get manageAccountUrl() {
        return this.routeResolverService
            .generateRatRoute(AppId.Portal, 'settings/account');
    }
    constructor(userProvider, httpClient, routeResolverService, domSanitizer, partnerService, productName) {
        this.userProvider = userProvider;
        this.httpClient = httpClient;
        this.routeResolverService = routeResolverService;
        this.domSanitizer = domSanitizer;
        this.partnerService = partnerService;
        this.productName = productName;
        this.class = 'w-100 d-flex justify-content-center';
        this.PartnerAuthenticationType = PartnerAuthenticationType;
        this.emailRequestSending = false;
        this.emailRequestSent = false;
        this.messaging$ = this.httpClient
            .get(this.partnerSettingsRoute)
            .pipe(map(settings => settings.authenticationType), map(authType => {
            let messaging;
            switch (true) {
                case authType === PartnerAuthenticationType.SkykickAuth && this.isAdmin:
                    messaging = {
                        message: this.nonO365AdminMessage,
                        displayButton: false
                    };
                    break;
                case authType === PartnerAuthenticationType.SkykickAuth && !this.isAdmin:
                    messaging = {
                        message: this.nonO365NonadminMessage,
                        displayButton: true
                    };
                    break;
                case authType === PartnerAuthenticationType.O365Auth && this.isAdmin:
                    messaging = {
                        message: this.o365AdminMessage,
                        displayButton: false
                    };
                    break;
                case authType === PartnerAuthenticationType.O365Auth && !this.isAdmin:
                    messaging = {
                        message: this.o365NonadminMessage,
                        displayButton: true
                    };
                    break;
            }
            messaging.message = this.domSanitizer
                .sanitize(SecurityContext.HTML, messaging.message);
            messaging.headerText = authType === PartnerAuthenticationType.O365Auth
                ? 'MFA is disabled on this Office 365 user account'
                : 'Multi-factor authentication is disabled';
            return messaging;
        }), catchError(_ => of({
            displayButton: false,
            message: 'An error occurred trying to communicate with our servers. Please try again or contact support.',
            headerText: 'Something went wrong'
        })));
        this.onDestroy$ = new Subject();
        this.nonO365AdminMessage = `${productName} requires each user to log in with strong authentication enabled. ` +
            `This must include explicit MFA enablement using Basic Authentication, or if using Microsoft 365 SSO to login to ConnectWise, ` +
            `activating MFA explicitly in M365, or by using Microsoft Security Defaults or Conditional Access Policies. ` +
            `Visit the <a href="${this.manageAccountUrl}">Account page</a> to modify Authentication settings.`;
        this.nonO365NonadminMessage = `${productName} requires each user to log in with strong authentication enabled. ` +
            `This must include explicit MFA enablement using Basic Authentication, or if using Microsoft 365 SSO to login to ConnectWise, ` +
            `activating MFA explicitly in M365, or by using Microsoft Security Defaults or Conditional Access Policies. ` +
            `Visit the <a href="${this.manageAccountUrl}">Account page</a> to modify Authentication settings. ` +
            `Click the button below to request assistance from your administrator.`;
        this.o365AdminMessage = `${productName} requires each user to log in with strong authentication enabled. ` +
            `This must include explicit MFA enablement using Basic Authentication, or if using Microsoft 365 SSO to login to ConnectWise, ` +
            `activating MFA explicitly in M365, or by using Microsoft Security Defaults or Conditional Access Policies. ` +
            `Visit the <a href="${this.manageAccountUrl}">Account page</a> to modify Authentication settings.`;
        this.o365NonadminMessage = `${productName} requires each user to log in with strong authentication enabled. ` +
            `This must include explicit MFA enablement using Basic Authentication, or if using Microsoft 365 SSO to login to ConnectWise, ` +
            `activating MFA explicitly in M365, or by using Microsoft Security Defaults or Conditional Access Policies. ` +
            `Visit the <a href="${this.manageAccountUrl}">Account page</a> to modify Authentication settings. ` +
            `Click the button below to request assistance from your administrator.`;
    }
    sendEmail() {
        this.emailRequestSending = true;
        this.partnerService
            .sendMfaRequest(this.productName)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
            this.emailRequestSending = false;
            this.emailRequestSent = true;
        });
    }
    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: MfaRequiredComponent, deps: [{ token: i2$1.AbstractUserProvider }, { token: i1$2.HttpClient }, { token: RouteResolverService }, { token: i4$1.DomSanitizer }, { token: PartnerService }, { token: PRODUCT_NAME_TOKEN }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: MfaRequiredComponent, selector: "sk-mfa-required", host: { properties: { "class": "this.class" } }, ngImport: i0, template: "<div class=\"d-flex flex-column align-items-center mt-5 w-50\">\r\n    <!-- BEGIN SVG GRAPHIC -->\r\n    <div class=\"my-4\">\r\n      <svg width=\"108px\" height=\"108px\" viewBox=\"0 0 108 108\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n        <title>art-access</title>\r\n        <g id=\"Empty-States\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n            <g id=\"Empty-Access\" transform=\"translate(-744.000000, -227.000000)\">\r\n                <g id=\"empty-state/CTA-Copy\" transform=\"translate(623.000000, 227.000000)\">\r\n                    <g id=\"art-access\" transform=\"translate(121.000000, 0.000000)\">\r\n                        <polygon id=\"Fill-1\" fill=\"#FFFFFF\" points=\"0 108 108 108 108 0 0 0\"></polygon>\r\n                        <g id=\"Group-30\" transform=\"translate(12.000000, 18.000000)\">\r\n                            <path d=\"M16.4796,12.5 C16.4796,13.881 15.3606,15 13.9796,15 C12.5986,15 11.4796,13.881 11.4796,12.5 C11.4796,11.119 12.5986,10 13.9796,10 C15.3606,10 16.4796,11.119 16.4796,12.5\" id=\"Fill-2\" fill=\"#00ADFF\"></path>\r\n                            <path d=\"M69.3868,26.8506 C69.1308,26.8506 68.8748,26.7526 68.6798,26.5576 C68.2888,26.1666 68.2888,25.5346 68.6798,25.1436 L70.8008,23.0226 C71.1918,22.6316 71.8248,22.6316 72.2148,23.0226 C72.6058,23.4126 72.6058,24.0456 72.2148,24.4366 L70.0938,26.5576 C69.8988,26.7526 69.6428,26.8506 69.3868,26.8506 M64.4378,31.7996 C64.1818,31.7996 63.9258,31.7026 63.7308,31.5066 C63.3398,31.1166 63.3398,30.4836 63.7308,30.0926 L65.8518,27.9716 C66.2418,27.5806 66.8748,27.5806 67.2658,27.9716 C67.6568,28.3626 67.6568,28.9956 67.2658,29.3856 L65.1448,31.5066 C64.9498,31.7026 64.6938,31.7996 64.4378,31.7996 M59.4878,36.7496 C59.2318,36.7496 58.9758,36.6526 58.7808,36.4566 C58.3898,36.0666 58.3898,35.4336 58.7808,35.0426 L60.9018,32.9216 C61.2918,32.5316 61.9248,32.5316 62.3158,32.9216 C62.7058,33.3126 62.7058,33.9456 62.3158,34.3356 L60.1948,36.4566 C59.9988,36.6526 59.7428,36.7496 59.4878,36.7496\" id=\"Fill-4\" fill=\"#DFE4EC\"></path>\r\n                            <path d=\"M20.3868,38.8506 C20.1308,38.8506 19.8748,38.7526 19.6798,38.5576 C19.2888,38.1666 19.2888,37.5346 19.6798,37.1436 L21.8008,35.0226 C22.1918,34.6316 22.8248,34.6316 23.2148,35.0226 C23.6058,35.4126 23.6058,36.0456 23.2148,36.4366 L21.0938,38.5576 C20.8988,38.7526 20.6428,38.8506 20.3868,38.8506 M15.4378,43.7996 C15.1818,43.7996 14.9258,43.7026 14.7308,43.5066 C14.3398,43.1166 14.3398,42.4836 14.7308,42.0926 L16.8518,39.9716 C17.2418,39.5806 17.8748,39.5806 18.2658,39.9716 C18.6568,40.3626 18.6568,40.9956 18.2658,41.3856 L16.1448,43.5066 C15.9498,43.7026 15.6938,43.7996 15.4378,43.7996 M10.4878,48.7496 C10.2318,48.7496 9.9758,48.6526 9.7808,48.4566 C9.3898,48.0666 9.3898,47.4336 9.7808,47.0426 L11.9018,44.9216 C12.2918,44.5316 12.9248,44.5316 13.3158,44.9216 C13.7058,45.3126 13.7058,45.9456 13.3158,46.3356 L11.1948,48.4566 C10.9988,48.6526 10.7428,48.7496 10.4878,48.7496\" id=\"Fill-6\" fill=\"#DFE4EC\"></path>\r\n                            <path d=\"M74.4552,55.3154 C74.0662,50.3704 69.8832,46.4424 64.8582,46.4424 C63.3582,46.4424 61.8872,46.7904 60.5542,47.4574 C58.2872,44.8364 54.9512,43.2944 51.4702,43.2944 C44.8582,43.2944 39.4792,48.6714 39.4792,55.2844 L39.4792,55.3344 C36.5892,56.8554 34.7552,59.8584 34.7552,63.1594 C34.7552,68.0344 38.7212,72.0004 43.5962,72.0004 L70.3712,72.0004 C75.2452,72.0004 79.2112,68.0344 79.2112,63.1594 C79.2112,59.8434 77.3632,56.8284 74.4552,55.3154\" id=\"Fill-8\" fill=\"#E1F1FF\"></path>\r\n                            <path d=\"M5.4796,25.5 C5.4796,26.881 4.3606,28 2.9796,28 C1.5986,28 0.4796,26.881 0.4796,25.5 C0.4796,24.119 1.5986,23 2.9796,23 C4.3606,23 5.4796,24.119 5.4796,25.5\" id=\"Fill-10\" fill=\"#DFE9EF\"></path>\r\n                            <path d=\"M78.4796,19.5 C78.4796,20.881 77.3606,22 75.9796,22 C74.5986,22 73.4796,20.881 73.4796,19.5 C73.4796,18.119 74.5986,17 75.9796,17 C77.3606,17 78.4796,18.119 78.4796,19.5\" id=\"Fill-12\" fill=\"#FFC007\"></path>\r\n                            <path d=\"M76.4796,41 C74.2736,41 72.4796,42.794 72.4796,45 C72.4796,47.206 74.2736,49 76.4796,49 C78.6856,49 80.4796,47.206 80.4796,45 C80.4796,42.794 78.6856,41 76.4796,41 M76.4796,50 C73.7226,50 71.4796,47.757 71.4796,45 C71.4796,42.243 73.7226,40 76.4796,40 C79.2366,40 81.4796,42.243 81.4796,45 C81.4796,47.757 79.2366,50 76.4796,50\" id=\"Fill-14\" fill=\"#FFC007\"></path>\r\n                            <path d=\"M42.5899,1 L41.4099,1 C32.2509,1 24.7979,8.452 24.7979,17.611 L24.7979,29.962 L32.3229,29.962 L32.3229,17.611 C32.3229,12.602 36.3999,8.524 41.4099,8.524 L42.5899,8.524 C47.5999,8.524 51.6769,12.602 51.6769,17.611 L51.6769,29.962 L59.2009,29.962 L59.2009,17.611 C59.2009,8.452 51.7489,1 42.5899,1\" id=\"Fill-16\" fill=\"#FFFFFF\"></path>\r\n                            <path d=\"M52.6768,28.9619 L58.2008,28.9619 L58.2008,17.6109 C58.2008,9.0029 51.1978,1.9999 42.5898,1.9999 L41.4098,1.9999 C32.8018,1.9999 25.7978,9.0029 25.7978,17.6109 L25.7978,28.9619 L31.3228,28.9619 L31.3228,17.6109 C31.3228,12.0499 35.8488,7.5249 41.4098,7.5249 L42.5898,7.5249 C48.1518,7.5249 52.6768,12.0499 52.6768,17.6109 L52.6768,28.9619 Z M60.2008,30.9619 L50.6768,30.9619 L50.6768,17.6109 C50.6768,13.1519 47.0488,9.5249 42.5898,9.5249 L41.4098,9.5249 C36.9508,9.5249 33.3228,13.1519 33.3228,17.6109 L33.3228,30.9619 L23.7978,30.9619 L23.7978,17.6109 C23.7978,7.8999 31.6978,-0.0001 41.4098,-0.0001 L42.5898,-0.0001 C52.3008,-0.0001 60.2008,7.8999 60.2008,17.6109 L60.2008,30.9619 Z\" id=\"Fill-18\" fill=\"#0859A3\"></path>\r\n                            <path d=\"M59.3565,63 L24.6435,63 C21.9475,63 19.7415,60.794 19.7415,58.098 L19.7415,34.864 C19.7415,32.168 21.9475,29.962 24.6435,29.962 L59.3565,29.962 C62.0525,29.962 64.2585,32.168 64.2585,34.864 L64.2585,58.098 C64.2585,60.794 62.0525,63 59.3565,63\" id=\"Fill-20\" fill=\"#FFFFFF\"></path>\r\n                            <path d=\"M24.6436,30.9619 C22.4926,30.9619 20.7416,32.7129 20.7416,34.8639 L20.7416,58.0979 C20.7416,60.2489 22.4926,61.9999 24.6436,61.9999 L59.3566,61.9999 C61.5076,61.9999 63.2586,60.2489 63.2586,58.0979 L63.2586,34.8639 C63.2586,32.7129 61.5076,30.9619 59.3566,30.9619 L24.6436,30.9619 Z M59.3566,63.9999 L24.6436,63.9999 C21.3886,63.9999 18.7416,61.3529 18.7416,58.0979 L18.7416,34.8639 C18.7416,31.6089 21.3886,28.9619 24.6436,28.9619 L59.3566,28.9619 C62.6116,28.9619 65.2586,31.6089 65.2586,34.8639 L65.2586,58.0979 C65.2586,61.3529 62.6116,63.9999 59.3566,63.9999 L59.3566,63.9999 Z\" id=\"Fill-22\" fill=\"#0859A3\"></path>\r\n                            <path d=\"M42.0001,39.3604 C39.9271,39.3604 38.2391,41.0474 38.2391,43.1214 C38.2391,44.2034 38.7251,45.2394 39.5701,45.9634 C39.7921,46.1524 39.9201,46.4304 39.9201,46.7224 L39.9201,52.5354 C39.9201,53.6814 40.8541,54.6154 42.0001,54.6154 C43.1461,54.6154 44.0801,53.6814 44.0801,52.5354 L44.0801,46.7224 C44.0801,46.4304 44.2081,46.1534 44.4301,45.9634 C45.2751,45.2394 45.7601,44.2034 45.7601,43.1214 C45.7601,41.0474 44.0731,39.3604 42.0001,39.3604 M42.0001,56.6154 C39.7501,56.6154 37.9201,54.7854 37.9201,52.5354 L37.9201,47.1584 C36.8471,46.0784 36.2391,44.6314 36.2391,43.1214 C36.2391,39.9444 38.8231,37.3604 42.0001,37.3604 C45.1761,37.3604 47.7601,39.9444 47.7601,43.1214 C47.7601,44.6314 47.1531,46.0784 46.0801,47.1574 L46.0801,52.5354 C46.0801,54.7854 44.2501,56.6154 42.0001,56.6154\" id=\"Fill-24\" fill=\"#00ADFF\"></path>\r\n                            <path d=\"M40.1114,33.8584 L26.9464,33.8584 C24.8594,33.8584 23.1524,35.5654 23.1524,37.6524 L23.1524,50.8174 L40.1114,33.8584 Z\" id=\"Fill-26\" fill=\"#EDF2F7\"></path>\r\n                            <path d=\"M5.4796,48 C3.2736,48 1.4796,49.794 1.4796,52 C1.4796,54.206 3.2736,56 5.4796,56 C7.6856,56 9.4796,54.206 9.4796,52 C9.4796,49.794 7.6856,48 5.4796,48 M5.4796,57 C2.7226,57 0.4796,54.757 0.4796,52 C0.4796,49.243 2.7226,47 5.4796,47 C8.2366,47 10.4796,49.243 10.4796,52 C10.4796,54.757 8.2366,57 5.4796,57\" id=\"Fill-28\" fill=\"#00ADFF\"></path>\r\n                        </g>\r\n                    </g>\r\n                </g>\r\n            </g>\r\n        </g>\r\n      </svg>\r\n    </div>\r\n    <!-- END SVG GRAPHIC -->\r\n\r\n    <ng-container *ngIf=\"messaging$ | async as messaging\" >\r\n        <h3>{{ messaging.headerText }}</h3>\r\n        <p class=\"pt-50 text-start text-muted\" [innerHTML]=\"messaging.message\"></p>\r\n        <ng-container *ngIf=\"messaging.displayButton\">\r\n            <button sk-spinner-button type=\"button\" (click)=\"sendEmail()\" [active]=\"emailRequestSending\" [color]=\"'primary'\" [disabled]=\"emailRequestSending || emailRequestSent\">{{ emailRequestSent ? 'MFA Request Sent To Admin' : 'Request MFA From Admin' }}</button>\r\n        </ng-container>\r\n    </ng-container>\r\n  </div>\r\n", dependencies: [{ kind: "component", type: SpinnerButtonComponent, selector: "button[sk-spinner-button]", inputs: ["disabled", "color", "active", "isSpinning"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: MfaRequiredComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-mfa-required', template: "<div class=\"d-flex flex-column align-items-center mt-5 w-50\">\r\n    <!-- BEGIN SVG GRAPHIC -->\r\n    <div class=\"my-4\">\r\n      <svg width=\"108px\" height=\"108px\" viewBox=\"0 0 108 108\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n        <title>art-access</title>\r\n        <g id=\"Empty-States\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n            <g id=\"Empty-Access\" transform=\"translate(-744.000000, -227.000000)\">\r\n                <g id=\"empty-state/CTA-Copy\" transform=\"translate(623.000000, 227.000000)\">\r\n                    <g id=\"art-access\" transform=\"translate(121.000000, 0.000000)\">\r\n                        <polygon id=\"Fill-1\" fill=\"#FFFFFF\" points=\"0 108 108 108 108 0 0 0\"></polygon>\r\n                        <g id=\"Group-30\" transform=\"translate(12.000000, 18.000000)\">\r\n                            <path d=\"M16.4796,12.5 C16.4796,13.881 15.3606,15 13.9796,15 C12.5986,15 11.4796,13.881 11.4796,12.5 C11.4796,11.119 12.5986,10 13.9796,10 C15.3606,10 16.4796,11.119 16.4796,12.5\" id=\"Fill-2\" fill=\"#00ADFF\"></path>\r\n                            <path d=\"M69.3868,26.8506 C69.1308,26.8506 68.8748,26.7526 68.6798,26.5576 C68.2888,26.1666 68.2888,25.5346 68.6798,25.1436 L70.8008,23.0226 C71.1918,22.6316 71.8248,22.6316 72.2148,23.0226 C72.6058,23.4126 72.6058,24.0456 72.2148,24.4366 L70.0938,26.5576 C69.8988,26.7526 69.6428,26.8506 69.3868,26.8506 M64.4378,31.7996 C64.1818,31.7996 63.9258,31.7026 63.7308,31.5066 C63.3398,31.1166 63.3398,30.4836 63.7308,30.0926 L65.8518,27.9716 C66.2418,27.5806 66.8748,27.5806 67.2658,27.9716 C67.6568,28.3626 67.6568,28.9956 67.2658,29.3856 L65.1448,31.5066 C64.9498,31.7026 64.6938,31.7996 64.4378,31.7996 M59.4878,36.7496 C59.2318,36.7496 58.9758,36.6526 58.7808,36.4566 C58.3898,36.0666 58.3898,35.4336 58.7808,35.0426 L60.9018,32.9216 C61.2918,32.5316 61.9248,32.5316 62.3158,32.9216 C62.7058,33.3126 62.7058,33.9456 62.3158,34.3356 L60.1948,36.4566 C59.9988,36.6526 59.7428,36.7496 59.4878,36.7496\" id=\"Fill-4\" fill=\"#DFE4EC\"></path>\r\n                            <path d=\"M20.3868,38.8506 C20.1308,38.8506 19.8748,38.7526 19.6798,38.5576 C19.2888,38.1666 19.2888,37.5346 19.6798,37.1436 L21.8008,35.0226 C22.1918,34.6316 22.8248,34.6316 23.2148,35.0226 C23.6058,35.4126 23.6058,36.0456 23.2148,36.4366 L21.0938,38.5576 C20.8988,38.7526 20.6428,38.8506 20.3868,38.8506 M15.4378,43.7996 C15.1818,43.7996 14.9258,43.7026 14.7308,43.5066 C14.3398,43.1166 14.3398,42.4836 14.7308,42.0926 L16.8518,39.9716 C17.2418,39.5806 17.8748,39.5806 18.2658,39.9716 C18.6568,40.3626 18.6568,40.9956 18.2658,41.3856 L16.1448,43.5066 C15.9498,43.7026 15.6938,43.7996 15.4378,43.7996 M10.4878,48.7496 C10.2318,48.7496 9.9758,48.6526 9.7808,48.4566 C9.3898,48.0666 9.3898,47.4336 9.7808,47.0426 L11.9018,44.9216 C12.2918,44.5316 12.9248,44.5316 13.3158,44.9216 C13.7058,45.3126 13.7058,45.9456 13.3158,46.3356 L11.1948,48.4566 C10.9988,48.6526 10.7428,48.7496 10.4878,48.7496\" id=\"Fill-6\" fill=\"#DFE4EC\"></path>\r\n                            <path d=\"M74.4552,55.3154 C74.0662,50.3704 69.8832,46.4424 64.8582,46.4424 C63.3582,46.4424 61.8872,46.7904 60.5542,47.4574 C58.2872,44.8364 54.9512,43.2944 51.4702,43.2944 C44.8582,43.2944 39.4792,48.6714 39.4792,55.2844 L39.4792,55.3344 C36.5892,56.8554 34.7552,59.8584 34.7552,63.1594 C34.7552,68.0344 38.7212,72.0004 43.5962,72.0004 L70.3712,72.0004 C75.2452,72.0004 79.2112,68.0344 79.2112,63.1594 C79.2112,59.8434 77.3632,56.8284 74.4552,55.3154\" id=\"Fill-8\" fill=\"#E1F1FF\"></path>\r\n                            <path d=\"M5.4796,25.5 C5.4796,26.881 4.3606,28 2.9796,28 C1.5986,28 0.4796,26.881 0.4796,25.5 C0.4796,24.119 1.5986,23 2.9796,23 C4.3606,23 5.4796,24.119 5.4796,25.5\" id=\"Fill-10\" fill=\"#DFE9EF\"></path>\r\n                            <path d=\"M78.4796,19.5 C78.4796,20.881 77.3606,22 75.9796,22 C74.5986,22 73.4796,20.881 73.4796,19.5 C73.4796,18.119 74.5986,17 75.9796,17 C77.3606,17 78.4796,18.119 78.4796,19.5\" id=\"Fill-12\" fill=\"#FFC007\"></path>\r\n                            <path d=\"M76.4796,41 C74.2736,41 72.4796,42.794 72.4796,45 C72.4796,47.206 74.2736,49 76.4796,49 C78.6856,49 80.4796,47.206 80.4796,45 C80.4796,42.794 78.6856,41 76.4796,41 M76.4796,50 C73.7226,50 71.4796,47.757 71.4796,45 C71.4796,42.243 73.7226,40 76.4796,40 C79.2366,40 81.4796,42.243 81.4796,45 C81.4796,47.757 79.2366,50 76.4796,50\" id=\"Fill-14\" fill=\"#FFC007\"></path>\r\n                            <path d=\"M42.5899,1 L41.4099,1 C32.2509,1 24.7979,8.452 24.7979,17.611 L24.7979,29.962 L32.3229,29.962 L32.3229,17.611 C32.3229,12.602 36.3999,8.524 41.4099,8.524 L42.5899,8.524 C47.5999,8.524 51.6769,12.602 51.6769,17.611 L51.6769,29.962 L59.2009,29.962 L59.2009,17.611 C59.2009,8.452 51.7489,1 42.5899,1\" id=\"Fill-16\" fill=\"#FFFFFF\"></path>\r\n                            <path d=\"M52.6768,28.9619 L58.2008,28.9619 L58.2008,17.6109 C58.2008,9.0029 51.1978,1.9999 42.5898,1.9999 L41.4098,1.9999 C32.8018,1.9999 25.7978,9.0029 25.7978,17.6109 L25.7978,28.9619 L31.3228,28.9619 L31.3228,17.6109 C31.3228,12.0499 35.8488,7.5249 41.4098,7.5249 L42.5898,7.5249 C48.1518,7.5249 52.6768,12.0499 52.6768,17.6109 L52.6768,28.9619 Z M60.2008,30.9619 L50.6768,30.9619 L50.6768,17.6109 C50.6768,13.1519 47.0488,9.5249 42.5898,9.5249 L41.4098,9.5249 C36.9508,9.5249 33.3228,13.1519 33.3228,17.6109 L33.3228,30.9619 L23.7978,30.9619 L23.7978,17.6109 C23.7978,7.8999 31.6978,-0.0001 41.4098,-0.0001 L42.5898,-0.0001 C52.3008,-0.0001 60.2008,7.8999 60.2008,17.6109 L60.2008,30.9619 Z\" id=\"Fill-18\" fill=\"#0859A3\"></path>\r\n                            <path d=\"M59.3565,63 L24.6435,63 C21.9475,63 19.7415,60.794 19.7415,58.098 L19.7415,34.864 C19.7415,32.168 21.9475,29.962 24.6435,29.962 L59.3565,29.962 C62.0525,29.962 64.2585,32.168 64.2585,34.864 L64.2585,58.098 C64.2585,60.794 62.0525,63 59.3565,63\" id=\"Fill-20\" fill=\"#FFFFFF\"></path>\r\n                            <path d=\"M24.6436,30.9619 C22.4926,30.9619 20.7416,32.7129 20.7416,34.8639 L20.7416,58.0979 C20.7416,60.2489 22.4926,61.9999 24.6436,61.9999 L59.3566,61.9999 C61.5076,61.9999 63.2586,60.2489 63.2586,58.0979 L63.2586,34.8639 C63.2586,32.7129 61.5076,30.9619 59.3566,30.9619 L24.6436,30.9619 Z M59.3566,63.9999 L24.6436,63.9999 C21.3886,63.9999 18.7416,61.3529 18.7416,58.0979 L18.7416,34.8639 C18.7416,31.6089 21.3886,28.9619 24.6436,28.9619 L59.3566,28.9619 C62.6116,28.9619 65.2586,31.6089 65.2586,34.8639 L65.2586,58.0979 C65.2586,61.3529 62.6116,63.9999 59.3566,63.9999 L59.3566,63.9999 Z\" id=\"Fill-22\" fill=\"#0859A3\"></path>\r\n                            <path d=\"M42.0001,39.3604 C39.9271,39.3604 38.2391,41.0474 38.2391,43.1214 C38.2391,44.2034 38.7251,45.2394 39.5701,45.9634 C39.7921,46.1524 39.9201,46.4304 39.9201,46.7224 L39.9201,52.5354 C39.9201,53.6814 40.8541,54.6154 42.0001,54.6154 C43.1461,54.6154 44.0801,53.6814 44.0801,52.5354 L44.0801,46.7224 C44.0801,46.4304 44.2081,46.1534 44.4301,45.9634 C45.2751,45.2394 45.7601,44.2034 45.7601,43.1214 C45.7601,41.0474 44.0731,39.3604 42.0001,39.3604 M42.0001,56.6154 C39.7501,56.6154 37.9201,54.7854 37.9201,52.5354 L37.9201,47.1584 C36.8471,46.0784 36.2391,44.6314 36.2391,43.1214 C36.2391,39.9444 38.8231,37.3604 42.0001,37.3604 C45.1761,37.3604 47.7601,39.9444 47.7601,43.1214 C47.7601,44.6314 47.1531,46.0784 46.0801,47.1574 L46.0801,52.5354 C46.0801,54.7854 44.2501,56.6154 42.0001,56.6154\" id=\"Fill-24\" fill=\"#00ADFF\"></path>\r\n                            <path d=\"M40.1114,33.8584 L26.9464,33.8584 C24.8594,33.8584 23.1524,35.5654 23.1524,37.6524 L23.1524,50.8174 L40.1114,33.8584 Z\" id=\"Fill-26\" fill=\"#EDF2F7\"></path>\r\n                            <path d=\"M5.4796,48 C3.2736,48 1.4796,49.794 1.4796,52 C1.4796,54.206 3.2736,56 5.4796,56 C7.6856,56 9.4796,54.206 9.4796,52 C9.4796,49.794 7.6856,48 5.4796,48 M5.4796,57 C2.7226,57 0.4796,54.757 0.4796,52 C0.4796,49.243 2.7226,47 5.4796,47 C8.2366,47 10.4796,49.243 10.4796,52 C10.4796,54.757 8.2366,57 5.4796,57\" id=\"Fill-28\" fill=\"#00ADFF\"></path>\r\n                        </g>\r\n                    </g>\r\n                </g>\r\n            </g>\r\n        </g>\r\n      </svg>\r\n    </div>\r\n    <!-- END SVG GRAPHIC -->\r\n\r\n    <ng-container *ngIf=\"messaging$ | async as messaging\" >\r\n        <h3>{{ messaging.headerText }}</h3>\r\n        <p class=\"pt-50 text-start text-muted\" [innerHTML]=\"messaging.message\"></p>\r\n        <ng-container *ngIf=\"messaging.displayButton\">\r\n            <button sk-spinner-button type=\"button\" (click)=\"sendEmail()\" [active]=\"emailRequestSending\" [color]=\"'primary'\" [disabled]=\"emailRequestSending || emailRequestSent\">{{ emailRequestSent ? 'MFA Request Sent To Admin' : 'Request MFA From Admin' }}</button>\r\n        </ng-container>\r\n    </ng-container>\r\n  </div>\r\n" }]
        }], ctorParameters: () => [{ type: i2$1.AbstractUserProvider }, { type: i1$2.HttpClient }, { type: RouteResolverService }, { type: i4$1.DomSanitizer }, { type: PartnerService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PRODUCT_NAME_TOKEN]
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });

class NoAccessComponent {
    constructor() { }
    ngOnInit() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NoAccessComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NoAccessComponent, selector: "sk-no-access", host: { classAttribute: "w-100" }, ngImport: i0, template: "<div class=\"d-flex flex-column align-items-center mt-5\">\r\n    <!-- BEGIN SVG GRAPHIC -->\r\n    <div class=\"my-4\">\r\n      <svg width=\"108px\" height=\"108px\" viewBox=\"0 0 108 108\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n        <title>art-access</title>\r\n        <g id=\"Empty-States\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n            <g id=\"Empty-Access\" transform=\"translate(-744.000000, -227.000000)\">\r\n                <g id=\"empty-state/CTA-Copy\" transform=\"translate(623.000000, 227.000000)\">\r\n                    <g id=\"art-access\" transform=\"translate(121.000000, 0.000000)\">\r\n                        <polygon id=\"Fill-1\" fill=\"#FFFFFF\" points=\"0 108 108 108 108 0 0 0\"></polygon>\r\n                        <g id=\"Group-30\" transform=\"translate(12.000000, 18.000000)\">\r\n                            <path d=\"M16.4796,12.5 C16.4796,13.881 15.3606,15 13.9796,15 C12.5986,15 11.4796,13.881 11.4796,12.5 C11.4796,11.119 12.5986,10 13.9796,10 C15.3606,10 16.4796,11.119 16.4796,12.5\" id=\"Fill-2\" fill=\"#00ADFF\"></path>\r\n                            <path d=\"M69.3868,26.8506 C69.1308,26.8506 68.8748,26.7526 68.6798,26.5576 C68.2888,26.1666 68.2888,25.5346 68.6798,25.1436 L70.8008,23.0226 C71.1918,22.6316 71.8248,22.6316 72.2148,23.0226 C72.6058,23.4126 72.6058,24.0456 72.2148,24.4366 L70.0938,26.5576 C69.8988,26.7526 69.6428,26.8506 69.3868,26.8506 M64.4378,31.7996 C64.1818,31.7996 63.9258,31.7026 63.7308,31.5066 C63.3398,31.1166 63.3398,30.4836 63.7308,30.0926 L65.8518,27.9716 C66.2418,27.5806 66.8748,27.5806 67.2658,27.9716 C67.6568,28.3626 67.6568,28.9956 67.2658,29.3856 L65.1448,31.5066 C64.9498,31.7026 64.6938,31.7996 64.4378,31.7996 M59.4878,36.7496 C59.2318,36.7496 58.9758,36.6526 58.7808,36.4566 C58.3898,36.0666 58.3898,35.4336 58.7808,35.0426 L60.9018,32.9216 C61.2918,32.5316 61.9248,32.5316 62.3158,32.9216 C62.7058,33.3126 62.7058,33.9456 62.3158,34.3356 L60.1948,36.4566 C59.9988,36.6526 59.7428,36.7496 59.4878,36.7496\" id=\"Fill-4\" fill=\"#DFE4EC\"></path>\r\n                            <path d=\"M20.3868,38.8506 C20.1308,38.8506 19.8748,38.7526 19.6798,38.5576 C19.2888,38.1666 19.2888,37.5346 19.6798,37.1436 L21.8008,35.0226 C22.1918,34.6316 22.8248,34.6316 23.2148,35.0226 C23.6058,35.4126 23.6058,36.0456 23.2148,36.4366 L21.0938,38.5576 C20.8988,38.7526 20.6428,38.8506 20.3868,38.8506 M15.4378,43.7996 C15.1818,43.7996 14.9258,43.7026 14.7308,43.5066 C14.3398,43.1166 14.3398,42.4836 14.7308,42.0926 L16.8518,39.9716 C17.2418,39.5806 17.8748,39.5806 18.2658,39.9716 C18.6568,40.3626 18.6568,40.9956 18.2658,41.3856 L16.1448,43.5066 C15.9498,43.7026 15.6938,43.7996 15.4378,43.7996 M10.4878,48.7496 C10.2318,48.7496 9.9758,48.6526 9.7808,48.4566 C9.3898,48.0666 9.3898,47.4336 9.7808,47.0426 L11.9018,44.9216 C12.2918,44.5316 12.9248,44.5316 13.3158,44.9216 C13.7058,45.3126 13.7058,45.9456 13.3158,46.3356 L11.1948,48.4566 C10.9988,48.6526 10.7428,48.7496 10.4878,48.7496\" id=\"Fill-6\" fill=\"#DFE4EC\"></path>\r\n                            <path d=\"M74.4552,55.3154 C74.0662,50.3704 69.8832,46.4424 64.8582,46.4424 C63.3582,46.4424 61.8872,46.7904 60.5542,47.4574 C58.2872,44.8364 54.9512,43.2944 51.4702,43.2944 C44.8582,43.2944 39.4792,48.6714 39.4792,55.2844 L39.4792,55.3344 C36.5892,56.8554 34.7552,59.8584 34.7552,63.1594 C34.7552,68.0344 38.7212,72.0004 43.5962,72.0004 L70.3712,72.0004 C75.2452,72.0004 79.2112,68.0344 79.2112,63.1594 C79.2112,59.8434 77.3632,56.8284 74.4552,55.3154\" id=\"Fill-8\" fill=\"#E1F1FF\"></path>\r\n                            <path d=\"M5.4796,25.5 C5.4796,26.881 4.3606,28 2.9796,28 C1.5986,28 0.4796,26.881 0.4796,25.5 C0.4796,24.119 1.5986,23 2.9796,23 C4.3606,23 5.4796,24.119 5.4796,25.5\" id=\"Fill-10\" fill=\"#DFE9EF\"></path>\r\n                            <path d=\"M78.4796,19.5 C78.4796,20.881 77.3606,22 75.9796,22 C74.5986,22 73.4796,20.881 73.4796,19.5 C73.4796,18.119 74.5986,17 75.9796,17 C77.3606,17 78.4796,18.119 78.4796,19.5\" id=\"Fill-12\" fill=\"#FFC007\"></path>\r\n                            <path d=\"M76.4796,41 C74.2736,41 72.4796,42.794 72.4796,45 C72.4796,47.206 74.2736,49 76.4796,49 C78.6856,49 80.4796,47.206 80.4796,45 C80.4796,42.794 78.6856,41 76.4796,41 M76.4796,50 C73.7226,50 71.4796,47.757 71.4796,45 C71.4796,42.243 73.7226,40 76.4796,40 C79.2366,40 81.4796,42.243 81.4796,45 C81.4796,47.757 79.2366,50 76.4796,50\" id=\"Fill-14\" fill=\"#FFC007\"></path>\r\n                            <path d=\"M42.5899,1 L41.4099,1 C32.2509,1 24.7979,8.452 24.7979,17.611 L24.7979,29.962 L32.3229,29.962 L32.3229,17.611 C32.3229,12.602 36.3999,8.524 41.4099,8.524 L42.5899,8.524 C47.5999,8.524 51.6769,12.602 51.6769,17.611 L51.6769,29.962 L59.2009,29.962 L59.2009,17.611 C59.2009,8.452 51.7489,1 42.5899,1\" id=\"Fill-16\" fill=\"#FFFFFF\"></path>\r\n                            <path d=\"M52.6768,28.9619 L58.2008,28.9619 L58.2008,17.6109 C58.2008,9.0029 51.1978,1.9999 42.5898,1.9999 L41.4098,1.9999 C32.8018,1.9999 25.7978,9.0029 25.7978,17.6109 L25.7978,28.9619 L31.3228,28.9619 L31.3228,17.6109 C31.3228,12.0499 35.8488,7.5249 41.4098,7.5249 L42.5898,7.5249 C48.1518,7.5249 52.6768,12.0499 52.6768,17.6109 L52.6768,28.9619 Z M60.2008,30.9619 L50.6768,30.9619 L50.6768,17.6109 C50.6768,13.1519 47.0488,9.5249 42.5898,9.5249 L41.4098,9.5249 C36.9508,9.5249 33.3228,13.1519 33.3228,17.6109 L33.3228,30.9619 L23.7978,30.9619 L23.7978,17.6109 C23.7978,7.8999 31.6978,-0.0001 41.4098,-0.0001 L42.5898,-0.0001 C52.3008,-0.0001 60.2008,7.8999 60.2008,17.6109 L60.2008,30.9619 Z\" id=\"Fill-18\" fill=\"#0859A3\"></path>\r\n                            <path d=\"M59.3565,63 L24.6435,63 C21.9475,63 19.7415,60.794 19.7415,58.098 L19.7415,34.864 C19.7415,32.168 21.9475,29.962 24.6435,29.962 L59.3565,29.962 C62.0525,29.962 64.2585,32.168 64.2585,34.864 L64.2585,58.098 C64.2585,60.794 62.0525,63 59.3565,63\" id=\"Fill-20\" fill=\"#FFFFFF\"></path>\r\n                            <path d=\"M24.6436,30.9619 C22.4926,30.9619 20.7416,32.7129 20.7416,34.8639 L20.7416,58.0979 C20.7416,60.2489 22.4926,61.9999 24.6436,61.9999 L59.3566,61.9999 C61.5076,61.9999 63.2586,60.2489 63.2586,58.0979 L63.2586,34.8639 C63.2586,32.7129 61.5076,30.9619 59.3566,30.9619 L24.6436,30.9619 Z M59.3566,63.9999 L24.6436,63.9999 C21.3886,63.9999 18.7416,61.3529 18.7416,58.0979 L18.7416,34.8639 C18.7416,31.6089 21.3886,28.9619 24.6436,28.9619 L59.3566,28.9619 C62.6116,28.9619 65.2586,31.6089 65.2586,34.8639 L65.2586,58.0979 C65.2586,61.3529 62.6116,63.9999 59.3566,63.9999 L59.3566,63.9999 Z\" id=\"Fill-22\" fill=\"#0859A3\"></path>\r\n                            <path d=\"M42.0001,39.3604 C39.9271,39.3604 38.2391,41.0474 38.2391,43.1214 C38.2391,44.2034 38.7251,45.2394 39.5701,45.9634 C39.7921,46.1524 39.9201,46.4304 39.9201,46.7224 L39.9201,52.5354 C39.9201,53.6814 40.8541,54.6154 42.0001,54.6154 C43.1461,54.6154 44.0801,53.6814 44.0801,52.5354 L44.0801,46.7224 C44.0801,46.4304 44.2081,46.1534 44.4301,45.9634 C45.2751,45.2394 45.7601,44.2034 45.7601,43.1214 C45.7601,41.0474 44.0731,39.3604 42.0001,39.3604 M42.0001,56.6154 C39.7501,56.6154 37.9201,54.7854 37.9201,52.5354 L37.9201,47.1584 C36.8471,46.0784 36.2391,44.6314 36.2391,43.1214 C36.2391,39.9444 38.8231,37.3604 42.0001,37.3604 C45.1761,37.3604 47.7601,39.9444 47.7601,43.1214 C47.7601,44.6314 47.1531,46.0784 46.0801,47.1574 L46.0801,52.5354 C46.0801,54.7854 44.2501,56.6154 42.0001,56.6154\" id=\"Fill-24\" fill=\"#00ADFF\"></path>\r\n                            <path d=\"M40.1114,33.8584 L26.9464,33.8584 C24.8594,33.8584 23.1524,35.5654 23.1524,37.6524 L23.1524,50.8174 L40.1114,33.8584 Z\" id=\"Fill-26\" fill=\"#EDF2F7\"></path>\r\n                            <path d=\"M5.4796,48 C3.2736,48 1.4796,49.794 1.4796,52 C1.4796,54.206 3.2736,56 5.4796,56 C7.6856,56 9.4796,54.206 9.4796,52 C9.4796,49.794 7.6856,48 5.4796,48 M5.4796,57 C2.7226,57 0.4796,54.757 0.4796,52 C0.4796,49.243 2.7226,47 5.4796,47 C8.2366,47 10.4796,49.243 10.4796,52 C10.4796,54.757 8.2366,57 5.4796,57\" id=\"Fill-28\" fill=\"#00ADFF\"></path>\r\n                        </g>\r\n                    </g>\r\n                </g>\r\n            </g>\r\n        </g>\r\n      </svg>\r\n    </div>\r\n    <!-- END SVG GRAPHIC -->\r\n\r\n    <div class=\"text-center\">\r\n        <div class=\"mb-1\">\r\n            <div #content><ng-content></ng-content></div>\r\n            <ng-container *ngIf=\"!content.hasChildNodes()\">\r\n                <h3>You do not have access.</h3>\r\n            </ng-container>\r\n        </div>\r\n    </div>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NoAccessComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-no-access', host: { class: 'w-100' }, template: "<div class=\"d-flex flex-column align-items-center mt-5\">\r\n    <!-- BEGIN SVG GRAPHIC -->\r\n    <div class=\"my-4\">\r\n      <svg width=\"108px\" height=\"108px\" viewBox=\"0 0 108 108\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n        <title>art-access</title>\r\n        <g id=\"Empty-States\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n            <g id=\"Empty-Access\" transform=\"translate(-744.000000, -227.000000)\">\r\n                <g id=\"empty-state/CTA-Copy\" transform=\"translate(623.000000, 227.000000)\">\r\n                    <g id=\"art-access\" transform=\"translate(121.000000, 0.000000)\">\r\n                        <polygon id=\"Fill-1\" fill=\"#FFFFFF\" points=\"0 108 108 108 108 0 0 0\"></polygon>\r\n                        <g id=\"Group-30\" transform=\"translate(12.000000, 18.000000)\">\r\n                            <path d=\"M16.4796,12.5 C16.4796,13.881 15.3606,15 13.9796,15 C12.5986,15 11.4796,13.881 11.4796,12.5 C11.4796,11.119 12.5986,10 13.9796,10 C15.3606,10 16.4796,11.119 16.4796,12.5\" id=\"Fill-2\" fill=\"#00ADFF\"></path>\r\n                            <path d=\"M69.3868,26.8506 C69.1308,26.8506 68.8748,26.7526 68.6798,26.5576 C68.2888,26.1666 68.2888,25.5346 68.6798,25.1436 L70.8008,23.0226 C71.1918,22.6316 71.8248,22.6316 72.2148,23.0226 C72.6058,23.4126 72.6058,24.0456 72.2148,24.4366 L70.0938,26.5576 C69.8988,26.7526 69.6428,26.8506 69.3868,26.8506 M64.4378,31.7996 C64.1818,31.7996 63.9258,31.7026 63.7308,31.5066 C63.3398,31.1166 63.3398,30.4836 63.7308,30.0926 L65.8518,27.9716 C66.2418,27.5806 66.8748,27.5806 67.2658,27.9716 C67.6568,28.3626 67.6568,28.9956 67.2658,29.3856 L65.1448,31.5066 C64.9498,31.7026 64.6938,31.7996 64.4378,31.7996 M59.4878,36.7496 C59.2318,36.7496 58.9758,36.6526 58.7808,36.4566 C58.3898,36.0666 58.3898,35.4336 58.7808,35.0426 L60.9018,32.9216 C61.2918,32.5316 61.9248,32.5316 62.3158,32.9216 C62.7058,33.3126 62.7058,33.9456 62.3158,34.3356 L60.1948,36.4566 C59.9988,36.6526 59.7428,36.7496 59.4878,36.7496\" id=\"Fill-4\" fill=\"#DFE4EC\"></path>\r\n                            <path d=\"M20.3868,38.8506 C20.1308,38.8506 19.8748,38.7526 19.6798,38.5576 C19.2888,38.1666 19.2888,37.5346 19.6798,37.1436 L21.8008,35.0226 C22.1918,34.6316 22.8248,34.6316 23.2148,35.0226 C23.6058,35.4126 23.6058,36.0456 23.2148,36.4366 L21.0938,38.5576 C20.8988,38.7526 20.6428,38.8506 20.3868,38.8506 M15.4378,43.7996 C15.1818,43.7996 14.9258,43.7026 14.7308,43.5066 C14.3398,43.1166 14.3398,42.4836 14.7308,42.0926 L16.8518,39.9716 C17.2418,39.5806 17.8748,39.5806 18.2658,39.9716 C18.6568,40.3626 18.6568,40.9956 18.2658,41.3856 L16.1448,43.5066 C15.9498,43.7026 15.6938,43.7996 15.4378,43.7996 M10.4878,48.7496 C10.2318,48.7496 9.9758,48.6526 9.7808,48.4566 C9.3898,48.0666 9.3898,47.4336 9.7808,47.0426 L11.9018,44.9216 C12.2918,44.5316 12.9248,44.5316 13.3158,44.9216 C13.7058,45.3126 13.7058,45.9456 13.3158,46.3356 L11.1948,48.4566 C10.9988,48.6526 10.7428,48.7496 10.4878,48.7496\" id=\"Fill-6\" fill=\"#DFE4EC\"></path>\r\n                            <path d=\"M74.4552,55.3154 C74.0662,50.3704 69.8832,46.4424 64.8582,46.4424 C63.3582,46.4424 61.8872,46.7904 60.5542,47.4574 C58.2872,44.8364 54.9512,43.2944 51.4702,43.2944 C44.8582,43.2944 39.4792,48.6714 39.4792,55.2844 L39.4792,55.3344 C36.5892,56.8554 34.7552,59.8584 34.7552,63.1594 C34.7552,68.0344 38.7212,72.0004 43.5962,72.0004 L70.3712,72.0004 C75.2452,72.0004 79.2112,68.0344 79.2112,63.1594 C79.2112,59.8434 77.3632,56.8284 74.4552,55.3154\" id=\"Fill-8\" fill=\"#E1F1FF\"></path>\r\n                            <path d=\"M5.4796,25.5 C5.4796,26.881 4.3606,28 2.9796,28 C1.5986,28 0.4796,26.881 0.4796,25.5 C0.4796,24.119 1.5986,23 2.9796,23 C4.3606,23 5.4796,24.119 5.4796,25.5\" id=\"Fill-10\" fill=\"#DFE9EF\"></path>\r\n                            <path d=\"M78.4796,19.5 C78.4796,20.881 77.3606,22 75.9796,22 C74.5986,22 73.4796,20.881 73.4796,19.5 C73.4796,18.119 74.5986,17 75.9796,17 C77.3606,17 78.4796,18.119 78.4796,19.5\" id=\"Fill-12\" fill=\"#FFC007\"></path>\r\n                            <path d=\"M76.4796,41 C74.2736,41 72.4796,42.794 72.4796,45 C72.4796,47.206 74.2736,49 76.4796,49 C78.6856,49 80.4796,47.206 80.4796,45 C80.4796,42.794 78.6856,41 76.4796,41 M76.4796,50 C73.7226,50 71.4796,47.757 71.4796,45 C71.4796,42.243 73.7226,40 76.4796,40 C79.2366,40 81.4796,42.243 81.4796,45 C81.4796,47.757 79.2366,50 76.4796,50\" id=\"Fill-14\" fill=\"#FFC007\"></path>\r\n                            <path d=\"M42.5899,1 L41.4099,1 C32.2509,1 24.7979,8.452 24.7979,17.611 L24.7979,29.962 L32.3229,29.962 L32.3229,17.611 C32.3229,12.602 36.3999,8.524 41.4099,8.524 L42.5899,8.524 C47.5999,8.524 51.6769,12.602 51.6769,17.611 L51.6769,29.962 L59.2009,29.962 L59.2009,17.611 C59.2009,8.452 51.7489,1 42.5899,1\" id=\"Fill-16\" fill=\"#FFFFFF\"></path>\r\n                            <path d=\"M52.6768,28.9619 L58.2008,28.9619 L58.2008,17.6109 C58.2008,9.0029 51.1978,1.9999 42.5898,1.9999 L41.4098,1.9999 C32.8018,1.9999 25.7978,9.0029 25.7978,17.6109 L25.7978,28.9619 L31.3228,28.9619 L31.3228,17.6109 C31.3228,12.0499 35.8488,7.5249 41.4098,7.5249 L42.5898,7.5249 C48.1518,7.5249 52.6768,12.0499 52.6768,17.6109 L52.6768,28.9619 Z M60.2008,30.9619 L50.6768,30.9619 L50.6768,17.6109 C50.6768,13.1519 47.0488,9.5249 42.5898,9.5249 L41.4098,9.5249 C36.9508,9.5249 33.3228,13.1519 33.3228,17.6109 L33.3228,30.9619 L23.7978,30.9619 L23.7978,17.6109 C23.7978,7.8999 31.6978,-0.0001 41.4098,-0.0001 L42.5898,-0.0001 C52.3008,-0.0001 60.2008,7.8999 60.2008,17.6109 L60.2008,30.9619 Z\" id=\"Fill-18\" fill=\"#0859A3\"></path>\r\n                            <path d=\"M59.3565,63 L24.6435,63 C21.9475,63 19.7415,60.794 19.7415,58.098 L19.7415,34.864 C19.7415,32.168 21.9475,29.962 24.6435,29.962 L59.3565,29.962 C62.0525,29.962 64.2585,32.168 64.2585,34.864 L64.2585,58.098 C64.2585,60.794 62.0525,63 59.3565,63\" id=\"Fill-20\" fill=\"#FFFFFF\"></path>\r\n                            <path d=\"M24.6436,30.9619 C22.4926,30.9619 20.7416,32.7129 20.7416,34.8639 L20.7416,58.0979 C20.7416,60.2489 22.4926,61.9999 24.6436,61.9999 L59.3566,61.9999 C61.5076,61.9999 63.2586,60.2489 63.2586,58.0979 L63.2586,34.8639 C63.2586,32.7129 61.5076,30.9619 59.3566,30.9619 L24.6436,30.9619 Z M59.3566,63.9999 L24.6436,63.9999 C21.3886,63.9999 18.7416,61.3529 18.7416,58.0979 L18.7416,34.8639 C18.7416,31.6089 21.3886,28.9619 24.6436,28.9619 L59.3566,28.9619 C62.6116,28.9619 65.2586,31.6089 65.2586,34.8639 L65.2586,58.0979 C65.2586,61.3529 62.6116,63.9999 59.3566,63.9999 L59.3566,63.9999 Z\" id=\"Fill-22\" fill=\"#0859A3\"></path>\r\n                            <path d=\"M42.0001,39.3604 C39.9271,39.3604 38.2391,41.0474 38.2391,43.1214 C38.2391,44.2034 38.7251,45.2394 39.5701,45.9634 C39.7921,46.1524 39.9201,46.4304 39.9201,46.7224 L39.9201,52.5354 C39.9201,53.6814 40.8541,54.6154 42.0001,54.6154 C43.1461,54.6154 44.0801,53.6814 44.0801,52.5354 L44.0801,46.7224 C44.0801,46.4304 44.2081,46.1534 44.4301,45.9634 C45.2751,45.2394 45.7601,44.2034 45.7601,43.1214 C45.7601,41.0474 44.0731,39.3604 42.0001,39.3604 M42.0001,56.6154 C39.7501,56.6154 37.9201,54.7854 37.9201,52.5354 L37.9201,47.1584 C36.8471,46.0784 36.2391,44.6314 36.2391,43.1214 C36.2391,39.9444 38.8231,37.3604 42.0001,37.3604 C45.1761,37.3604 47.7601,39.9444 47.7601,43.1214 C47.7601,44.6314 47.1531,46.0784 46.0801,47.1574 L46.0801,52.5354 C46.0801,54.7854 44.2501,56.6154 42.0001,56.6154\" id=\"Fill-24\" fill=\"#00ADFF\"></path>\r\n                            <path d=\"M40.1114,33.8584 L26.9464,33.8584 C24.8594,33.8584 23.1524,35.5654 23.1524,37.6524 L23.1524,50.8174 L40.1114,33.8584 Z\" id=\"Fill-26\" fill=\"#EDF2F7\"></path>\r\n                            <path d=\"M5.4796,48 C3.2736,48 1.4796,49.794 1.4796,52 C1.4796,54.206 3.2736,56 5.4796,56 C7.6856,56 9.4796,54.206 9.4796,52 C9.4796,49.794 7.6856,48 5.4796,48 M5.4796,57 C2.7226,57 0.4796,54.757 0.4796,52 C0.4796,49.243 2.7226,47 5.4796,47 C8.2366,47 10.4796,49.243 10.4796,52 C10.4796,54.757 8.2366,57 5.4796,57\" id=\"Fill-28\" fill=\"#00ADFF\"></path>\r\n                        </g>\r\n                    </g>\r\n                </g>\r\n            </g>\r\n        </g>\r\n      </svg>\r\n    </div>\r\n    <!-- END SVG GRAPHIC -->\r\n\r\n    <div class=\"text-center\">\r\n        <div class=\"mb-1\">\r\n            <div #content><ng-content></ng-content></div>\r\n            <ng-container *ngIf=\"!content.hasChildNodes()\">\r\n                <h3>You do not have access.</h3>\r\n            </ng-container>\r\n        </div>\r\n    </div>\r\n</div>\r\n" }]
        }], ctorParameters: () => [] });

class NotFoundComponent {
    constructor() { }
    ngOnInit() {
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotFoundComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotFoundComponent, selector: "sk-not-found", host: { classAttribute: "w-100" }, ngImport: i0, template: "<div class=\"d-flex flex-column align-items-center mt-5\">\r\n    <!-- BEGIN SVG GRAPHIC -->\r\n    <div class=\"my-4\">\r\n        <svg width=\"117px\" height=\"113px\" viewBox=\"0 0 117 113\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\"\r\n            xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n            <!-- Generator: Sketch 63.1 (92452) - https://sketch.com -->\r\n            <title>404-illustration</title>\r\n            <desc>Created with Sketch.</desc>\r\n            <g id=\"Design\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n                <g id=\"Screen-1\" transform=\"translate(-743.000000, -115.000000)\">\r\n                    <g id=\"404-illustration\" transform=\"translate(743.000000, 115.000000)\">\r\n                        <g>\r\n                            <g id=\"cloud\">\r\n                                <path\r\n                                    d=\"M110.025377,48.0468794 C109.528174,41.7491049 104.18324,36.7481943 97.7630285,36.7481943 C95.8446029,36.7481943 93.9651149,37.1914263 92.2623437,38.0390889 C89.3659853,34.7021638 85.1023181,32.7382132 80.6544461,32.7382132 C72.2049869,32.7382132 65.3325005,39.58667 65.3325005,48.0065856 L65.3325005,48.0692649 C61.6394189,50.0063529 59.2956749,53.8297885 59.2956749,58.0337769 C59.2956749,64.2420096 64.3635533,69.2906758 70.5920717,69.2906758 L104.806241,69.2906758 C111.03476,69.2906758 116.102638,64.2420096 116.102638,58.0337769 C116.102638,53.8118801 113.740923,49.9720285 110.025377,48.0468794\"\r\n                                    id=\"Fill-3\" fill=\"#E1F1FF\"></path>\r\n                                <path\r\n                                    d=\"M66.5744602,19.4982615 C66.1910746,19.4982615 65.807689,19.3520099 65.515657,19.060999 C64.9300954,18.4774848 64.9300954,17.534311 65.515657,16.9507968 L68.6920666,13.7854935 C69.2776282,13.2019793 70.225609,13.2019793 70.809673,13.7854935 C71.3952346,14.3675153 71.3952346,15.3121815 70.809673,15.8956957 L67.6332634,19.060999 C67.3412314,19.3520099 66.9578458,19.4982615 66.5744602,19.4982615 M59.1613402,26.8854615 C58.7779546,26.8854615 58.394569,26.7407023 58.102537,26.448199 C57.5169754,25.8661772 57.5169754,24.921511 58.102537,24.3379968 L61.2789466,21.1726935 C61.8630106,20.5891793 62.8109914,20.5891793 63.396553,21.1726935 C63.9821146,21.7562077 63.9821146,22.7008739 63.396553,23.2828957 L60.2201434,26.448199 C59.9281114,26.7407023 59.5447258,26.8854615 59.1613402,26.8854615 M51.7497178,34.2711692 C51.3663322,34.2711692 50.9829466,34.1264099 50.6909146,33.8339066 C50.105353,33.2518848 50.105353,32.3072186 50.6909146,31.7237044 L53.8673242,28.5584012 C54.4513882,27.9763793 55.399369,27.9763793 55.9849306,28.5584012 C56.5689946,29.1419153 56.5689946,30.0865815 55.9849306,30.6686033 L52.808521,33.8339066 C52.5149914,34.1264099 52.1316058,34.2711692 51.7497178,34.2711692 M44.3351002,41.6598615 C43.9532122,41.6598615 43.5698266,41.5136099 43.276297,41.222599 C42.692233,40.6390848 42.692233,39.6944186 43.276297,39.1123968 L46.4527066,35.9456012 C47.0382682,35.3635793 47.986249,35.3635793 48.5718106,35.9456012 C49.1558746,36.5291153 49.1558746,37.4737815 48.5718106,38.0572957 L45.3939034,41.222599 C45.1018714,41.5136099 44.7184858,41.6598615 44.3351002,41.6598615 M39.5966938,46.3817001 C39.2133082,46.3817001 38.8314202,46.2354484 38.5378906,45.9444375 C37.9538266,45.3609233 37.9538266,44.4162572 38.5378906,43.8342353 L39.0410842,43.3328012 C39.6266458,42.749287 40.5746266,42.749287 41.1586906,43.3328012 C41.7442522,43.914823 41.7442522,44.8594892 41.1586906,45.4430033 L40.655497,45.9444375 C40.363465,46.2354484 39.9800794,46.3817001 39.5966938,46.3817001\"\r\n                                    id=\"Fill-5\" fill=\"#DFE4EC\"></path>\r\n                                <path\r\n                                    d=\"M61.2626227,83.8054045 C60.8792371,83.8054045 60.4958515,83.6591529 60.2038195,83.368142 C59.6182579,82.7846278 59.6182579,81.841454 60.2038195,81.2579398 L63.3802291,78.0926365 C63.9657907,77.5091223 64.9137715,77.5091223 65.4978355,78.0926365 C66.0833971,78.6746583 66.0833971,79.6193245 65.4978355,80.2028387 L62.3214259,83.368142 C62.0293939,83.6591529 61.6460083,83.8054045 61.2626227,83.8054045 M53.8510003,91.1911121 C53.4676147,91.1911121 53.0842291,91.0463529 52.7921971,90.7538496 C52.2066355,90.1718278 52.2066355,89.2271616 52.7921971,88.6436474 L55.9686067,85.4783441 C56.5526707,84.89483 57.5006515,84.89483 58.0862131,85.4783441 C58.6717747,86.0618583 58.6717747,87.0065245 58.0862131,87.5885463 L54.9098035,90.7538496 C54.6177715,91.0463529 54.2343859,91.1911121 53.8510003,91.1911121 M46.4378803,98.5783121 C46.0544947,98.5783121 45.6711091,98.4335529 45.3790771,98.1410496 C44.7935155,97.5590278 44.7935155,96.6143616 45.3790771,96.0308474 L48.5554867,92.8655441 C49.1395507,92.2835223 50.0875315,92.2835223 50.6730931,92.8655441 C51.2571571,93.4490583 51.2571571,94.3937245 50.6730931,94.9757463 L47.4966835,98.1410496 C47.2031539,98.4335529 46.8197683,98.5783121 46.4378803,98.5783121\"\r\n                                    id=\"Fill-7\" fill=\"#DFE4EC\"></path>\r\n                                <path\r\n                                    d=\"M42.8688,7.35818182 C42.8688,11.0667055 39.851136,14.0738182 36.1296,14.0738182 C32.408064,14.0738182 29.3904,11.0667055 29.3904,7.35818182 C29.3904,3.64965818 32.408064,0.642545455 36.1296,0.642545455 C39.851136,0.642545455 42.8688,3.64965818 42.8688,7.35818182\"\r\n                                    id=\"Fill-9\" fill=\"#E1F1FF\"></path>\r\n                                <path\r\n                                    d=\"M53.1165773,52.5239703 C52.6193741,46.2261958 47.2744397,41.2252852 40.8527309,41.2252852 C38.9373005,41.2252852 37.0563149,41.6685172 35.3520461,42.5161798 C32.4571853,39.1792547 28.1935181,37.2153041 23.7456461,37.2153041 C15.2976845,37.2153041 8.42370048,44.0637609 8.42370048,52.4836765 L8.42370048,52.5463558 C4.73061888,54.4834438 2.38687488,58.3068794 2.38687488,62.5108678 C2.38687488,68.7191005 7.45475328,73.7677667 13.6832717,73.7677667 L47.8974413,73.7677667 C54.1259597,73.7677667 59.1938381,68.7191005 59.1938381,62.5108678 C59.1938381,58.2889711 56.8321229,54.4491194 53.1165773,52.5239703\"\r\n                                    id=\"Fill-11\" fill=\"#E1F1FF\"></path>\r\n                                <path\r\n                                    d=\"M23.4,19.2970909 C23.4,21.3580451 21.7241856,23.028 19.656,23.028 C17.5878144,23.028 15.912,21.3580451 15.912,19.2970909 C15.912,17.2361367 17.5878144,15.5661818 19.656,15.5661818 C21.7241856,15.5661818 23.4,17.2361367 23.4,19.2970909\"\r\n                                    id=\"Fill-13\" fill=\"#DFE9EF\"></path>\r\n                                <path\r\n                                    d=\"M98.2960243,51.0083258 C104.605413,53.7468131 109.005362,60.0147404 109.005362,67.3094138 C109.005362,77.1052887 101.020159,85.0849571 91.1674483,85.0849571 L30.5116531,85.0849571 C20.6379763,85.0849571 12.6737395,77.1276742 12.6737395,67.3094138 C12.6737395,60.0192175 17.0961523,53.7318895 23.4160243,50.9948945 C23.3890675,50.5128611 23.3770867,50.0218735 23.3770867,49.5323782 C23.3770867,35.783232 34.5581683,24.6442298 48.3480691,24.6442298 C56.5489267,24.6442298 63.8242675,28.5825775 68.3784691,34.6579898 C71.3706739,32.8208902 74.9005171,31.7523578 78.6789619,31.7523578 C89.4122611,31.7523578 98.1372787,40.34688 98.2960243,51.0083258\"\r\n                                    id=\"Fill-15\" fill=\"#FFFFFF\"></path>\r\n                                <path\r\n                                    d=\"M48.3488179,25.987954 C35.3226931,25.987954 24.7256755,36.5494115 24.7256755,49.5314828 C24.7256755,49.9985926 24.7361587,50.4657024 24.7601203,50.9268428 L24.8080435,51.8565853 L23.9514163,52.2266915 C17.9190835,54.8413126 14.0208307,60.7615191 14.0208307,67.3085184 C14.0208307,76.3701504 21.4189747,83.7409344 30.5109043,83.7409344 L91.1681971,83.7409344 C100.260127,83.7409344 107.658271,76.3701504 107.658271,67.3085184 C107.658271,60.7659962 103.771999,54.8502668 97.7576371,52.2401228 L96.9609139,51.8938944 L96.9489331,51.0283235 C96.8006707,41.139922 88.6058035,33.096082 78.6797107,33.096082 C75.2876467,33.096082 71.9704627,34.031794 69.0860851,35.8017373 L68.0362675,36.4464384 L67.2994483,35.4629708 C62.7871795,29.4397911 55.8787507,25.987954 48.3488179,25.987954 M91.1681971,86.4286813 L30.5109043,86.4286813 C19.9303603,86.4286813 11.3236531,77.8520675 11.3236531,67.3085184 C11.3236531,60.0004137 15.4989619,53.3638726 22.0359859,50.152306 C22.0314931,49.9463599 22.0284979,49.7389213 22.0284979,49.5314828 C22.0284979,35.0689868 33.8370739,23.3002071 48.3488179,23.3002071 C56.3370163,23.3002071 63.6947251,26.7878609 68.7206707,32.9125213 C71.7757747,31.2709213 75.1932979,30.4083351 78.6797107,30.4083351 C89.7829171,30.4083351 98.9991475,39.1819409 99.6131635,50.13589 C106.165164,53.3325329 110.353951,59.9825053 110.353951,67.3085184 C110.353951,77.8520675 101.747244,86.4286813 91.1681971,86.4286813\"\r\n                                    id=\"cloudv2\" stroke=\"#FFFFFF\" stroke-width=\"0.65\" fill=\"#0859A3\"></path>\r\n                                <path\r\n                                    d=\"M48.4497562,29.6197702 C37.5696922,29.6197702 28.7188762,38.4411316 28.7188762,49.2846458 C28.7188762,49.6831069 28.7293594,50.081568 28.7503258,50.4770444 L28.9345306,54.0960262 L25.6038682,55.5376495 C20.9942554,57.534432 18.015529,62.0577862 18.015529,67.0616815 C18.015529,68.4301789 18.2446618,69.7449513 18.652009,70.979136 L57.7977754,31.9702429 C54.9718042,30.4554938 51.7894042,29.6197702 48.4497562,29.6197702\"\r\n                                    id=\"Fill-21\" fill=\"#EDF2F7\"></path>\r\n                                <path\r\n                                    d=\"M102.7728,96.9 C102.7728,98.9609542 101.096986,100.630909 99.0288,100.630909 C96.9606144,100.630909 95.2848,98.9609542 95.2848,96.9 C95.2848,94.8390458 96.9606144,93.1690909 99.0288,93.1690909 C101.096986,93.1690909 102.7728,94.8390458 102.7728,96.9\"\r\n                                    id=\"Fill-23\" fill=\"#DFE9EF\"></path>\r\n                                <path\r\n                                    d=\"M44.3664,102.869455 C44.3664,104.930409 42.6905856,106.600364 40.6224,106.600364 C38.5542144,106.600364 36.8784,104.930409 36.8784,102.869455 C36.8784,100.8085 38.5542144,99.1385455 40.6224,99.1385455 C42.6905856,99.1385455 44.3664,100.8085 44.3664,102.869455\"\r\n                                    id=\"Fill-25\" fill=\"#00ADFF\"></path>\r\n                                <path\r\n                                    d=\"M87.048,105.108 C87.048,108.816524 84.030336,111.823636 80.3088,111.823636 C76.587264,111.823636 73.5696,108.816524 73.5696,105.108 C73.5696,101.399476 76.587264,98.3923636 80.3088,98.3923636 C84.030336,98.3923636 87.048,101.399476 87.048,105.108\"\r\n                                    id=\"Fill-27\" fill=\"#FFFFFF\"></path>\r\n                                <path\r\n                                    d=\"M80.3088,99.5116364 C77.2132608,99.5116364 74.6928,102.023284 74.6928,105.108 C74.6928,108.192716 77.2132608,110.704364 80.3088,110.704364 C83.4043392,110.704364 85.9248,108.192716 85.9248,105.108 C85.9248,102.023284 83.4043392,99.5116364 80.3088,99.5116364 M80.3088,112.942909 C75.973248,112.942909 72.4464,109.428393 72.4464,105.108 C72.4464,100.787607 75.973248,97.2730909 80.3088,97.2730909 C84.644352,97.2730909 88.1712,100.787607 88.1712,105.108 C88.1712,109.428393 84.644352,112.942909 80.3088,112.942909\"\r\n                                    id=\"Fill-29\" fill=\"#E1F1FF\"></path>\r\n                                <path\r\n                                    d=\"M4.8672,80.937039 C2.95091383,80.937039 1.39062857,82.4918687 1.39062857,84.4014545 C1.39062857,86.3110404 2.95091383,87.8658701 4.8672,87.8658701 C6.78348617,87.8658701 8.34377143,86.3110404 8.34377143,84.4014545 C8.34377143,82.4918687 6.78348617,80.937039 4.8672,80.937039 M4.8672,89.2516364 C2.18328686,89.2516364 0,87.0759834 0,84.4014545 C0,81.7269257 2.18328686,79.5512727 4.8672,79.5512727 C7.55111314,79.5512727 9.7344,81.7269257 9.7344,84.4014545 C9.7344,87.0759834 7.55111314,89.2516364 4.8672,89.2516364\"\r\n                                    id=\"Fill-49\" fill=\"#FFC007\"></path>\r\n                                <path\r\n                                    d=\"M76.3776,2.32145455 C73.2820608,2.32145455 70.7616,4.83310255 70.7616,7.91781818 C70.7616,11.0025338 73.2820608,13.5141818 76.3776,13.5141818 C79.4731392,13.5141818 81.9936,11.0025338 81.9936,7.91781818 C81.9936,4.83310255 79.4731392,2.32145455 76.3776,2.32145455 M76.3776,15.7527273 C72.042048,15.7527273 68.5152,12.2382109 68.5152,7.91781818 C68.5152,3.59742545 72.042048,0.0829090909 76.3776,0.0829090909 C80.713152,0.0829090909 84.24,3.59742545 84.24,7.91781818 C84.24,12.2382109 80.713152,15.7527273 76.3776,15.7527273\"\r\n                                    id=\"Fill-51\" fill=\"#00ADFF\"></path>\r\n                                <path\r\n                                    d=\"M112.32,28.0647273 C112.32,30.1256815 110.644186,31.7956364 108.576,31.7956364 C106.507814,31.7956364 104.832,30.1256815 104.832,28.0647273 C104.832,26.0037731 106.507814,24.3338182 108.576,24.3338182 C110.644186,24.3338182 112.32,26.0037731 112.32,28.0647273\"\r\n                                    id=\"Fill-53\" fill=\"#FFC007\"></path>\r\n                            </g>\r\n                            <g id=\"telescope\" transform=\"translate(39.520000, 41.454545)\" stroke=\"#FFC007\"\r\n                                stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"1.56\">\r\n                                <polygon id=\"Stroke-1\"\r\n                                    points=\"42.1824 14.7184264 32.2104806 18.334722 27.0707174 4.26402118 37.0426368 0.646432233\">\r\n                                </polygon>\r\n                                <polygon id=\"Stroke-3\"\r\n                                    points=\"7.95417293 24.2589281 31.6191483 16.7159253 27.6617902 5.88255919 4.66783949 15.2621641\">\r\n                                </polygon>\r\n                                <polygon id=\"Stroke-4\"\r\n                                    points=\"7.03394765 21.740455 2.09536205 23.5317888 0.649479168 19.5727471 5.58806477 17.7814132\">\r\n                                </polygon>\r\n                                <line x1=\"19.2571092\" y1=\"20.6567304\" x2=\"14.04\" y2=\"34.7181818\" id=\"Stroke-6\"></line>\r\n                                <line x1=\"19.2571092\" y1=\"20.6567304\" x2=\"23.4\" y2=\"34.7181818\" id=\"Stroke-8\"></line>\r\n                            </g>\r\n                        </g>\r\n                    </g>\r\n                </g>\r\n            </g>\r\n        </svg>\r\n    </div>\r\n    <!-- END SVG GRAPHIC -->\r\n    <div class=\"mb-1\">\r\n        <h3>Oops! This page cannot be found.</h3>\r\n    </div>\r\n    <div class=\"text-muted mb-3\">Error code: 404</div>\r\n\r\n</div>\r\n", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotFoundComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-not-found', host: { class: 'w-100' }, template: "<div class=\"d-flex flex-column align-items-center mt-5\">\r\n    <!-- BEGIN SVG GRAPHIC -->\r\n    <div class=\"my-4\">\r\n        <svg width=\"117px\" height=\"113px\" viewBox=\"0 0 117 113\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\"\r\n            xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n            <!-- Generator: Sketch 63.1 (92452) - https://sketch.com -->\r\n            <title>404-illustration</title>\r\n            <desc>Created with Sketch.</desc>\r\n            <g id=\"Design\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n                <g id=\"Screen-1\" transform=\"translate(-743.000000, -115.000000)\">\r\n                    <g id=\"404-illustration\" transform=\"translate(743.000000, 115.000000)\">\r\n                        <g>\r\n                            <g id=\"cloud\">\r\n                                <path\r\n                                    d=\"M110.025377,48.0468794 C109.528174,41.7491049 104.18324,36.7481943 97.7630285,36.7481943 C95.8446029,36.7481943 93.9651149,37.1914263 92.2623437,38.0390889 C89.3659853,34.7021638 85.1023181,32.7382132 80.6544461,32.7382132 C72.2049869,32.7382132 65.3325005,39.58667 65.3325005,48.0065856 L65.3325005,48.0692649 C61.6394189,50.0063529 59.2956749,53.8297885 59.2956749,58.0337769 C59.2956749,64.2420096 64.3635533,69.2906758 70.5920717,69.2906758 L104.806241,69.2906758 C111.03476,69.2906758 116.102638,64.2420096 116.102638,58.0337769 C116.102638,53.8118801 113.740923,49.9720285 110.025377,48.0468794\"\r\n                                    id=\"Fill-3\" fill=\"#E1F1FF\"></path>\r\n                                <path\r\n                                    d=\"M66.5744602,19.4982615 C66.1910746,19.4982615 65.807689,19.3520099 65.515657,19.060999 C64.9300954,18.4774848 64.9300954,17.534311 65.515657,16.9507968 L68.6920666,13.7854935 C69.2776282,13.2019793 70.225609,13.2019793 70.809673,13.7854935 C71.3952346,14.3675153 71.3952346,15.3121815 70.809673,15.8956957 L67.6332634,19.060999 C67.3412314,19.3520099 66.9578458,19.4982615 66.5744602,19.4982615 M59.1613402,26.8854615 C58.7779546,26.8854615 58.394569,26.7407023 58.102537,26.448199 C57.5169754,25.8661772 57.5169754,24.921511 58.102537,24.3379968 L61.2789466,21.1726935 C61.8630106,20.5891793 62.8109914,20.5891793 63.396553,21.1726935 C63.9821146,21.7562077 63.9821146,22.7008739 63.396553,23.2828957 L60.2201434,26.448199 C59.9281114,26.7407023 59.5447258,26.8854615 59.1613402,26.8854615 M51.7497178,34.2711692 C51.3663322,34.2711692 50.9829466,34.1264099 50.6909146,33.8339066 C50.105353,33.2518848 50.105353,32.3072186 50.6909146,31.7237044 L53.8673242,28.5584012 C54.4513882,27.9763793 55.399369,27.9763793 55.9849306,28.5584012 C56.5689946,29.1419153 56.5689946,30.0865815 55.9849306,30.6686033 L52.808521,33.8339066 C52.5149914,34.1264099 52.1316058,34.2711692 51.7497178,34.2711692 M44.3351002,41.6598615 C43.9532122,41.6598615 43.5698266,41.5136099 43.276297,41.222599 C42.692233,40.6390848 42.692233,39.6944186 43.276297,39.1123968 L46.4527066,35.9456012 C47.0382682,35.3635793 47.986249,35.3635793 48.5718106,35.9456012 C49.1558746,36.5291153 49.1558746,37.4737815 48.5718106,38.0572957 L45.3939034,41.222599 C45.1018714,41.5136099 44.7184858,41.6598615 44.3351002,41.6598615 M39.5966938,46.3817001 C39.2133082,46.3817001 38.8314202,46.2354484 38.5378906,45.9444375 C37.9538266,45.3609233 37.9538266,44.4162572 38.5378906,43.8342353 L39.0410842,43.3328012 C39.6266458,42.749287 40.5746266,42.749287 41.1586906,43.3328012 C41.7442522,43.914823 41.7442522,44.8594892 41.1586906,45.4430033 L40.655497,45.9444375 C40.363465,46.2354484 39.9800794,46.3817001 39.5966938,46.3817001\"\r\n                                    id=\"Fill-5\" fill=\"#DFE4EC\"></path>\r\n                                <path\r\n                                    d=\"M61.2626227,83.8054045 C60.8792371,83.8054045 60.4958515,83.6591529 60.2038195,83.368142 C59.6182579,82.7846278 59.6182579,81.841454 60.2038195,81.2579398 L63.3802291,78.0926365 C63.9657907,77.5091223 64.9137715,77.5091223 65.4978355,78.0926365 C66.0833971,78.6746583 66.0833971,79.6193245 65.4978355,80.2028387 L62.3214259,83.368142 C62.0293939,83.6591529 61.6460083,83.8054045 61.2626227,83.8054045 M53.8510003,91.1911121 C53.4676147,91.1911121 53.0842291,91.0463529 52.7921971,90.7538496 C52.2066355,90.1718278 52.2066355,89.2271616 52.7921971,88.6436474 L55.9686067,85.4783441 C56.5526707,84.89483 57.5006515,84.89483 58.0862131,85.4783441 C58.6717747,86.0618583 58.6717747,87.0065245 58.0862131,87.5885463 L54.9098035,90.7538496 C54.6177715,91.0463529 54.2343859,91.1911121 53.8510003,91.1911121 M46.4378803,98.5783121 C46.0544947,98.5783121 45.6711091,98.4335529 45.3790771,98.1410496 C44.7935155,97.5590278 44.7935155,96.6143616 45.3790771,96.0308474 L48.5554867,92.8655441 C49.1395507,92.2835223 50.0875315,92.2835223 50.6730931,92.8655441 C51.2571571,93.4490583 51.2571571,94.3937245 50.6730931,94.9757463 L47.4966835,98.1410496 C47.2031539,98.4335529 46.8197683,98.5783121 46.4378803,98.5783121\"\r\n                                    id=\"Fill-7\" fill=\"#DFE4EC\"></path>\r\n                                <path\r\n                                    d=\"M42.8688,7.35818182 C42.8688,11.0667055 39.851136,14.0738182 36.1296,14.0738182 C32.408064,14.0738182 29.3904,11.0667055 29.3904,7.35818182 C29.3904,3.64965818 32.408064,0.642545455 36.1296,0.642545455 C39.851136,0.642545455 42.8688,3.64965818 42.8688,7.35818182\"\r\n                                    id=\"Fill-9\" fill=\"#E1F1FF\"></path>\r\n                                <path\r\n                                    d=\"M53.1165773,52.5239703 C52.6193741,46.2261958 47.2744397,41.2252852 40.8527309,41.2252852 C38.9373005,41.2252852 37.0563149,41.6685172 35.3520461,42.5161798 C32.4571853,39.1792547 28.1935181,37.2153041 23.7456461,37.2153041 C15.2976845,37.2153041 8.42370048,44.0637609 8.42370048,52.4836765 L8.42370048,52.5463558 C4.73061888,54.4834438 2.38687488,58.3068794 2.38687488,62.5108678 C2.38687488,68.7191005 7.45475328,73.7677667 13.6832717,73.7677667 L47.8974413,73.7677667 C54.1259597,73.7677667 59.1938381,68.7191005 59.1938381,62.5108678 C59.1938381,58.2889711 56.8321229,54.4491194 53.1165773,52.5239703\"\r\n                                    id=\"Fill-11\" fill=\"#E1F1FF\"></path>\r\n                                <path\r\n                                    d=\"M23.4,19.2970909 C23.4,21.3580451 21.7241856,23.028 19.656,23.028 C17.5878144,23.028 15.912,21.3580451 15.912,19.2970909 C15.912,17.2361367 17.5878144,15.5661818 19.656,15.5661818 C21.7241856,15.5661818 23.4,17.2361367 23.4,19.2970909\"\r\n                                    id=\"Fill-13\" fill=\"#DFE9EF\"></path>\r\n                                <path\r\n                                    d=\"M98.2960243,51.0083258 C104.605413,53.7468131 109.005362,60.0147404 109.005362,67.3094138 C109.005362,77.1052887 101.020159,85.0849571 91.1674483,85.0849571 L30.5116531,85.0849571 C20.6379763,85.0849571 12.6737395,77.1276742 12.6737395,67.3094138 C12.6737395,60.0192175 17.0961523,53.7318895 23.4160243,50.9948945 C23.3890675,50.5128611 23.3770867,50.0218735 23.3770867,49.5323782 C23.3770867,35.783232 34.5581683,24.6442298 48.3480691,24.6442298 C56.5489267,24.6442298 63.8242675,28.5825775 68.3784691,34.6579898 C71.3706739,32.8208902 74.9005171,31.7523578 78.6789619,31.7523578 C89.4122611,31.7523578 98.1372787,40.34688 98.2960243,51.0083258\"\r\n                                    id=\"Fill-15\" fill=\"#FFFFFF\"></path>\r\n                                <path\r\n                                    d=\"M48.3488179,25.987954 C35.3226931,25.987954 24.7256755,36.5494115 24.7256755,49.5314828 C24.7256755,49.9985926 24.7361587,50.4657024 24.7601203,50.9268428 L24.8080435,51.8565853 L23.9514163,52.2266915 C17.9190835,54.8413126 14.0208307,60.7615191 14.0208307,67.3085184 C14.0208307,76.3701504 21.4189747,83.7409344 30.5109043,83.7409344 L91.1681971,83.7409344 C100.260127,83.7409344 107.658271,76.3701504 107.658271,67.3085184 C107.658271,60.7659962 103.771999,54.8502668 97.7576371,52.2401228 L96.9609139,51.8938944 L96.9489331,51.0283235 C96.8006707,41.139922 88.6058035,33.096082 78.6797107,33.096082 C75.2876467,33.096082 71.9704627,34.031794 69.0860851,35.8017373 L68.0362675,36.4464384 L67.2994483,35.4629708 C62.7871795,29.4397911 55.8787507,25.987954 48.3488179,25.987954 M91.1681971,86.4286813 L30.5109043,86.4286813 C19.9303603,86.4286813 11.3236531,77.8520675 11.3236531,67.3085184 C11.3236531,60.0004137 15.4989619,53.3638726 22.0359859,50.152306 C22.0314931,49.9463599 22.0284979,49.7389213 22.0284979,49.5314828 C22.0284979,35.0689868 33.8370739,23.3002071 48.3488179,23.3002071 C56.3370163,23.3002071 63.6947251,26.7878609 68.7206707,32.9125213 C71.7757747,31.2709213 75.1932979,30.4083351 78.6797107,30.4083351 C89.7829171,30.4083351 98.9991475,39.1819409 99.6131635,50.13589 C106.165164,53.3325329 110.353951,59.9825053 110.353951,67.3085184 C110.353951,77.8520675 101.747244,86.4286813 91.1681971,86.4286813\"\r\n                                    id=\"cloudv2\" stroke=\"#FFFFFF\" stroke-width=\"0.65\" fill=\"#0859A3\"></path>\r\n                                <path\r\n                                    d=\"M48.4497562,29.6197702 C37.5696922,29.6197702 28.7188762,38.4411316 28.7188762,49.2846458 C28.7188762,49.6831069 28.7293594,50.081568 28.7503258,50.4770444 L28.9345306,54.0960262 L25.6038682,55.5376495 C20.9942554,57.534432 18.015529,62.0577862 18.015529,67.0616815 C18.015529,68.4301789 18.2446618,69.7449513 18.652009,70.979136 L57.7977754,31.9702429 C54.9718042,30.4554938 51.7894042,29.6197702 48.4497562,29.6197702\"\r\n                                    id=\"Fill-21\" fill=\"#EDF2F7\"></path>\r\n                                <path\r\n                                    d=\"M102.7728,96.9 C102.7728,98.9609542 101.096986,100.630909 99.0288,100.630909 C96.9606144,100.630909 95.2848,98.9609542 95.2848,96.9 C95.2848,94.8390458 96.9606144,93.1690909 99.0288,93.1690909 C101.096986,93.1690909 102.7728,94.8390458 102.7728,96.9\"\r\n                                    id=\"Fill-23\" fill=\"#DFE9EF\"></path>\r\n                                <path\r\n                                    d=\"M44.3664,102.869455 C44.3664,104.930409 42.6905856,106.600364 40.6224,106.600364 C38.5542144,106.600364 36.8784,104.930409 36.8784,102.869455 C36.8784,100.8085 38.5542144,99.1385455 40.6224,99.1385455 C42.6905856,99.1385455 44.3664,100.8085 44.3664,102.869455\"\r\n                                    id=\"Fill-25\" fill=\"#00ADFF\"></path>\r\n                                <path\r\n                                    d=\"M87.048,105.108 C87.048,108.816524 84.030336,111.823636 80.3088,111.823636 C76.587264,111.823636 73.5696,108.816524 73.5696,105.108 C73.5696,101.399476 76.587264,98.3923636 80.3088,98.3923636 C84.030336,98.3923636 87.048,101.399476 87.048,105.108\"\r\n                                    id=\"Fill-27\" fill=\"#FFFFFF\"></path>\r\n                                <path\r\n                                    d=\"M80.3088,99.5116364 C77.2132608,99.5116364 74.6928,102.023284 74.6928,105.108 C74.6928,108.192716 77.2132608,110.704364 80.3088,110.704364 C83.4043392,110.704364 85.9248,108.192716 85.9248,105.108 C85.9248,102.023284 83.4043392,99.5116364 80.3088,99.5116364 M80.3088,112.942909 C75.973248,112.942909 72.4464,109.428393 72.4464,105.108 C72.4464,100.787607 75.973248,97.2730909 80.3088,97.2730909 C84.644352,97.2730909 88.1712,100.787607 88.1712,105.108 C88.1712,109.428393 84.644352,112.942909 80.3088,112.942909\"\r\n                                    id=\"Fill-29\" fill=\"#E1F1FF\"></path>\r\n                                <path\r\n                                    d=\"M4.8672,80.937039 C2.95091383,80.937039 1.39062857,82.4918687 1.39062857,84.4014545 C1.39062857,86.3110404 2.95091383,87.8658701 4.8672,87.8658701 C6.78348617,87.8658701 8.34377143,86.3110404 8.34377143,84.4014545 C8.34377143,82.4918687 6.78348617,80.937039 4.8672,80.937039 M4.8672,89.2516364 C2.18328686,89.2516364 0,87.0759834 0,84.4014545 C0,81.7269257 2.18328686,79.5512727 4.8672,79.5512727 C7.55111314,79.5512727 9.7344,81.7269257 9.7344,84.4014545 C9.7344,87.0759834 7.55111314,89.2516364 4.8672,89.2516364\"\r\n                                    id=\"Fill-49\" fill=\"#FFC007\"></path>\r\n                                <path\r\n                                    d=\"M76.3776,2.32145455 C73.2820608,2.32145455 70.7616,4.83310255 70.7616,7.91781818 C70.7616,11.0025338 73.2820608,13.5141818 76.3776,13.5141818 C79.4731392,13.5141818 81.9936,11.0025338 81.9936,7.91781818 C81.9936,4.83310255 79.4731392,2.32145455 76.3776,2.32145455 M76.3776,15.7527273 C72.042048,15.7527273 68.5152,12.2382109 68.5152,7.91781818 C68.5152,3.59742545 72.042048,0.0829090909 76.3776,0.0829090909 C80.713152,0.0829090909 84.24,3.59742545 84.24,7.91781818 C84.24,12.2382109 80.713152,15.7527273 76.3776,15.7527273\"\r\n                                    id=\"Fill-51\" fill=\"#00ADFF\"></path>\r\n                                <path\r\n                                    d=\"M112.32,28.0647273 C112.32,30.1256815 110.644186,31.7956364 108.576,31.7956364 C106.507814,31.7956364 104.832,30.1256815 104.832,28.0647273 C104.832,26.0037731 106.507814,24.3338182 108.576,24.3338182 C110.644186,24.3338182 112.32,26.0037731 112.32,28.0647273\"\r\n                                    id=\"Fill-53\" fill=\"#FFC007\"></path>\r\n                            </g>\r\n                            <g id=\"telescope\" transform=\"translate(39.520000, 41.454545)\" stroke=\"#FFC007\"\r\n                                stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"1.56\">\r\n                                <polygon id=\"Stroke-1\"\r\n                                    points=\"42.1824 14.7184264 32.2104806 18.334722 27.0707174 4.26402118 37.0426368 0.646432233\">\r\n                                </polygon>\r\n                                <polygon id=\"Stroke-3\"\r\n                                    points=\"7.95417293 24.2589281 31.6191483 16.7159253 27.6617902 5.88255919 4.66783949 15.2621641\">\r\n                                </polygon>\r\n                                <polygon id=\"Stroke-4\"\r\n                                    points=\"7.03394765 21.740455 2.09536205 23.5317888 0.649479168 19.5727471 5.58806477 17.7814132\">\r\n                                </polygon>\r\n                                <line x1=\"19.2571092\" y1=\"20.6567304\" x2=\"14.04\" y2=\"34.7181818\" id=\"Stroke-6\"></line>\r\n                                <line x1=\"19.2571092\" y1=\"20.6567304\" x2=\"23.4\" y2=\"34.7181818\" id=\"Stroke-8\"></line>\r\n                            </g>\r\n                        </g>\r\n                    </g>\r\n                </g>\r\n            </g>\r\n        </svg>\r\n    </div>\r\n    <!-- END SVG GRAPHIC -->\r\n    <div class=\"mb-1\">\r\n        <h3>Oops! This page cannot be found.</h3>\r\n    </div>\r\n    <div class=\"text-muted mb-3\">Error code: 404</div>\r\n\r\n</div>\r\n" }]
        }], ctorParameters: () => [] });

class NotLicensedComponent {
    constructor(userProvider, partnerService, routeResolverService, route) {
        this.userProvider = userProvider;
        this.partnerService = partnerService;
        this.routeResolverService = routeResolverService;
        this.manageUsersUrl = this.routeResolverService.generateRatRoute(AppId.Portal, 'settings/users/members');
        this.user = this.userProvider.getCurrentUser();
        this.configuration = route.snapshot.data;
    }
    canAssignLicenses() {
        return this.user.hasPermission('admin_editall');
    }
    requestLicenseFromAdmins() {
        this.sendingRequest = true;
        this.partnerService
            .sendLicenseRequest(this.configuration.productId, this.reason)
            .pipe(take(1))
            .subscribe({
            complete: () => {
                this.sendingRequest = false;
                this.sentRequest = true;
            },
            error: () => {
                this.sendingRequest = false;
                this.sentRequest = false;
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotLicensedComponent, deps: [{ token: i2$1.AbstractUserProvider }, { token: PartnerService }, { token: RouteResolverService }, { token: i2.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: NotLicensedComponent, selector: "sk-not-licensed", host: { classAttribute: "w-100" }, ngImport: i0, template: "<div class=\"d-flex flex-column align-items-center mt-5\">\r\n  <!-- BEGIN SVG GRAPHIC -->\r\n  <div class=\"my-4\">\r\n    <svg width=\"108px\" height=\"108px\" viewBox=\"0 0 108 108\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n      <title>art-access</title>\r\n      <g id=\"Empty-States\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n          <g id=\"Empty-Access\" transform=\"translate(-744.000000, -227.000000)\">\r\n              <g id=\"empty-state/CTA-Copy\" transform=\"translate(623.000000, 227.000000)\">\r\n                  <g id=\"art-access\" transform=\"translate(121.000000, 0.000000)\">\r\n                      <polygon id=\"Fill-1\" fill=\"#FFFFFF\" points=\"0 108 108 108 108 0 0 0\"></polygon>\r\n                      <g id=\"Group-30\" transform=\"translate(12.000000, 18.000000)\">\r\n                          <path d=\"M16.4796,12.5 C16.4796,13.881 15.3606,15 13.9796,15 C12.5986,15 11.4796,13.881 11.4796,12.5 C11.4796,11.119 12.5986,10 13.9796,10 C15.3606,10 16.4796,11.119 16.4796,12.5\" id=\"Fill-2\" fill=\"#00ADFF\"></path>\r\n                          <path d=\"M69.3868,26.8506 C69.1308,26.8506 68.8748,26.7526 68.6798,26.5576 C68.2888,26.1666 68.2888,25.5346 68.6798,25.1436 L70.8008,23.0226 C71.1918,22.6316 71.8248,22.6316 72.2148,23.0226 C72.6058,23.4126 72.6058,24.0456 72.2148,24.4366 L70.0938,26.5576 C69.8988,26.7526 69.6428,26.8506 69.3868,26.8506 M64.4378,31.7996 C64.1818,31.7996 63.9258,31.7026 63.7308,31.5066 C63.3398,31.1166 63.3398,30.4836 63.7308,30.0926 L65.8518,27.9716 C66.2418,27.5806 66.8748,27.5806 67.2658,27.9716 C67.6568,28.3626 67.6568,28.9956 67.2658,29.3856 L65.1448,31.5066 C64.9498,31.7026 64.6938,31.7996 64.4378,31.7996 M59.4878,36.7496 C59.2318,36.7496 58.9758,36.6526 58.7808,36.4566 C58.3898,36.0666 58.3898,35.4336 58.7808,35.0426 L60.9018,32.9216 C61.2918,32.5316 61.9248,32.5316 62.3158,32.9216 C62.7058,33.3126 62.7058,33.9456 62.3158,34.3356 L60.1948,36.4566 C59.9988,36.6526 59.7428,36.7496 59.4878,36.7496\" id=\"Fill-4\" fill=\"#DFE4EC\"></path>\r\n                          <path d=\"M20.3868,38.8506 C20.1308,38.8506 19.8748,38.7526 19.6798,38.5576 C19.2888,38.1666 19.2888,37.5346 19.6798,37.1436 L21.8008,35.0226 C22.1918,34.6316 22.8248,34.6316 23.2148,35.0226 C23.6058,35.4126 23.6058,36.0456 23.2148,36.4366 L21.0938,38.5576 C20.8988,38.7526 20.6428,38.8506 20.3868,38.8506 M15.4378,43.7996 C15.1818,43.7996 14.9258,43.7026 14.7308,43.5066 C14.3398,43.1166 14.3398,42.4836 14.7308,42.0926 L16.8518,39.9716 C17.2418,39.5806 17.8748,39.5806 18.2658,39.9716 C18.6568,40.3626 18.6568,40.9956 18.2658,41.3856 L16.1448,43.5066 C15.9498,43.7026 15.6938,43.7996 15.4378,43.7996 M10.4878,48.7496 C10.2318,48.7496 9.9758,48.6526 9.7808,48.4566 C9.3898,48.0666 9.3898,47.4336 9.7808,47.0426 L11.9018,44.9216 C12.2918,44.5316 12.9248,44.5316 13.3158,44.9216 C13.7058,45.3126 13.7058,45.9456 13.3158,46.3356 L11.1948,48.4566 C10.9988,48.6526 10.7428,48.7496 10.4878,48.7496\" id=\"Fill-6\" fill=\"#DFE4EC\"></path>\r\n                          <path d=\"M74.4552,55.3154 C74.0662,50.3704 69.8832,46.4424 64.8582,46.4424 C63.3582,46.4424 61.8872,46.7904 60.5542,47.4574 C58.2872,44.8364 54.9512,43.2944 51.4702,43.2944 C44.8582,43.2944 39.4792,48.6714 39.4792,55.2844 L39.4792,55.3344 C36.5892,56.8554 34.7552,59.8584 34.7552,63.1594 C34.7552,68.0344 38.7212,72.0004 43.5962,72.0004 L70.3712,72.0004 C75.2452,72.0004 79.2112,68.0344 79.2112,63.1594 C79.2112,59.8434 77.3632,56.8284 74.4552,55.3154\" id=\"Fill-8\" fill=\"#E1F1FF\"></path>\r\n                          <path d=\"M5.4796,25.5 C5.4796,26.881 4.3606,28 2.9796,28 C1.5986,28 0.4796,26.881 0.4796,25.5 C0.4796,24.119 1.5986,23 2.9796,23 C4.3606,23 5.4796,24.119 5.4796,25.5\" id=\"Fill-10\" fill=\"#DFE9EF\"></path>\r\n                          <path d=\"M78.4796,19.5 C78.4796,20.881 77.3606,22 75.9796,22 C74.5986,22 73.4796,20.881 73.4796,19.5 C73.4796,18.119 74.5986,17 75.9796,17 C77.3606,17 78.4796,18.119 78.4796,19.5\" id=\"Fill-12\" fill=\"#FFC007\"></path>\r\n                          <path d=\"M76.4796,41 C74.2736,41 72.4796,42.794 72.4796,45 C72.4796,47.206 74.2736,49 76.4796,49 C78.6856,49 80.4796,47.206 80.4796,45 C80.4796,42.794 78.6856,41 76.4796,41 M76.4796,50 C73.7226,50 71.4796,47.757 71.4796,45 C71.4796,42.243 73.7226,40 76.4796,40 C79.2366,40 81.4796,42.243 81.4796,45 C81.4796,47.757 79.2366,50 76.4796,50\" id=\"Fill-14\" fill=\"#FFC007\"></path>\r\n                          <path d=\"M42.5899,1 L41.4099,1 C32.2509,1 24.7979,8.452 24.7979,17.611 L24.7979,29.962 L32.3229,29.962 L32.3229,17.611 C32.3229,12.602 36.3999,8.524 41.4099,8.524 L42.5899,8.524 C47.5999,8.524 51.6769,12.602 51.6769,17.611 L51.6769,29.962 L59.2009,29.962 L59.2009,17.611 C59.2009,8.452 51.7489,1 42.5899,1\" id=\"Fill-16\" fill=\"#FFFFFF\"></path>\r\n                          <path d=\"M52.6768,28.9619 L58.2008,28.9619 L58.2008,17.6109 C58.2008,9.0029 51.1978,1.9999 42.5898,1.9999 L41.4098,1.9999 C32.8018,1.9999 25.7978,9.0029 25.7978,17.6109 L25.7978,28.9619 L31.3228,28.9619 L31.3228,17.6109 C31.3228,12.0499 35.8488,7.5249 41.4098,7.5249 L42.5898,7.5249 C48.1518,7.5249 52.6768,12.0499 52.6768,17.6109 L52.6768,28.9619 Z M60.2008,30.9619 L50.6768,30.9619 L50.6768,17.6109 C50.6768,13.1519 47.0488,9.5249 42.5898,9.5249 L41.4098,9.5249 C36.9508,9.5249 33.3228,13.1519 33.3228,17.6109 L33.3228,30.9619 L23.7978,30.9619 L23.7978,17.6109 C23.7978,7.8999 31.6978,-0.0001 41.4098,-0.0001 L42.5898,-0.0001 C52.3008,-0.0001 60.2008,7.8999 60.2008,17.6109 L60.2008,30.9619 Z\" id=\"Fill-18\" fill=\"#0859A3\"></path>\r\n                          <path d=\"M59.3565,63 L24.6435,63 C21.9475,63 19.7415,60.794 19.7415,58.098 L19.7415,34.864 C19.7415,32.168 21.9475,29.962 24.6435,29.962 L59.3565,29.962 C62.0525,29.962 64.2585,32.168 64.2585,34.864 L64.2585,58.098 C64.2585,60.794 62.0525,63 59.3565,63\" id=\"Fill-20\" fill=\"#FFFFFF\"></path>\r\n                          <path d=\"M24.6436,30.9619 C22.4926,30.9619 20.7416,32.7129 20.7416,34.8639 L20.7416,58.0979 C20.7416,60.2489 22.4926,61.9999 24.6436,61.9999 L59.3566,61.9999 C61.5076,61.9999 63.2586,60.2489 63.2586,58.0979 L63.2586,34.8639 C63.2586,32.7129 61.5076,30.9619 59.3566,30.9619 L24.6436,30.9619 Z M59.3566,63.9999 L24.6436,63.9999 C21.3886,63.9999 18.7416,61.3529 18.7416,58.0979 L18.7416,34.8639 C18.7416,31.6089 21.3886,28.9619 24.6436,28.9619 L59.3566,28.9619 C62.6116,28.9619 65.2586,31.6089 65.2586,34.8639 L65.2586,58.0979 C65.2586,61.3529 62.6116,63.9999 59.3566,63.9999 L59.3566,63.9999 Z\" id=\"Fill-22\" fill=\"#0859A3\"></path>\r\n                          <path d=\"M42.0001,39.3604 C39.9271,39.3604 38.2391,41.0474 38.2391,43.1214 C38.2391,44.2034 38.7251,45.2394 39.5701,45.9634 C39.7921,46.1524 39.9201,46.4304 39.9201,46.7224 L39.9201,52.5354 C39.9201,53.6814 40.8541,54.6154 42.0001,54.6154 C43.1461,54.6154 44.0801,53.6814 44.0801,52.5354 L44.0801,46.7224 C44.0801,46.4304 44.2081,46.1534 44.4301,45.9634 C45.2751,45.2394 45.7601,44.2034 45.7601,43.1214 C45.7601,41.0474 44.0731,39.3604 42.0001,39.3604 M42.0001,56.6154 C39.7501,56.6154 37.9201,54.7854 37.9201,52.5354 L37.9201,47.1584 C36.8471,46.0784 36.2391,44.6314 36.2391,43.1214 C36.2391,39.9444 38.8231,37.3604 42.0001,37.3604 C45.1761,37.3604 47.7601,39.9444 47.7601,43.1214 C47.7601,44.6314 47.1531,46.0784 46.0801,47.1574 L46.0801,52.5354 C46.0801,54.7854 44.2501,56.6154 42.0001,56.6154\" id=\"Fill-24\" fill=\"#00ADFF\"></path>\r\n                          <path d=\"M40.1114,33.8584 L26.9464,33.8584 C24.8594,33.8584 23.1524,35.5654 23.1524,37.6524 L23.1524,50.8174 L40.1114,33.8584 Z\" id=\"Fill-26\" fill=\"#EDF2F7\"></path>\r\n                          <path d=\"M5.4796,48 C3.2736,48 1.4796,49.794 1.4796,52 C1.4796,54.206 3.2736,56 5.4796,56 C7.6856,56 9.4796,54.206 9.4796,52 C9.4796,49.794 7.6856,48 5.4796,48 M5.4796,57 C2.7226,57 0.4796,54.757 0.4796,52 C0.4796,49.243 2.7226,47 5.4796,47 C8.2366,47 10.4796,49.243 10.4796,52 C10.4796,54.757 8.2366,57 5.4796,57\" id=\"Fill-28\" fill=\"#00ADFF\"></path>\r\n                      </g>\r\n                  </g>\r\n              </g>\r\n          </g>\r\n      </g>\r\n    </svg>\r\n  </div>\r\n  <!-- END SVG GRAPHIC -->\r\n\r\n  <div *ngIf=\"canAssignLicenses()\" class=\"text-center\">\r\n    <div class=\"mb-1\">\r\n        <h3>Looks like you need to assign a {{ this.configuration.productDisplayName }} license to this user account.</h3>\r\n    </div>\r\n    <div class=\"text-muted mb-3\">As a ConnectWise Administrator, you can assign licenses in Manage Users.</div>\r\n    <a class=\"btn btn-primary\" [href]=\"manageUsersUrl\">Assign a license</a>\r\n  </div>\r\n  <div *ngIf=\"!canAssignLicenses()\" class=\"text-center\">\r\n    <div>\r\n        <h3>Looks like you need to request access to {{ this.configuration.productDisplayName }}</h3>\r\n    </div>\r\n    <div *ngIf=\"!sentRequest\">\r\n        <textarea class=\"form-control my-100\" [disabled]=\"sendingRequest\" (change)=\"reason = $event.target.value\" placeholder=\"Add a message to your ConnectWise Administrator (optional)\"></textarea>\r\n        <button class=\"btn btn-primary\" [disabled]=\"sendingRequest\" (click)=\"requestLicenseFromAdmins()\">Request access</button>\r\n    </div>\r\n    <div *ngIf=\"sentRequest\" class=\"text-center py-4\">\r\n        Your request has been sent.\r\n    </div>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotLicensedComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-not-licensed', host: { class: 'w-100' }, template: "<div class=\"d-flex flex-column align-items-center mt-5\">\r\n  <!-- BEGIN SVG GRAPHIC -->\r\n  <div class=\"my-4\">\r\n    <svg width=\"108px\" height=\"108px\" viewBox=\"0 0 108 108\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\">\r\n      <title>art-access</title>\r\n      <g id=\"Empty-States\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\">\r\n          <g id=\"Empty-Access\" transform=\"translate(-744.000000, -227.000000)\">\r\n              <g id=\"empty-state/CTA-Copy\" transform=\"translate(623.000000, 227.000000)\">\r\n                  <g id=\"art-access\" transform=\"translate(121.000000, 0.000000)\">\r\n                      <polygon id=\"Fill-1\" fill=\"#FFFFFF\" points=\"0 108 108 108 108 0 0 0\"></polygon>\r\n                      <g id=\"Group-30\" transform=\"translate(12.000000, 18.000000)\">\r\n                          <path d=\"M16.4796,12.5 C16.4796,13.881 15.3606,15 13.9796,15 C12.5986,15 11.4796,13.881 11.4796,12.5 C11.4796,11.119 12.5986,10 13.9796,10 C15.3606,10 16.4796,11.119 16.4796,12.5\" id=\"Fill-2\" fill=\"#00ADFF\"></path>\r\n                          <path d=\"M69.3868,26.8506 C69.1308,26.8506 68.8748,26.7526 68.6798,26.5576 C68.2888,26.1666 68.2888,25.5346 68.6798,25.1436 L70.8008,23.0226 C71.1918,22.6316 71.8248,22.6316 72.2148,23.0226 C72.6058,23.4126 72.6058,24.0456 72.2148,24.4366 L70.0938,26.5576 C69.8988,26.7526 69.6428,26.8506 69.3868,26.8506 M64.4378,31.7996 C64.1818,31.7996 63.9258,31.7026 63.7308,31.5066 C63.3398,31.1166 63.3398,30.4836 63.7308,30.0926 L65.8518,27.9716 C66.2418,27.5806 66.8748,27.5806 67.2658,27.9716 C67.6568,28.3626 67.6568,28.9956 67.2658,29.3856 L65.1448,31.5066 C64.9498,31.7026 64.6938,31.7996 64.4378,31.7996 M59.4878,36.7496 C59.2318,36.7496 58.9758,36.6526 58.7808,36.4566 C58.3898,36.0666 58.3898,35.4336 58.7808,35.0426 L60.9018,32.9216 C61.2918,32.5316 61.9248,32.5316 62.3158,32.9216 C62.7058,33.3126 62.7058,33.9456 62.3158,34.3356 L60.1948,36.4566 C59.9988,36.6526 59.7428,36.7496 59.4878,36.7496\" id=\"Fill-4\" fill=\"#DFE4EC\"></path>\r\n                          <path d=\"M20.3868,38.8506 C20.1308,38.8506 19.8748,38.7526 19.6798,38.5576 C19.2888,38.1666 19.2888,37.5346 19.6798,37.1436 L21.8008,35.0226 C22.1918,34.6316 22.8248,34.6316 23.2148,35.0226 C23.6058,35.4126 23.6058,36.0456 23.2148,36.4366 L21.0938,38.5576 C20.8988,38.7526 20.6428,38.8506 20.3868,38.8506 M15.4378,43.7996 C15.1818,43.7996 14.9258,43.7026 14.7308,43.5066 C14.3398,43.1166 14.3398,42.4836 14.7308,42.0926 L16.8518,39.9716 C17.2418,39.5806 17.8748,39.5806 18.2658,39.9716 C18.6568,40.3626 18.6568,40.9956 18.2658,41.3856 L16.1448,43.5066 C15.9498,43.7026 15.6938,43.7996 15.4378,43.7996 M10.4878,48.7496 C10.2318,48.7496 9.9758,48.6526 9.7808,48.4566 C9.3898,48.0666 9.3898,47.4336 9.7808,47.0426 L11.9018,44.9216 C12.2918,44.5316 12.9248,44.5316 13.3158,44.9216 C13.7058,45.3126 13.7058,45.9456 13.3158,46.3356 L11.1948,48.4566 C10.9988,48.6526 10.7428,48.7496 10.4878,48.7496\" id=\"Fill-6\" fill=\"#DFE4EC\"></path>\r\n                          <path d=\"M74.4552,55.3154 C74.0662,50.3704 69.8832,46.4424 64.8582,46.4424 C63.3582,46.4424 61.8872,46.7904 60.5542,47.4574 C58.2872,44.8364 54.9512,43.2944 51.4702,43.2944 C44.8582,43.2944 39.4792,48.6714 39.4792,55.2844 L39.4792,55.3344 C36.5892,56.8554 34.7552,59.8584 34.7552,63.1594 C34.7552,68.0344 38.7212,72.0004 43.5962,72.0004 L70.3712,72.0004 C75.2452,72.0004 79.2112,68.0344 79.2112,63.1594 C79.2112,59.8434 77.3632,56.8284 74.4552,55.3154\" id=\"Fill-8\" fill=\"#E1F1FF\"></path>\r\n                          <path d=\"M5.4796,25.5 C5.4796,26.881 4.3606,28 2.9796,28 C1.5986,28 0.4796,26.881 0.4796,25.5 C0.4796,24.119 1.5986,23 2.9796,23 C4.3606,23 5.4796,24.119 5.4796,25.5\" id=\"Fill-10\" fill=\"#DFE9EF\"></path>\r\n                          <path d=\"M78.4796,19.5 C78.4796,20.881 77.3606,22 75.9796,22 C74.5986,22 73.4796,20.881 73.4796,19.5 C73.4796,18.119 74.5986,17 75.9796,17 C77.3606,17 78.4796,18.119 78.4796,19.5\" id=\"Fill-12\" fill=\"#FFC007\"></path>\r\n                          <path d=\"M76.4796,41 C74.2736,41 72.4796,42.794 72.4796,45 C72.4796,47.206 74.2736,49 76.4796,49 C78.6856,49 80.4796,47.206 80.4796,45 C80.4796,42.794 78.6856,41 76.4796,41 M76.4796,50 C73.7226,50 71.4796,47.757 71.4796,45 C71.4796,42.243 73.7226,40 76.4796,40 C79.2366,40 81.4796,42.243 81.4796,45 C81.4796,47.757 79.2366,50 76.4796,50\" id=\"Fill-14\" fill=\"#FFC007\"></path>\r\n                          <path d=\"M42.5899,1 L41.4099,1 C32.2509,1 24.7979,8.452 24.7979,17.611 L24.7979,29.962 L32.3229,29.962 L32.3229,17.611 C32.3229,12.602 36.3999,8.524 41.4099,8.524 L42.5899,8.524 C47.5999,8.524 51.6769,12.602 51.6769,17.611 L51.6769,29.962 L59.2009,29.962 L59.2009,17.611 C59.2009,8.452 51.7489,1 42.5899,1\" id=\"Fill-16\" fill=\"#FFFFFF\"></path>\r\n                          <path d=\"M52.6768,28.9619 L58.2008,28.9619 L58.2008,17.6109 C58.2008,9.0029 51.1978,1.9999 42.5898,1.9999 L41.4098,1.9999 C32.8018,1.9999 25.7978,9.0029 25.7978,17.6109 L25.7978,28.9619 L31.3228,28.9619 L31.3228,17.6109 C31.3228,12.0499 35.8488,7.5249 41.4098,7.5249 L42.5898,7.5249 C48.1518,7.5249 52.6768,12.0499 52.6768,17.6109 L52.6768,28.9619 Z M60.2008,30.9619 L50.6768,30.9619 L50.6768,17.6109 C50.6768,13.1519 47.0488,9.5249 42.5898,9.5249 L41.4098,9.5249 C36.9508,9.5249 33.3228,13.1519 33.3228,17.6109 L33.3228,30.9619 L23.7978,30.9619 L23.7978,17.6109 C23.7978,7.8999 31.6978,-0.0001 41.4098,-0.0001 L42.5898,-0.0001 C52.3008,-0.0001 60.2008,7.8999 60.2008,17.6109 L60.2008,30.9619 Z\" id=\"Fill-18\" fill=\"#0859A3\"></path>\r\n                          <path d=\"M59.3565,63 L24.6435,63 C21.9475,63 19.7415,60.794 19.7415,58.098 L19.7415,34.864 C19.7415,32.168 21.9475,29.962 24.6435,29.962 L59.3565,29.962 C62.0525,29.962 64.2585,32.168 64.2585,34.864 L64.2585,58.098 C64.2585,60.794 62.0525,63 59.3565,63\" id=\"Fill-20\" fill=\"#FFFFFF\"></path>\r\n                          <path d=\"M24.6436,30.9619 C22.4926,30.9619 20.7416,32.7129 20.7416,34.8639 L20.7416,58.0979 C20.7416,60.2489 22.4926,61.9999 24.6436,61.9999 L59.3566,61.9999 C61.5076,61.9999 63.2586,60.2489 63.2586,58.0979 L63.2586,34.8639 C63.2586,32.7129 61.5076,30.9619 59.3566,30.9619 L24.6436,30.9619 Z M59.3566,63.9999 L24.6436,63.9999 C21.3886,63.9999 18.7416,61.3529 18.7416,58.0979 L18.7416,34.8639 C18.7416,31.6089 21.3886,28.9619 24.6436,28.9619 L59.3566,28.9619 C62.6116,28.9619 65.2586,31.6089 65.2586,34.8639 L65.2586,58.0979 C65.2586,61.3529 62.6116,63.9999 59.3566,63.9999 L59.3566,63.9999 Z\" id=\"Fill-22\" fill=\"#0859A3\"></path>\r\n                          <path d=\"M42.0001,39.3604 C39.9271,39.3604 38.2391,41.0474 38.2391,43.1214 C38.2391,44.2034 38.7251,45.2394 39.5701,45.9634 C39.7921,46.1524 39.9201,46.4304 39.9201,46.7224 L39.9201,52.5354 C39.9201,53.6814 40.8541,54.6154 42.0001,54.6154 C43.1461,54.6154 44.0801,53.6814 44.0801,52.5354 L44.0801,46.7224 C44.0801,46.4304 44.2081,46.1534 44.4301,45.9634 C45.2751,45.2394 45.7601,44.2034 45.7601,43.1214 C45.7601,41.0474 44.0731,39.3604 42.0001,39.3604 M42.0001,56.6154 C39.7501,56.6154 37.9201,54.7854 37.9201,52.5354 L37.9201,47.1584 C36.8471,46.0784 36.2391,44.6314 36.2391,43.1214 C36.2391,39.9444 38.8231,37.3604 42.0001,37.3604 C45.1761,37.3604 47.7601,39.9444 47.7601,43.1214 C47.7601,44.6314 47.1531,46.0784 46.0801,47.1574 L46.0801,52.5354 C46.0801,54.7854 44.2501,56.6154 42.0001,56.6154\" id=\"Fill-24\" fill=\"#00ADFF\"></path>\r\n                          <path d=\"M40.1114,33.8584 L26.9464,33.8584 C24.8594,33.8584 23.1524,35.5654 23.1524,37.6524 L23.1524,50.8174 L40.1114,33.8584 Z\" id=\"Fill-26\" fill=\"#EDF2F7\"></path>\r\n                          <path d=\"M5.4796,48 C3.2736,48 1.4796,49.794 1.4796,52 C1.4796,54.206 3.2736,56 5.4796,56 C7.6856,56 9.4796,54.206 9.4796,52 C9.4796,49.794 7.6856,48 5.4796,48 M5.4796,57 C2.7226,57 0.4796,54.757 0.4796,52 C0.4796,49.243 2.7226,47 5.4796,47 C8.2366,47 10.4796,49.243 10.4796,52 C10.4796,54.757 8.2366,57 5.4796,57\" id=\"Fill-28\" fill=\"#00ADFF\"></path>\r\n                      </g>\r\n                  </g>\r\n              </g>\r\n          </g>\r\n      </g>\r\n    </svg>\r\n  </div>\r\n  <!-- END SVG GRAPHIC -->\r\n\r\n  <div *ngIf=\"canAssignLicenses()\" class=\"text-center\">\r\n    <div class=\"mb-1\">\r\n        <h3>Looks like you need to assign a {{ this.configuration.productDisplayName }} license to this user account.</h3>\r\n    </div>\r\n    <div class=\"text-muted mb-3\">As a ConnectWise Administrator, you can assign licenses in Manage Users.</div>\r\n    <a class=\"btn btn-primary\" [href]=\"manageUsersUrl\">Assign a license</a>\r\n  </div>\r\n  <div *ngIf=\"!canAssignLicenses()\" class=\"text-center\">\r\n    <div>\r\n        <h3>Looks like you need to request access to {{ this.configuration.productDisplayName }}</h3>\r\n    </div>\r\n    <div *ngIf=\"!sentRequest\">\r\n        <textarea class=\"form-control my-100\" [disabled]=\"sendingRequest\" (change)=\"reason = $event.target.value\" placeholder=\"Add a message to your ConnectWise Administrator (optional)\"></textarea>\r\n        <button class=\"btn btn-primary\" [disabled]=\"sendingRequest\" (click)=\"requestLicenseFromAdmins()\">Request access</button>\r\n    </div>\r\n    <div *ngIf=\"sentRequest\" class=\"text-center py-4\">\r\n        Your request has been sent.\r\n    </div>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i2$1.AbstractUserProvider }, { type: PartnerService }, { type: RouteResolverService }, { type: i2.ActivatedRoute }] });

class ErrorHandlingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ErrorHandlingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: ErrorHandlingModule, declarations: [NotFoundComponent,
            NotLicensedComponent,
            MfaRequiredComponent,
            NoAccessComponent], imports: [ButtonsModule,
            CommonModule,
            UtilityModule], exports: [NotFoundComponent,
            NotLicensedComponent,
            MfaRequiredComponent,
            NoAccessComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ErrorHandlingModule, imports: [ButtonsModule,
            CommonModule,
            UtilityModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ErrorHandlingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        NotFoundComponent,
                        NotLicensedComponent,
                        MfaRequiredComponent,
                        NoAccessComponent
                    ],
                    imports: [
                        ButtonsModule,
                        CommonModule,
                        UtilityModule,
                    ],
                    exports: [
                        NotFoundComponent,
                        NotLicensedComponent,
                        MfaRequiredComponent,
                        NoAccessComponent
                    ]
                }]
        }] });

class AccordionBodyDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionBodyDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: AccordionBodyDirective, selector: "[sk-accordion-body]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionBodyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[sk-accordion-body]"
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }] });

class AccordionHeaderDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionHeaderDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: AccordionHeaderDirective, selector: "[sk-accordion-header]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionHeaderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[sk-accordion-header]"
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }] });

class AccordionTitleDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionTitleDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: AccordionTitleDirective, selector: "[sk-accordion-title]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionTitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[sk-accordion-title]"
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }] });

class AccordionItemDirective {
    constructor() {
        this.title = "";
        this.disabled = false;
        this.hide = new Observable();
        /**
         * Emits whenever the expanded state of the accordion changes.
         * Primarily used to facilitate two-way binding.
         *  @ignore
         */
        this.expandedChange = new EventEmitter();
        this._expanded = false;
    }
    /** Whether the AccordionItem is expanded. */
    get expanded() {
        return this._expanded;
    }
    set expanded(val) {
        // Only emit events and update the internal value if the value changes.
        if (this._expanded !== val) {
            this._expanded = val;
            this.expandedChange.emit(val);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionItemDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: AccordionItemDirective, selector: "sk-accordion-item", inputs: { title: "title", disabled: "disabled", hide: "hide", expanded: "expanded" }, outputs: { expandedChange: "expandedChange" }, queries: [{ propertyName: "content", first: true, predicate: AccordionBodyDirective, descendants: true }, { propertyName: "customTitle", first: true, predicate: AccordionTitleDirective, descendants: true }, { propertyName: "customHeader", first: true, predicate: AccordionHeaderDirective, descendants: true }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "sk-accordion-item"
                }]
        }], propDecorators: { title: [{
                type: Input
            }], disabled: [{
                type: Input
            }], hide: [{
                type: Input
            }], expandedChange: [{
                type: Output
            }], expanded: [{
                type: Input
            }], content: [{
                type: ContentChild,
                args: [AccordionBodyDirective]
            }], customTitle: [{
                type: ContentChild,
                args: [AccordionTitleDirective]
            }], customHeader: [{
                type: ContentChild,
                args: [AccordionHeaderDirective]
            }] } });

class AccordionComponent {
    constructor(cdr) {
        this.cdr = cdr;
        this.expanded = new Set();
        this.hidden = new Set();
        /**
         * When true, all items can be open.
         */
        this.expandable = false;
        this.toggleState = (item, index) => {
            if (this.expanded.has(index)) {
                item.expanded = false;
                this.expanded.delete(index);
            }
            else {
                if (!this.expandable) {
                    this.expanded.clear();
                }
                item.expanded = true;
                this.expanded.add(index);
            }
        };
        this.itemChangeSubscription = new Subscription();
    }
    ngAfterViewInit() {
        this.itemChangeSubscription.add(merge(this.items.changes, of(this.items))
            .pipe(map(() => this.items.toArray()))
            .subscribe((items) => {
            items.forEach((item, index) => {
                this.itemChangeSubscription.add(item.expandedChange.subscribe(() => {
                    this.toggleState(item, index);
                    this.cdr.detectChanges();
                }));
                this.itemChangeSubscription.add(item.hide.subscribe((value) => {
                    if (value) {
                        this.hidden.add(index);
                    }
                    else {
                        this.hidden.delete(index);
                    }
                    this.cdr.detectChanges();
                }));
                if (item.expanded) {
                    this.expanded.add(index);
                }
            });
            this.cdr.detectChanges();
        }));
    }
    ngOnDestroy() {
        this.itemChangeSubscription.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: AccordionComponent, selector: "sk-accordion", inputs: { expandable: "expandable" }, queries: [{ propertyName: "items", predicate: AccordionItemDirective }], ngImport: i0, template: "<div class=\"accordion\">\r\n    <!-- accordion-item -->\r\n     <ng-container *ngFor=\"let item of items;index as i\">\r\n        <div class=\"card\" [ngClass]=\"{'d-none': hidden.has(i) }\">\r\n            <ng-container [ngTemplateOutlet]=\"(item?.customHeader?.templateRef || defaultHeader)\"\r\n                [ngTemplateOutletContext]=\"{$implicit: item, index: i}\"></ng-container>\r\n            <div [@expansion]=\"expanded.has(i)\">\r\n                <div class=\"card-block border-top\">\r\n                    <ng-container *ngTemplateOutlet=\"item?.content?.templateRef\"></ng-container>\r\n                </div>\r\n            </div>\r\n        </div>\r\n    </ng-container>\r\n</div>\r\n\r\n\r\n<ng-template #defaultHeader let-item let-index=\"index\">\r\n    <!-- accordion-header -->\r\n    <header role=\"button\" class=\"sk-card-header d-flex justify-content-between\"\r\n        (click)=\"item.disabled ? {} :toggleState(item, index)\">\r\n        <ng-container *ngTemplateOutlet=\"item?.customTitle?.templateRef || defaultTitle\"></ng-container>\r\n\r\n        <button class=\"btn sk-btn-icon border-0\" [disabled]=\"item.disabled\">\r\n            <i class=\"material-icons md-22\">{{ 'keyboard_arrow_' + (expanded.has(index) ? 'up' : 'down') }}</i>\r\n        </button>\r\n    </header>\r\n    <ng-template #defaultTitle>\r\n        <h4 [class.text-muted]=\"item.disabled\">{{item?.title}}</h4>\r\n    </ng-template>\r\n</ng-template>\r\n", styles: ["header.text-muted{pointer-events:none}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], animations: [
            trigger('expansion', [
                state('true', style({ height: AUTO_STYLE, visibility: AUTO_STYLE })),
                state('false', style({ height: '0', visibility: 'hidden' })),
                transition('true <=> false', animate('200ms cubic-bezier(.37,1.04,.68,.98)')),
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-accordion', changeDetection: ChangeDetectionStrategy.OnPush, animations: [
                        trigger('expansion', [
                            state('true', style({ height: AUTO_STYLE, visibility: AUTO_STYLE })),
                            state('false', style({ height: '0', visibility: 'hidden' })),
                            transition('true <=> false', animate('200ms cubic-bezier(.37,1.04,.68,.98)')),
                        ])
                    ], template: "<div class=\"accordion\">\r\n    <!-- accordion-item -->\r\n     <ng-container *ngFor=\"let item of items;index as i\">\r\n        <div class=\"card\" [ngClass]=\"{'d-none': hidden.has(i) }\">\r\n            <ng-container [ngTemplateOutlet]=\"(item?.customHeader?.templateRef || defaultHeader)\"\r\n                [ngTemplateOutletContext]=\"{$implicit: item, index: i}\"></ng-container>\r\n            <div [@expansion]=\"expanded.has(i)\">\r\n                <div class=\"card-block border-top\">\r\n                    <ng-container *ngTemplateOutlet=\"item?.content?.templateRef\"></ng-container>\r\n                </div>\r\n            </div>\r\n        </div>\r\n    </ng-container>\r\n</div>\r\n\r\n\r\n<ng-template #defaultHeader let-item let-index=\"index\">\r\n    <!-- accordion-header -->\r\n    <header role=\"button\" class=\"sk-card-header d-flex justify-content-between\"\r\n        (click)=\"item.disabled ? {} :toggleState(item, index)\">\r\n        <ng-container *ngTemplateOutlet=\"item?.customTitle?.templateRef || defaultTitle\"></ng-container>\r\n\r\n        <button class=\"btn sk-btn-icon border-0\" [disabled]=\"item.disabled\">\r\n            <i class=\"material-icons md-22\">{{ 'keyboard_arrow_' + (expanded.has(index) ? 'up' : 'down') }}</i>\r\n        </button>\r\n    </header>\r\n    <ng-template #defaultTitle>\r\n        <h4 [class.text-muted]=\"item.disabled\">{{item?.title}}</h4>\r\n    </ng-template>\r\n</ng-template>\r\n", styles: ["header.text-muted{pointer-events:none}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { expandable: [{
                type: Input
            }], items: [{
                type: ContentChildren,
                args: [AccordionItemDirective]
            }] } });

const references = [
    AccordionComponent,
    AccordionHeaderDirective,
    AccordionBodyDirective,
    AccordionTitleDirective,
    AccordionItemDirective
];
class AccordionModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: AccordionModule, declarations: [AccordionComponent,
            AccordionHeaderDirective,
            AccordionBodyDirective,
            AccordionTitleDirective,
            AccordionItemDirective], imports: [CommonModule], exports: [AccordionComponent,
            AccordionHeaderDirective,
            AccordionBodyDirective,
            AccordionTitleDirective,
            AccordionItemDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccordionModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: references,
                    imports: [
                        CommonModule,
                    ],
                    exports: references
                }]
        }] });

var AvatarSize;
(function (AvatarSize) {
    AvatarSize["Small"] = "sm";
    AvatarSize["Medium"] = "";
    AvatarSize["Large"] = "lg";
})(AvatarSize || (AvatarSize = {}));
var AvatarShape;
(function (AvatarShape) {
    AvatarShape["Circle"] = "circle";
    AvatarShape["Square"] = "square";
})(AvatarShape || (AvatarShape = {}));
class AvatarComponent {
    constructor() {
        /**
         *
         */
        this.size = AvatarSize.Medium;
        /**
         * Set the shape of the Avatar component.
         */
        this.shape = AvatarShape.Circle;
    }
    /**
     *
     * @ignore
     */
    getBackgroundClass() {
        const color = this.getInitials().split('').reduce((acc, current) => acc + current.charCodeAt(0), 0);
        return `avatar-bg-${color % 10 + 1}`;
    }
    /**
     *
     * @ignore
     */
    getSizeClass() {
        if (this.size === AvatarSize.Medium) {
            return '';
        }
        return `avatar-${this.size}`;
    }
    /**
     *
     * @ignore
     */
    getAvatarClass() {
        if (this.hasDefaultImage()) {
            return this.getSizeClass();
        }
        return [
            this.getBackgroundClass(),
            this.getSizeClass(),
            this.getShapeClass()
        ];
    }
    /**
     *
     * @ignore
     */
    getShapeClass() {
        switch (this.shape) {
            case AvatarShape.Square:
                return 'rounded'; //this comes from Barry's library.
            default:
                return '';
        }
    }
    /**
     *
     * @ignore
     */
    getDefaultSize() {
        switch (this.size) {
            case AvatarSize.Small:
                return 'md-22';
            case AvatarSize.Medium:
                return 'md-32';
            case AvatarSize.Large:
                return 'md-48';
        }
    }
    /**
     *
     * @ignore
     */
    getInitials() {
        if (this.initials) {
            return this.initials;
        }
        if (this.fullName) {
            return this.fullName.split(' ').map(s => s[0]).join('').substring(0, 2);
        }
        return (this.firstName && this.firstName[0] ? this.firstName[0] : '') +
            (this.lastName && this.lastName[0] ? this.lastName[0] : '');
    }
    hasInitials() {
        return !this.imageURL && !!this.getInitials();
    }
    hasDefaultImage() {
        return !this.imageURL && !this.getInitials();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AvatarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: AvatarComponent, selector: "sk-avatar", inputs: { imageURL: "imageURL", initials: "initials", firstName: "firstName", lastName: "lastName", fullName: "fullName", size: "size", shape: "shape" }, ngImport: i0, template: "<div class=\"sk-user-avatar\" [ngClass]=\"getAvatarClass()\">\r\n    <img *ngIf=\"imageURL\" [src]=\"imageURL\" />\r\n    <span *ngIf=\"!imageURL && !hasDefaultImage()\" class=\"avatar-initials\" [ngClass]=\"getBackgroundClass()\">\r\n        {{getInitials()}}\r\n    </span>\r\n    <i *ngIf=\"hasDefaultImage()\" class=\"material-icons icon-muted\" [ngClass]=\"getDefaultSize()\">\r\n        person\r\n    </i>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AvatarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-avatar', template: "<div class=\"sk-user-avatar\" [ngClass]=\"getAvatarClass()\">\r\n    <img *ngIf=\"imageURL\" [src]=\"imageURL\" />\r\n    <span *ngIf=\"!imageURL && !hasDefaultImage()\" class=\"avatar-initials\" [ngClass]=\"getBackgroundClass()\">\r\n        {{getInitials()}}\r\n    </span>\r\n    <i *ngIf=\"hasDefaultImage()\" class=\"material-icons icon-muted\" [ngClass]=\"getDefaultSize()\">\r\n        person\r\n    </i>\r\n</div>\r\n" }]
        }], propDecorators: { imageURL: [{
                type: Input
            }], initials: [{
                type: Input
            }], firstName: [{
                type: Input
            }], lastName: [{
                type: Input
            }], fullName: [{
                type: Input
            }], size: [{
                type: Input
            }], shape: [{
                type: Input
            }] } });

class AvatarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AvatarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: AvatarModule, declarations: [AvatarComponent], imports: [CommonModule], exports: [AvatarComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AvatarModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AvatarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        AvatarComponent
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        AvatarComponent
                    ]
                }]
        }] });

class LoadMoreListComponent {
    constructor() {
        this.displayCount = true;
        this.hideLoadMoreButton = false;
        this.stickyCount = false;
        this.loadMore = new EventEmitter();
    }
    get showLoadMore() {
        return this.totalCount === undefined || this.totalCount === null || this.currentCount < this.totalCount;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: LoadMoreListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: LoadMoreListComponent, selector: "sk-load-more-list", inputs: { currentCount: "currentCount", totalCount: "totalCount", isLoading: "isLoading", countClass: "countClass", displayCount: "displayCount", hideLoadMoreButton: "hideLoadMoreButton", stickyCount: "stickyCount" }, outputs: { loadMore: "loadMore" }, ngImport: i0, template: "<div *ngIf=\"displayCount && (totalCount === 0 || !!totalCount)\" class=\"px-4 pb-100 border-bottom\" [class]=\"countClass\">\r\n    <span class=\"text-muted\">Showing {{ currentCount }} of {{ totalCount }} results</span>\r\n</div>\r\n\r\n<div class=\"sk-scroll-container\" *ngIf=\"stickyCount; else content\">\r\n    <ng-container *ngTemplateOutlet=\"content\"></ng-container>\r\n</div>\r\n\r\n<ng-template #content>\r\n    <ng-content></ng-content>\r\n\r\n    <div (click)=\"loadMore.emit()\" class=\"row justify-content-center p-2 load-more\" *ngIf=\"!hideLoadMoreButton && !isLoading && showLoadMore\">\r\n        <div class=\"d-flex align-items-center\">\r\n            <button class=\"btn btn-link\">Load more</button>\r\n            <i class=\"material-icons md-18 sk-arrow-down\">keyboard_arrow_down</i>\r\n        </div>\r\n    </div>\r\n\r\n    <div class=\"d-flex justify-content-center py-4 px-200\" *ngIf=\"isLoading\">\r\n        <div class=\"sk-spin-loader\"></div>\r\n    </div>\r\n</ng-template>\r\n", styles: [".load-more{padding:5px 0;margin:0;cursor:pointer}.load-more:hover,.load-more.active{background-color:#f0f8ff;border-color:#d2e4f9;border-top:1px solid #d2e4f9}.load-more div button{padding-right:0}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: LoadMoreListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-load-more-list', template: "<div *ngIf=\"displayCount && (totalCount === 0 || !!totalCount)\" class=\"px-4 pb-100 border-bottom\" [class]=\"countClass\">\r\n    <span class=\"text-muted\">Showing {{ currentCount }} of {{ totalCount }} results</span>\r\n</div>\r\n\r\n<div class=\"sk-scroll-container\" *ngIf=\"stickyCount; else content\">\r\n    <ng-container *ngTemplateOutlet=\"content\"></ng-container>\r\n</div>\r\n\r\n<ng-template #content>\r\n    <ng-content></ng-content>\r\n\r\n    <div (click)=\"loadMore.emit()\" class=\"row justify-content-center p-2 load-more\" *ngIf=\"!hideLoadMoreButton && !isLoading && showLoadMore\">\r\n        <div class=\"d-flex align-items-center\">\r\n            <button class=\"btn btn-link\">Load more</button>\r\n            <i class=\"material-icons md-18 sk-arrow-down\">keyboard_arrow_down</i>\r\n        </div>\r\n    </div>\r\n\r\n    <div class=\"d-flex justify-content-center py-4 px-200\" *ngIf=\"isLoading\">\r\n        <div class=\"sk-spin-loader\"></div>\r\n    </div>\r\n</ng-template>\r\n", styles: [".load-more{padding:5px 0;margin:0;cursor:pointer}.load-more:hover,.load-more.active{background-color:#f0f8ff;border-color:#d2e4f9;border-top:1px solid #d2e4f9}.load-more div button{padding-right:0}\n"] }]
        }], propDecorators: { currentCount: [{
                type: Input
            }], totalCount: [{
                type: Input
            }], isLoading: [{
                type: Input
            }], countClass: [{
                type: Input
            }], displayCount: [{
                type: Input
            }], hideLoadMoreButton: [{
                type: Input
            }], stickyCount: [{
                type: Input
            }], loadMore: [{
                type: Output
            }] } });

class LoadMoreListModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: LoadMoreListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: LoadMoreListModule, declarations: [LoadMoreListComponent], imports: [CommonModule], exports: [LoadMoreListComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: LoadMoreListModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: LoadMoreListModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        LoadMoreListComponent
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        LoadMoreListComponent
                    ]
                }]
        }] });

class FilterSortPanelComponent {
    constructor(self) {
        this.self = self;
        this.isSortOpen = false;
        this.currentSort = null;
        this.currentSortAscending = false;
        this.optionalSectionBeforeFilterSortOptions = null;
        this.filterChange = new EventEmitter();
        this.sortChange = new EventEmitter();
        this.appliedFilters = [];
        this.collapsedCategories = [];
        this.shownAllCategories = [];
        this.subscriptions = [];
        this.optionsShown = 5;
    }
    ngOnInit() {
        if (this.clearInitialOptions$) {
            this.subscriptions.push(this.clearInitialOptions$.subscribe(() => {
                this.initialOptions = null;
            }));
        }
    }
    onDocumentClick(event) {
        if (!this.isSortOpen) {
            return;
        }
        const button = this.self.nativeElement.querySelector('.sk-header-flush');
        if (button.contains(event.target)) {
            return;
        }
        const panel = this.self.nativeElement.querySelector('.submenu');
        if (!panel.contains(event.target)) {
            this.isSortOpen = false;
        }
    }
    getAllOptions(currentOptions) {
        if (!this.initialOptions) {
            this.initialOptions = currentOptions;
            return this.initialOptions;
        }
        this.initialOptions.forEach(initialOption => {
            const currentOption = currentOptions.find(c => c.name === initialOption.name);
            if (!currentOption) {
                return;
            }
            initialOption.options.forEach(option => {
                const currentValue = currentOption.options.find(o => o.value === option.value);
                option.count = currentValue ? (currentValue.count || 0) : 0;
                // if this value is not part of the most recent category and there are no
                // options for it, it should not be shown in the UI
                if (this.mostRecentCategory !== currentOption.name && option.count === 0) {
                    option.hidden = true;
                }
                else if (option.count) {
                    option.hidden = false;
                }
            });
        });
        if (!this.appliedFilters.length) {
            this.mostRecentCategory = null;
        }
        return this.initialOptions;
    }
    toggleCategoryCollapsed(categoryName) {
        const index = this.collapsedCategories.indexOf(categoryName);
        if (index >= 0) {
            this.collapsedCategories.splice(index, 1);
        }
        else {
            this.collapsedCategories.push(categoryName);
        }
    }
    isCategoryCollapsed(categoryName) {
        return this.collapsedCategories.indexOf(categoryName) >= 0;
    }
    showAllOptions(categoryName, $event) {
        $event.preventDefault();
        this.shownAllCategories.push(categoryName);
    }
    allOptionsShown(category) {
        const allShown = this.shownAllCategories.indexOf(category.name) >= 0;
        if (allShown) {
            return true;
        }
        const hasFewerThanThreshold = this.getOptionCount(category) <= this.optionsShown;
        if (hasFewerThanThreshold) {
            return true;
        }
        return false;
    }
    getOptions(category) {
        if (this.isCategoryCollapsed(category.name)) {
            return [];
        }
        const options = this.getOptionsForCategory(category);
        return this.allOptionsShown(category)
            ? options
            : options.slice(0, this.optionsShown);
    }
    getOptionCount(category) {
        return this.getOptionsForCategory(category).length;
    }
    getOptionsForCategory(category) {
        // if all applied filters match this and most recent category, don't filter values
        const allAppliedAreMostRecent = this.appliedFilters.every(filter => filter.categoryName === this.mostRecentCategory);
        return allAppliedAreMostRecent && category.name === this.mostRecentCategory
            ? category.options
            : category.options.filter(o => !o.hidden || this.getApplied(category.name, o.value));
    }
    toggleApplied(categoryName, value) {
        this.mostRecentCategory = categoryName;
        const applied = this.getApplied(categoryName, value);
        if (applied) {
            const index = this.appliedFilters.indexOf(applied);
            this.appliedFilters.splice(index, 1);
        }
        else {
            this.appliedFilters.push({ categoryName, value });
        }
        this.emitFilterChange(this.appliedFilters);
    }
    getApplied(categoryName, value) {
        return this.appliedFilters.find(applied => applied.categoryName === categoryName && applied.value === value);
    }
    clearFilters($event) {
        $event.preventDefault();
        this.mostRecentCategory = null;
        this.appliedFilters.length = 0;
        this.emitFilterChange(this.appliedFilters);
    }
    emitFilterChange(appliedFilters) {
        const filter = this.buildFilter(appliedFilters);
        this.filterChange.emit(filter);
    }
    buildFilter(appliedFilters) {
        return appliedFilters
            .reduce((categories, filter) => {
            let category = categories.find(f => f.name === filter.categoryName);
            if (!category) {
                category = {
                    name: filter.categoryName,
                    values: []
                };
                categories.push(category);
            }
            category.values.push(filter.value);
            return categories;
        }, []);
    }
    hasSortOptions() {
        return this.sortOptions && this.sortOptions.length;
    }
    toggleSortOpen() {
        this.isSortOpen = !this.isSortOpen;
    }
    setSort(value) {
        this.currentSort = value;
        this.emitSortChange();
    }
    toggleSortDirection() {
        this.currentSortAscending = !this.currentSortAscending;
        this.emitSortChange();
    }
    emitSortChange() {
        this.sortChange.emit({
            name: this.currentSort,
            ascending: this.currentSortAscending
        });
    }
    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterSortPanelComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: FilterSortPanelComponent, selector: "sk-filter-sort-panel", inputs: { headerRowClass: "headerRowClass", optionRowClass: "optionRowClass", clearInitialOptions$: "clearInitialOptions$", optionalSectionBeforeFilterSortOptions: "optionalSectionBeforeFilterSortOptions", filterOptions$: "filterOptions$", sortOptions: "sortOptions" }, outputs: { filterChange: "filterChange", sortChange: "sortChange" }, host: { listeners: { "document:mousedown": "onDocumentClick($event)" } }, ngImport: i0, template: "<div class=\"d-flex flex-column flex-shrink-1\" *ngIf=\"filterOptions$ | async as filterOptions\">\r\n    <ng-container *ngIf=\"optionalSectionBeforeFilterSortOptions\">\r\n        <ng-container [ngTemplateOutlet]=\"optionalSectionBeforeFilterSortOptions\"></ng-container>\r\n    </ng-container>\r\n    <ng-container *ngIf=\"hasSortOptions()\">\r\n        <span\r\n            class=\"dropdown-header sk-input-icon sk-header-flush\"\r\n            [ngClass]=\"headerRowClass\"\r\n            (click)=\"toggleSortOpen()\">\r\n            Sort\r\n            <i class=\"material-icons md-24\">keyboard_arrow_right</i>\r\n        </span>\r\n        <div class=\"dropdown-menu submenu mt-0\" [class.show]=\"isSortOpen\">\r\n            <span class=\"dropdown-header bg-white border-0\">Sort by</span>\r\n            <a class=\"dropdown-item\" (click)=\"setSort(null)\">\r\n                <i\r\n                    class=\"material-icons md-22\"\r\n                    [class.icon-transparent]=\"currentSort  !== null\">\r\n                    done\r\n                </i>\r\n                Relevance\r\n            </a>\r\n            <a *ngFor=\"let option of sortOptions\" class=\"dropdown-item\" (click)=\"setSort(option.name)\">\r\n                <i\r\n                    class=\"material-icons md-22\"\r\n                    [class.icon-transparent]=\"currentSort !== option.name\">\r\n                    done\r\n                </i>\r\n                {{option.displayName || option.name}}\r\n            </a>\r\n            <div class=\"dropdown-divider\"></div>\r\n            <span class=\"dropdown-header bg-white border-0\">Sort order</span>\r\n            <a class=\"dropdown-item\" (click)=\"toggleSortDirection()\">\r\n                <i\r\n                    class=\"material-icons md-22\"\r\n                    [class.icon-transparent]=\"!currentSortAscending\">\r\n                    done\r\n                </i>\r\n                Ascending\r\n            </a>\r\n            <a class=\"dropdown-item\" (click)=\"toggleSortDirection()\">\r\n                <i\r\n                    class=\"material-icons md-22\"\r\n                    [class.icon-transparent]=\"currentSortAscending\">\r\n                    done\r\n                </i>\r\n                Descending\r\n            </a>\r\n        </div>\r\n    </ng-container>\r\n    <div class=\"overflow-auto flex-shrink-1\">\r\n        <ng-container *ngFor=\"let category of getAllOptions(filterOptions); index as i\">\r\n\r\n            <!-- category -->\r\n            <span\r\n                *ngIf=\"(category.displayName || category.name) && !category.headerHidden; else hiddenHeader\"\r\n                class=\"dropdown-header sk-input-icon\"\r\n                [ngClass]=\"headerRowClass\"\r\n                [class.sk-header-flush]=\"i === 0 && !hasSortOptions()\"\r\n                (click)=\"toggleCategoryCollapsed(category.name)\">\r\n                {{category.displayName || category.name}} ({{getOptionCount(category)}})\r\n                <i class=\"material-icons md-24\" *ngIf=\"isCategoryCollapsed(category.name); else upArrow\">keyboard_arrow_down</i>\r\n                <ng-template #upArrow>\r\n                    <i class=\"material-icons md-24\">keyboard_arrow_up</i>\r\n                </ng-template>\r\n            </span>\r\n\r\n            <ng-template #hiddenHeader>\r\n                <div *ngIf=\"i !== 0\" class=\"border-top\"></div>\r\n            </ng-template>\r\n\r\n            <!-- option -->\r\n            <div *ngFor=\"let option of getOptions(category)\" ngbDropdownItem [ngClass]=\"optionRowClass\">\r\n                <div class=\"form-check\" (click)=\"toggleApplied(category.name, option.value)\">\r\n                    <input type=\"checkbox\" class=\"form-check-input form-check-input-sm\" [checked]=\"getApplied(category.name, option.value)\" />\r\n                    <label class=\"form-check-label align-items-center d-flex\">\r\n                        <i class=\"material-icons md-16 mx-1\" [class]=\"option.iconClass\" *ngIf=\"option.iconName\">{{option.iconName}}</i>\r\n                        <div [class]=\"option.displayNameClass\">{{option.displayName || option.value}}</div>\r\n                        <ng-container *ngIf=\"option.count || getApplied(category.name, option.value)\">&nbsp;({{option.count}})</ng-container>\r\n                    </label>\r\n                </div>\r\n            </div>\r\n\r\n            <!-- show all -->\r\n            <div ngbDropdownItem *ngIf=\"!allOptionsShown(category) && !isCategoryCollapsed(category.name)\" [ngClass]=\"optionRowClass\">\r\n                <a href=\"#\" (click)=\"showAllOptions(category.name, $event)\">Show all</a>\r\n            </div>\r\n        </ng-container>\r\n    </div>\r\n    <div ngbDropdownItem class=\"border-top\" [ngClass]=\"optionRowClass\">\r\n        <a href=\"#\" (click)=\"clearFilters($event)\">Clear filter</a>\r\n    </div>\r\n</div>\r\n", styles: [":host{display:flex;flex-direction:column;flex-shrink:1;min-height:0;position:relative}.dropdown-item{white-space:normal}.dropdown-menu.submenu{top:-1px;left:calc(100% + 1px + .125rem);z-index:2000;max-height:100vh}.dropdown-menu.submenu .dropdown-item{white-space:nowrap}.flex-shrink-1{min-height:0}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$1.NgbDropdownItem, selector: "[ngbDropdownItem]", inputs: ["tabindex", "disabled"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterSortPanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-filter-sort-panel', template: "<div class=\"d-flex flex-column flex-shrink-1\" *ngIf=\"filterOptions$ | async as filterOptions\">\r\n    <ng-container *ngIf=\"optionalSectionBeforeFilterSortOptions\">\r\n        <ng-container [ngTemplateOutlet]=\"optionalSectionBeforeFilterSortOptions\"></ng-container>\r\n    </ng-container>\r\n    <ng-container *ngIf=\"hasSortOptions()\">\r\n        <span\r\n            class=\"dropdown-header sk-input-icon sk-header-flush\"\r\n            [ngClass]=\"headerRowClass\"\r\n            (click)=\"toggleSortOpen()\">\r\n            Sort\r\n            <i class=\"material-icons md-24\">keyboard_arrow_right</i>\r\n        </span>\r\n        <div class=\"dropdown-menu submenu mt-0\" [class.show]=\"isSortOpen\">\r\n            <span class=\"dropdown-header bg-white border-0\">Sort by</span>\r\n            <a class=\"dropdown-item\" (click)=\"setSort(null)\">\r\n                <i\r\n                    class=\"material-icons md-22\"\r\n                    [class.icon-transparent]=\"currentSort  !== null\">\r\n                    done\r\n                </i>\r\n                Relevance\r\n            </a>\r\n            <a *ngFor=\"let option of sortOptions\" class=\"dropdown-item\" (click)=\"setSort(option.name)\">\r\n                <i\r\n                    class=\"material-icons md-22\"\r\n                    [class.icon-transparent]=\"currentSort !== option.name\">\r\n                    done\r\n                </i>\r\n                {{option.displayName || option.name}}\r\n            </a>\r\n            <div class=\"dropdown-divider\"></div>\r\n            <span class=\"dropdown-header bg-white border-0\">Sort order</span>\r\n            <a class=\"dropdown-item\" (click)=\"toggleSortDirection()\">\r\n                <i\r\n                    class=\"material-icons md-22\"\r\n                    [class.icon-transparent]=\"!currentSortAscending\">\r\n                    done\r\n                </i>\r\n                Ascending\r\n            </a>\r\n            <a class=\"dropdown-item\" (click)=\"toggleSortDirection()\">\r\n                <i\r\n                    class=\"material-icons md-22\"\r\n                    [class.icon-transparent]=\"currentSortAscending\">\r\n                    done\r\n                </i>\r\n                Descending\r\n            </a>\r\n        </div>\r\n    </ng-container>\r\n    <div class=\"overflow-auto flex-shrink-1\">\r\n        <ng-container *ngFor=\"let category of getAllOptions(filterOptions); index as i\">\r\n\r\n            <!-- category -->\r\n            <span\r\n                *ngIf=\"(category.displayName || category.name) && !category.headerHidden; else hiddenHeader\"\r\n                class=\"dropdown-header sk-input-icon\"\r\n                [ngClass]=\"headerRowClass\"\r\n                [class.sk-header-flush]=\"i === 0 && !hasSortOptions()\"\r\n                (click)=\"toggleCategoryCollapsed(category.name)\">\r\n                {{category.displayName || category.name}} ({{getOptionCount(category)}})\r\n                <i class=\"material-icons md-24\" *ngIf=\"isCategoryCollapsed(category.name); else upArrow\">keyboard_arrow_down</i>\r\n                <ng-template #upArrow>\r\n                    <i class=\"material-icons md-24\">keyboard_arrow_up</i>\r\n                </ng-template>\r\n            </span>\r\n\r\n            <ng-template #hiddenHeader>\r\n                <div *ngIf=\"i !== 0\" class=\"border-top\"></div>\r\n            </ng-template>\r\n\r\n            <!-- option -->\r\n            <div *ngFor=\"let option of getOptions(category)\" ngbDropdownItem [ngClass]=\"optionRowClass\">\r\n                <div class=\"form-check\" (click)=\"toggleApplied(category.name, option.value)\">\r\n                    <input type=\"checkbox\" class=\"form-check-input form-check-input-sm\" [checked]=\"getApplied(category.name, option.value)\" />\r\n                    <label class=\"form-check-label align-items-center d-flex\">\r\n                        <i class=\"material-icons md-16 mx-1\" [class]=\"option.iconClass\" *ngIf=\"option.iconName\">{{option.iconName}}</i>\r\n                        <div [class]=\"option.displayNameClass\">{{option.displayName || option.value}}</div>\r\n                        <ng-container *ngIf=\"option.count || getApplied(category.name, option.value)\">&nbsp;({{option.count}})</ng-container>\r\n                    </label>\r\n                </div>\r\n            </div>\r\n\r\n            <!-- show all -->\r\n            <div ngbDropdownItem *ngIf=\"!allOptionsShown(category) && !isCategoryCollapsed(category.name)\" [ngClass]=\"optionRowClass\">\r\n                <a href=\"#\" (click)=\"showAllOptions(category.name, $event)\">Show all</a>\r\n            </div>\r\n        </ng-container>\r\n    </div>\r\n    <div ngbDropdownItem class=\"border-top\" [ngClass]=\"optionRowClass\">\r\n        <a href=\"#\" (click)=\"clearFilters($event)\">Clear filter</a>\r\n    </div>\r\n</div>\r\n", styles: [":host{display:flex;flex-direction:column;flex-shrink:1;min-height:0;position:relative}.dropdown-item{white-space:normal}.dropdown-menu.submenu{top:-1px;left:calc(100% + 1px + .125rem);z-index:2000;max-height:100vh}.dropdown-menu.submenu .dropdown-item{white-space:nowrap}.flex-shrink-1{min-height:0}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { headerRowClass: [{
                type: Input
            }], optionRowClass: [{
                type: Input
            }], clearInitialOptions$: [{
                type: Input
            }], optionalSectionBeforeFilterSortOptions: [{
                type: Input
            }], filterOptions$: [{
                type: Input
            }], filterChange: [{
                type: Output
            }], sortOptions: [{
                type: Input
            }], sortChange: [{
                type: Output
            }], onDocumentClick: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }] } });

class FilterSortButtonComponent {
    constructor() {
        this.appliedFilterCount = 0;
        this.BadgeType = BadgeType;
        this.optionalSectionBeforeFilterSortOptions = null;
        this.filterChange = new EventEmitter();
        this.sortChange = new EventEmitter();
        this.tooltipPlacement = ['right'];
        this.dropdownPlacement = 'bottom-start bottom-end top-start top-end';
    }
    get tooltipText() {
        return this.hasSortOptions() ?
            'Filter & Sort' :
            'Filter';
    }
    hasSortOptions() {
        return this.sortOptions && this.sortOptions.length;
    }
    onFilterChange($event) {
        this.appliedFilterCount = $event.reduce((count, category) => count += category.values.length, 0);
        this.filterChange.emit($event);
    }
    onSortChange($event) {
        this.sortChange.emit($event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterSortButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: FilterSortButtonComponent, selector: "sk-filter-sort-button", inputs: { filterOptions$: "filterOptions$", clearInitialOptions$: "clearInitialOptions$", optionalSectionBeforeFilterSortOptions: "optionalSectionBeforeFilterSortOptions", sortOptions: "sortOptions", filterMenuClass: "filterMenuClass", tooltipPlacement: "tooltipPlacement", dropdownPlacement: "dropdownPlacement" }, outputs: { filterChange: "filterChange", sortChange: "sortChange" }, ngImport: i0, template: "<div\r\n    ngbDropdown\r\n    autoClose=\"outside\"\r\n    [placement]=\"dropdownPlacement\"\r\n    class=\"position-relative\">\r\n    <button\r\n        ngbDropdownToggle\r\n        id=\"dropdownToggle\"\r\n        class=\"btn sk-btn-icon sk-btn-icon-more btn-outline-secondary ms-1\"\r\n        [placement]=\"tooltipPlacement\"\r\n        [ngbTooltip]=\"tooltipText\"\r\n        [openDelay]=\"500\"\r\n        [closeDelay]=\"100\">\r\n        <i class=\"material-icons md-22\">filter_list</i>\r\n        <sk-badge\r\n            skBadgePill\r\n            *ngIf=\"appliedFilterCount\"\r\n            [type]=\"BadgeType.Default\">{{appliedFilterCount}}</sk-badge>\r\n    </button>\r\n\r\n    <div\r\n        ngbDropdownMenu\r\n        aria-labelledby=\"dropdownToggle\"\r\n        class=\"filter-menu ms-1 flex-column\"\r\n        [ngClass]=\"filterMenuClass\">\r\n\r\n        <sk-filter-sort-panel\r\n            [filterOptions$]=\"filterOptions$\"\r\n            [clearInitialOptions$]=\"clearInitialOptions$\"\r\n            (filterChange)=\"onFilterChange($event)\"\r\n            [sortOptions]=\"sortOptions\"\r\n            (sortChange)=\"onSortChange($event)\"\r\n            [optionalSectionBeforeFilterSortOptions]=\"optionalSectionBeforeFilterSortOptions\">\r\n        </sk-filter-sort-panel>\r\n\r\n    </div>\r\n</div>\r\n", styles: [".filter-menu{z-index:2000;min-width:300px;max-height:calc(100vh - 300px)!important}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: BadgeComponent, selector: "sk-badge", inputs: ["type", "skBadgePill", "size", "class"] }, { kind: "directive", type: i1$1.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "popperOptions", "triggers", "positionTarget", "container", "disableTooltip", "tooltipClass", "tooltipContext", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "directive", type: i1$1.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i1$1.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i1$1.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "component", type: FilterSortPanelComponent, selector: "sk-filter-sort-panel", inputs: ["headerRowClass", "optionRowClass", "clearInitialOptions$", "optionalSectionBeforeFilterSortOptions", "filterOptions$", "sortOptions"], outputs: ["filterChange", "sortChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterSortButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-filter-sort-button', template: "<div\r\n    ngbDropdown\r\n    autoClose=\"outside\"\r\n    [placement]=\"dropdownPlacement\"\r\n    class=\"position-relative\">\r\n    <button\r\n        ngbDropdownToggle\r\n        id=\"dropdownToggle\"\r\n        class=\"btn sk-btn-icon sk-btn-icon-more btn-outline-secondary ms-1\"\r\n        [placement]=\"tooltipPlacement\"\r\n        [ngbTooltip]=\"tooltipText\"\r\n        [openDelay]=\"500\"\r\n        [closeDelay]=\"100\">\r\n        <i class=\"material-icons md-22\">filter_list</i>\r\n        <sk-badge\r\n            skBadgePill\r\n            *ngIf=\"appliedFilterCount\"\r\n            [type]=\"BadgeType.Default\">{{appliedFilterCount}}</sk-badge>\r\n    </button>\r\n\r\n    <div\r\n        ngbDropdownMenu\r\n        aria-labelledby=\"dropdownToggle\"\r\n        class=\"filter-menu ms-1 flex-column\"\r\n        [ngClass]=\"filterMenuClass\">\r\n\r\n        <sk-filter-sort-panel\r\n            [filterOptions$]=\"filterOptions$\"\r\n            [clearInitialOptions$]=\"clearInitialOptions$\"\r\n            (filterChange)=\"onFilterChange($event)\"\r\n            [sortOptions]=\"sortOptions\"\r\n            (sortChange)=\"onSortChange($event)\"\r\n            [optionalSectionBeforeFilterSortOptions]=\"optionalSectionBeforeFilterSortOptions\">\r\n        </sk-filter-sort-panel>\r\n\r\n    </div>\r\n</div>\r\n", styles: [".filter-menu{z-index:2000;min-width:300px;max-height:calc(100vh - 300px)!important}\n"] }]
        }], propDecorators: { filterOptions$: [{
                type: Input
            }], clearInitialOptions$: [{
                type: Input
            }], optionalSectionBeforeFilterSortOptions: [{
                type: Input
            }], filterChange: [{
                type: Output
            }], sortOptions: [{
                type: Input
            }], sortChange: [{
                type: Output
            }], filterMenuClass: [{
                type: Input
            }], tooltipPlacement: [{
                type: Input
            }], dropdownPlacement: [{
                type: Input
            }] } });

class FilterSortModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterSortModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: FilterSortModule, declarations: [FilterSortButtonComponent,
            FilterSortPanelComponent], imports: [CommonModule,
            BadgesModule,
            NgbTooltipModule,
            NgbDropdownModule], exports: [FilterSortButtonComponent,
            FilterSortPanelComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterSortModule, imports: [CommonModule,
            BadgesModule,
            NgbTooltipModule,
            NgbDropdownModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterSortModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        FilterSortButtonComponent,
                        FilterSortPanelComponent
                    ],
                    imports: [
                        CommonModule,
                        BadgesModule,
                        NgbTooltipModule,
                        NgbDropdownModule
                    ],
                    exports: [
                        FilterSortButtonComponent,
                        FilterSortPanelComponent
                    ]
                }]
        }] });

var FeedbackType;
(function (FeedbackType) {
    FeedbackType["Danger"] = "danger";
    FeedbackType["Info"] = "info";
    FeedbackType["Tip"] = "tip";
    FeedbackType["Help"] = "help";
    FeedbackType["Success"] = "success";
    FeedbackType["Warning"] = "warning";
})(FeedbackType || (FeedbackType = {}));

var FeedbackIcon;
(function (FeedbackIcon) {
    FeedbackIcon["Danger"] = "warning";
    FeedbackIcon["Info"] = "info";
    FeedbackIcon["Tip"] = "lightbulb_outline";
    FeedbackIcon["Help"] = "help";
    FeedbackIcon["Success"] = "done";
    FeedbackIcon["Warning"] = "error";
})(FeedbackIcon || (FeedbackIcon = {}));

class BaseFeedback {
    constructor() {
        /**
         * The class provided in the HTML
         * @ignore
         */
        this.hostClass = '';
    }
    /**
     *
     * @ignore
     */
    mapTypeToClass(type) {
        switch (type) {
            case FeedbackType.Danger:
            case FeedbackType.Info:
            case FeedbackType.Success:
            case FeedbackType.Warning:
                return `alert-${type}`;
            case FeedbackType.Help:
            case FeedbackType.Tip:
                return 'alert-note';
            default:
                return `alert-info`;
        }
    }
    /**
     *
     * @ignore
     */
    getIcon(type) {
        switch (type) {
            case FeedbackType.Danger:
                return FeedbackIcon.Danger;
            case FeedbackType.Info:
                return FeedbackIcon.Info;
            case FeedbackType.Help:
                return FeedbackIcon.Help;
            case FeedbackType.Tip:
                return FeedbackIcon.Tip;
            case FeedbackType.Success:
                return FeedbackIcon.Success;
            case FeedbackType.Warning:
                return FeedbackIcon.Warning;
            default:
                return FeedbackIcon.Info;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BaseFeedback, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: BaseFeedback, inputs: { hostClass: ["class", "hostClass"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BaseFeedback, decorators: [{
            type: Directive
        }], propDecorators: { hostClass: [{
                type: Input,
                args: ['class']
            }] } });

class FeedbackComponent extends BaseFeedback {
    /**
     * @ignore
     */
    constructor(host) {
        super();
        this.host = host;
        /**
         * Property to define the type of feedback, e.g. danger, info, tip, help, success, warning.
         */
        this.type = FeedbackType.Info;
        /**
         * Property to define if the component should be closeable.
         */
        this.dismissible = false;
        /**
         * Property to define the custom icon to be displayed. If nothing provided it will use the icon from the type.
        */
        this.icon = null;
        /**
         * Property to define the class list for custom icon. This is used when an icon is provided.
         */
        this.iconClass = '';
        /**
         * Event emitted when close icon is clicked
         */
        this.closed = new EventEmitter();
        /**
         * Sets the id of feedback block, if nothing provided it will generate a random string.
         * @ignore
         */
        this.id = (Math.random() + 1).toString(36).substring(7);
    }
    /**
    * @ignore
    */
    ngOnInit() {
        const tempClasses = [
            this.mapTypeToClass(this.type),
            'alert',
            'sk-alert-icon-24',
            'd-flex',
            'justify-content-between',
            'align-items-center',
            this.hostClass,
        ];
        if (this.dismissible) {
            tempClasses.push('alert-dismissible');
        }
        this.classes = tempClasses.join(' ');
    }
    /**
     * @ignore
     */
    clickCloseAction() {
        this.host.nativeElement.remove();
        this.closed.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FeedbackComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: FeedbackComponent, selector: "sk-feedback", inputs: { type: "type", message: "message", subject: "subject", dismissible: "dismissible", icon: "icon", iconClass: "iconClass", id: "id" }, outputs: { closed: "closed" }, host: { properties: { "class": "this.classes" } }, usesInheritance: true, ngImport: i0, template: "<div class=\"d-flex align-items-center\">\r\n    <ng-container *ngIf=\"!icon; else customIcon\">\r\n        <i class=\"material-icons md-22\">{{ getIcon(type) }}</i>\r\n    </ng-container>\r\n    <div class=\"ml-100\">\r\n        <span class=\"sk-alert-text\">\r\n            <strong *ngIf=\"!!subject\">{{ subject }}</strong>\r\n            {{ !!subject ? '\u2013' : ''}}\r\n            <ng-container *ngIf=\"!message; else messageValue\">\r\n                <ng-content></ng-content>\r\n            </ng-container>\r\n        </span>\r\n    </div>\r\n</div>\r\n<div class=\"flex-shrink-0\">\r\n    <ng-content select=\"button[sk-button], button[sk-spinner-button]\"></ng-content>\r\n    <button [id]=\"id + 'CloseBtn'\" *ngIf=\"dismissible\" type=\"button\" class=\"btn-close\" (click)=\"clickCloseAction()\">\r\n    </button>\r\n</div>\r\n\r\n<ng-template #messageValue >\r\n    <span [innerHtml]=\"message\"></span>\r\n</ng-template>\r\n\r\n<ng-template #customIcon>\r\n    <i class=\"material-icons md-22\" [class]=\"iconClass\">{{ icon }}</i>\r\n</ng-template>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FeedbackComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-feedback', template: "<div class=\"d-flex align-items-center\">\r\n    <ng-container *ngIf=\"!icon; else customIcon\">\r\n        <i class=\"material-icons md-22\">{{ getIcon(type) }}</i>\r\n    </ng-container>\r\n    <div class=\"ml-100\">\r\n        <span class=\"sk-alert-text\">\r\n            <strong *ngIf=\"!!subject\">{{ subject }}</strong>\r\n            {{ !!subject ? '\u2013' : ''}}\r\n            <ng-container *ngIf=\"!message; else messageValue\">\r\n                <ng-content></ng-content>\r\n            </ng-container>\r\n        </span>\r\n    </div>\r\n</div>\r\n<div class=\"flex-shrink-0\">\r\n    <ng-content select=\"button[sk-button], button[sk-spinner-button]\"></ng-content>\r\n    <button [id]=\"id + 'CloseBtn'\" *ngIf=\"dismissible\" type=\"button\" class=\"btn-close\" (click)=\"clickCloseAction()\">\r\n    </button>\r\n</div>\r\n\r\n<ng-template #messageValue >\r\n    <span [innerHtml]=\"message\"></span>\r\n</ng-template>\r\n\r\n<ng-template #customIcon>\r\n    <i class=\"material-icons md-22\" [class]=\"iconClass\">{{ icon }}</i>\r\n</ng-template>\r\n" }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { type: [{
                type: Input
            }], message: [{
                type: Input
            }], subject: [{
                type: Input
            }], dismissible: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconClass: [{
                type: Input
            }], closed: [{
                type: Output
            }], classes: [{
                type: HostBinding,
                args: ['class']
            }], id: [{
                type: Input
            }] } });

class FeedbackModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FeedbackModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: FeedbackModule, declarations: [FeedbackComponent], imports: [CommonModule,
            ButtonsModule], exports: [FeedbackComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FeedbackModule, imports: [CommonModule,
            ButtonsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FeedbackModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        FeedbackComponent
                    ],
                    imports: [
                        CommonModule,
                        ButtonsModule
                    ],
                    exports: [
                        FeedbackComponent
                    ]
                }]
        }] });

// https://google.github.io/material-design-icons/#sizing

// https://material.io/design/iconography/system-icons.html#icon-themes
var IconTheme;
(function (IconTheme) {
    IconTheme["Filled"] = "material-icons";
    IconTheme["Outlined"] = "material-icons-outlined";
    IconTheme["Rounded"] = "material-icons-round";
    IconTheme["TwoTone"] = "material-icons-two-tone";
    IconTheme["Sharp"] = "material-icons-sharp";
})(IconTheme || (IconTheme = {}));

class IconComponent {
    constructor() {
        this._theme = IconTheme.Filled;
        this._iconSize = '24';
    }
    set theme(theme) {
        this._theme = theme || IconTheme.Filled;
        this.setIconClasses();
    }
    set iconSize(iconSize) {
        this._iconSize = iconSize || '24';
        this.setIconClasses();
    }
    ngOnInit() {
        this.setIconClasses();
    }
    setIconClasses() {
        this.iconClasses = `${this._theme} md-${this._iconSize}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: IconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: IconComponent, selector: "i.sk-icon", inputs: { icon: "icon", theme: "theme", iconSize: "iconSize" }, host: { properties: { "class": "this.iconClasses" } }, ngImport: i0, template: '{{ icon }}', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: IconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'i.sk-icon',
                    template: '{{ icon }}'
                }]
        }], propDecorators: { iconClasses: [{
                type: HostBinding,
                args: ['class']
            }], icon: [{
                type: Input
            }], theme: [{
                type: Input
            }], iconSize: [{
                type: Input
            }] } });

class IconsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: IconsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: IconsModule, declarations: [IconComponent], exports: [IconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: IconsModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: IconsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        IconComponent
                    ],
                    exports: [
                        IconComponent
                    ]
                }]
        }] });

var CreditCardType;
(function (CreditCardType) {
    CreditCardType["AMERICANEXPRESS"] = "AmericanExpress";
    CreditCardType["DISCOVER"] = "Discover";
    CreditCardType["MASTERCARD"] = "Mastercard";
    CreditCardType["VISA"] = "Visa";
})(CreditCardType || (CreditCardType = {}));

class CardValidationService {
    constructor() {
        this._cardType = null;
        this.cardTypeSubject = new BehaviorSubject(this._cardType);
        this.cardType$ = this.cardTypeSubject.asObservable();
        this.visaPattern = /^4[0-9]{6,}$/;
        this.mastPattern = /^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/;
        this.amexPattern = /^3[47][0-9]{13}$/;
        this.discPattern = /^6(?:011|5[0-9]{2})[0-9]{3,}$/;
        this.expirationPattern = /^(0[1-9]|1[0-2])\/?([0-9]{2})$/;
        this.securityCodePattern = /^[0-9]{3}$/;
        this.securityCodeAmexPattern = /^[0-9]{4}$/;
        this.fullNamePattern = /[<>]/;
    }
    set cardType(value) {
        this._cardType = value;
        this.cardTypeSubject.next(this._cardType);
    }
    get cardType() {
        return this._cardType;
    }
    validCreditCard() {
        return (control) => {
            if (!control) {
                this.cardType = null;
                return null;
            }
            if (!control.value) {
                this.cardType = null;
                return null;
            }
            if (this.visaPattern.test(control.value)) {
                this.cardType = CreditCardType.VISA;
            }
            else if (this.mastPattern.test(control.value)) {
                this.cardType = CreditCardType.MASTERCARD;
            }
            else if (this.amexPattern.test(control.value)) {
                this.cardType = CreditCardType.AMERICANEXPRESS;
            }
            else if (this.discPattern.test(control.value)) {
                this.cardType = CreditCardType.DISCOVER;
            }
            else {
                this.cardType = null;
            }
            return null;
        };
    }
    validExpirationDate() {
        return (control) => {
            if (control && control.value && !this.expirationPattern.test(control.value)) {
                return { 'pattern': true };
            }
            else {
                this.cardType = null;
                return null;
            }
        };
    }
    validSecurityCode() {
        return (control) => {
            if (control && control.value) {
                if (this.cardType === CreditCardType.AMERICANEXPRESS) {
                    if (!this.securityCodeAmexPattern.test(control.value)) {
                        return { 'length': true };
                    }
                }
                else if (!this.securityCodePattern.test(control.value)) {
                    return { 'length': true };
                }
            }
            return null;
        };
    }
    validFullName() {
        return (control) => {
            if (!control) {
                return null;
            }
            if (!control.value) {
                return null;
            }
            if (this.fullNamePattern.test(control.value)) {
                return { 'pattern': true };
            }
            return null;
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardValidationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardValidationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CardValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class PaymentResourcesService {
    handleError(errorResponse) {
        if (errorResponse.error instanceof ErrorEvent) {
            console.error('Client Side Error: ', errorResponse.error.message);
        }
        else {
            console.error('Server Side Error: ', errorResponse);
        }
        console.log('There is a problem with the service. We are notified & working on it. Please try again later.');
        return throwError(errorResponse);
    }
    constructor(http, abstractUserProvider) {
        this.http = http;
        this.abstractUserProvider = abstractUserProvider;
    }
    getPaymentProfiles(accountingHostname) {
        return this.http.get(`${accountingHostname}/api/v1/payment/profiles/${this.abstractUserProvider.getCurrentUser().partnerId}`)
            .pipe(catchError(this.handleError), map(res => {
            res.forEach(item => {
                item.cardNumber = item.cardNumber.replace(/\D/g, '');
            });
            return res;
        }));
    }
    addCreditCard(accountingHostname, payload) {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.post(`${accountingHostname}/api/v1/payment/profile/${this.abstractUserProvider.getCurrentUser().partnerId}/secureAddCard`, payload, { headers: headers })
            .pipe(catchError(this.handleError));
    }
    applyPayment(accountingHostname, payload) {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.post(`${accountingHostname}/api/v1/payment/apply`, payload, { headers: headers })
            .pipe(catchError(this.handleError));
    }
    getPaymentClientInfo(accountingHostname) {
        return this.http.get(`${accountingHostname}/api/v1/payment/clientinfo`)
            .pipe(catchError(this.handleError));
    }
    setDefaultCreditCard(accountingHostname, partnerId, paymentProfileId) {
        return this.http.put(`${accountingHostname}/api/v1/payment/profile/${partnerId}/${paymentProfileId}`, null)
            .pipe(catchError(this.handleError));
    }
    getActiveOrdersForPartnerByPaymentProfile(accountingHostname, partnerId, paymentProfileId) {
        return this.http.get(`${accountingHostname}/api/v1/payment/active-orders/${partnerId}/${paymentProfileId}`)
            .pipe(catchError(this.handleError));
    }
    removeCreditCard(accountingHostname, partnerId, paymentProfileId) {
        return this.http.delete(`${accountingHostname}/api/v1/payment/profile/${partnerId}/${paymentProfileId}`)
            .pipe(catchError(this.handleError));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PaymentResourcesService, deps: [{ token: i1$2.HttpClient }, { token: i2$1.AbstractUserProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PaymentResourcesService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PaymentResourcesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.HttpClient }, { type: i2$1.AbstractUserProvider }] });

class CreditCardComponent {
    constructor(activeModal, formBuilder, paymentResourcesService, cardValidationService, toastrService
    //private authorizenetService: AuthorizeNetService
    ) {
        this.activeModal = activeModal;
        this.formBuilder = formBuilder;
        this.paymentResourcesService = paymentResourcesService;
        this.cardValidationService = cardValidationService;
        this.toastrService = toastrService;
        this.onDestroy$ = new Subject();
        this.CreditCardType = CreditCardType;
        this.errors = [];
        this.amexMask = "0000 000000 00000";
        this.ccMask = "0000 0000 0000 0000";
    }
    ngOnInit() {
        this.cardValidationService.cardType$.subscribe(res => {
            this.creditCardType = res;
        });
        this.form = this.formBuilder.group({
            nameoncard: [null, [Validators.required, this.cardValidationService.validFullName()]],
            cardnumber: [null, [Validators.required, this.cardValidationService.validCreditCard(), Validators.pattern("^[0-9]*$")]],
            expirationdate: [null, [Validators.required, this.cardValidationService.validExpirationDate()]],
            securitycode: [null, [Validators.required, Validators.pattern("^[0-9]*$"), this.cardValidationService.validSecurityCode()]],
            zipcode: [null, [Validators.required]],
            isDefault: [this.isDefaultCheckboxDisabled, []]
        });
        this.form.disable();
        this.paymentResourcesService.getPaymentClientInfo(this.config.accountingHostname).pipe(takeUntil(this.onDestroy$)).subscribe(res => {
            this.authorizeNetRequest = {
                authData: {
                    clientKey: res.publicClientKey,
                    apiLoginID: res.loginId
                },
                cardData: {
                    cardNumber: null,
                    cardCode: null,
                    month: null,
                    year: null,
                    zip: null,
                    fullName: null
                }
            };
            this.enableForm();
        });
    }
    enableForm() {
        Object.entries(this.form.controls)
            .filter(([key, value]) => !this.isDefaultCheckboxDisabled || key != "isDefault")
            .forEach(([key, value]) => value.enable());
    }
    ngOnDestroy() {
        this.onDestroy$.next();
    }
    handleDispatchData(response) {
        if (response.messages.resultCode === "Error") {
            response.messages.message.forEach(item => {
                this.errors.push(item.text);
            });
            this.enableForm();
        }
        else {
            this.creditCard = {
                opaqueDataValue: response.opaqueData.dataValue,
                isDefault: this.form.get('isDefault').value
            };
            this.paymentResourcesService.addCreditCard(this.config.accountingHostname, this.creditCard).pipe(takeUntil(this.onDestroy$)).subscribe({
                next: res => {
                    this.toastrService.success('Credit card successfully added.');
                    this.activeModal.close(res);
                },
                error: res => {
                    if (res.error) {
                        this.errors.push(res.error);
                    }
                    this.enableForm();
                }
            });
        }
    }
    addCreditCard() {
        this.errors.length = 0;
        if (this.form.invalid) {
            this.errors.push("Please make sure all fields are filled in correctly.");
            return;
        }
        //first lets get a Nonce.
        let cd = this.authorizeNetRequest.cardData;
        cd.cardNumber = this.form.get('cardnumber').value;
        cd.cardCode = this.form.get('securitycode').value;
        cd.month = this.form.get('expirationdate').value.substring(0, 2);
        cd.year = this.form.get('expirationdate').value.substring(2, 4);
        cd.zip = this.form.get('zipcode').value;
        cd.fullName = this.form.get('nameoncard').value;
        this.form.disable();
        Accept.dispatchData(this.authorizeNetRequest, response => {
            this.handleDispatchData(response);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CreditCardComponent, deps: [{ token: i1$1.NgbActiveModal }, { token: i2$2.UntypedFormBuilder }, { token: PaymentResourcesService }, { token: CardValidationService }, { token: i5.ToastrService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: CreditCardComponent, selector: "[sk-creditcard]", inputs: { config: "config", isDefaultCheckboxDisabled: "isDefaultCheckboxDisabled" }, host: { classAttribute: "modal-content" }, ngImport: i0, template: "<div class=\"modal-header bg-white pb-0\">\r\n    <div class=\"d-flex flex-column\">\r\n        <div class=\"d-flex align-items-center \">\r\n            <h3 class=\"modal-title font-weight-normal\" translate>sk_credit_card.credit_card_modal.add_a_credit_card</h3>\r\n        </div>\r\n    </div>\r\n\r\n    <button type=\"button\" class=\"btn-close\" (click)=\"activeModal.dismiss()\" id=\"closeBtn\">\r\n    </button>\r\n</div>\r\n<div class=\"modal-body\">\r\n    <div class=\"container text-center\">\r\n        <img src=\"/assets/images/flat-rounded/Visa.svg\" />\r\n        <img src=\"/assets/images/flat-rounded/MasterCard.svg\" />\r\n        <img src=\"/assets/images/flat-rounded/AmericanExpress.svg\" />\r\n        <img src=\"/assets/images/flat-rounded/Discover.svg\" />\r\n    </div>\r\n\r\n    <form [formGroup]=\"form\" novalidate class=\"mt-100\">\r\n        <fieldset class=\"mb-100\"\r\n            [class.is-invalid]=\"form.get('nameoncard').invalid && !form.get('nameoncard').pristine\">\r\n            <label class=\"form-label font-medium\" for=\"nameoncard\" translate>sk_credit_card.credit_card_modal.name_on_card</label>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"nameoncard\" id=\"nameoncard\" />\r\n            <small *ngIf=\"form.get('nameoncard').hasError('pattern')\" class=\"invalid-feedback\"\r\n                translate>sk_credit_card.credit_card_modal.format_error</small>\r\n        </fieldset>\r\n\r\n        <fieldset class=\"mb-100\"\r\n            [class.is-invalid]=\"form.get('cardnumber').invalid && !form.get('cardnumber').pristine\">\r\n            <label class=\"form-label font-medium\" for=\"cardnumber\" translate>sk_credit_card.credit_card_modal.card_number</label>\r\n            <input type=\"text\" class=\"form-control ccinput\" [class.visa]=\"creditCardType === CreditCardType.VISA\"\r\n                [class.mc]=\"creditCardType === CreditCardType.MASTERCARD\"\r\n                [class.discover]=\"creditCardType === CreditCardType.DISCOVER\"\r\n                [class.amex]=\"creditCardType === CreditCardType.AMERICANEXPRESS\" formControlName=\"cardnumber\"\r\n                id=\"cardnumber\" (keydown.space)=\"$event.preventDefault()\"\r\n                mask=\"{{creditCardType === CreditCardType.AMERICANEXPRESS ? amexMask : ccMask}}\" [validation]=\"false\" />\r\n            <small *ngIf=\"form.get('cardnumber').hasError('pattern')\" class=\"invalid-feedback\"\r\n                translate>sk_credit_card.credit_card_modal.number_error</small>\r\n        </fieldset>\r\n\r\n        <fieldset class=\"mb-100\"\r\n            [class.is-invalid]=\"form.get('expirationdate').invalid && !form.get('expirationdate').pristine\">\r\n            <label class=\"form-label font-medium\" for=\"expirationdate\" translate>sk_credit_card.credit_card_modal.expiration_date</label>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"expirationdate\" id=\"expirationdate\"\r\n                mask=\"00/00\" />\r\n            <small class=\"sk-form-text text-muted\" translate>sk_credit_card.credit_card_modal.mm_yy</small>\r\n            <small *ngIf=\"form.get('expirationdate').hasError('pattern')\" class=\"invalid-feedback\"\r\n                translate>sk_credit_card.credit_card_modal.format_error</small>\r\n        </fieldset>\r\n\r\n        <fieldset class=\"mb-100\"\r\n            [class.is-invalid]=\"form.get('securitycode').invalid && !form.get('securitycode').pristine\">\r\n            <label class=\"form-label font-medium\" for=\"securitycode\" translate>sk_credit_card.credit_card_modal.security_code</label>\r\n            <button type=\"button\" class=\"btn sk-btn-icon\" container=\"body\" triggers=\"mouseenter:mouseleave\"\r\n                [ngbPopover]=\"SecurityContent\" tabindex=\"-1\">\r\n                <i class=\"material-icons md-20 icon-muted\">help_outline</i>\r\n            </button>\r\n            <ng-template #SecurityContent>\r\n                <ng-container [ngSwitch]=\"creditCardType\">\r\n                    <div *ngSwitchCase=\"CreditCardType.VISA || CreditCardType.MASTERCARD || CreditCardType.DISCOVER\"\r\n                        class=\"text-center\">\r\n                        <div [innerHTML]=\"'sk_credit_card.credit_card_modal.security_others_help' | translate\"></div>\r\n                        <div class=\"text-center\"><img src=\"/assets/images/securitycode-others.png\" width=\"150\" /></div>\r\n                    </div>\r\n                    <div *ngSwitchCase=\"CreditCardType.AMERICANEXPRESS\" class=\"text-center\">\r\n                        <div [innerHTML]=\"'sk_credit_card.credit_card_modal.security_amex_help' | translate\"></div>\r\n                        <div class=\"text-center\"><img src=\"/assets/images/securitycode-amex.png\" width=\"150\" /></div>\r\n                    </div>\r\n                    <div *ngSwitchDefault>\r\n                        <div [innerHTML]=\"'sk_credit_card.credit_card_modal.security_both_help' | translate\"></div>\r\n                        <div class=\"text-center\"><img src=\"/assets/images/securitycode.png\" width=\"220\" /></div>\r\n                    </div>\r\n                </ng-container>\r\n            </ng-template>\r\n            <input class=\"form-control\" type=\"text\" formControlName=\"securitycode\"\r\n                [maxlength]=\"(creditCardType === CreditCardType.AMERICANEXPRESS) ? 4: 3\" id=\"securitycode\"\r\n                (keydown.space)=\"$event.preventDefault()\" />\r\n            <small *ngIf=\"form.get('securitycode').hasError('pattern')\" class=\"invalid-feedback\"\r\n                translate>sk_credit_card.credit_card_modal.number_error</small>\r\n            <small *ngIf=\"form.get('securitycode').hasError('length')\" class=\"invalid-feedback\"\r\n                translate>sk_credit_card.credit_card_modal.length_error</small>\r\n        </fieldset>\r\n\r\n        <fieldset class=\"mb-100\" [class.is-invalid]=\"form.get('zipcode').invalid && !form.get('zipcode').pristine\">\r\n            <label class=\"form-label font-medium\" for=\"zipcode\" translate>sk_credit_card.credit_card_modal.zip_code</label>\r\n            <button type=\"button\" class=\"btn sk-btn-icon\" container=\"body\" triggers=\"mouseenter:mouseleave\"\r\n                ngbPopover=\"{{'sk_credit_card.credit_card_modal.zipcode_help' | translate}}\" tabindex=\"-1\">\r\n                <i class=\"material-icons md-20 icon-muted\">help_outline</i>\r\n            </button>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"zipcode\" id=\"zipcode\" />\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-check mt-200 ml-50\" [disabled]=\"isDefaultCheckboxDisabled\">\r\n            <input type=\"checkbox\" formControlName=\"isDefault\" id=\"isDefault\" class=\"form-check-input\">\r\n            <label for=\"isDefault\" class=\"form-check-label font-medium\" translate>sk_credit_card.credit_card_modal.set_as_default_credit_card</label>\r\n        </fieldset>\r\n\r\n    </form>\r\n\r\n    <!-- <button type=\"button\" class=\"btn btn-secondary mr-100\" (click)=\"authenticateTestRequest()\">authenticateTestRequest</button> -->\r\n    <!-- <button type=\"button\" class=\"btn btn-secondary mr-50\" (click)=\"getCustomerProfileIdsRequest()\">getCustomerProfileIdsRequest</button>\r\n    <button type=\"button\" class=\"btn btn-secondary mr-50\" (click)=\"getCustomerProfileRequest()\">getCustomerProfileRequest</button> -->\r\n\r\n    <div *ngIf=\"errors\" class=\"row mt-3\">\r\n        <div class=\"col\">\r\n            <div>\r\n                <small class=\"invalid-feedback\" *ngFor=\"let error of errors\" [innerText]=\"error\"></small>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</div>\r\n<div class=\"modal-footer justify-content-start\">\r\n    <button sk-spinner-button id=\"submitBtn\" (click)=\"addCreditCard()\" [isSpinning]=\"this.form.disabled\" [color]=\"'primary'\" \r\n        [disabled]=\"this.form.invalid\">{{ \"sk_credit_card.credit_card_modal.add_card\" | translate }}\r\n    </button>\r\n    <button id=\"cancelBtn\" type=\"button\" class=\"btn btn-outline-secondary\" (click)=\"activeModal.dismiss()\"\r\n        translate>sk_credit_card.credit_card_modal.cancel</button>\r\n</div>", styles: [".container img{margin-right:5px;width:40px}.ccinput{background-repeat:no-repeat;background-position:right;padding-right:50px;background-size:50px}.visa{background-image:url(/assets/images/flat-rounded/Visa.svg)}.mc{background-image:url(/assets/images/flat-rounded/Mastercard.svg)}.discover{background-image:url(/assets/images/flat-rounded/Discover.svg)}.amex{background-image:url(/assets/images/flat-rounded/AmericanExpress.svg)}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i1.NgSwitchDefault, selector: "[ngSwitchDefault]" }, { kind: "directive", type: i2$2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: i1$1.NgbPopover, selector: "[ngbPopover]", inputs: ["animation", "autoClose", "ngbPopover", "popoverTitle", "placement", "popperOptions", "triggers", "positionTarget", "container", "disablePopover", "popoverClass", "popoverContext", "openDelay", "closeDelay"], outputs: ["shown", "hidden"], exportAs: ["ngbPopover"] }, { kind: "component", type: SpinnerButtonComponent, selector: "button[sk-spinner-button]", inputs: ["disabled", "color", "active", "isSpinning"] }, { kind: "directive", type: i2$3.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }, { kind: "directive", type: i9.NgxMaskDirective, selector: "input[mask], textarea[mask]", inputs: ["mask", "specialCharacters", "patterns", "prefix", "suffix", "thousandSeparator", "decimalMarker", "dropSpecialCharacters", "hiddenInput", "showMaskTyped", "placeHolderCharacter", "shownMaskExpression", "showTemplate", "clearIfNotMatch", "validation", "separatorLimit", "allowNegativeNumbers", "leadZeroDateTime", "leadZero", "triggerOnMaskChange", "apm", "inputTransformFn", "outputTransformFn", "keepCharacterPositions"], outputs: ["maskFilled"], exportAs: ["mask", "ngxMask"] }, { kind: "pipe", type: i2$3.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CreditCardComponent, decorators: [{
            type: Component,
            args: [{ selector: '[sk-creditcard]', host: {
                        'class': 'modal-content'
                    }, template: "<div class=\"modal-header bg-white pb-0\">\r\n    <div class=\"d-flex flex-column\">\r\n        <div class=\"d-flex align-items-center \">\r\n            <h3 class=\"modal-title font-weight-normal\" translate>sk_credit_card.credit_card_modal.add_a_credit_card</h3>\r\n        </div>\r\n    </div>\r\n\r\n    <button type=\"button\" class=\"btn-close\" (click)=\"activeModal.dismiss()\" id=\"closeBtn\">\r\n    </button>\r\n</div>\r\n<div class=\"modal-body\">\r\n    <div class=\"container text-center\">\r\n        <img src=\"/assets/images/flat-rounded/Visa.svg\" />\r\n        <img src=\"/assets/images/flat-rounded/MasterCard.svg\" />\r\n        <img src=\"/assets/images/flat-rounded/AmericanExpress.svg\" />\r\n        <img src=\"/assets/images/flat-rounded/Discover.svg\" />\r\n    </div>\r\n\r\n    <form [formGroup]=\"form\" novalidate class=\"mt-100\">\r\n        <fieldset class=\"mb-100\"\r\n            [class.is-invalid]=\"form.get('nameoncard').invalid && !form.get('nameoncard').pristine\">\r\n            <label class=\"form-label font-medium\" for=\"nameoncard\" translate>sk_credit_card.credit_card_modal.name_on_card</label>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"nameoncard\" id=\"nameoncard\" />\r\n            <small *ngIf=\"form.get('nameoncard').hasError('pattern')\" class=\"invalid-feedback\"\r\n                translate>sk_credit_card.credit_card_modal.format_error</small>\r\n        </fieldset>\r\n\r\n        <fieldset class=\"mb-100\"\r\n            [class.is-invalid]=\"form.get('cardnumber').invalid && !form.get('cardnumber').pristine\">\r\n            <label class=\"form-label font-medium\" for=\"cardnumber\" translate>sk_credit_card.credit_card_modal.card_number</label>\r\n            <input type=\"text\" class=\"form-control ccinput\" [class.visa]=\"creditCardType === CreditCardType.VISA\"\r\n                [class.mc]=\"creditCardType === CreditCardType.MASTERCARD\"\r\n                [class.discover]=\"creditCardType === CreditCardType.DISCOVER\"\r\n                [class.amex]=\"creditCardType === CreditCardType.AMERICANEXPRESS\" formControlName=\"cardnumber\"\r\n                id=\"cardnumber\" (keydown.space)=\"$event.preventDefault()\"\r\n                mask=\"{{creditCardType === CreditCardType.AMERICANEXPRESS ? amexMask : ccMask}}\" [validation]=\"false\" />\r\n            <small *ngIf=\"form.get('cardnumber').hasError('pattern')\" class=\"invalid-feedback\"\r\n                translate>sk_credit_card.credit_card_modal.number_error</small>\r\n        </fieldset>\r\n\r\n        <fieldset class=\"mb-100\"\r\n            [class.is-invalid]=\"form.get('expirationdate').invalid && !form.get('expirationdate').pristine\">\r\n            <label class=\"form-label font-medium\" for=\"expirationdate\" translate>sk_credit_card.credit_card_modal.expiration_date</label>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"expirationdate\" id=\"expirationdate\"\r\n                mask=\"00/00\" />\r\n            <small class=\"sk-form-text text-muted\" translate>sk_credit_card.credit_card_modal.mm_yy</small>\r\n            <small *ngIf=\"form.get('expirationdate').hasError('pattern')\" class=\"invalid-feedback\"\r\n                translate>sk_credit_card.credit_card_modal.format_error</small>\r\n        </fieldset>\r\n\r\n        <fieldset class=\"mb-100\"\r\n            [class.is-invalid]=\"form.get('securitycode').invalid && !form.get('securitycode').pristine\">\r\n            <label class=\"form-label font-medium\" for=\"securitycode\" translate>sk_credit_card.credit_card_modal.security_code</label>\r\n            <button type=\"button\" class=\"btn sk-btn-icon\" container=\"body\" triggers=\"mouseenter:mouseleave\"\r\n                [ngbPopover]=\"SecurityContent\" tabindex=\"-1\">\r\n                <i class=\"material-icons md-20 icon-muted\">help_outline</i>\r\n            </button>\r\n            <ng-template #SecurityContent>\r\n                <ng-container [ngSwitch]=\"creditCardType\">\r\n                    <div *ngSwitchCase=\"CreditCardType.VISA || CreditCardType.MASTERCARD || CreditCardType.DISCOVER\"\r\n                        class=\"text-center\">\r\n                        <div [innerHTML]=\"'sk_credit_card.credit_card_modal.security_others_help' | translate\"></div>\r\n                        <div class=\"text-center\"><img src=\"/assets/images/securitycode-others.png\" width=\"150\" /></div>\r\n                    </div>\r\n                    <div *ngSwitchCase=\"CreditCardType.AMERICANEXPRESS\" class=\"text-center\">\r\n                        <div [innerHTML]=\"'sk_credit_card.credit_card_modal.security_amex_help' | translate\"></div>\r\n                        <div class=\"text-center\"><img src=\"/assets/images/securitycode-amex.png\" width=\"150\" /></div>\r\n                    </div>\r\n                    <div *ngSwitchDefault>\r\n                        <div [innerHTML]=\"'sk_credit_card.credit_card_modal.security_both_help' | translate\"></div>\r\n                        <div class=\"text-center\"><img src=\"/assets/images/securitycode.png\" width=\"220\" /></div>\r\n                    </div>\r\n                </ng-container>\r\n            </ng-template>\r\n            <input class=\"form-control\" type=\"text\" formControlName=\"securitycode\"\r\n                [maxlength]=\"(creditCardType === CreditCardType.AMERICANEXPRESS) ? 4: 3\" id=\"securitycode\"\r\n                (keydown.space)=\"$event.preventDefault()\" />\r\n            <small *ngIf=\"form.get('securitycode').hasError('pattern')\" class=\"invalid-feedback\"\r\n                translate>sk_credit_card.credit_card_modal.number_error</small>\r\n            <small *ngIf=\"form.get('securitycode').hasError('length')\" class=\"invalid-feedback\"\r\n                translate>sk_credit_card.credit_card_modal.length_error</small>\r\n        </fieldset>\r\n\r\n        <fieldset class=\"mb-100\" [class.is-invalid]=\"form.get('zipcode').invalid && !form.get('zipcode').pristine\">\r\n            <label class=\"form-label font-medium\" for=\"zipcode\" translate>sk_credit_card.credit_card_modal.zip_code</label>\r\n            <button type=\"button\" class=\"btn sk-btn-icon\" container=\"body\" triggers=\"mouseenter:mouseleave\"\r\n                ngbPopover=\"{{'sk_credit_card.credit_card_modal.zipcode_help' | translate}}\" tabindex=\"-1\">\r\n                <i class=\"material-icons md-20 icon-muted\">help_outline</i>\r\n            </button>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"zipcode\" id=\"zipcode\" />\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-check mt-200 ml-50\" [disabled]=\"isDefaultCheckboxDisabled\">\r\n            <input type=\"checkbox\" formControlName=\"isDefault\" id=\"isDefault\" class=\"form-check-input\">\r\n            <label for=\"isDefault\" class=\"form-check-label font-medium\" translate>sk_credit_card.credit_card_modal.set_as_default_credit_card</label>\r\n        </fieldset>\r\n\r\n    </form>\r\n\r\n    <!-- <button type=\"button\" class=\"btn btn-secondary mr-100\" (click)=\"authenticateTestRequest()\">authenticateTestRequest</button> -->\r\n    <!-- <button type=\"button\" class=\"btn btn-secondary mr-50\" (click)=\"getCustomerProfileIdsRequest()\">getCustomerProfileIdsRequest</button>\r\n    <button type=\"button\" class=\"btn btn-secondary mr-50\" (click)=\"getCustomerProfileRequest()\">getCustomerProfileRequest</button> -->\r\n\r\n    <div *ngIf=\"errors\" class=\"row mt-3\">\r\n        <div class=\"col\">\r\n            <div>\r\n                <small class=\"invalid-feedback\" *ngFor=\"let error of errors\" [innerText]=\"error\"></small>\r\n            </div>\r\n        </div>\r\n    </div>\r\n</div>\r\n<div class=\"modal-footer justify-content-start\">\r\n    <button sk-spinner-button id=\"submitBtn\" (click)=\"addCreditCard()\" [isSpinning]=\"this.form.disabled\" [color]=\"'primary'\" \r\n        [disabled]=\"this.form.invalid\">{{ \"sk_credit_card.credit_card_modal.add_card\" | translate }}\r\n    </button>\r\n    <button id=\"cancelBtn\" type=\"button\" class=\"btn btn-outline-secondary\" (click)=\"activeModal.dismiss()\"\r\n        translate>sk_credit_card.credit_card_modal.cancel</button>\r\n</div>", styles: [".container img{margin-right:5px;width:40px}.ccinput{background-repeat:no-repeat;background-position:right;padding-right:50px;background-size:50px}.visa{background-image:url(/assets/images/flat-rounded/Visa.svg)}.mc{background-image:url(/assets/images/flat-rounded/Mastercard.svg)}.discover{background-image:url(/assets/images/flat-rounded/Discover.svg)}.amex{background-image:url(/assets/images/flat-rounded/AmericanExpress.svg)}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.NgbActiveModal }, { type: i2$2.UntypedFormBuilder }, { type: PaymentResourcesService }, { type: CardValidationService }, { type: i5.ToastrService }], propDecorators: { config: [{
                type: Input
            }], isDefaultCheckboxDisabled: [{
                type: Input
            }] } });

class CustomerPaymentProfileService {
    setCustomerPaymentProfile(value) {
        this.customerPaymentProfileSubject.next(value);
    }
    disableCustomerPaymentProfiles(value) {
        this.disableCustomerPaymentProfilesSubject.next(value);
    }
    constructor() {
        this.customerPaymentProfileSubject = new BehaviorSubject(null);
        this.customerPaymentProfile$ = this.customerPaymentProfileSubject.asObservable();
        this.disableCustomerPaymentProfilesSubject = new BehaviorSubject(false);
        this.disableCustomerPaymentProfiles$ = this.disableCustomerPaymentProfilesSubject.asObservable();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CustomerPaymentProfileService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CustomerPaymentProfileService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CustomerPaymentProfileService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

var ReactiveFormStatus;
(function (ReactiveFormStatus) {
    ReactiveFormStatus["VALID"] = "VALID";
    ReactiveFormStatus["INVALID"] = "INVALID";
    ReactiveFormStatus["PENDING"] = "PENDING";
    ReactiveFormStatus["DISABLED"] = "DISABLED";
})(ReactiveFormStatus || (ReactiveFormStatus = {}));

const ACTIONOPTIONS = [
    {
        key: "sk_credit_card.customer_profiles.set_default_option",
        value: "Default",
        iconName: "credit_score"
    },
    {
        key: "sk_credit_card.customer_profiles.remove_option",
        value: "Remove",
        iconName: "remove_circle_outline"
    }
];

class RoleProvider {
    static { this.PartnerPortalAdmin = {
        key: 'PartnerPortalAdmin'
    }; }
}

class AccessProvider {
    static { this.Billing = {
        key: 'Billing_Full_Access'
    }; }
}

class CustomerPaymentProfilesComponent {
    constructor(abstractUserProvider, paymentResourcesService, formBuilder, modalService, renderer, customerPaymentProfileService, translate, toastrService, ngbPopoverConfig, ngbDropDownConfig) {
        this.abstractUserProvider = abstractUserProvider;
        this.paymentResourcesService = paymentResourcesService;
        this.formBuilder = formBuilder;
        this.modalService = modalService;
        this.renderer = renderer;
        this.customerPaymentProfileService = customerPaymentProfileService;
        this.translate = translate;
        this.toastrService = toastrService;
        this.ngbPopoverConfig = ngbPopoverConfig;
        this.ngbDropDownConfig = ngbDropDownConfig;
        this.onDestroy$ = new Subject();
        this.overlayConfig = { backdrop: 'static', windowClass: 'modal-panel modal-right', size: 'sm' };
        this.creditCardInfo = [];
        this.isLoading = true;
        ngbPopoverConfig.placement = 'right';
        ngbDropDownConfig.placement = 'bottom-right';
    }
    addJsToHead(src) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = src;
        this.renderer.appendChild(document.head, script);
        return script;
    }
    ngOnInit() {
        this.addJsToHead(this.config.acceptJSHostname);
        this.form = this.formBuilder.group({
            paymentProfile: [null, [Validators.required]]
        });
        this.form.statusChanges.pipe(takeUntil(this.onDestroy$)).subscribe(result => {
            if (result === ReactiveFormStatus.VALID) {
                this.customerPaymentProfileService.setCustomerPaymentProfile(this.form.get('paymentProfile').value);
            }
            else {
                this.customerPaymentProfileService.setCustomerPaymentProfile(null);
            }
        });
        this.getPaymentProfiles();
        this.customerPaymentProfileService.disableCustomerPaymentProfiles$.pipe(takeUntil(this.onDestroy$)).subscribe(res => {
            if (res) {
                this.form.disable();
            }
            else {
                this.form.enable();
            }
        });
    }
    ngOnDestroy() {
        this.onDestroy$.next();
    }
    isExpired(expirationDateString) {
        const expirationDate = moment(expirationDateString);
        const currentDate = moment();
        return currentDate.year() > expirationDate.year() ||
            currentDate.year() === expirationDate.year() &&
                currentDate.month() > expirationDate.month();
    }
    isActionsEnabled() {
        return this.abstractUserProvider.getCurrentUser().hasPermission(RoleProvider.PartnerPortalAdmin.key) ||
            this.abstractUserProvider.getCurrentUser().hasPermission(AccessProvider.Billing.key);
    }
    isAddCardDisabled() {
        return this.form.disabled || this.isLoading || !this.isActionsEnabled();
    }
    addNewCard() {
        const modalRef = this.modalService.open(CreditCardComponent, this.overlayConfig);
        modalRef.componentInstance.config = this.config;
        modalRef.componentInstance.isDefaultCheckboxDisabled = this.creditCardInfo.length === 0;
        modalRef.result.then(result => {
            this.getPaymentProfiles();
        }).catch((res) => { console.error(res.message); });
    }
    mapPaymentProfiles(customerPaymentProfiles) {
        return customerPaymentProfiles.sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
            .map(cp => {
            return {
                customerPaymentProfile: cp,
                isExpired: this.isExpired(cp.expirationDate),
                actionOptions: ACTIONOPTIONS.map(x => {
                    return {
                        value: x.value,
                        iconName: x.iconName,
                        key: this.translate.instant(x.key),
                        disabled: this.isActionOptionDisabled(x.value, cp)
                    };
                })
            };
        });
    }
    isActionOptionDisabled(actionName, paymentProfile) {
        if (actionName !== "Default")
            return false;
        return paymentProfile.isDefault || this.isExpired(paymentProfile.expirationDate);
    }
    hasExpiredAndDefaultCard() {
        return this.creditCardInfo.some(c => c.customerPaymentProfile.isDefault && c.isExpired);
    }
    getPaymentProfiles() {
        this.isLoading = true;
        this.customerPaymentProfileService.setCustomerPaymentProfile(null);
        this.paymentResourcesService.getPaymentProfiles(this.config.accountingHostname).pipe(takeUntil(this.onDestroy$)).subscribe(res => {
            this.creditCardInfo = this.mapPaymentProfiles(res);
            this.isLoading = false;
            if (this.disable) {
                this.form.disable();
            }
        });
    }
    selectAction(event, item) {
        switch (event) {
            case "Default": {
                this.openSetDefaultCreditCardModal(item);
                break;
            }
            case "Remove": {
                if (item.customerPaymentProfile.isDefault) {
                    this.openRemoveDefaultCreditCardModal();
                }
                else {
                    item.isLoading = true;
                    this.paymentResourcesService.getActiveOrdersForPartnerByPaymentProfile(this.config.accountingHostname, this.abstractUserProvider.getCurrentUser().partnerId, item.customerPaymentProfile.paymentProfileId).pipe(takeUntil(this.onDestroy$)).subscribe({
                        next: (res) => {
                            item.isLoading = false;
                            if (res.length) {
                                this.openActiveOrdersExistErrorModal(res.length);
                            }
                            else {
                                this.openRemoveCreditCardModal(item);
                            }
                        },
                        error: () => {
                            this.toastrService.error(`${this.translate.instant('sk_credit_card.customer_profiles.error_active_orders_fetching')}`);
                            item.isLoading = false;
                        }
                    });
                }
                break;
            }
            default: break;
        }
    }
    updateCreditCardInfoAfterSetAsDefault(paymentProfileId) {
        const paymentProfiles = cloneDeep(this.creditCardInfo.map(ci => ci.customerPaymentProfile));
        paymentProfiles.find(p => p.isDefault).isDefault = false;
        paymentProfiles.find(p => p.paymentProfileId === paymentProfileId).isDefault = true;
        this.creditCardInfo = this.mapPaymentProfiles(paymentProfiles);
    }
    updateCreditCardInfoAfterRemoval(paymentProfileId) {
        const paymentProfiles = cloneDeep(this.creditCardInfo.map(ci => ci.customerPaymentProfile));
        const indexToRemove = paymentProfiles.findIndex(p => p.paymentProfileId === paymentProfileId);
        paymentProfiles.splice(indexToRemove, 1);
        this.creditCardInfo = this.mapPaymentProfiles(paymentProfiles);
    }
    setDefaultCreditCard(item) {
        item.isLoading = true;
        this.paymentResourcesService.setDefaultCreditCard(this.config.accountingHostname, this.abstractUserProvider.getCurrentUser().partnerId, item.customerPaymentProfile.paymentProfileId).pipe(takeUntil(this.onDestroy$)).subscribe({
            next: () => {
                this.updateCreditCardInfoAfterSetAsDefault(item.customerPaymentProfile.paymentProfileId);
                this.toastrService.success(`${this.translate.instant('sk_credit_card.customer_profiles.success_set_default_card')}`);
                item.isLoading = false;
            },
            error: () => {
                this.toastrService.error(`${this.translate.instant('sk_credit_card.customer_profiles.error_set_default_card')}`);
                item.isLoading = false;
            }
        });
    }
    removeCreditCard(item) {
        item.isLoading = true;
        this.paymentResourcesService.removeCreditCard(this.config.accountingHostname, this.abstractUserProvider.getCurrentUser().partnerId, item.customerPaymentProfile.paymentProfileId).pipe(takeUntil(this.onDestroy$)).subscribe({
            next: () => {
                this.updateCreditCardInfoAfterRemoval(item.customerPaymentProfile.paymentProfileId);
                this.toastrService.success(`${this.translate.instant('sk_credit_card.customer_profiles.success_remove_card')}`);
                item.isLoading = false;
            },
            error: () => {
                this.toastrService.error(`${this.translate.instant('sk_credit_card.customer_profiles.error_remove_card')}`);
                item.isLoading = false;
            }
        });
    }
    openSetDefaultCreditCardModal(item) {
        const modalRef = this.modalService.simple({
            body: `<b>${this.translate.instant('sk_credit_card.customer_profiles.card_ending_in')} ${item.customerPaymentProfile.cardNumber}</b>
            <p class="my-150">${this.translate.instant('sk_credit_card.credit_card_modal.set_as_default_text')}</p>`,
            title: this.translate.instant('sk_credit_card.credit_card_modal.set_as_default_question'),
            btnLabel: this.translate.instant('sk_credit_card.credit_card_modal.set_as_default_confirmation'),
            alternative: {
                btnLabel: this.translate.instant('sk_credit_card.credit_card_modal.cancel'),
                btnCallback: () => {
                    modalRef.dismiss();
                }
            }
        });
        modalRef.result.then((result) => {
            if (result.wasClosed) { // is true when confirm button is clicked
                this.setDefaultCreditCard(item);
            }
        }).catch((res) => { console.error(res.message); });
    }
    openRemoveDefaultCreditCardModal() {
        this.modalService.simple({
            body: this.translate.instant('sk_credit_card.credit_card_modal.remove_default_card_body'),
            title: this.translate.instant('sk_credit_card.credit_card_modal.remove_default_card_title'),
            btnLabel: this.translate.instant('sk_credit_card.credit_card_modal.ok_got_it_btn_label'),
        });
    }
    openActiveOrdersExistErrorModal(activeOrdersAmount) {
        this.modalService.error({
            body: `${this.translate.instant('sk_credit_card.credit_card_modal.active_orders_exist_error_body_first_part')} ${activeOrdersAmount} 
            ${this.translate.instant('sk_credit_card.credit_card_modal.active_orders_exist_error_body_second_part')}`,
            title: this.translate.instant('sk_credit_card.credit_card_modal.active_orders_exist_error_title'),
            btnLabel: this.translate.instant('sk_credit_card.credit_card_modal.ok_got_it_btn_label')
        });
    }
    openRemoveCreditCardModal(item) {
        const modalRef = this.modalService.error({
            body: `<b>${this.translate.instant('sk_credit_card.customer_profiles.card_ending_in')} ${item.customerPaymentProfile.cardNumber}</b>
            <p class="my-150">${this.translate.instant('sk_credit_card.credit_card_modal.remove_card_text')}</p>`,
            title: this.translate.instant('sk_credit_card.credit_card_modal.remove_card_question'),
            btnLabel: this.translate.instant('sk_credit_card.credit_card_modal.remove_card_confirmation'),
            alternative: {
                btnLabel: this.translate.instant('sk_credit_card.credit_card_modal.cancel'),
                btnCallback: () => {
                    modalRef.dismiss();
                }
            }
        });
        modalRef.result.then((result) => {
            if (result.wasClosed) { // is true when confirm button is clicked
                this.removeCreditCard(item);
            }
        }).catch((res) => { console.error(res.message); });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CustomerPaymentProfilesComponent, deps: [{ token: i2$1.AbstractUserProvider }, { token: PaymentResourcesService }, { token: i2$2.UntypedFormBuilder }, { token: SkyKickModalService }, { token: i0.Renderer2 }, { token: CustomerPaymentProfileService }, { token: i2$3.TranslateService }, { token: i5.ToastrService }, { token: i1$1.NgbPopoverConfig }, { token: i1$1.NgbDropdownConfig }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: CustomerPaymentProfilesComponent, selector: "sk-customer-payment-profiles", inputs: { disable: "disable", config: "config", displayOnly: "displayOnly" }, providers: [NgbPopoverConfig], ngImport: i0, template: "<div class=\"mt-200\">\r\n    <p\r\n        [innerHTML]=\"'sk_credit_card.customer_profiles.summary' | translate: { url : 'https://support.skykick.com/hc/en-us/articles/360035625493-Payment-options-for-SkyKick-services' }\">\r\n    </p>\r\n    <sk-feedback class=\"alert-danger\" *ngIf=\"hasExpiredAndDefaultCard()\" type=\"warning\"\r\n        [subject]=\"'sk_credit_card.customer_profiles.default_card_expired_alert_subject' | translate\"\r\n        [message]=\"'sk_credit_card.customer_profiles.default_card_expired_alert_text' | translate: { url : 'https://support.skykick.com/hc/en-us/articles/360035625493-Payment-options-for-SkyKick-services' }\"\r\n        dismissible=\"true\">\r\n    </sk-feedback>\r\n    <div class=\"card mt-150\">\r\n        <div class=\"card-header d-flex align-items-center sk-card-header\">\r\n            <h4 class=\"mr-50\" translate>sk_credit_card.credit_cards</h4>\r\n\r\n            <div (mouseleave)=\"p.close()\">\r\n                <ng-template #popContent>\r\n                    <span class=\"mr-50\" translate>sk_credit_card.payment_help</span>\r\n                    <a href=\"https://support.skykick.com/hc/en-us/articles/360035625493-Payment-options-for-SkyKick-services\"\r\n                        target=\"_blank\" translate>sk_credit_card.learn_more</a>\r\n                </ng-template>\r\n                <a class=\"btn sk-btn-icon px-0 height-inherit border-0\" [ngbPopover]=\"popContent\"\r\n                    popoverClass=\"sk-popover-shim\" #p=\"ngbPopover\" (mouseenter)=\"p.open()\">\r\n                    <i class=\"material-icons md-20 icon-muted\">help_outline</i>\r\n                </a>\r\n            </div>\r\n        </div>\r\n        <div class=\"card-block\">\r\n            <fieldset>\r\n                <form [formGroup]=\"form\" *ngIf=\"!isLoading && creditCardInfo.length\">\r\n                    <table class=\"table sk-basic-table\">\r\n                        <thead>\r\n                            <tr>\r\n                                <th translate>sk_credit_card.customer_profiles.name</th>\r\n                                <th translate>sk_credit_card.customer_profiles.expiration_date</th>\r\n                                <th></th>\r\n                                <th></th>\r\n                            </tr>\r\n                        </thead>\r\n                        <tbody>\r\n                            <tr *ngFor=\"let item of creditCardInfo; index as index\">\r\n                                <td class=\"primary\">\r\n                                    <ng-container *ngIf=\"displayOnly\">\r\n                                        <span>\r\n                                            <img *ngIf=\"item.customerPaymentProfile.cardType\"\r\n                                                src=\"/assets/images/flat-rounded/{{item.customerPaymentProfile.cardType}}.svg\"\r\n                                                width=\"40\" class=\"mr-100\">\r\n                                            <span translate> sk_credit_card.customer_profiles.card_ending_in </span>\r\n                                            <span>{{item.customerPaymentProfile.cardNumber}}</span>\r\n                                        </span>\r\n                                    </ng-container>\r\n\r\n                                    <div *ngIf=\"!displayOnly\" class=\"custom-control custom-radio sk-radio\">\r\n                                        <input type=\"radio\" id=\"paymentProfile{{index}}\" name=\"paymentProfile\"\r\n                                            class=\"form-check-input\" formControlName=\"paymentProfile\" [value]=\"item\">\r\n                                        <label class=\"form-check-label\" for=\"paymentProfile{{index}}\">\r\n                                            <img *ngIf=\"item.customerPaymentProfile.cardType\"\r\n                                                src=\"/assets/images/flat-rounded/{{item.customerPaymentProfile.cardType}}.svg\"\r\n                                                width=\"40\" class=\"mr-100\">\r\n                                            <span translate> sk_credit_card.customer_profiles.card_ending_in </span>\r\n                                            <span>{{item.customerPaymentProfile.cardNumber}}</span>\r\n                                        </label>\r\n                                    </div>\r\n                                </td>\r\n                                <td [class.text-danger]=\"item.isExpired\">{{item.customerPaymentProfile.expirationDate}}\r\n                                </td>\r\n                                <td class=\"text-center\">\r\n                                    <span *ngIf=\"item.customerPaymentProfile.isDefault\"\r\n                                        class=\"badge badge-default mr-100\"\r\n                                        translate>sk_credit_card.customer_profiles.default_card</span>\r\n                                    <span *ngIf=\"item.isExpired\" class=\"badge badge-danger\"\r\n                                        translate>sk_credit_card.customer_profiles.expired_card</span>\r\n                                </td>\r\n                                <td class=\"text-center\">\r\n                                    <sk-action-menu *ngIf=\"isActionsEnabled() && !item.isLoading\"\r\n                                        [options]=\"item.actionOptions\" (optionSelected)=\"selectAction($event, item)\">\r\n                                    </sk-action-menu>\r\n                                    <sk-text-spinner *ngIf=\"item.isLoading\" size=\"sm\"></sk-text-spinner>\r\n                                </td>\r\n                            </tr>\r\n                        </tbody>\r\n                    </table>\r\n                </form>\r\n            </fieldset>\r\n\r\n            <sk-text-spinner *ngIf=\"isLoading\"\r\n                message=\"{{'sk_credit_card.customer_profiles.fetching_customer_profiles' | translate}}\"></sk-text-spinner>\r\n\r\n            <button id=\"btnAddNewCard\" (click)=\"addNewCard()\" [disabled]=\"isAddCardDisabled()\" type=\"button\"\r\n                sk-combo-button color=\"tertiary\" icon=\"add\">{{ 'sk_credit_card.customer_profiles.add_card' |\r\n                translate}}</button>\r\n            <!-- <button type=\"button\" class=\"btn btn-secondary mr-50\" (click)=\"getCustomerProfileRequest()\">getCustomerProfileRequest</button>\r\n        <pre *ngFor=\"let item of paymentProfiles\">{{item | json}}</pre> -->\r\n        </div>\r\n    </div>\r\n</div>", styles: [".custom-radio .form-check-label:before,.custom-radio .form-check-label:after{top:3.25px}.sk-basic-table td{height:64px;padding-left:1rem}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: i1$1.NgbPopover, selector: "[ngbPopover]", inputs: ["animation", "autoClose", "ngbPopover", "popoverTitle", "placement", "popperOptions", "triggers", "positionTarget", "container", "disablePopover", "popoverClass", "popoverContext", "openDelay", "closeDelay"], outputs: ["shown", "hidden"], exportAs: ["ngbPopover"] }, { kind: "component", type: TextSpinnerComponent, selector: "sk-text-spinner", inputs: ["message", "size", "spinnerLocation", "loaderWrapperClasses"] }, { kind: "component", type: ActionMenuComponent, selector: "sk-action-menu", inputs: ["options"], outputs: ["optionSelected"] }, { kind: "component", type: ComboButtonComponent, selector: "button[sk-combo-button]", inputs: ["color", "icon"] }, { kind: "component", type: FeedbackComponent, selector: "sk-feedback", inputs: ["type", "message", "subject", "dismissible", "icon", "iconClass", "id"], outputs: ["closed"] }, { kind: "directive", type: i2$3.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }, { kind: "pipe", type: i2$3.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CustomerPaymentProfilesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-customer-payment-profiles', providers: [NgbPopoverConfig], template: "<div class=\"mt-200\">\r\n    <p\r\n        [innerHTML]=\"'sk_credit_card.customer_profiles.summary' | translate: { url : 'https://support.skykick.com/hc/en-us/articles/360035625493-Payment-options-for-SkyKick-services' }\">\r\n    </p>\r\n    <sk-feedback class=\"alert-danger\" *ngIf=\"hasExpiredAndDefaultCard()\" type=\"warning\"\r\n        [subject]=\"'sk_credit_card.customer_profiles.default_card_expired_alert_subject' | translate\"\r\n        [message]=\"'sk_credit_card.customer_profiles.default_card_expired_alert_text' | translate: { url : 'https://support.skykick.com/hc/en-us/articles/360035625493-Payment-options-for-SkyKick-services' }\"\r\n        dismissible=\"true\">\r\n    </sk-feedback>\r\n    <div class=\"card mt-150\">\r\n        <div class=\"card-header d-flex align-items-center sk-card-header\">\r\n            <h4 class=\"mr-50\" translate>sk_credit_card.credit_cards</h4>\r\n\r\n            <div (mouseleave)=\"p.close()\">\r\n                <ng-template #popContent>\r\n                    <span class=\"mr-50\" translate>sk_credit_card.payment_help</span>\r\n                    <a href=\"https://support.skykick.com/hc/en-us/articles/360035625493-Payment-options-for-SkyKick-services\"\r\n                        target=\"_blank\" translate>sk_credit_card.learn_more</a>\r\n                </ng-template>\r\n                <a class=\"btn sk-btn-icon px-0 height-inherit border-0\" [ngbPopover]=\"popContent\"\r\n                    popoverClass=\"sk-popover-shim\" #p=\"ngbPopover\" (mouseenter)=\"p.open()\">\r\n                    <i class=\"material-icons md-20 icon-muted\">help_outline</i>\r\n                </a>\r\n            </div>\r\n        </div>\r\n        <div class=\"card-block\">\r\n            <fieldset>\r\n                <form [formGroup]=\"form\" *ngIf=\"!isLoading && creditCardInfo.length\">\r\n                    <table class=\"table sk-basic-table\">\r\n                        <thead>\r\n                            <tr>\r\n                                <th translate>sk_credit_card.customer_profiles.name</th>\r\n                                <th translate>sk_credit_card.customer_profiles.expiration_date</th>\r\n                                <th></th>\r\n                                <th></th>\r\n                            </tr>\r\n                        </thead>\r\n                        <tbody>\r\n                            <tr *ngFor=\"let item of creditCardInfo; index as index\">\r\n                                <td class=\"primary\">\r\n                                    <ng-container *ngIf=\"displayOnly\">\r\n                                        <span>\r\n                                            <img *ngIf=\"item.customerPaymentProfile.cardType\"\r\n                                                src=\"/assets/images/flat-rounded/{{item.customerPaymentProfile.cardType}}.svg\"\r\n                                                width=\"40\" class=\"mr-100\">\r\n                                            <span translate> sk_credit_card.customer_profiles.card_ending_in </span>\r\n                                            <span>{{item.customerPaymentProfile.cardNumber}}</span>\r\n                                        </span>\r\n                                    </ng-container>\r\n\r\n                                    <div *ngIf=\"!displayOnly\" class=\"custom-control custom-radio sk-radio\">\r\n                                        <input type=\"radio\" id=\"paymentProfile{{index}}\" name=\"paymentProfile\"\r\n                                            class=\"form-check-input\" formControlName=\"paymentProfile\" [value]=\"item\">\r\n                                        <label class=\"form-check-label\" for=\"paymentProfile{{index}}\">\r\n                                            <img *ngIf=\"item.customerPaymentProfile.cardType\"\r\n                                                src=\"/assets/images/flat-rounded/{{item.customerPaymentProfile.cardType}}.svg\"\r\n                                                width=\"40\" class=\"mr-100\">\r\n                                            <span translate> sk_credit_card.customer_profiles.card_ending_in </span>\r\n                                            <span>{{item.customerPaymentProfile.cardNumber}}</span>\r\n                                        </label>\r\n                                    </div>\r\n                                </td>\r\n                                <td [class.text-danger]=\"item.isExpired\">{{item.customerPaymentProfile.expirationDate}}\r\n                                </td>\r\n                                <td class=\"text-center\">\r\n                                    <span *ngIf=\"item.customerPaymentProfile.isDefault\"\r\n                                        class=\"badge badge-default mr-100\"\r\n                                        translate>sk_credit_card.customer_profiles.default_card</span>\r\n                                    <span *ngIf=\"item.isExpired\" class=\"badge badge-danger\"\r\n                                        translate>sk_credit_card.customer_profiles.expired_card</span>\r\n                                </td>\r\n                                <td class=\"text-center\">\r\n                                    <sk-action-menu *ngIf=\"isActionsEnabled() && !item.isLoading\"\r\n                                        [options]=\"item.actionOptions\" (optionSelected)=\"selectAction($event, item)\">\r\n                                    </sk-action-menu>\r\n                                    <sk-text-spinner *ngIf=\"item.isLoading\" size=\"sm\"></sk-text-spinner>\r\n                                </td>\r\n                            </tr>\r\n                        </tbody>\r\n                    </table>\r\n                </form>\r\n            </fieldset>\r\n\r\n            <sk-text-spinner *ngIf=\"isLoading\"\r\n                message=\"{{'sk_credit_card.customer_profiles.fetching_customer_profiles' | translate}}\"></sk-text-spinner>\r\n\r\n            <button id=\"btnAddNewCard\" (click)=\"addNewCard()\" [disabled]=\"isAddCardDisabled()\" type=\"button\"\r\n                sk-combo-button color=\"tertiary\" icon=\"add\">{{ 'sk_credit_card.customer_profiles.add_card' |\r\n                translate}}</button>\r\n            <!-- <button type=\"button\" class=\"btn btn-secondary mr-50\" (click)=\"getCustomerProfileRequest()\">getCustomerProfileRequest</button>\r\n        <pre *ngFor=\"let item of paymentProfiles\">{{item | json}}</pre> -->\r\n        </div>\r\n    </div>\r\n</div>", styles: [".custom-radio .form-check-label:before,.custom-radio .form-check-label:after{top:3.25px}.sk-basic-table td{height:64px;padding-left:1rem}\n"] }]
        }], ctorParameters: () => [{ type: i2$1.AbstractUserProvider }, { type: PaymentResourcesService }, { type: i2$2.UntypedFormBuilder }, { type: SkyKickModalService }, { type: i0.Renderer2 }, { type: CustomerPaymentProfileService }, { type: i2$3.TranslateService }, { type: i5.ToastrService }, { type: i1$1.NgbPopoverConfig }, { type: i1$1.NgbDropdownConfig }], propDecorators: { disable: [{
                type: Input
            }], config: [{
                type: Input
            }], displayOnly: [{
                type: Input
            }] } });

class CreditCardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CreditCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: CreditCardModule, declarations: [CustomerPaymentProfilesComponent,
            CreditCardComponent], imports: [CommonModule,
            ReactiveFormsModule,
            NgbModule,
            ToastsModule,
            StatusSpinnerModule,
            AuthModule,
            ActionMenuModule,
            ButtonsModule,
            FeedbackModule, i2$3.TranslateModule, NgxMaskDirective,
            NgxMaskPipe], exports: [CustomerPaymentProfilesComponent,
            CreditCardComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CreditCardModule, providers: [
            CookieService,
            CustomerPaymentProfileService,
            provideEnvironmentNgxMask()
        ], imports: [CommonModule,
            ReactiveFormsModule,
            NgbModule,
            ToastsModule,
            StatusSpinnerModule,
            AuthModule,
            ActionMenuModule,
            ButtonsModule,
            FeedbackModule,
            TranslateModule.forChild()] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: CreditCardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CustomerPaymentProfilesComponent,
                        CreditCardComponent
                    ],
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        NgbModule,
                        ToastsModule,
                        StatusSpinnerModule,
                        AuthModule,
                        ActionMenuModule,
                        ButtonsModule,
                        FeedbackModule,
                        TranslateModule.forChild(),
                        NgxMaskDirective,
                        NgxMaskPipe
                    ],
                    providers: [
                        CookieService,
                        CustomerPaymentProfileService,
                        provideEnvironmentNgxMask()
                    ],
                    exports: [
                        CustomerPaymentProfilesComponent,
                        CreditCardComponent
                    ]
                }]
        }] });

class HubspotInfoService {
    handleError(errorResponse) {
        if (errorResponse.error instanceof ErrorEvent) {
            console.error('Client Side Error: ', errorResponse.error.message);
        }
        else {
            console.error('Server Side Error: ', errorResponse);
        }
        this.toastrService.error('There is a problem with the service. We are notified & working on it. Please try again later.');
        return throwError(errorResponse);
    }
    constructor(http, toastrService) {
        this.http = http;
        this.toastrService = toastrService;
    }
    submitSalePricingContactRequest(payload) {
        return this.http.post('https://api.hsforms.com/submissions/v3/integration/submit/2047087/4b38868d-ebf1-4171-92c2-6859b353b5fa', payload)
            .pipe(catchError(this.handleError));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HubspotInfoService, deps: [{ token: i1$2.HttpClient }, { token: i5.ToastrService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HubspotInfoService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HubspotInfoService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.HttpClient }, { type: i5.ToastrService }] });

class PricingContactRequestComponent {
    constructor(formBuilder, activeModal, hubspotInfoService, translateService, toastrService) {
        this.formBuilder = formBuilder;
        this.activeModal = activeModal;
        this.hubspotInfoService = hubspotInfoService;
        this.translateService = translateService;
        this.toastrService = toastrService;
    }
    ngOnInit() {
        this.form = this.formBuilder.group({
            firstname: ['', [Validators.required]],
            lastname: ['', [Validators.required]],
            company: null,
            email: ['', [Validators.required, Validators.email]],
            phone: null,
            notes: null
        });
    }
    submit() {
        if (this.form.valid) {
            this.form.disable();
            this.isVerifying = true;
            let payload = {
                "fields": [{
                        "name": "firstname",
                        "value": this.form.get('firstname').value
                    }, {
                        "name": "lastname",
                        "value": this.form.get('lastname').value
                    }, {
                        "name": "company",
                        "value": this.form.get('company').value
                    }, {
                        "name": "email",
                        "value": this.form.get('email').value
                    }, {
                        "name": "phone",
                        "value": this.form.get('phone').value
                    }, {
                        "name": "notes",
                        "value": this.form.get('notes').value
                    }
                ]
            };
            this.hubspotInfoService.submitSalePricingContactRequest(payload).subscribe({
                complete: () => {
                    this.activeModal.close();
                    this.toastrService.success(this.translateService.instant('sk_contact_sales.you_will_be_contacted_shortly'));
                },
                error: error => {
                    this.activeModal.close();
                    this.toastrService.error(error.message);
                }
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PricingContactRequestComponent, deps: [{ token: i2$2.UntypedFormBuilder }, { token: i1$1.NgbActiveModal }, { token: HubspotInfoService }, { token: i2$3.TranslateService }, { token: i5.ToastrService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PricingContactRequestComponent, selector: "sk-pricing-contact-request", ngImport: i0, template: "<div class=\"modal-header bg-white\">\r\n    <div class=\"d-flex flex-column\">\r\n        <div class=\"d-flex align-items-center \">\r\n            <h3 class=\"modal-title font-weight-normal\" id=\"myModalLabel\" translate>sk_contact_sales.contact_sales</h3>\r\n        </div>\r\n    </div>\r\n\r\n    <button type=\"button\" class=\"btn-close\" (click)=\"activeModal.dismiss()\" id=\"closeBtn\">\r\n    </button>\r\n</div>\r\n<div class=\"modal-body\">\r\n\r\n    <form [formGroup]=\"form\">\r\n        <fieldset class=\"form-group\" [class.is-invalid]=\"form.get('firstname').invalid && !form.get('firstname').pristine\">\r\n            <label translate>sk_contact_sales.first_name</label>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"firstname\" id=\"firstname\" />\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-group\" [class.is-invalid]=\"form.get('lastname').invalid && !form.get('lastname').pristine\">\r\n            <label translate>sk_contact_sales.last_name</label>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"lastname\" id=\"lastname\" />\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-group\">\r\n            <label translate>sk_contact_sales.company_name</label>\r\n            <small class=\"sk-label-text text-muted\" translate>sk_contact_sales.optional</small>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"company\" id=\"company\" />\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-group\" [class.is-invalid]=\"form.get('email').invalid && !form.get('email').pristine\">\r\n            <label translate>sk_contact_sales.email</label>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"email\" id=\"email\" />\r\n            <small *ngIf=\"form.get('email').hasError('email')\" class=\"invalid-feedback\" translate>sk_contact_sales.must_be_a_valid_email</small>\r\n            <small *ngIf=\"form.get('email').hasError('required') && !form.get('email').pristine\" class=\"invalid-feedback\" translate>sk_contact_sales.email_is_required</small>\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-group\">\r\n            <label translate>sk_contact_sales.phone_number</label>\r\n            <small class=\"sk-label-text text-muted\" translate>sk_contact_sales.optional</small>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"phone\" id=\"phone\" />\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-group\">\r\n            <label translate>sk_contact_sales.other_details</label>\r\n            <small class=\"sk-label-text text-muted\" translate>sk_contact_sales.optional</small>\r\n            <textarea type=\"text\" class=\"form-control\" formControlName=\"notes\" id=\"notes\"></textarea>\r\n        </fieldset>\r\n    </form>\r\n</div>\r\n<div class=\"modal-footer justify-content-start\">\r\n    <button id=\"submitBtn\" class=\"btn btn-primary sk-has-spinner sk-abs-spinner\" [class.active]=\"isVerifying\"\r\n        [disabled]=\"form.invalid || form.pristine || isVerifying\" (click)=\"submit()\">\r\n        <div class=\"sk-btn-spinner\">\r\n            <div class=\"sk-spin-loader loader-dark\"></div>\r\n        </div>\r\n        <span class=\"sk-loader-label\" translate>sk_contact_sales.submit</span>\r\n    </button>\r\n    <button id=\"cancelBtn\" type=\"button\" class=\"btn btn-secondary\" [disabled]=\"isVerifying\"\r\n        (click)=\"activeModal.dismiss()\" translate>sk_contact_sales.cancel</button>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: i2$3.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PricingContactRequestComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-pricing-contact-request', template: "<div class=\"modal-header bg-white\">\r\n    <div class=\"d-flex flex-column\">\r\n        <div class=\"d-flex align-items-center \">\r\n            <h3 class=\"modal-title font-weight-normal\" id=\"myModalLabel\" translate>sk_contact_sales.contact_sales</h3>\r\n        </div>\r\n    </div>\r\n\r\n    <button type=\"button\" class=\"btn-close\" (click)=\"activeModal.dismiss()\" id=\"closeBtn\">\r\n    </button>\r\n</div>\r\n<div class=\"modal-body\">\r\n\r\n    <form [formGroup]=\"form\">\r\n        <fieldset class=\"form-group\" [class.is-invalid]=\"form.get('firstname').invalid && !form.get('firstname').pristine\">\r\n            <label translate>sk_contact_sales.first_name</label>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"firstname\" id=\"firstname\" />\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-group\" [class.is-invalid]=\"form.get('lastname').invalid && !form.get('lastname').pristine\">\r\n            <label translate>sk_contact_sales.last_name</label>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"lastname\" id=\"lastname\" />\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-group\">\r\n            <label translate>sk_contact_sales.company_name</label>\r\n            <small class=\"sk-label-text text-muted\" translate>sk_contact_sales.optional</small>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"company\" id=\"company\" />\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-group\" [class.is-invalid]=\"form.get('email').invalid && !form.get('email').pristine\">\r\n            <label translate>sk_contact_sales.email</label>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"email\" id=\"email\" />\r\n            <small *ngIf=\"form.get('email').hasError('email')\" class=\"invalid-feedback\" translate>sk_contact_sales.must_be_a_valid_email</small>\r\n            <small *ngIf=\"form.get('email').hasError('required') && !form.get('email').pristine\" class=\"invalid-feedback\" translate>sk_contact_sales.email_is_required</small>\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-group\">\r\n            <label translate>sk_contact_sales.phone_number</label>\r\n            <small class=\"sk-label-text text-muted\" translate>sk_contact_sales.optional</small>\r\n            <input type=\"text\" class=\"form-control\" formControlName=\"phone\" id=\"phone\" />\r\n        </fieldset>\r\n\r\n        <fieldset class=\"form-group\">\r\n            <label translate>sk_contact_sales.other_details</label>\r\n            <small class=\"sk-label-text text-muted\" translate>sk_contact_sales.optional</small>\r\n            <textarea type=\"text\" class=\"form-control\" formControlName=\"notes\" id=\"notes\"></textarea>\r\n        </fieldset>\r\n    </form>\r\n</div>\r\n<div class=\"modal-footer justify-content-start\">\r\n    <button id=\"submitBtn\" class=\"btn btn-primary sk-has-spinner sk-abs-spinner\" [class.active]=\"isVerifying\"\r\n        [disabled]=\"form.invalid || form.pristine || isVerifying\" (click)=\"submit()\">\r\n        <div class=\"sk-btn-spinner\">\r\n            <div class=\"sk-spin-loader loader-dark\"></div>\r\n        </div>\r\n        <span class=\"sk-loader-label\" translate>sk_contact_sales.submit</span>\r\n    </button>\r\n    <button id=\"cancelBtn\" type=\"button\" class=\"btn btn-secondary\" [disabled]=\"isVerifying\"\r\n        (click)=\"activeModal.dismiss()\" translate>sk_contact_sales.cancel</button>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i2$2.UntypedFormBuilder }, { type: i1$1.NgbActiveModal }, { type: HubspotInfoService }, { type: i2$3.TranslateService }, { type: i5.ToastrService }] });

class ContactSalesDirective {
    constructor(modalService) {
        this.modalService = modalService;
        this.overlayConfig = { backdrop: 'static', windowClass: 'modal-panel modal-right' };
    }
    onClick(e) {
        e.preventDefault();
        this.modalService.open(PricingContactRequestComponent, this.overlayConfig);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ContactSalesDirective, deps: [{ token: i1$1.NgbModal }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: ContactSalesDirective, selector: "[skContactSales]", host: { listeners: { "click": "onClick($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ContactSalesDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[skContactSales]'
                }]
        }], ctorParameters: () => [{ type: i1$1.NgbModal }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class HubspotFormsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HubspotFormsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: HubspotFormsModule, declarations: [ContactSalesDirective,
            PricingContactRequestComponent], imports: [CommonModule,
            ReactiveFormsModule,
            ToastsModule, i2$3.TranslateModule], exports: [ContactSalesDirective,
            PricingContactRequestComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HubspotFormsModule, providers: [
            HubspotInfoService,
            CookieService
        ], imports: [CommonModule,
            ReactiveFormsModule,
            ToastsModule,
            TranslateModule.forChild()] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HubspotFormsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ContactSalesDirective,
                        PricingContactRequestComponent
                    ],
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        ToastsModule,
                        TranslateModule.forChild()
                    ],
                    providers: [
                        HubspotInfoService,
                        CookieService
                    ],
                    exports: [
                        ContactSalesDirective,
                        PricingContactRequestComponent
                    ]
                }]
        }] });

const NOTIFICATIONS_INIT_CONFIG = 'notificationsInitConfig';
class NotificationsControlService {
    constructor(modalService, cacheService) {
        this.modalService = modalService;
        this.cacheService = cacheService;
    }
    /* Sets the notifications modal orders filter */
    setModalFilter(openModal, product, severity) {
        const config = {
            CustomerId: null,
            BackupServiceId: null,
            AlertIds: null,
            OrderFilter: {
                product,
                severity,
                startDate: null,
                endDate: null,
                searchCustomer: null,
                page: 0,
                orderBy: { orderByType: 'Descending', orderByField: 'Severity' }
            },
            AlertsFilter: null
        };
        this.setCache(config);
        if (openModal) {
            this.open();
        }
    }
    /**  Sets the the notifications modal to a specific customer/order and only specific alertIds if provided.
     *   If only one alertId is provided, the modal will navigate to that alert.
     */
    setModalOrder(openModal, customerId, backupServiceId, alertIds) {
        const config = {
            CustomerId: customerId,
            BackupServiceId: backupServiceId,
            AlertIds: alertIds,
            OrderFilter: null,
            AlertsFilter: null
        };
        this.setCache(config);
        if (openModal) {
            this.open();
        }
    }
    /* Sets the notifications modal to a given customer/order and sets the alerts filter. */
    setModalOrderWithAlertsFilter(openModal, customerId, status, severity, backupServiceId) {
        const config = {
            AlertsFilter: {
                status,
                severity,
                startDate: null,
                endDate: null
            },
            CustomerId: customerId,
            BackupServiceId: backupServiceId,
            AlertIds: null,
            OrderFilter: null,
        };
        this.setCache(config);
        if (openModal) {
            this.open();
        }
    }
    /* Opens the notifications modal with no filters. */
    open() {
        const modalRef = this.modalService.open(NotificationsComponent, NotificationsComponent.overlayConfig);
        modalRef.result.then((data) => this.handleModalClosure(data, null), (reason) => this.handleModalClosure(null, reason));
    }
    handleModalClosure(data, reason) {
        this.cacheService.root().remove(NOTIFICATIONS_INIT_CONFIG);
    }
    setCache(config) {
        this.cacheService.root().set(NOTIFICATIONS_INIT_CONFIG, config);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsControlService, deps: [{ token: i1$1.NgbModal }, { token: i4.CacheService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsControlService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationsControlService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.NgbModal }, { type: i4.CacheService }] });

class FilterService {
    constructor() {
        this.filters = [];
        this.init = (s) => {
            this.selectableItems = s;
        };
    }
    registerFilter(filter) {
        this.filters.push(filter);
        filter.init(this.selectableItems);
    }
    unregisterFilter(filter) {
        if (this.filters.indexOf(filter) >= 0) {
            this.filters.splice(this.filters.indexOf(filter), 1);
        }
    }
    updateItemsVisibility() {
        if (!this.selectableItems) {
            return;
        }
        this.selectableItems.forEach(item => {
            let isVisible = true;
            this.filters.forEach(filter => {
                isVisible &&= filter.isItemVisible(item);
            });
            if (item.isVisible !== isVisible) {
                item.isVisible = isVisible;
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

class FilterModelBase {
    constructor() {
        this.options = [];
        this.onChangeSubject = new BehaviorSubject(void 0);
        this.onChange = this.onChangeSubject.asObservable();
        this.handleSelect = () => this.onChangeSubject.next();
    }
    handleReset() {
        this.options.forEach(i => i.isSelected = false);
        this.onChangeSubject.next();
    }
    handleSearch(text) {
        this.options.forEach(i => i.isVisible = i.value.toLowerCase().includes(text.toLowerCase()));
    }
    initByOptions(inputOptions) {
        if (inputOptions) {
            this.options.forEach(o => o.isSelected =
                !!inputOptions.find(io => io.value === o.value)?.isSelected);
            this.onChangeSubject.next();
        }
    }
}

class FilterLabelNamePipe {
    constructor(translate) {
        this.translate = translate;
    }
    transform(filterModel, filterName, isTranslatable) {
        return filterModel
            .options
            .filter(i => i.isSelected)
            .map(i => isTranslatable ?
            this.translate.instant(`FILTER.${filterName.toUpperCase()}.${i.value.toUpperCase()}`) :
            i.value)
            .join(', ');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterLabelNamePipe, deps: [{ token: i2$3.TranslateService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: FilterLabelNamePipe, name: "filterLabelName", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterLabelNamePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'filterLabelName',
                    pure: false
                }]
        }], ctorParameters: () => [{ type: i2$3.TranslateService }] });

class FilterLabelVisibilityPipe {
    transform(optionsCount, element) {
        if (element && element.scrollWidth && element.clientWidth) {
            return element.scrollWidth > element.clientWidth && optionsCount > 1;
        }
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterLabelVisibilityPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: FilterLabelVisibilityPipe, name: "filterLabelVisibility", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterLabelVisibilityPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'filterLabelVisibility',
                    pure: false
                }]
        }] });

class FilterOptionsCountPipe {
    transform(filterModel) {
        return filterModel?.options ? filterModel.options.filter(o => o.isSelected).length : 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterOptionsCountPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: FilterOptionsCountPipe, name: "filterOptionsCount", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterOptionsCountPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'filterOptionsCount',
                    pure: false
                }]
        }] });

class FilterLabelComponent {
    constructor() {
        this.filterName = '';
        this.isNameTranslatable = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: FilterLabelComponent, selector: "sk-filter-label", inputs: { filterName: "filterName", isNameTranslatable: "isNameTranslatable", width: "width", model: "model" }, ngImport: i0, template: "<button type=\"button\" class=\"btn sk-btn-icon d-flex align-items-center p-0\">\r\n    <span *ngIf=\"(model | filterOptionsCount) === 0 else optionsLabel\" translate>FILTER.{{filterName.toUpperCase()}}.EMPTY_VALUE</span>\r\n    <ng-template #optionsLabel class=\"d-flex align-items-center\">\r\n        <span class=\"d-inline-block text-truncate me-1 text-end max-w-12\" [style.width.px]=\"width\"\r\n            #filterLabel>\r\n            {{model | filterLabelName: filterName : isNameTranslatable}}\r\n        </span>\r\n        <sk-badge skBadgePill *ngIf=\"(model | filterOptionsCount) | filterLabelVisibility : filterLabel\">\r\n            +{{(model | filterOptionsCount)-1}}\r\n        </sk-badge>\r\n    </ng-template>\r\n</button>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$3.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }, { kind: "component", type: BadgeComponent, selector: "sk-badge", inputs: ["type", "skBadgePill", "size", "class"] }, { kind: "pipe", type: FilterLabelNamePipe, name: "filterLabelName" }, { kind: "pipe", type: FilterLabelVisibilityPipe, name: "filterLabelVisibility" }, { kind: "pipe", type: FilterOptionsCountPipe, name: "filterOptionsCount" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterLabelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-filter-label', template: "<button type=\"button\" class=\"btn sk-btn-icon d-flex align-items-center p-0\">\r\n    <span *ngIf=\"(model | filterOptionsCount) === 0 else optionsLabel\" translate>FILTER.{{filterName.toUpperCase()}}.EMPTY_VALUE</span>\r\n    <ng-template #optionsLabel class=\"d-flex align-items-center\">\r\n        <span class=\"d-inline-block text-truncate me-1 text-end max-w-12\" [style.width.px]=\"width\"\r\n            #filterLabel>\r\n            {{model | filterLabelName: filterName : isNameTranslatable}}\r\n        </span>\r\n        <sk-badge skBadgePill *ngIf=\"(model | filterOptionsCount) | filterLabelVisibility : filterLabel\">\r\n            +{{(model | filterOptionsCount)-1}}\r\n        </sk-badge>\r\n    </ng-template>\r\n</button>\r\n" }]
        }], propDecorators: { filterName: [{
                type: Input
            }], isNameTranslatable: [{
                type: Input
            }], width: [{
                type: Input
            }], model: [{
                type: Input
            }] } });

class FilterOptionNamePipe {
    constructor(translate) {
        this.translate = translate;
    }
    transform(option, filterName) {
        return this.translate.instant(`FILTER.${filterName.toUpperCase()}.${option.value.toUpperCase()}`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterOptionNamePipe, deps: [{ token: i2$3.TranslateService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: FilterOptionNamePipe, name: "filterOptionName", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterOptionNamePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'filterOptionName',
                    pure: false
                }]
        }], ctorParameters: () => [{ type: i2$3.TranslateService }] });

class FilterOptionsItemComponent {
    constructor() {
        this.optionSelected = new EventEmitter();
    }
    onChange() {
        this.optionSelected.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterOptionsItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: FilterOptionsItemComponent, selector: "sk-filter-options-item", inputs: { filterName: "filterName", item: "item", isNameTranslatable: "isNameTranslatable" }, outputs: { optionSelected: "optionSelected" }, ngImport: i0, template: "<div class=\"form-check w-100 ms-3\" [hidden]=\"item.isVisible !== undefined && !item.isVisible\">\r\n    <input class=\"form-check-input form-check-input-sm\" type=\"checkbox\" (change)=\"onChange()\" id=\"{{item.value}}\"\r\n        [(ngModel)]=\"item.isSelected\" />\r\n    <label class=\"form-check-label d-inline-flex w-100 pe-3\" style=\"height: 3em;\"\r\n        for=\"{{item.value}}\">\r\n        <span class=\"d-inline-block text-truncate\" style=\"max-width: 150px\">\r\n            {{isNameTranslatable === true ? (item | filterOptionName: filterName) : item.value}}\r\n        </span>\r\n    </label>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i2$2.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "pipe", type: FilterOptionNamePipe, name: "filterOptionName" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterOptionsItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-filter-options-item', template: "<div class=\"form-check w-100 ms-3\" [hidden]=\"item.isVisible !== undefined && !item.isVisible\">\r\n    <input class=\"form-check-input form-check-input-sm\" type=\"checkbox\" (change)=\"onChange()\" id=\"{{item.value}}\"\r\n        [(ngModel)]=\"item.isSelected\" />\r\n    <label class=\"form-check-label d-inline-flex w-100 pe-3\" style=\"height: 3em;\"\r\n        for=\"{{item.value}}\">\r\n        <span class=\"d-inline-block text-truncate\" style=\"max-width: 150px\">\r\n            {{isNameTranslatable === true ? (item | filterOptionName: filterName) : item.value}}\r\n        </span>\r\n    </label>\r\n</div>\r\n" }]
        }], propDecorators: { filterName: [{
                type: Input
            }], item: [{
                type: Input
            }], isNameTranslatable: [{
                type: Input
            }], optionSelected: [{
                type: Output
            }] } });

class ClearFilterButtonComponent {
    constructor() {
        this.clear = new EventEmitter();
    }
    onClick() {
        this.clear.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ClearFilterButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ClearFilterButtonComponent, selector: "sk-filter-clear-button", outputs: { clear: "clear" }, ngImport: i0, template: "<button type=\"button\" id=\"close-left\"\r\n        class=\"btn sk-btn-icon text-muted sk-btn-combo d-flex align-items-center justify-content-start w-100\"\r\n        (click)=\"onClick()\">\r\n    <i class=\"material-icons md-22\">close</i>\r\n    <label class=\"text-md mb-0 cursor-pointer\" translate>FILTER.CLEAR</label>\r\n</button>\r\n", styles: [":host{display:flex}\n"], dependencies: [{ kind: "directive", type: i2$3.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ClearFilterButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-filter-clear-button', template: "<button type=\"button\" id=\"close-left\"\r\n        class=\"btn sk-btn-icon text-muted sk-btn-combo d-flex align-items-center justify-content-start w-100\"\r\n        (click)=\"onClick()\">\r\n    <i class=\"material-icons md-22\">close</i>\r\n    <label class=\"text-md mb-0 cursor-pointer\" translate>FILTER.CLEAR</label>\r\n</button>\r\n", styles: [":host{display:flex}\n"] }]
        }], propDecorators: { clear: [{
                type: Output
            }] } });

class BaseFilterComponent {
    constructor(filterService, route, router, logger) {
        this.filterService = filterService;
        this.route = route;
        this.router = router;
        this.logger = logger;
        this.withSearch = false;
        this.isOptionTranslatable = false;
        this.tooltipContainer = 'body';
        this.filterChange = new EventEmitter();
        this.destroy$ = new Subject();
        this.handleFilter = () => {
            this.model.handleSelect();
            this.router.navigate([], { relativeTo: this.route });
        };
        this.resetFilter = () => {
            this.router.navigate([], { relativeTo: this.route });
            this.model.handleReset();
        };
    }
    ngOnInit() {
        this.filterService.registerFilter(this.model);
        this.model.onChange
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.filterService.updateItemsVisibility());
        if (this.initState) {
            this.initState
                .pipe(first())
                .subscribe({
                next: options => {
                    this.model.initByOptions(options);
                    // use 'source' query param as pre filter value
                    this.route.queryParams
                        .pipe(first())
                        .subscribe(queryParams => {
                        this.model.initByQuery(queryParams);
                    });
                },
                error: err => this.logger.error('Error while getting Devices Filters from Redux!' + err),
            });
        }
        this.model.onChange
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.filterChange.emit({
            name: this.model.filterName, options: this.model.options.filter(o => o.isSelected).map(o => ({ ...o }))
        }));
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.filterService.unregisterFilter(this.model);
    }
    searchOptions(text) {
        this.searchText = text;
        this.model.handleSearch(text);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BaseFilterComponent, deps: [{ token: FilterService }, { token: i2.ActivatedRoute }, { token: i2.Router }, { token: LoggerService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: BaseFilterComponent, selector: "sk-base-filter", inputs: { model: "model", width: "width", withSearch: "withSearch", isOptionTranslatable: "isOptionTranslatable", initState: "initState", tooltipContainer: "tooltipContainer" }, outputs: { filterChange: "filterChange" }, ngImport: i0, template: "<div ngbDropdown class=\"btn-group\">\r\n    <sk-filter-label\r\n        ngbDropdownToggle\r\n        class=\"d-flex align-items-center pr-100\"\r\n        [isNameTranslatable]=\"isOptionTranslatable\"\r\n        [filterName]=\"model.filterName\"\r\n        [model]=\"model\"\r\n        [width]=\"width\">\r\n    </sk-filter-label>\r\n    <div ngbDropdownMenu>\r\n        <div class=\"card border-0\">\r\n            <div [hidden]=\"!withSearch\" class=\"border-bottom sk-input-icon px-1 py-2\">\r\n                <div class=\"sk-input-group\">\r\n                    <input type=\"text\" class=\"form-control\" [placeholder]=\"'FILTER.SEARCH.PLACEHOLDER' | translate\"\r\n                        aria-label=\"'FILTER.SEARCH.RESET' | translate\" [(ngModel)]=\"searchText\" (keyup)=\"searchOptions($event.target.value)\">\r\n                    <i *ngIf=\"searchText; else search\" [title]=\"'FILTER.SEARCH.RESET' | translate\"\r\n                        class=\"material-icons md-18 sk-reset-search cursor-pointer\"\r\n                        (click)=\"searchOptions('')\">close</i>\r\n                    <ng-template #search>\r\n                        <i [title]=\"'FILTER.SEARCH.TITLE' | translate\" class=\"material-icons md-18\">search</i>\r\n                    </ng-template>\r\n                </div>\r\n            </div>\r\n            <div class=\"border-bottom sk-input-icon ps-1 pe-1 pt-2 pb-2 bg-gray-50\">\r\n                <span class=\"pl-100\" translate>FILTER.{{model.filterName.toUpperCase()}}.EMPTY_VALUE</span>\r\n            </div>\r\n            <div class=\"border-bottom p-0 sk-scroll-container\" style=\"max-height:280px;\">\r\n                <sk-filter-options-item\r\n                    NgbDropdownItem\r\n                    class=\"dropdown-item p-0 justify-content-between align-self-stretch\" *ngFor=\"let item of model.options\"\r\n                    [item]=\"item\" [isNameTranslatable]=\"isOptionTranslatable\"\r\n                    [filterName]=\"model.filterName\" (optionSelected)=\"handleFilter()\"\r\n                    [ngbTooltip]=\"isOptionTranslatable === true ? (item | filterOptionName: model.filterName) : item.value\"\r\n                    [openDelay]=\"300\"\r\n                    [container]=\"tooltipContainer\">\r\n                </sk-filter-options-item>\r\n            </div>\r\n            <sk-filter-clear-button (clear)=\"resetFilter()\"></sk-filter-clear-button>\r\n        </div>\r\n    </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$3.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }, { kind: "directive", type: i1$1.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i1$1.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i1$1.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "directive", type: i1$1.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "popperOptions", "triggers", "positionTarget", "container", "disableTooltip", "tooltipClass", "tooltipContext", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: FilterLabelComponent, selector: "sk-filter-label", inputs: ["filterName", "isNameTranslatable", "width", "model"] }, { kind: "component", type: FilterOptionsItemComponent, selector: "sk-filter-options-item", inputs: ["filterName", "item", "isNameTranslatable"], outputs: ["optionSelected"] }, { kind: "component", type: ClearFilterButtonComponent, selector: "sk-filter-clear-button", outputs: ["clear"] }, { kind: "pipe", type: i2$3.TranslatePipe, name: "translate" }, { kind: "pipe", type: FilterOptionNamePipe, name: "filterOptionName" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BaseFilterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sk-base-filter', template: "<div ngbDropdown class=\"btn-group\">\r\n    <sk-filter-label\r\n        ngbDropdownToggle\r\n        class=\"d-flex align-items-center pr-100\"\r\n        [isNameTranslatable]=\"isOptionTranslatable\"\r\n        [filterName]=\"model.filterName\"\r\n        [model]=\"model\"\r\n        [width]=\"width\">\r\n    </sk-filter-label>\r\n    <div ngbDropdownMenu>\r\n        <div class=\"card border-0\">\r\n            <div [hidden]=\"!withSearch\" class=\"border-bottom sk-input-icon px-1 py-2\">\r\n                <div class=\"sk-input-group\">\r\n                    <input type=\"text\" class=\"form-control\" [placeholder]=\"'FILTER.SEARCH.PLACEHOLDER' | translate\"\r\n                        aria-label=\"'FILTER.SEARCH.RESET' | translate\" [(ngModel)]=\"searchText\" (keyup)=\"searchOptions($event.target.value)\">\r\n                    <i *ngIf=\"searchText; else search\" [title]=\"'FILTER.SEARCH.RESET' | translate\"\r\n                        class=\"material-icons md-18 sk-reset-search cursor-pointer\"\r\n                        (click)=\"searchOptions('')\">close</i>\r\n                    <ng-template #search>\r\n                        <i [title]=\"'FILTER.SEARCH.TITLE' | translate\" class=\"material-icons md-18\">search</i>\r\n                    </ng-template>\r\n                </div>\r\n            </div>\r\n            <div class=\"border-bottom sk-input-icon ps-1 pe-1 pt-2 pb-2 bg-gray-50\">\r\n                <span class=\"pl-100\" translate>FILTER.{{model.filterName.toUpperCase()}}.EMPTY_VALUE</span>\r\n            </div>\r\n            <div class=\"border-bottom p-0 sk-scroll-container\" style=\"max-height:280px;\">\r\n                <sk-filter-options-item\r\n                    NgbDropdownItem\r\n                    class=\"dropdown-item p-0 justify-content-between align-self-stretch\" *ngFor=\"let item of model.options\"\r\n                    [item]=\"item\" [isNameTranslatable]=\"isOptionTranslatable\"\r\n                    [filterName]=\"model.filterName\" (optionSelected)=\"handleFilter()\"\r\n                    [ngbTooltip]=\"isOptionTranslatable === true ? (item | filterOptionName: model.filterName) : item.value\"\r\n                    [openDelay]=\"300\"\r\n                    [container]=\"tooltipContainer\">\r\n                </sk-filter-options-item>\r\n            </div>\r\n            <sk-filter-clear-button (clear)=\"resetFilter()\"></sk-filter-clear-button>\r\n        </div>\r\n    </div>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: FilterService }, { type: i2.ActivatedRoute }, { type: i2.Router }, { type: LoggerService }], propDecorators: { model: [{
                type: Input
            }], width: [{
                type: Input
            }], withSearch: [{
                type: Input
            }], isOptionTranslatable: [{
                type: Input
            }], initState: [{
                type: Input
            }], tooltipContainer: [{
                type: Input
            }], filterChange: [{
                type: Output
            }] } });

class FilterGroupComponent {
    constructor(filterService) {
        this.filterService = filterService;
        this.destroy$ = new Subject();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    ngOnInit() {
        this.filterService.init(this.items);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterGroupComponent, deps: [{ token: FilterService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: FilterGroupComponent, selector: "sk-filter-group", inputs: { items: "items" }, providers: [FilterService], ngImport: i0, template: `<ng-content></ng-content>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FilterGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sk-filter-group',
                    template: `<ng-content></ng-content>`,
                    providers: [FilterService]
                }]
        }], ctorParameters: () => [{ type: FilterService }], propDecorators: { items: [{
                type: Input
            }] } });

class TableFiltersModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableFiltersModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: TableFiltersModule, declarations: [BaseFilterComponent,
            FilterLabelComponent,
            FilterOptionsItemComponent,
            ClearFilterButtonComponent,
            FilterLabelNamePipe,
            FilterLabelVisibilityPipe,
            FilterOptionNamePipe,
            FilterOptionsCountPipe,
            FilterGroupComponent], imports: [CommonModule,
            TranslateModule,
            NgbModule,
            BadgesModule,
            FormsModule$1], exports: [BaseFilterComponent,
            FilterLabelComponent,
            FilterOptionsItemComponent,
            ClearFilterButtonComponent,
            FilterLabelNamePipe,
            FilterLabelVisibilityPipe,
            FilterOptionNamePipe,
            FilterOptionsCountPipe,
            FilterGroupComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableFiltersModule, imports: [CommonModule,
            TranslateModule,
            NgbModule,
            BadgesModule,
            FormsModule$1] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TableFiltersModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TranslateModule,
                        NgbModule,
                        BadgesModule,
                        FormsModule$1,
                    ],
                    declarations: [
                        BaseFilterComponent,
                        FilterLabelComponent,
                        FilterOptionsItemComponent,
                        ClearFilterButtonComponent,
                        FilterLabelNamePipe,
                        FilterLabelVisibilityPipe,
                        FilterOptionNamePipe,
                        FilterOptionsCountPipe,
                        FilterGroupComponent,
                    ],
                    exports: [
                        BaseFilterComponent,
                        FilterLabelComponent,
                        FilterOptionsItemComponent,
                        ClearFilterButtonComponent,
                        FilterLabelNamePipe,
                        FilterLabelVisibilityPipe,
                        FilterOptionNamePipe,
                        FilterOptionsCountPipe,
                        FilterGroupComponent,
                    ]
                }]
        }] });

var FeatureFlags;
(function (FeatureFlags) {
    FeatureFlags["HelpCenterWidget"] = "portal.helpcenter";
})(FeatureFlags || (FeatureFlags = {}));

class FeatureFlagsService {
    constructor(http, routeResolverService) {
        this.http = http;
        this.routeResolverService = routeResolverService;
    }
    /**
     * @param {flag} The feature flag to request from the Azure function.
     * @param {source} The source app config that the feature flag should be loaded from.
     */
    isFlagEnabled(flag, source) {
        return this.getFeatureFlag(flag, source)
            .pipe(map(result => result.isActive), catchError(() => {
            return of(false);
        }));
    }
    getFeatureFlag(featureFlagName, source) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = `${this.routeResolverService.generatePipeRoute(AppId.PlatformApis)}features/${source}/isactive?flag=${featureFlagName}`;
        return this.http.get(url, { headers })
            .pipe(catchError(this.getFeatureFlagError));
    }
    getFeatureFlagError(errorResponse) {
        console.error('FeatureFlagsService error: ', errorResponse);
        return throwError(errorResponse);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FeatureFlagsService, deps: [{ token: i1$2.HttpClient }, { token: RouteResolverService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FeatureFlagsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: FeatureFlagsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.HttpClient }, { type: RouteResolverService }] });

var FeatureFlagSource;
(function (FeatureFlagSource) {
    FeatureFlagSource["Platform"] = "platform";
})(FeatureFlagSource || (FeatureFlagSource = {}));

class ProgressBarClassPipe {
    constructor() {
        this.map = new Map([
            ['error', 'bg-danger'],
            ['paused', 'disabled'],
            ['indeterminate', 'progress-bar-striped']
        ]);
    }
    transform(obj, args) {
        return this.map.get(obj);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ProgressBarClassPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: ProgressBarClassPipe, name: "ProgressBarGetClass" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ProgressBarClassPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ProgressBarGetClass'
                }]
        }] });

class ProgressBarComponent {
    constructor() {
        /**
         * 0 - 100 values.
         * value is alway 100 when state is set to `indeterminate` .
         */
        this.value = 0;
    }
    get elementClass() {
        return this.state === 'error' ? 'is-invalid mb-100' : null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ProgressBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: ProgressBarComponent, selector: "sk-progress-bar", inputs: { elementClass: ["class", "elementClass"], state: "state", message: "message", animated: "animated", value: "value" }, host: { properties: { "class": "this.elementClass" }, classAttribute: "progress-group" }, ngImport: i0, template: `<span class="status-label" [innerText]="message || value + '%'"></span>
                    <div class="progress">
                    <div class="progress-bar" [ngClass]="state | ProgressBarGetClass" [class.progress-bar-animated]="animated"  role="progressbar"
                    [style.width.%]="state === 'indeterminate'? 100 :value" [attr.aria-valuenow]="value" aria-valuemin="0" aria-valuemax="100"></div>
                </div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "pipe", type: ProgressBarClassPipe, name: "ProgressBarGetClass" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ProgressBarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sk-progress-bar',
                    host: { 'class': 'progress-group' },
                    template: `<span class="status-label" [innerText]="message || value + '%'"></span>
                    <div class="progress">
                    <div class="progress-bar" [ngClass]="state | ProgressBarGetClass" [class.progress-bar-animated]="animated"  role="progressbar"
                    [style.width.%]="state === 'indeterminate'? 100 :value" [attr.aria-valuenow]="value" aria-valuemin="0" aria-valuemax="100"></div>
                </div>`
                }]
        }], propDecorators: { elementClass: [{
                type: Input,
                args: ['class']
            }, {
                type: HostBinding,
                args: ['class']
            }], state: [{
                type: Input
            }], message: [{
                type: Input
            }], animated: [{
                type: Input
            }], value: [{
                type: Input
            }] } });

class ProgressBarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ProgressBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: ProgressBarModule, declarations: [ProgressBarComponent,
            ProgressBarClassPipe], imports: [CommonModule], exports: [ProgressBarComponent,
            ProgressBarClassPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ProgressBarModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ProgressBarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ProgressBarComponent,
                        ProgressBarClassPipe
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        ProgressBarComponent,
                        ProgressBarClassPipe
                    ]
                }]
        }] });

// ZenDesk offers a number of embeddables (for example, the help center embeddable, a chat embeddable, and a ticket embeddable).
// This module provides a service for working with the help center embeddable.
class HelpCenterService {
    constructor(document, http, routeResolverService, featureFlagService) {
        this.document = document;
        this.http = http;
        this.routeResolverService = routeResolverService;
        this.featureFlagService = featureFlagService;
    }
    async init() {
        this.featureFlagService.isFlagEnabled(FeatureFlags.HelpCenterWidget, FeatureFlagSource.Platform).subscribe((isEnabled) => {
            if (isEnabled) {
                this.getJWT().subscribe((x) => {
                    this.configureHelpCenterWidget(x);
                    this.createHelpCenterWidgetScript();
                });
            }
            else {
                console.log('Help center feature is disabled.');
            }
        });
    }
    createHelpCenterWidgetScript() {
        const elementId = 'ze-snippet';
        const script = this.document.createElement('script');
        script.src =
            'https://static.zdassets.com/ekr/snippet.js?key=641dae67-291a-4f48-98b9-81e4d99d59e2';
        script.id = elementId;
        if (!this.document.getElementById(elementId)) {
            this.document.head.appendChild(script);
        }
    }
    configureHelpCenterWidget(jwt) {
        if (window && window.hasOwnProperty('zESettings')) {
            return;
        }
        //When testing locally generate a jwt and set it below.
        //jwt = "";
        const scriptElement = this.document.createElement('script');
        scriptElement.type = 'text/javascript';
        const code = `
             window.zESettings = {
               webWidget: {
                authenticate: {
                   jwtFn: function (callback) {
                     callback("${jwt}");
                   },
                 },
                 helpCenter: {
                    filter: {
                      label_names: '${this.getLabelsByProduct()}'
                    }
                  }
               },
             };
            `;
        try {
            scriptElement.appendChild(this.document.createTextNode(code));
            this.document.head.appendChild(scriptElement);
        }
        catch (e) {
            scriptElement.text = code;
            this.document.head.appendChild(scriptElement);
        }
    }
    getLabelsByProduct() {
        let labels = "";
        let origin = window.origin;
        if (labels.length === 0) {
            if (origin.includes("portal")) {
                labels = "HomePageWidget";
            }
            else if (origin.includes("migrate")) {
                labels = "MigrationsPage";
            }
            else if (origin.includes("backup")) {
                labels = "CloudBackupPage";
            }
            else if (origin.includes("cm")) {
                labels = "CloudManagerPage,SecurityManagerPage";
            }
            else if (origin.includes("localhost")) {
                labels = "HomePageWidget,CloudBackupPage,MigrationsPage,CloudManagerPage,SecurityManagerPage";
            }
        }
        return labels;
    }
    getJWT() {
        let papiRoute = this.routeResolverService.generatePipeRoute(AppId.Papi);
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http
            .get(`${papiRoute}common/zendesk/sso/jwt`, { headers })
            .pipe(catchError(this.onGetJWTError));
    }
    onGetJWTError(errorResponse) {
        return throwError(errorResponse);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HelpCenterService, deps: [{ token: DOCUMENT }, { token: i1$2.HttpClient }, { token: RouteResolverService }, { token: FeatureFlagsService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HelpCenterService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: HelpCenterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1$2.HttpClient }, { type: RouteResolverService }, { type: FeatureFlagsService }] });

/*
 * Public API Surface of @skykick/core
 */
/*
 * Navigation
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AccordionBodyDirective, AccordionComponent, AccordionHeaderDirective, AccordionItemDirective, AccordionModule, AccordionTitleDirective, ActionMenuComponent, ActionMenuModule, AlertsFilterStatus, ApiConfigService, ApiEndpointIdentifier, AppId, AvatarComponent, AvatarModule, AvatarShape, AvatarSize, BadgeComponent, BadgeType, BadgesModule, BannerContainerComponent, BannerModule, BaseButton, BaseDropdown, BaseFeedback, BaseFilterComponent, BasicInputComponent, BasicSearchControlComponent, BrandingLogoComponent, ButtonDirective, ButtonType, ButtonsModule, CacheService, CanDeactivateGuard, CanDeactivatePage, CardBodyDirective, CardComponent, CardFooterDirective, CardHeaderComponent, CardModule, CardStatusComponent, CardTitleComponent, ClearFilterButtonComponent, ComboButtonComponent, ContactSalesDirective, CreditCardComponent, CreditCardModule, CrmPattern, CustomerPaymentProfileService, CustomerPaymentProfilesComponent, DateBucket, DateBucketService, DedupePipe, DestructiveModalComponent, DownloadService, DropdownComponent, DropdownType, DropdownsModule, EMAIL_REGEX, EmptyPanelComponent, ErrorHandlingModule, ErrorModalComponent, EventHub, FeatureFlagSource, FeatureFlags, FeatureFlagsService, FeedbackComponent, FeedbackModule, FeedbackType, FilterGroupComponent, FilterLabelComponent, FilterLabelNamePipe, FilterLabelVisibilityPipe, FilterModelBase, FilterOptionNamePipe, FilterOptionsCountPipe, FilterOptionsItemComponent, FilterPipe, FilterService, FilterSortButtonComponent, FilterSortModule, FilterSortPanelComponent, FolderPathControlComponent, FormsModule, HeaderComponent, HeaderModule, HelpCenterService, HelpPopoverComponent, HubspotFormsModule, HumanizePipe, IconButtonComponent, IconButtonType, IconComponent, IconTheme, IconsModule, InputModule, LayoutModule, LoadMoreListComponent, LoadMoreListModule, LoggerService, MfaRequiredComponent, ModalBodyComponent, ModalComponent, ModalFooterComponent, ModalHeaderComponent, ModalType, ModalsModule, MultiSelectInfo, MultiSelectPanelComponent, NavigationArea, NavigationComponent, NavigationGuard, NavigationGuardData, NavigationItem, NavigationModule, NavigationPermission, NoAccessComponent, NoDetailsComponent, NotFoundComponent, NotLicensedComponent, NotificationsBellComponent, NotificationsComponent, NotificationsControlService, NotificationsFilterPipe, NotificationsModule, NotificationsSearchComponent, NotificationsSortableHeaderDirective, Operation, OperationCancelledError, OperationCancelledType, OperationViewBase, OverlayComponent, OverlayService, POSTAL_CODE_REGEX, PRODUCT_NAME_TOKEN, Page, PageHeaderComponent, PageHeaderNavComponent, PageHeaderNavItemComponent, PageHeaderToolbarComponent, PagePanel, PagePanelComponent, PagePanelItemComponent, PagePanelService, PageableResourceCollectionManager, PageableResourceCollectionManagerBuilder, PanelCloseEvent, PanelComponent, PanelDismissEvent, PanelEvent, PanelOpenEvent, PanelSearchComponent, PanelTabViewComponent, PanelViewComponent, PanelViewportOutlet, PartnerModule, PartnerService, PlatformNavigationComponent, PlatformNavigationPrimaryComponent, PlatformNavigationSecondaryComponent, PlatformNavigationService, PopoverModule, PricingContactRequestComponent, ProductTypes, ProfilePictureComponent, ProgressBarClassPipe, ProgressBarComponent, ProgressBarModule, QueryOptions, ResourceCollectionManager, ResourceCollectionManagerBuilder, ResourceTableComponent, RouteResolverService, ScopedTaskBase, ScopedTaskHandle, SeverityTypes, SimpleModalComponent, SimpleResourceCollectionManager, SimpleResourceCollectionManagerBuilder, SkyKickDatePipe, SkyKickModal, SkyKickModalResult, SkyKickModalService, SkyKickPanelTabView, SkyKickPanelTabViewBase, SkyKickPattern, SkyKickProductPage, SkyKickTabViewContainer, SkyKickDateService as SkykickDateService, SkykickPlatformPage, SortDirective, SortOperation, SortableProperty, SortingModule, SortingRack, SortingRackBuilder, SpinnerButtonComponent, StatusSpinnerModule, StopPropagationDirective, SuccessModalComponent, SwitchComponent, TableCapDirective, TableEmptyComponent, TableFiltersModule, TableHandleComponent, TableLoaderComponent, TableWrapDirective, TablesModule, TagsInputComponent, TagsInputEmailComponent, TagsInputViewMode, Task, TaskManagerFactoryService, TaskManagerService, TasksModule, TextSpinnerComponent, ToastsModule, ToolbarBtnComponent, ToolbarComponent, ToolbarModule, TransparentInputComponent, TreeBuilder, TreeControl, TreeNode, TreeViewComponent, TreeViewModule, UniversalTaskButtonDirective, UtilityModule, ViewTask, WarningModalComponent, formatPattern, getCustomFlattenedPermissionName, getFlattenedPermissionName, populateContentWithPlaceholders };
